import React, {
  useCallback,
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  AreaSeries,
  LastPriceAnimationMode,
  createChart,
  createSeriesMarkers,
  HistogramSeries,
  LineSeries,
} from "lightweight-charts";
import * as XLSX from "xlsx";
import ChartMirrorPane from "./ChartMirrorPane";
import { EfficiencyRegimePrimitive } from "./efficiencyRegimePrimitive";
import {
  decodeHMMRegimeBars,
  detectHMMRegimes,
  HMM_LOOKBACK_OPTIONS,
  HMM_MODEL_OPTIONS,
  HMM_TRAINING_SPLITS,
} from "./hmmRegimes";
import {
  DEFAULT_D_REGIME_SETTINGS,
  D_REGIME_SETTING_DEFS,
  detectDRegimes,
} from "./dRegimes";
import { detectBOCPDRegimes, detectGMMRegimes } from "./regimeModels";
import {
  CHART_CROSSHAIR_OPTIONS,
  CHART_INTERACTION_DEFAULTS,
  CHART_PRICE_SCALE_OPTIONS,
  attachChartWheelPan,
  chartLocalizationForPriceScale,
  crosshairModeFromValue,
  priceScaleModeFromValue,
} from "./chartDisplayConstants";
import {
  CHART_TIME_ZONE_OPTIONS,
  DEFAULT_CHART_TIME_ZONE,
  formatChartTickMark,
  formatChartTimeZoneLabel,
  formatChartTooltipTime,
} from "./chartTimeZoneUtils";
import {
  aggregateOhlcToPeriod,
  CUSTOM_TIMEFRAME_ID,
  CUSTOM_TIMEFRAME_UNITS,
  customTimeframeToSeconds,
  resolvedBarPeriodSeconds,
  TIMEFRAME_OPTIONS,
} from "./ohlcAggregation";
import { addMainPriceSeries, applyMainPriceSeriesColors } from "./chartPriceSeries";
import {
  computeRatioCandles,
  defaultRangeSize,
  defaultRenkoBrickSize,
  getPriceChartPayload,
  KAGI_MODE,
  LINE_CHART_LINE_TYPE,
  LINE_CHART_LINE_TYPE_OPTIONS,
  LINE_PRICE_SOURCE,
  LINE_PRICE_SOURCE_OPTIONS,
  PRICE_SERIES_OPTIONS,
  PRICE_SERIES_STYLES,
  RENKO_BRICK_MODE,
} from "./seriesTransforms";
import { runUserScript } from "./userScriptRunner";
import useIsMobile from "./useIsMobile";
import MobileChartChrome from "./MobileChartChrome";
import LivePriceTicker from "./LivePriceTicker";
import ChartLoadingOverlay from "./ChartLoadingOverlay";
import RangeGauge from "./RangeGauge";
import DrawingLayer from "./DrawingLayer";
import { moveDrawing } from "./drawingGeometry";
import {
  anchorDrawing,
  loadSavedDrawings,
  magnetPrice,
  makeTimeAxis,
  resyncDrawing,
  saveDrawings,
} from "./drawingAnchors";
import { publishChartData } from "./quantStore";
import { useAuth } from "../auth/AuthContext";
import { MARKET_API_BASE, MARKET_WS_BASE, getAuthToken, marketFetch, openMarketSocket } from "../backendConfig";
import { categorizeSymbol, SYMBOL_CATEGORY_ORDER } from "./symbolCategories";
import sampleData from "../SampleData/sampleData";
import {
  calculateADX,
  calculateATR,
  calculateAccumulationDistribution,
  calculateAwesomeOscillator,
  calculateBollingerBands,
  calculateCCI,
  calculateChaikinVolatility,
  calculateCMF,
  calculateDEMA,
  calculateDonchianChannels,
  calculateEMA,
  calculateHMA,
  calculateIchimoku,
  calculateKaufmanEfficiencyRatio,
  calculateKeltner,
  calculateMACD,
  calculateMFI,
  calculateMomentum,
  calculateOBV,
  calculateParabolicSAR,
  calculateROC,
  calculateRollingStdDev,
  calculateRSI,
  calculateSMA,
  calculateStochastic,
  calculateStochRSI,
  calculateTEMA,
  calculateTSI,
  calculateVWAP,
  calculateVolumeOscillator,
  calculateWMA,
  calculateWilliamsR,
  dataHasRealVolume,
} from "./indicatorCalculations";
import { OSCILLATOR_ORDER_KEYS } from "./indicatorRegistry";
import { groupIndicatorDefinitions } from "./indicatorCatalog";
import {
  GO_INDICATORS,
  GO_INDICATOR_KEYS,
  GO_DEFAULT_COLORS,
  toSeriesPoints,
} from "./goIndicators";
import {
  ORDERFLOW_DEFAULT_ON,
  ORDERFLOW_SIGNALS,
  analyzeOrderflow,
  buySellVwap,
  marketProfileFromCandles,
  orderflowMarkers,
  volumeProfile,
} from "./orderflow/orderflowSignals";
import { BarStatsPrimitive, OrderflowPrimitive } from "./orderflow/orderflowPrimitives";
import {
  buildPatternMarkers,
  DEFAULT_PATTERN_TOGGLES,
  scanAllPatterns,
} from "./candlestickPatterns";

// eslint-disable-next-line no-console
console.log("[TZ-MARKER-v3] Chart.js module loaded at", new Date().toISOString());

const EMPTY_CANDLES = [];

// Keep the loaded chart history alive when the chart route is temporarily unmounted
// while switching terminal sections (for example, Scanner back to Chart).
let retainedMarketChartState = null;

const DEFAULT_CCI_PERIOD = 20;
const DEFAULT_WILLIAMS_PERIOD = 14;
const DEFAULT_ROC_PERIOD = 12;
const DEFAULT_MOM_PERIOD = 10;
const DEFAULT_STOCH_RSI_RSI = 14;
const DEFAULT_STOCH_RSI_STOCH = 14;
const DEFAULT_STOCH_RSI_K = 3;
const DEFAULT_STOCH_RSI_D = 3;
const DEFAULT_ADX_PERIOD = 14;
const DEFAULT_HMA_PERIOD = 16;
const DEFAULT_DEMA_PERIOD = 21;
const DEFAULT_TEMA_PERIOD = 21;
const DEFAULT_KELT_EMA = 20;
const DEFAULT_KELT_ATR = 10;
const DEFAULT_KELT_MULT = 2;
const DEFAULT_VOLUME_OSC_FAST = 5;
const DEFAULT_VOLUME_OSC_SLOW = 10;
const DEFAULT_CHAIKIN_VOL_EMA = 10;
const DEFAULT_CHAIKIN_VOL_ROC = 10;
const DEFAULT_STDEV_PERIOD = 20;
const DEFAULT_KAUFMAN_RANGE_PERIOD = 20;
const DEFAULT_KAUFMAN_RANGE_SMOOTH = 5;
const DEFAULT_ICH_TENKAN = 9;
const DEFAULT_ICH_KIJUN = 26;
const DEFAULT_ICH_SENKOU_B = 52;
const DEFAULT_ICH_DISP = 26;

const USER_SCRIPT_DEFAULT = `return {
  pane: "price",
  series: [
    { name: "SMA(20)", data: helpers.sma(candles, 20) },
  ],
};`;

const findClosestCandleByTime = (candles, time) => {
  if (!candles?.length || !Number.isFinite(time)) {
    return null;
  }

  let best = candles[0];
  let bestDist = Math.abs(candles[0].time - time);

  for (let i = 1; i < candles.length; i += 1) {
    const d = Math.abs(candles[i].time - time);

    if (d < bestDist) {
      bestDist = d;
      best = candles[i];
    }
  }

  return best;
};

const TIME_COLUMN_CANDIDATES = ["timestamp", "date", "datetime", "time"];
const ZOOM_FACTOR = 0.2;
const PAN_FACTOR = 0.33;
const DEFAULT_MA_PERIOD = 20;
const DEFAULT_SMA_PERIOD = 20;
const DEFAULT_EMA_PERIOD = 20;
const DEFAULT_WMA_PERIOD = 20;
const DEFAULT_BB_PERIOD = 20;
const DEFAULT_BB_DEVIATION = 2;
const DEFAULT_RSI_PERIOD = 14;
const DEFAULT_RSI_UPPER = 70;
const DEFAULT_RSI_LOWER = 30;
const DEFAULT_ATR_PERIOD = 14;
const DEFAULT_DONCHIAN_PERIOD = 20;
const DEFAULT_STOCH_K_PERIOD = 14;
const DEFAULT_STOCH_D_PERIOD = 3;
const DEFAULT_STOCH_UPPER = 80;
const DEFAULT_STOCH_LOWER = 20;
const DEFAULT_MACD_FAST_PERIOD = 12;
const DEFAULT_MACD_SLOW_PERIOD = 26;
const DEFAULT_MACD_SIGNAL_PERIOD = 9;

const MAIN_PANE_STRETCH = 3;
const OSCILLATOR_PANE_STRETCH = 1;
const REPLAY_WINDOW_BARS = 80;
const REPLAY_INTERVAL_MS = 500;
const REPLAY_HOLD_DELAY_MS = 220;
const BACKEND_API_BASE = MARKET_API_BASE;
const BACKEND_STREAM_BASE = MARKET_WS_BASE;
const BACKEND_DEFAULT_SYMBOL = "EURUSD";
const BACKEND_DEFAULT_TF = "1m";
const BACKEND_SYMBOL_FALLBACK = ["EURUSD", "GBPUSD", "USDJPY", "BTCUSD", "ETHUSD"];
function clearBacktestOverlayFromMain() {
  try {
    window.localStorage.setItem("lwt.backtestShowOnMain", "0");
    window.localStorage.removeItem("lwt.backtestTrades");
    window.dispatchEvent(new Event("lwt:backtest-overlay-updated"));
    window.dispatchEvent(new Event("lwt:backtest-overlay-cleared"));
  } catch { /* storage can be disabled */ }
}
/** Symbol search groups results under these headers (in this order), matching
 * TradingView's categorized search. Known symbols are mapped explicitly; anything else
 * falls back to a pattern-based guess so a live/real symbol list still groups sensibly. */
/** Trigger an older-history backfill once the visible range gets within this many bars
 * of the start of what's currently loaded (backend feeds only load the most recent
 * `limit` bars up front; brokers like cTrader hold years more than that). */
const BACKFILL_LOGICAL_THRESHOLD = 50;

export const formatPrice = (value) => {
  if (!Number.isFinite(value)) return "-";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
};

const stripFileExtension = (name) => (name ? name.replace(/\.[^./]+$/, "") : name);

const formatPercentage = (value) => {
  if (!Number.isFinite(value)) return "-";
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
};

const formatTimestamp = (time, timeZone = "UTC") => {
  if (!Number.isFinite(time)) return "-";
  if (String(timeZone || "UTC").toUpperCase() === "UTC") {
    const d = new Date(time * 1000);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${months[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(
      2,
      "0"
    )}, ${d.getUTCFullYear()}, ${String(d.getUTCHours()).padStart(
      2,
      "0"
    )}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  }

  const d = new Date(time * 1000);
  try {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    });
  } catch {
    // An unrecognized zone must fail safe to UTC (the original, unconverted timestamp)
    // rather than throw and blank out the hover/replay/measure display.
    return formatTimestamp(time, "UTC");
  }
};

const formatDateLabel = (value) => {
  if (!value) return "Select date";

  const parsedDate = new Date(`${value}T00:00:00Z`);

  if (Number.isNaN(parsedDate.getTime())) {
    return "Select date";
  }

  return parsedDate.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const normalizeEpochSeconds = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return NaN;
  if (Math.abs(n) > 100000000000) {
    return Math.floor(n / 1000);
  }
  return Math.floor(n);
};

const waitForRetry = (ms, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    const error = new Error("Request aborted");
    error.name = "AbortError";
    reject(error);
    return;
  }
  const finish = () => {
    signal?.removeEventListener("abort", abort);
    resolve();
  };
  const timer = window.setTimeout(finish, ms);
  const abort = () => {
    window.clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
    const error = new Error("Request aborted");
    error.name = "AbortError";
    reject(error);
  };
  signal?.addEventListener("abort", abort, { once: true });
});

async function fetchCandlesWithWakeRetry(url, signal, onRetry) {
  const delays = [5000, 10000, 15000, 20000, 20000];
  let lastError = null;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    try {
      const res = await marketFetch(url, { signal });
      const payload = await res.json().catch(() => null);
      if (res.ok) return payload;
      const error = new Error(payload?.detail || `HTTP ${res.status}`);
      error.status = res.status;
      if (![502, 503, 504].includes(res.status) || attempt === delays.length) throw error;
      lastError = error;
    } catch (error) {
      if (error?.name === "AbortError" || signal?.aborted) throw error;
      if (error?.status && ![502, 503, 504].includes(error.status)) throw error;
      if (attempt === delays.length) throw error;
      lastError = error;
    }
    onRetry?.(attempt + 1, delays[attempt]);
    await waitForRetry(delays[attempt], signal);
  }
  throw lastError || new Error("Market data is temporarily unavailable.");
}

const normalizeBackendCandles = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const t = normalizeEpochSeconds(row?.time);
      const o = Number(row?.open);
      const h = Number(row?.high);
      const l = Number(row?.low);
      const c = Number(row?.close);
      const v = Number(row?.volume);
      if (
        !Number.isFinite(t) ||
        !Number.isFinite(o) ||
        !Number.isFinite(h) ||
        !Number.isFinite(l) ||
        !Number.isFinite(c)
      ) {
        return null;
      }
      const candle = {
        time: t,
        open: o,
        high: h,
        low: l,
        close: c,
        dataSource: typeof row?.data_source === "string" ? row.data_source : "unknown",
        dataMode: typeof row?.data_mode === "string" ? row.data_mode : "unknown",
        isSimulated: typeof row?.is_simulated === "boolean" ? row.is_simulated : null,
        dataStatus: typeof row?.data_status === "string" ? row.data_status : "unknown",
        volumeKind: typeof row?.volume_kind === "string" ? row.volume_kind : "unknown",
      };
      if (Number.isFinite(v) && v >= 0) {
        candle.volume = v;
      }
      return candle;
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time)
    .filter((row, index, arr) => index === 0 || row.time !== arr[index - 1].time);
};

const describeMarketCandle = (candle, { streaming = false } = {}) => {
  if (candle?.isSimulated || candle?.dataStatus === "simulated") {
    return streaming
      ? "Simulated sample feed is updating; these are not live broker prices."
      : "Simulated sample data loaded; these are not live broker prices.";
  }
  const source = candle?.dataSource && candle.dataSource !== "unknown" ? candle.dataSource : "market provider";
  const mode = candle?.dataMode && candle.dataMode !== "unknown" ? ` ${candle.dataMode}` : "";
  const archived = candle?.dataStatus === "cached" ? "Cached " : "";
  if (streaming) return `Receiving ${source}${mode} quote updates.`;
  if (candle?.volumeKind === "broker_reported_bar_volume") {
    return `${archived}${source}${mode} history loaded. Bar volume is broker-reported, not consolidated exchange volume.`;
  }
  return `${archived}${source}${mode} history loaded.`;
};

/** YYYY-MM-DD in UTC calendar (matches chart timestamps and `<input type="date">`). */
const utcYmdFromUnixSeconds = (timeSeconds) => {
  if (!Number.isFinite(timeSeconds)) return "";
  const d = new Date(timeSeconds * 1000);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const YMD_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const unixSecondsFromUtcYmd = (dateValue) => {
  const match = String(dateValue || "").trim().match(YMD_INPUT_RE);
  if (!match) return NaN;
  const [, y, mo, d] = match;
  return Math.floor(Date.UTC(Number(y), Number(mo) - 1, Number(d)) / 1000);
};

const formatInputDate = (time) => {
  if (!Number.isFinite(time)) return "";
  return utcYmdFromUnixSeconds(time);
};

/** Unix seconds for a series point `time` (UTCTimestamp, BusinessDay, or ISO-like string). */
const seriesPointTimeToUnix = (t) => {
  if (t == null) return NaN;
  if (typeof t === "number") {
    return normalizeEpochSeconds(t);
  }
  if (typeof t === "string") {
    const trimmed = t.trim();
    if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
      return normalizeEpochSeconds(trimmed);
    }
    if (YMD_INPUT_RE.test(trimmed)) {
      return unixSecondsFromUtcYmd(trimmed);
    }
    const parsed = Date.parse(t);
    return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : NaN;
  }
  if (typeof t === "object" && "year" in t && "month" in t && "day" in t) {
    const { year, month, day } = t;
    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      return Math.floor(Date.UTC(year, month - 1, day) / 1000);
    }
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

/** Logical index in displayed series for a calendar day (UTC). */
const findLogicalIndexForCalendarDate = (seriesPoints, dateValue) => {
  if (!seriesPoints?.length || !dateValue) return null;
  const target = String(dateValue).trim();
  if (!YMD_INPUT_RE.test(target)) return null;

  const firstOnDay = seriesPoints.findIndex((p) => {
    if (p?.time == null) return false;
    const t = seriesPointTimeToUnix(p.time);
    if (!Number.isFinite(t)) return false;
    return utcYmdFromUnixSeconds(t) === target;
  });
  if (firstOnDay >= 0) return firstOnDay;

  const dayStart = unixSecondsFromUtcYmd(target);
  const firstAfter = seriesPoints.findIndex((p) => {
    if (p?.time == null) return false;
    const t = seriesPointTimeToUnix(p.time);
    return Number.isFinite(t) && t >= dayStart;
  });
  if (firstAfter >= 0) return firstAfter;

  return null;
};

const findNearestSeriesTimeIndex = (seriesPoints, targetTime) => {
  if (!seriesPoints?.length || !Number.isFinite(targetTime)) return 0;
  return seriesPoints.reduce((closestIndex, p, index) => {
    const t = seriesPointTimeToUnix(p.time);
    const bestT = seriesPointTimeToUnix(seriesPoints[closestIndex].time);
    if (!Number.isFinite(t) || !Number.isFinite(bestT)) return closestIndex;
    return Math.abs(t - targetTime) < Math.abs(bestT - targetTime)
      ? index
      : closestIndex;
  }, 0);
};

const normalizeHexColor = (value, fallback) => {
  const normalizedValue = String(value ?? "").trim();

  if (/^#([0-9a-f]{6})$/i.test(normalizedValue)) {
    return normalizedValue;
  }

  return fallback;
};

/** Fading fill / glow under line series. */
const hexToRgba = (hex, alpha) => {
  const h = normalizeHexColor(hex, "#888888");
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const INDICATOR_LINE_WIDTH = 2;
const INDICATOR_FILL_TOP_ALPHA = 0.28;
const INDICATOR_LINE_WIDTH_OPTIONS = [1, 2, 3, 4, 5];
const INDICATOR_KEYS_WITH_LINE_SIZE = new Set([
  "ma",
  "sma",
  "ema",
  "wma",
  "bb",
  "rsi",
  "atr",
  "donchian",
  "stochastic",
  "macd",
  "vwap",
  "hma",
  "dema",
  "tema",
  "ichimoku",
  "sar",
  "keltner",
  "cci",
  "williams",
  "roc",
  "mom",
  "stochRsi",
  "adx",
  "tsi",
  "obv",
  "cmf",
  "mfi",
  "adLine",
  "chaikinVol",
  "volOsc",
  "stdDev",
  "kaufmanRange",
]);

const normalizeIndicatorLineWidth = (value, fallback = INDICATOR_LINE_WIDTH) => {
  const width = Math.round(Number(value));
  if (!Number.isFinite(width)) {
    return fallback;
  }
  return Math.min(5, Math.max(1, width));
};

/** Maps ext-series key → `getIndicatorColor` key when they differ. */
const extSeriesIndicatorColorKey = (seriesKey) => seriesKey;

const syncPaneLineFill = (fillSeries, show, lineData, colorHex) => {
  if (!fillSeries) return;
  if (!show || !lineData?.length) {
    fillSeries.applyOptions({ visible: false });
    fillSeries.setData([]);
    return;
  }
  const c = normalizeHexColor(colorHex, "#888888");
  fillSeries.applyOptions({ visible: true });
  fillSeries.setData(
    lineData.map((p) => ({
      time: p.time,
      value: p.value,
      topColor: hexToRgba(c, INDICATOR_FILL_TOP_ALPHA),
      bottomColor: hexToRgba(c, 0),
    }))
  );
};

/**
 * Indicator types that support stacking multiple independent instances (e.g. 3 EMAs at
 * different periods) on top of their single "base" slot in `indicatorDefinitions` —
 * clicking an already-active entry of one of these types in the indicator picker adds
 * another instance instead of just re-selecting it. Limited to simple price-pane
 * overlays for now; oscillators aren't included yet (they'd need pane-index handling).
 */
const DUPLICABLE_INDICATOR_TYPES = {
  ma: {
    fullLabel: "Moving Average",
    shortLabel: "MA",
    shape: "line",
    paramDefs: [{ key: "period", label: "Period", default: DEFAULT_MA_PERIOD, min: 1, step: 1 }],
    compute: (candles, p) => calculateSMA(candles, p.period),
  },
  sma: {
    fullLabel: "Simple MA",
    shortLabel: "SMA",
    shape: "line",
    paramDefs: [{ key: "period", label: "Period", default: DEFAULT_SMA_PERIOD, min: 1, step: 1 }],
    compute: (candles, p) => calculateSMA(candles, p.period),
  },
  ema: {
    fullLabel: "Exponential MA",
    shortLabel: "EMA",
    shape: "line",
    paramDefs: [{ key: "period", label: "Period", default: DEFAULT_EMA_PERIOD, min: 1, step: 1 }],
    compute: (candles, p) => calculateEMA(candles, p.period),
  },
  wma: {
    fullLabel: "Weighted MA",
    shortLabel: "WMA",
    shape: "line",
    paramDefs: [{ key: "period", label: "Period", default: DEFAULT_WMA_PERIOD, min: 1, step: 1 }],
    compute: (candles, p) => calculateWMA(candles, p.period),
  },
  hma: {
    fullLabel: "Hull MA",
    shortLabel: "HMA",
    shape: "line",
    paramDefs: [{ key: "period", label: "Period", default: DEFAULT_HMA_PERIOD, min: 2, step: 1 }],
    compute: (candles, p) => calculateHMA(candles, p.period),
  },
  dema: {
    fullLabel: "Double EMA",
    shortLabel: "DEMA",
    shape: "line",
    paramDefs: [{ key: "period", label: "Period", default: DEFAULT_DEMA_PERIOD, min: 1, step: 1 }],
    compute: (candles, p) => calculateDEMA(candles, p.period),
  },
  tema: {
    fullLabel: "Triple EMA",
    shortLabel: "TEMA",
    shape: "line",
    paramDefs: [{ key: "period", label: "Period", default: DEFAULT_TEMA_PERIOD, min: 1, step: 1 }],
    compute: (candles, p) => calculateTEMA(candles, p.period),
  },
  vwap: {
    fullLabel: "VWAP",
    shortLabel: "VWAP",
    shape: "line",
    paramDefs: [],
    compute: (candles) => calculateVWAP(candles),
  },
  bb: {
    fullLabel: "Bollinger Bands",
    shortLabel: "BB",
    shape: "band",
    paramDefs: [
      { key: "period", label: "Period", default: DEFAULT_BB_PERIOD, min: 2, step: 1 },
      { key: "deviation", label: "Deviation", default: DEFAULT_BB_DEVIATION, min: 0.1, step: 0.1 },
    ],
    compute: (candles, p) => calculateBollingerBands(candles, p.period, p.deviation),
  },
  keltner: {
    fullLabel: "Keltner Channels",
    shortLabel: "KELT",
    shape: "band",
    paramDefs: [
      { key: "emaPeriod", label: "EMA", default: DEFAULT_KELT_EMA, min: 1, step: 1 },
      { key: "atrPeriod", label: "ATR", default: DEFAULT_KELT_ATR, min: 1, step: 1 },
      { key: "multiplier", label: "Mult", default: DEFAULT_KELT_MULT, min: 0.1, step: 0.1 },
    ],
    compute: (candles, p) => calculateKeltner(candles, p.emaPeriod, p.atrPeriod, p.multiplier),
  },
  donchian: {
    fullLabel: "Donchian Channel",
    shortLabel: "DON",
    shape: "band",
    paramDefs: [{ key: "period", label: "Period", default: DEFAULT_DONCHIAN_PERIOD, min: 1, step: 1 }],
    compute: (candles, p) => calculateDonchianChannels(candles, p.period),
  },
  sar: {
    fullLabel: "Parabolic SAR",
    shortLabel: "SAR",
    shape: "line",
    seriesOptions: { pointMarkersVisible: true },
    paramDefs: [
      { key: "step", label: "Step", default: 0.02, min: 0.001, step: 0.001 },
      { key: "maxStep", label: "Max step", default: 0.2, min: 0.01, step: 0.01 },
    ],
    compute: (candles, p) => calculateParabolicSAR(candles, p.step, p.maxStep),
  },
  ichimoku: {
    fullLabel: "Ichimoku Cloud",
    shortLabel: "ICH",
    shape: "multi",
    paramDefs: [
      { key: "tenkan", label: "Tenkan", default: DEFAULT_ICH_TENKAN, min: 1, step: 1 },
      { key: "kijun", label: "Kijun", default: DEFAULT_ICH_KIJUN, min: 1, step: 1 },
      { key: "senkouB", label: "Senkou B", default: DEFAULT_ICH_SENKOU_B, min: 1, step: 1 },
      { key: "displacement", label: "Displacement", default: DEFAULT_ICH_DISP, min: 1, step: 1 },
    ],
    compute: (candles, p) => calculateIchimoku(candles, p.tenkan, p.kijun, p.senkouB, p.displacement),
  },
};

const MAX_EXTRA_INSTANCES_PER_TYPE = 9; // + the base slot = 10 total, matching the requested cap

const EXTRA_INSTANCE_DEFAULT_COLORS = [
  "#f59e0b",
  "#3b82f6",
  "#ef4444",
  "#10b981",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
  "#eab308",
  "#6366f1",
];

/** Creates the lightweight-charts line series backing one duplicated indicator instance. */
function createExtraInstanceSeriesBundle(chart, def) {
  const base = {
    lastValueVisible: false,
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    lineWidth: INDICATOR_LINE_WIDTH,
    lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
  };
  const mkLine = (extra = {}) => {
    const series = chart.addSeries(LineSeries, { ...base, ...extra });
    series.moveToPane(0);
    return series;
  };
  if (def.shape === "band") {
    return {
      upper: mkLine(),
      middle: mkLine({ lineStyle: 2 }),
      lower: mkLine(),
    };
  }
  if (def.shape === "multi") {
    return {
      tenkan: mkLine({ lineWidth: 2 }),
      kijun: mkLine({ lineWidth: 2 }),
      spanA: mkLine({ lineWidth: 1 }),
      spanB: mkLine({ lineWidth: 1 }),
      chikou: mkLine({ lineWidth: 1, lineStyle: 2 }),
    };
  }
  return { main: mkLine(def.seriesOptions ?? {}) };
}

/** Pushes freshly computed data + color into a duplicated indicator instance's series bundle. */
function applyExtraInstanceSeriesData(def, bundle, data, color) {
  const setLine = (series, points, col) => {
    if (!series) return;
    series.applyOptions({ visible: true, color: col });
    series.setData(Array.isArray(points) ? points.filter((p) => Number.isFinite(p?.value)) : []);
  };
  if (def.shape === "band") {
    setLine(bundle.upper, data?.upper, color);
    setLine(bundle.middle, data?.middle, hexToRgba(color, 0.7));
    setLine(bundle.lower, data?.lower, color);
    return;
  }
  if (def.shape === "multi") {
    setLine(bundle.tenkan, data?.tenkan, color);
    setLine(bundle.kijun, data?.kijun, hexToRgba(color, 0.75));
    setLine(bundle.spanA, data?.spanA, hexToRgba(color, 0.5));
    setLine(bundle.spanB, data?.spanB, hexToRgba(color, 0.35));
    setLine(bundle.chikou, data?.chikou, hexToRgba(color, 0.6));
    return;
  }
  setLine(bundle.main, data, color);
}

function createIndicatorAreaSeries(chart, paletteColor, priceScaleId = "") {
  const base = normalizeHexColor(
    typeof paletteColor === "string" ? paletteColor : "#888888",
    "#888888"
  );
  return chart.addSeries(AreaSeries, {
    lineVisible: false,
    lastValueVisible: false,
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    pointMarkersVisible: false,
    invertFilledArea: false,
    relativeGradient: true,
    topColor: hexToRgba(base, INDICATOR_FILL_TOP_ALPHA),
    bottomColor: hexToRgba(base, 0),
    lineWidth: 1,
    visible: false,
    priceScaleId: priceScaleId || "",
  });
}

/** Simple stroke-based icon wrapper — inherits `currentColor`. */
function StrokeIcon({ size = 16, children, viewBox = "0 0 24 24" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  );
}

function FxIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 18c2 0 2-12 5-12" />
      <path d="M3 12h5" />
      <path d="M12 9l6 10M18 9l-6 10" />
    </StrokeIcon>
  );
}

function ScriptIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M9 4H7a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2h2" />
      <path d="M15 4h2a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-2" />
    </StrokeIcon>
  );
}

function AiSparkIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M12 3v3M12 18v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M3 12h3M18 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
      <circle cx="12" cy="12" r="3" />
    </StrokeIcon>
  );
}

function ReplayIcon(props) {
  return (
    <StrokeIcon {...props}>
      <polygon points="6 4 20 12 6 20 6 4" />
    </StrokeIcon>
  );
}

function RulerIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M3 17l14-14 4 4L7 21z" />
      <path d="M7 13l2 2M10 10l2 2M13 7l2 2" />
    </StrokeIcon>
  );
}

function TrashIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 7h16M10 7V4h4v3M6 7l1 13h10l1-13" />
    </StrokeIcon>
  );
}

function MagnetIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M6 4v8a6 6 0 0 0 12 0V4" />
      <path d="M6 4h4v8a2 2 0 0 0 4 0V4h4" />
      <path d="M6 8h4M14 8h4" />
    </StrokeIcon>
  );
}

function StayDrawIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 20l4-1 10-10-3-3L5 16z" />
      <path d="M13 8l3 3" />
      <path d="M15 20h5M17.5 17.5v5" />
    </StrokeIcon>
  );
}

function UndoIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M9 7L4 12l5 5" />
      <path d="M4 12h11a5 5 0 0 1 0 10h-2" />
    </StrokeIcon>
  );
}

function RedoIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M15 7l5 5-5 5" />
      <path d="M20 12H9a5 5 0 0 0 0 10h2" />
    </StrokeIcon>
  );
}

function CameraIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 8h3l2-2h6l2 2h3v12H4z" />
      <circle cx="12" cy="14" r="3.5" />
    </StrokeIcon>
  );
}

function UploadIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M12 16V4M7 9l5-5 5 5M4 20h16" />
    </StrokeIcon>
  );
}

function FullscreenIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6" />
    </StrokeIcon>
  );
}

function FullscreenExitIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6" />
    </StrokeIcon>
  );
}

/** Drawing toolbar glyphs. */
function DrawTrendIcon(props) { return (<StrokeIcon {...props}><path d="M4 20L20 4" /></StrokeIcon>); }
function DrawHLineIcon(props) { return (<StrokeIcon {...props}><path d="M3 12h18" /><circle cx="12" cy="12" r="1.4" /></StrokeIcon>); }
function DrawVLineIcon(props) { return (<StrokeIcon {...props}><path d="M12 3v18" /><circle cx="12" cy="12" r="1.4" /></StrokeIcon>); }
function DrawRectIcon(props) { return (<StrokeIcon {...props}><rect x="4" y="6" width="16" height="12" rx="1.5" /></StrokeIcon>); }
function DrawRrIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 20L20 4" />
      <rect x="5" y="5" width="8" height="5" fill="currentColor" fillOpacity="0.2" />
      <rect x="12" y="10" width="8" height="9" fill="currentColor" fillOpacity="0.2" />
    </StrokeIcon>
  );
}
function DrawRayIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 20L20 4" />
      <circle cx="4" cy="20" r="1.6" fill="currentColor" />
    </StrokeIcon>
  );
}
function DrawFibIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M3 6h18M3 10h18M3 14h18M3 18h18" strokeOpacity="0.55" />
      <path d="M3 12h18" />
    </StrokeIcon>
  );
}
function DrawFibExtIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M3 5h18M3 9h18M3 13h18M3 17h18M3 21h18" strokeOpacity="0.55" />
      <path d="M3 13h18" />
      <path d="M3 5v16" strokeDasharray="2 2" />
    </StrokeIcon>
  );
}
function DrawArrowIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 20L20 4" />
      <path d="M20 4h-6M20 4v6" />
    </StrokeIcon>
  );
}
function DrawEllipseIcon(props) {
  return (
    <StrokeIcon {...props}>
      <ellipse cx="12" cy="12" rx="8" ry="6" />
    </StrokeIcon>
  );
}
function DrawChannelIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M3 16L19 6" />
      <path d="M5 20L21 10" />
    </StrokeIcon>
  );
}
function DrawPitchforkIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 20L14 10M14 10l-8-4M14 10l7-3M14 10l7 7" />
      <circle cx="4" cy="20" r="1.4" fill="currentColor" />
    </StrokeIcon>
  );
}
function DrawTextIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M5 6h14M12 6v12" />
    </StrokeIcon>
  );
}
function DrawBrushIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 20c3-6 4-9 4-12a4 4 0 118 0c0 5-5 6-5 12" />
      <path d="M9 20h6" />
    </StrokeIcon>
  );
}
function DrawPriceRangeIcon(props) {
  return (
    <StrokeIcon {...props}>
      <path d="M4 7h16M4 17h16M8 7v10M16 7v10" />
    </StrokeIcon>
  );
}

function EyeIcon({ size = 14, off = false }) {
  return (
    <StrokeIcon size={size}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <path d="M4 4l16 16" /> : null}
    </StrokeIcon>
  );
}

/** Watchlist / details / info icons for the right tab rail. */
function ListIcon(props) { return (<StrokeIcon {...props}><path d="M8 6h12M8 12h12M8 18h12" /><circle cx="4" cy="6" r="1.2" /><circle cx="4" cy="12" r="1.2" /><circle cx="4" cy="18" r="1.2" /></StrokeIcon>); }
function InfoIcon(props) { return (<StrokeIcon {...props}><circle cx="12" cy="12" r="9" /><path d="M12 8v.01M11 12h1v5h1" /></StrokeIcon>); }
function NewsIcon(props) { return (<StrokeIcon {...props}><rect x="3" y="5" width="15" height="14" rx="2" /><path d="M7 9h7M7 13h7M7 17h4" /><path d="M18 8h3v9a2 2 0 0 1-2 2h-1" /></StrokeIcon>); }

/** Gear / settings control — stroke uses `currentColor`. */
function SettingsGearIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.09a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

const CANDLE_COLOR_PRESETS = [
  {
    key: "bw",
    label: "Black / White",
    bullishBody: "#111111",
    bullishBorder: "#111111",
    bullishWick: "#111111",
    bearishBody: "#ffffff",
    bearishBorder: "#7a7a7a",
    bearishWick: "#7a7a7a",
    chartBackground: "#ffffff",
  },
  {
    key: "wb",
    label: "White / Black",
    bullishBody: "#ffffff",
    bullishBorder: "#a3a3a3",
    bullishWick: "#a3a3a3",
    bearishBody: "#111111",
    bearishBorder: "#111111",
    bearishWick: "#111111",
    chartBackground: "#000000",
  },
  {
    key: "blue",
    label: "Blue / Light Blue",
    bullishBody: "#2563eb",
    bullishBorder: "#2563eb",
    bullishWick: "#2563eb",
    bearishBody: "#7dd3fc",
    bearishBorder: "#7dd3fc",
    bearishWick: "#7dd3fc",
    chartBackground: "#ffffff",
  },
  {
    key: "rg",
    label: "Red / Green",
    bullishBody: "#16a34a",
    bullishBorder: "#16a34a",
    bullishWick: "#16a34a",
    bearishBody: "#dc2626",
    bearishBorder: "#dc2626",
    bearishWick: "#dc2626",
    chartBackground: "#ffffff",
  },
  {
    key: "gg",
    label: "Green / Light Green",
    bullishBody: "#16a34a",
    bullishBorder: "#16a34a",
    bullishWick: "#16a34a",
    bearishBody: "#86efac",
    bearishBorder: "#86efac",
    bearishWick: "#86efac",
    chartBackground: "#000000",
  },
  {
    key: "teal",
    label: "Teal / Orange",
    bullishBody: "#0f766e",
    bullishBorder: "#14b8a6",
    bullishWick: "#14b8a6",
    bearishBody: "#ea580c",
    bearishBorder: "#fb923c",
    bearishWick: "#fb923c",
    chartBackground: "#ffffff",
  },
  {
    key: "violet",
    label: "Violet / Pink",
    bullishBody: "#7c3aed",
    bullishBorder: "#8b5cf6",
    bullishWick: "#8b5cf6",
    bearishBody: "#db2777",
    bearishBorder: "#ec4899",
    bearishWick: "#ec4899",
    chartBackground: "#ffffff",
  },
  {
    key: "gold",
    label: "Gold / Charcoal",
    bullishBody: "#d97706",
    bullishBorder: "#f59e0b",
    bullishWick: "#f59e0b",
    bearishBody: "#1f2937",
    bearishBorder: "#4b5563",
    bearishWick: "#4b5563",
    chartBackground: "#ffffff",
  },
  {
    key: "ice",
    label: "Ice / Slate",
    bullishBody: "#e0f2fe",
    bullishBorder: "#38bdf8",
    bullishWick: "#38bdf8",
    bearishBody: "#475569",
    bearishBorder: "#64748b",
    bearishWick: "#64748b",
    chartBackground: "#f8fafc",
  },
  {
    // Colour-blind friendly pair from the "instrument panel" redesign.
    key: "bo",
    label: "Blue / Orange",
    bullishBody: "#4f94db",
    bullishBorder: "#4f94db",
    bullishWick: "#4f94db",
    bearishBody: "#db8352",
    bearishBorder: "#db8352",
    bearishWick: "#db8352",
    chartBackground: "#070a12",
  },
];

/** Boxed grid: 1, 2 (side by side), 4 (2×2), 8 (4×2) */
const CHART_BOX_LAYOUT = {
  1: { cols: 1, rows: 1 },
  2: { cols: 2, rows: 1 },
  4: { cols: 2, rows: 2 },
  8: { cols: 4, rows: 2 },
};

/** Shown in grid layout picker (popup list), largest first */
export const CHART_GRID_SIZE_OPTIONS = [8, 4, 2, 1];

/** Timeframe IDs surfaced as quick-access chips (TradingView-style). */
export const QUICK_INTERVAL_IDS = ["1s", "5s", "1m", "5m", "15m", "1h", "4h", "1d", "1w"];
const FIB_RETRACEMENT_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
const FIB_EXTENSION_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.272, 1.618, 2, 2.618];
/** How far a "ray" draws past its second point, in px, to look like it extends indefinitely. */
const RAY_EXTENSION_PX = 4000;
/** TradingView-style bottom date-range picker — sets the chart's visible time range to
 * roughly this many trailing seconds of the most recent loaded bar. "All" fits the whole
 * loaded dataset instead; "YTD" computes seconds-since-Jan-1 dynamically. */
export const DATE_RANGE_PRESETS = [
  { key: "1D", label: "1D", seconds: 86400 },
  { key: "5D", label: "5D", seconds: 5 * 86400 },
  { key: "1M", label: "1M", seconds: 30 * 86400 },
  { key: "3M", label: "3M", seconds: 90 * 86400 },
  { key: "6M", label: "6M", seconds: 182 * 86400 },
  { key: "YTD", label: "YTD", seconds: null },
  { key: "1Y", label: "1Y", seconds: 365 * 86400 },
  { key: "5Y", label: "5Y", seconds: 5 * 365 * 86400 },
  { key: "ALL", label: "All", seconds: null },
];

/** Most order-flow bars kept in memory (older history is added as the chart is scrolled back). */
const OF_MAX_KEPT_BARS = 3000;
/** First order-flow request is small so something shows within seconds; the rest follows right after. */
const OF_FIRST_BARS = 40;

/** Multi-pane grid is under absolute toolbars (z-index 4); inset so pane headers are clickable */
/** Phone layout: the chart sits below the header card and above the bottom bar (MobileChartChrome). */
const MOBILE_STAGE_TOP = 112;
const MOBILE_STAGE_BOTTOM = 84;
const MULTI_GRID_PADDING_TOP = 112;
const MULTI_GRID_PADDING_RIGHT = 132;

export const getChartGridOptionLabel = (n) => {
  switch (n) {
    case 1:
      return "1× — Single chart";
    case 2:
      return "2× — Two panes";
    case 4:
      return "4× — 2×2 grid";
    case 8:
      return "8× — 4×2 grid";
    default:
      return `${n}×`;
  }
};

const CHART_BACKGROUND_PRESETS = [
  { key: "white", label: "White", color: "#ffffff" },
  { key: "black", label: "Black", color: "#000000" },
  { key: "navy", label: "Navy", color: "#07111f" },
  { key: "tradingview", label: "TradingView", color: "#131722" },
  { key: "slate", label: "Slate", color: "#0f172a" },
  { key: "charcoal", label: "Charcoal", color: "#171717" },
  { key: "zinc", label: "Zinc", color: "#18181b" },
  { key: "cream", label: "Cream", color: "#faf8f5" },
  { key: "ice", label: "Ice", color: "#f8fafc" },
  { key: "midnight", label: "Midnight", color: "#020617" },
  { key: "deepBlue", label: "Deep blue", color: "#0c1929" },
  { key: "forest", label: "Forest", color: "#052e16" },
  { key: "wine", label: "Wine", color: "#1c1917" },
];

const buildMeasurePoint = (logical, time, price) => {
  if (!Number.isFinite(logical) || !Number.isFinite(price)) {
    return null;
  }

  const normalizedTime = Number.isFinite(time) ? time : null;

  return {
    id: `${logical}-${price}-${normalizedTime ?? "na"}`,
    logical,
    time: normalizedTime,
    price,
  };
};

/** shellTopOffset: extra px the page puts above the chart (the quant strip), taken off its height. */
const ExcelChart = ({ themeMode = "black", onThemeChange, shellTopOffset = 0, workspace = "chart" }) => {
  const { user } = useAuth();
  const initialMarketChartStateRef = useRef(retainedMarketChartState);
  const initialMarketChartState = initialMarketChartStateRef.current;
  const isAdmin = Boolean(user?.is_admin);
  const isWhiteTheme = themeMode === "white";
  const isBlackTheme = themeMode === "black";
  const isNavyTheme = themeMode === "navy";
  const isTradingViewTheme = themeMode === "tradingview";
  const isChartXTheme = themeMode === "chartx";
  const isInstrumentTheme = themeMode === "instrument";
  const isMobile = useIsMobile();
  const [mobileSheet, setMobileSheet] = useState(null);
  const [chartContainerEl, setChartContainerEl] = useState(null);
  const chartShellRef = useRef(null);
  /** Space theme: the flush top bar can wrap to two rows; the side rails and chart start below it. */
  const [topBarEl, setTopBarEl] = useState(null);
  const [topBarHeight, setTopBarHeight] = useState(52);
  useEffect(() => {
    if (!topBarEl || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setTopBarHeight(Math.round(topBarEl.getBoundingClientRect().height)));
    ro.observe(topBarEl);
    return () => ro.disconnect();
  }, [topBarEl]);
  const [isChartFullscreen, setIsChartFullscreen] = useState(false);
  const [chartBoxCount, setChartBoxCount] = useState(() =>
    Math.max(1, Number(initialMarketChartState?.chartBoxCount) || 1)
  );
  const [chartGridMenuOpen, setChartGridMenuOpen] = useState(false);
  const [settingsChartGridMenuOpen, setSettingsChartGridMenuOpen] = useState(false);
  const chartGridToolbarWrapRef = useRef(null);
  const chartGridSettingsWrapRef = useRef(null);
  const chartGridSettingsDropdownRef = useRef(null);
  const [settingsChartGridRect, setSettingsChartGridRect] = useState(null);
  const [priceSeriesStyle, setPriceSeriesStyle] = useState(PRICE_SERIES_STYLES.CANDLES);
  const [renkoBrickInput, setRenkoBrickInput] = useState("");
  const [renkoBrickMode, setRenkoBrickMode] = useState(RENKO_BRICK_MODE.FIXED);
  const [renkoSource, setRenkoSource] = useState(LINE_PRICE_SOURCE.CLOSE);
  const [renkoAtrPeriodInput, setRenkoAtrPeriodInput] = useState("14");
  const [rangeSizeInput, setRangeSizeInput] = useState("");
  const [rangeSource, setRangeSource] = useState(LINE_PRICE_SOURCE.CLOSE);
  const [lineBreakLinesInput, setLineBreakLinesInput] = useState("3");
  const [kagiReversalInput, setKagiReversalInput] = useState("");
  const [kagiMode, setKagiMode] = useState(KAGI_MODE.ABSOLUTE);
  const [pnfBoxInput, setPnfBoxInput] = useState("");
  const [pnfReversalInput, setPnfReversalInput] = useState("");
  const [linePriceSource, setLinePriceSource] = useState(LINE_PRICE_SOURCE.CLOSE);
  const [lineChartLineType, setLineChartLineType] = useState(LINE_CHART_LINE_TYPE.SIMPLE);
  const [baselinePriceInput, setBaselinePriceInput] = useState("");
  const [chartCrosshairMode, setChartCrosshairMode] = useState("normal");
  const [mainPriceScaleMode, setMainPriceScaleMode] = useState("normal");
  const [chartTimeframeId, setChartTimeframeId] = useState(() => initialMarketChartState?.chartTimeframeId || "5m");
  const [customTimeframeInput, setCustomTimeframeInput] = useState(() => initialMarketChartState?.customTimeframeInput || "15");
  const [customTimeframeUnit, setCustomTimeframeUnit] = useState(() => initialMarketChartState?.customTimeframeUnit || "m");
  const [chartTimeZone, setChartTimeZone] = useState(DEFAULT_CHART_TIME_ZONE);
  const [yAxisSide, setYAxisSide] = useState("right");
  const chartTimeZoneOptions = useMemo(
    () =>
      CHART_TIME_ZONE_OPTIONS.map((timeZone) => ({
        value: timeZone,
        label: formatChartTimeZoneLabel(timeZone),
      })),
    []
  );
  const prevPriceSeriesStyleRef = useRef(PRICE_SERIES_STYLES.CANDLES);
  const chartRef = useRef(null);
  const goToDateInputRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const markersRef = useRef(null);
  const measureSeriesRef = useRef(null);
  const movingAverageSeriesRef = useRef(null);
  const smaSeriesRef = useRef(null);
  const emaSeriesRef = useRef(null);
  const wmaSeriesRef = useRef(null);
  const bbUpperSeriesRef = useRef(null);
  const bbMiddleSeriesRef = useRef(null);
  const bbLowerSeriesRef = useRef(null);
  const rsiSeriesRef = useRef(null);
  const rsiUpperSeriesRef = useRef(null);
  const rsiLowerSeriesRef = useRef(null);
  const atrSeriesRef = useRef(null);
  const donchianUpperSeriesRef = useRef(null);
  const donchianMiddleSeriesRef = useRef(null);
  const donchianLowerSeriesRef = useRef(null);
  const stochasticKSeriesRef = useRef(null);
  const stochasticDSeriesRef = useRef(null);
  const stochasticUpperSeriesRef = useRef(null);
  const stochasticLowerSeriesRef = useRef(null);
  const macdLineSeriesRef = useRef(null);
  const macdSignalSeriesRef = useRef(null);
  const macdHistogramSeriesRef = useRef(null);
  /** Area layers under price / oscillator line overlays (MA, MACD line, etc.). */
  const paneLineFillsRef = useRef(null);
  const userScriptLine1Ref = useRef(null);
  const userScriptLine2Ref = useRef(null);
  const userScriptLine3Ref = useRef(null);
  /** Dynamic indicator series (VWAP, CCI, Ichimoku, …) keyed by string */
  const extSeriesRef = useRef({});
  const resizeObserverRef = useRef(null);
  const replayTimerRef = useRef(null);
  const replayHoldTimeoutRef = useRef(null);
  const isMeasureModeRef = useRef(false);
  const isReplaySelectingRef = useRef(false);
  const activePanelRef = useRef("none");
  const drawToolRef = useRef("none");
  const measurePointsRef = useRef([]);
  const drawStartPointRef = useRef(null);
  /** Brush is the one drawing tool that's drag-based rather than click-to-place, so it
   * needs its own mousedown/mouseup tracking (see the chart-mount effect) instead of the
   * click-driven handleDrawingClick path every other tool uses. */
  const isBrushDrawingRef = useRef(false);
  const brushPointsBufferRef = useRef([]);
  const [brushPreviewPoints, setBrushPreviewPoints] = useState([]);
  const drawRrStopPointRef = useRef(null);
  const replayAnchorIndexRef = useRef(null);
  /** Skips overlay refresh when time scale moves from replay code (avoids feedback loops). */
  const replayProgrammaticScrollRef = useRef(false);
  const dataRef = useRef([]);
  const marketStreamRef = useRef(null);
  /** Backing refs for the history-backfill subscription set up once in the chart-mount
   * effect — kept fresh via small sync effects below so that long-lived subscription
   * doesn't read stale closed-over values. */
  const isReplayModeRef = useRef(false);
  const marketSymbolRef = useRef(BACKEND_DEFAULT_SYMBOL);
  const backendFeedTimeframeRef = useRef(BACKEND_DEFAULT_TF);
  const isBackendFeedActiveRef = useRef(true);
  const ratioModeEnabledRef = useRef(false);
  const liveRatioSymbolsRef = useRef(null);
  const ratioCandles1Ref = useRef(EMPTY_CANDLES);
  const ratioCandles2Ref = useRef(EMPTY_CANDLES);

  const [boxData, setBoxData] = useState(() =>
    Array.isArray(initialMarketChartState?.boxData) && initialMarketChartState.boxData.length
      ? initialMarketChartState.boxData
      : [[]]
  );
  const mainCandlesRef = useRef(Array.isArray(boxData[0]) ? boxData[0] : EMPTY_CANDLES);
  mainCandlesRef.current = Array.isArray(boxData[0]) ? boxData[0] : EMPTY_CANDLES;
  const [selectedBoxIndex, setSelectedBoxIndex] = useState(0);
  /** Symbol shown in each extra pane (index 1+). null = follow the main chart's symbol. */
  const [paneSymbols, setPaneSymbols] = useState([]);
  /** Panes (index 1+) holding an uploaded spreadsheet instead of live data. */
  const paneFileUploadRef = useRef(new Set());
  /** "SYMBOL:TF" each extra pane last loaded, so a pane is only refetched when it changes. */
  const paneLoadedKeyRef = useRef({});
  const selectedBoxIndexRef = useRef(0);
  const [ratioCandles1, setRatioCandles1] = useState(EMPTY_CANDLES);
  const [ratioCandles2, setRatioCandles2] = useState(EMPTY_CANDLES);
  const [ratioFileName1, setRatioFileName1] = useState("");
  const [ratioFileName2, setRatioFileName2] = useState("");
  const [ratioModeEnabled, setRatioModeEnabled] = useState(false);
  const ratioData = useMemo(
    () => computeRatioCandles(ratioCandles1, ratioCandles2),
    [ratioCandles1, ratioCandles2]
  );
  /**
   * When ratio mode is on, always show ratioData — even when it's empty — rather than
   * silently falling back to the normal chart. Falling back used to make a failed ratio
   * join (e.g. Ratio 1/Ratio 2 have no exactly-matching bar times) look like a working
   * chart showing one of the two raw datasets, with no indication anything was wrong.
   */
  const data = useMemo(
    () => (ratioModeEnabled ? ratioData : boxData[0] ?? EMPTY_CANDLES),
    [boxData, ratioModeEnabled, ratioData]
  );
  const [goToDate, setGoToDate] = useState("");
  const [bullishCandleBodyColor, setBullishCandleBodyColor] = useState(
    isWhiteTheme ? "#111111" : isInstrumentTheme ? "#5fe0a8" : isTradingViewTheme ? "#26a69a" : isChartXTheme ? "#22C55E" : "#16a34a"
  );
  const [bullishCandleBorderColor, setBullishCandleBorderColor] = useState(
    isWhiteTheme ? "#111111" : isInstrumentTheme ? "#5fe0a8" : isTradingViewTheme ? "#26a69a" : isChartXTheme ? "#22C55E" : "#16a34a"
  );
  const [bullishCandleWickColor, setBullishCandleWickColor] = useState(
    isWhiteTheme ? "#111111" : isInstrumentTheme ? "#5fe0a8" : isTradingViewTheme ? "#26a69a" : isChartXTheme ? "#22C55E" : "#16a34a"
  );
  const [bearishCandleBodyColor, setBearishCandleBodyColor] = useState(
    isWhiteTheme ? "#7a7a7a" : isInstrumentTheme ? "#ff7d8c" : isTradingViewTheme ? "#ef5350" : isChartXTheme ? "#EF4444" : "#86efac"
  );
  const [bearishCandleBorderColor, setBearishCandleBorderColor] = useState(
    isWhiteTheme ? "#7a7a7a" : isInstrumentTheme ? "#ff7d8c" : isTradingViewTheme ? "#ef5350" : isChartXTheme ? "#EF4444" : "#86efac"
  );
  const [bearishCandleWickColor, setBearishCandleWickColor] = useState(
    isWhiteTheme ? "#7a7a7a" : isInstrumentTheme ? "#ff7d8c" : isTradingViewTheme ? "#ef5350" : isChartXTheme ? "#EF4444" : "#86efac"
  );
  const [chartBackgroundColor, setChartBackgroundColor] = useState(
    isWhiteTheme
      ? "#ffffff"
      : isBlackTheme
        ? "#000000"
        : isInstrumentTheme
          ? "#050608"
          : isTradingViewTheme
            ? "#000000"
            : isChartXTheme
              ? "#080B10"
              : "#07111f"
  );
  const [maPeriodInput, setMaPeriodInput] = useState(String(DEFAULT_MA_PERIOD));
  const [maPeriod, setMaPeriod] = useState(DEFAULT_MA_PERIOD);
  const [showMovingAverage, setShowMovingAverage] = useState(false);
  const [smaPeriodInput, setSmaPeriodInput] = useState(String(DEFAULT_SMA_PERIOD));
  const [smaPeriod, setSmaPeriod] = useState(DEFAULT_SMA_PERIOD);
  const [emaPeriodInput, setEmaPeriodInput] = useState(String(DEFAULT_EMA_PERIOD));
  const [emaPeriod, setEmaPeriod] = useState(DEFAULT_EMA_PERIOD);
  const [wmaPeriodInput, setWmaPeriodInput] = useState(String(DEFAULT_WMA_PERIOD));
  const [wmaPeriod, setWmaPeriod] = useState(DEFAULT_WMA_PERIOD);
  const [bbPeriodInput, setBbPeriodInput] = useState(String(DEFAULT_BB_PERIOD));
  const [bbPeriod, setBbPeriod] = useState(DEFAULT_BB_PERIOD);
  const [bbDeviationInput, setBbDeviationInput] = useState(
    String(DEFAULT_BB_DEVIATION)
  );
  const [bbDeviation, setBbDeviation] = useState(DEFAULT_BB_DEVIATION);
  const [rsiPeriodInput, setRsiPeriodInput] = useState(String(DEFAULT_RSI_PERIOD));
  const [rsiPeriod, setRsiPeriod] = useState(DEFAULT_RSI_PERIOD);
  const [rsiUpperInput, setRsiUpperInput] = useState(String(DEFAULT_RSI_UPPER));
  const [rsiUpper, setRsiUpper] = useState(DEFAULT_RSI_UPPER);
  const [rsiLowerInput, setRsiLowerInput] = useState(String(DEFAULT_RSI_LOWER));
  const [rsiLower, setRsiLower] = useState(DEFAULT_RSI_LOWER);
  const [atrPeriodInput, setAtrPeriodInput] = useState(String(DEFAULT_ATR_PERIOD));
  const [atrPeriod, setAtrPeriod] = useState(DEFAULT_ATR_PERIOD);
  const [donchianPeriodInput, setDonchianPeriodInput] = useState(
    String(DEFAULT_DONCHIAN_PERIOD)
  );
  const [donchianPeriod, setDonchianPeriod] = useState(DEFAULT_DONCHIAN_PERIOD);
  const [stochKPeriodInput, setStochKPeriodInput] = useState(
    String(DEFAULT_STOCH_K_PERIOD)
  );
  const [stochKPeriod, setStochKPeriod] = useState(DEFAULT_STOCH_K_PERIOD);
  const [stochDPeriodInput, setStochDPeriodInput] = useState(
    String(DEFAULT_STOCH_D_PERIOD)
  );
  const [stochDPeriod, setStochDPeriod] = useState(DEFAULT_STOCH_D_PERIOD);
  const [stochUpperInput, setStochUpperInput] = useState(
    String(DEFAULT_STOCH_UPPER)
  );
  const [stochUpper, setStochUpper] = useState(DEFAULT_STOCH_UPPER);
  const [stochLowerInput, setStochLowerInput] = useState(
    String(DEFAULT_STOCH_LOWER)
  );
  const [stochLower, setStochLower] = useState(DEFAULT_STOCH_LOWER);
  const [macdFastPeriodInput, setMacdFastPeriodInput] = useState(
    String(DEFAULT_MACD_FAST_PERIOD)
  );
  const [macdFastPeriod, setMacdFastPeriod] = useState(DEFAULT_MACD_FAST_PERIOD);
  const [macdSlowPeriodInput, setMacdSlowPeriodInput] = useState(
    String(DEFAULT_MACD_SLOW_PERIOD)
  );
  const [macdSlowPeriod, setMacdSlowPeriod] = useState(DEFAULT_MACD_SLOW_PERIOD);
  const [macdSignalPeriodInput, setMacdSignalPeriodInput] = useState(
    String(DEFAULT_MACD_SIGNAL_PERIOD)
  );
  const [macdSignalPeriod, setMacdSignalPeriod] = useState(
    DEFAULT_MACD_SIGNAL_PERIOD
  );
  const [showSma, setShowSma] = useState(false);
  const [showEma, setShowEma] = useState(false);
  const [showWma, setShowWma] = useState(false);
  const [showBollinger, setShowBollinger] = useState(false);
  const [showRsi, setShowRsi] = useState(false);
  const [showAtr, setShowAtr] = useState(false);
  const [showDonchian, setShowDonchian] = useState(false);
  const [showStochastic, setShowStochastic] = useState(false);
  const [showMacd, setShowMacd] = useState(false);
  const [showVwap, setShowVwap] = useState(false);
  const [showHma, setShowHma] = useState(false);
  const [hmaPeriodInput, setHmaPeriodInput] = useState(String(DEFAULT_HMA_PERIOD));
  const [hmaPeriod, setHmaPeriod] = useState(DEFAULT_HMA_PERIOD);
  const [showDema, setShowDema] = useState(false);
  const [demaPeriodInput, setDemaPeriodInput] = useState(String(DEFAULT_DEMA_PERIOD));
  const [demaPeriod, setDemaPeriod] = useState(DEFAULT_DEMA_PERIOD);
  const [showTema, setShowTema] = useState(false);
  const [temaPeriodInput, setTemaPeriodInput] = useState(String(DEFAULT_TEMA_PERIOD));
  const [temaPeriod, setTemaPeriod] = useState(DEFAULT_TEMA_PERIOD);
  const [showIchimoku, setShowIchimoku] = useState(false);
  const [showSar, setShowSar] = useState(false);
  const [showKeltner, setShowKeltner] = useState(false);
  const [keltEmaInput, setKeltEmaInput] = useState(String(DEFAULT_KELT_EMA));
  const [keltEma, setKeltEma] = useState(DEFAULT_KELT_EMA);
  const [keltAtrInput, setKeltAtrInput] = useState(String(DEFAULT_KELT_ATR));
  const [keltAtr, setKeltAtr] = useState(DEFAULT_KELT_ATR);
  const [keltMultInput, setKeltMultInput] = useState(String(DEFAULT_KELT_MULT));
  const [keltMult, setKeltMult] = useState(DEFAULT_KELT_MULT);
  const [showCci, setShowCci] = useState(false);
  const [cciPeriodInput, setCciPeriodInput] = useState(String(DEFAULT_CCI_PERIOD));
  const [cciPeriod, setCciPeriod] = useState(DEFAULT_CCI_PERIOD);
  const [showWilliams, setShowWilliams] = useState(false);
  const [williamsPeriodInput, setWilliamsPeriodInput] = useState(
    String(DEFAULT_WILLIAMS_PERIOD)
  );
  const [williamsPeriod, setWilliamsPeriod] = useState(DEFAULT_WILLIAMS_PERIOD);
  const [showRoc, setShowRoc] = useState(false);
  const [rocPeriodInput, setRocPeriodInput] = useState(String(DEFAULT_ROC_PERIOD));
  const [rocPeriod, setRocPeriod] = useState(DEFAULT_ROC_PERIOD);
  const [showMom, setShowMom] = useState(false);
  const [momPeriodInput, setMomPeriodInput] = useState(String(DEFAULT_MOM_PERIOD));
  const [momPeriod, setMomPeriod] = useState(DEFAULT_MOM_PERIOD);
  const [showStochRsi, setShowStochRsi] = useState(false);
  const [stochRsiRsiInput, setStochRsiRsiInput] = useState(String(DEFAULT_STOCH_RSI_RSI));
  const [stochRsiRsi, setStochRsiRsi] = useState(DEFAULT_STOCH_RSI_RSI);
  const [stochRsiStochInput, setStochRsiStochInput] = useState(
    String(DEFAULT_STOCH_RSI_STOCH)
  );
  const [stochRsiStoch, setStochRsiStoch] = useState(DEFAULT_STOCH_RSI_STOCH);
  const [stochRsiKInput, setStochRsiKInput] = useState(String(DEFAULT_STOCH_RSI_K));
  const [stochRsiK, setStochRsiK] = useState(DEFAULT_STOCH_RSI_K);
  const [stochRsiDInput, setStochRsiDInput] = useState(String(DEFAULT_STOCH_RSI_D));
  const [stochRsiD, setStochRsiD] = useState(DEFAULT_STOCH_RSI_D);
  const [showAdx, setShowAdx] = useState(false);
  const [adxPeriodInput, setAdxPeriodInput] = useState(String(DEFAULT_ADX_PERIOD));
  const [adxPeriod, setAdxPeriod] = useState(DEFAULT_ADX_PERIOD);
  const [showAo, setShowAo] = useState(false);
  const [showTsi, setShowTsi] = useState(false);
  const [showObv, setShowObv] = useState(false);
  const [showCmf, setShowCmf] = useState(false);
  const [cmfPeriodInput, setCmfPeriodInput] = useState("20");
  const [cmfPeriod, setCmfPeriod] = useState(20);
  const [showMfi, setShowMfi] = useState(false);
  const [mfiPeriodInput, setMfiPeriodInput] = useState("14");
  const [mfiPeriod, setMfiPeriod] = useState(14);
  const [showAdLine, setShowAdLine] = useState(false);
  const [showChaikinVol, setShowChaikinVol] = useState(false);
  const [showVolOsc, setShowVolOsc] = useState(false);
  const [showStdDev, setShowStdDev] = useState(false);
  const [stdDevPeriodInput, setStdDevPeriodInput] = useState(String(DEFAULT_STDEV_PERIOD));
  const [stdDevPeriod, setStdDevPeriod] = useState(DEFAULT_STDEV_PERIOD);
  const [showKaufmanRange, setShowKaufmanRange] = useState(false);
  const [kaufmanRangePeriodInput, setKaufmanRangePeriodInput] = useState(
    String(DEFAULT_KAUFMAN_RANGE_PERIOD)
  );
  const [kaufmanRangePeriod, setKaufmanRangePeriod] = useState(DEFAULT_KAUFMAN_RANGE_PERIOD);
  const [hmmModelMode, setHmmModelMode] = useState("holdout");
  const [hmmTrainingSplit, setHmmTrainingSplit] = useState("70");
  const [bocpdExpectedRunLength, setBocpdExpectedRunLength] = useState(100);
  const [bocpdChangeThreshold, setBocpdChangeThreshold] = useState(0.25);
  const [kaufmanRangeDisplayMode, setKaufmanRangeDisplayMode] = useState("efficiency");
  const [kaufmanRangeRegimeMethod, setKaufmanRangeRegimeMethod] = useState("kaufman");
  const [dRegimeSettings, setDRegimeSettings] = useState(() => ({ ...DEFAULT_D_REGIME_SETTINGS }));
  const [dRegimeInputs, setDRegimeInputs] = useState(() => Object.fromEntries(
    D_REGIME_SETTING_DEFS.map(({ key }) => [key, String(DEFAULT_D_REGIME_SETTINGS[key])])
  ));
  const [kaufmanRangeTrendThresholdInput, setKaufmanRangeTrendThresholdInput] = useState("0.40");
  const [kaufmanRangeTrendThreshold, setKaufmanRangeTrendThreshold] = useState(0.4);
  const [kaufmanRangeRangeThresholdInput, setKaufmanRangeRangeThresholdInput] = useState("0.25");
  const [kaufmanRangeRangeThreshold, setKaufmanRangeRangeThreshold] = useState(0.25);
  const [kaufmanRangeSmoothInput, setKaufmanRangeSmoothInput] = useState(
    String(DEFAULT_KAUFMAN_RANGE_SMOOTH)
  );
  const [kaufmanRangeSmooth, setKaufmanRangeSmooth] = useState(DEFAULT_KAUFMAN_RANGE_SMOOTH);
  const efficiencyRegimePrimitiveRef = useRef(null);
  const [showPatterns, setShowPatterns] = useState(false);
  const [indicatorColors, setIndicatorColors] = useState({});
  const [indicatorLineWidths, setIndicatorLineWidths] = useState({});
  const [extraIndicatorInstances, setExtraIndicatorInstances] = useState([]);
  const extraInstanceIdRef = useRef(0);
  const extraInstanceSeriesRef = useRef(new Map());
  /** GoCharting indicators that are switched on: [{ type, params, paramInputs }]. */
  const [goInstances, setGoInstances] = useState([]);
  /** type -> { chart, series[], panes[], levelsKey, levelLines[] } for drawn GoCharting indicators. */
  const goSeriesRef = useRef(new Map());
  /** Order flow (footprint from cTrader ticks): which tools are on, their settings and data. */
  const [ofTools, setOfTools] = useState({
    trader: workspace === "orderflow",
    barStats: false,
    deltaBars: false,
    volumeProfile: false,
    marketProfile: false,
    buySellVwap: false,
  });
  const [ofSignals, setOfSignals] = useState(() => new Set(ORDERFLOW_DEFAULT_ON));
  const OF_SETTING_DEFS = [
    { key: "bars", label: "Bars loaded", min: 10, max: 300 },
    { key: "rowPoints", label: "Row size (points, 0 = auto)", min: 0, max: 100000 },
    { key: "imbalancePct", label: "Imbalance %", min: 101, max: 5000 },
    { key: "imbalanceMin", label: "Imbalance min ticks", min: 1, max: 10000 },
    { key: "stackCount", label: "Stacked levels", min: 2, max: 20 },
    { key: "sweepRows", label: "Sweep rows", min: 1, max: 50 },
    { key: "tpoMinutes", label: "TPO period (min)", min: 5, max: 240 },
    { key: "sessionHour", label: "Session start (UTC hour)", min: 0, max: 23 },
  ];
  const [ofSettings, setOfSettings] = useState({
    bars: 150,
    rowPoints: 0,
    imbalancePct: 300,
    imbalanceMin: 2,
    stackCount: 3,
    sweepRows: 3,
    tpoMinutes: 30,
    sessionHour: 21,
  });
  const [ofSettingInputs, setOfSettingInputs] = useState({
    bars: "150",
    rowPoints: "0",
    imbalancePct: "300",
    imbalanceMin: "2",
    stackCount: "3",
    sweepRows: "3",
    tpoMinutes: "30",
    sessionHour: "21",
  });
  const [ofData, setOfData] = useState(null);
  /** True while older order-flow history is being fetched (chart scrolled back). */
  const [ofLoadingOlder, setOfLoadingOlder] = useState(false);
  const ofDataRef = useRef(null);
  const [ofError, setOfError] = useState("");
  const ofPrimitiveRef = useRef(null);
  const ofBarStatsRef = useRef(null);
  const ofSeriesRef = useRef(new Map());
  const [indicatorSearchTerm, setIndicatorSearchTerm] = useState("");
  const [openIndicatorSubcategories, setOpenIndicatorSubcategories] = useState({});
  const [openIndicatorCategories, setOpenIndicatorCategories] = useState({});
  const [patternToggles, setPatternToggles] = useState(() => ({
    ...DEFAULT_PATTERN_TOGGLES,
  }));
  const [userScriptCode, setUserScriptCode] = useState(USER_SCRIPT_DEFAULT);
  const [userScriptEnabled, setUserScriptEnabled] = useState(false);
  const [userScriptRunRequest, setUserScriptRunRequest] = useState(0);
  const [userScriptRunning, setUserScriptRunning] = useState(false);
  const [userScriptOutput, setUserScriptOutput] = useState(null);
  const [userScriptError, setUserScriptError] = useState(null);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState("");
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [aiChatError, setAiChatError] = useState(null);
  const [aiIncludeContext, setAiIncludeContext] = useState(true);
  const [isMeasureMode, setIsMeasureMode] = useState(false);
  const [activePanel, setActivePanel] = useState("none");
  const [rightSideTab, setRightSideTab] = useState(null);
  const [activeIndicatorKey, setActiveIndicatorKey] = useState(null);
  const [backtestTrades, setBacktestTrades] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem("lwt.backtestTrades") || "null"); } catch { return null; }
  });
  const [backtestContextMenu, setBacktestContextMenu] = useState(null);
  const [drawTool, setDrawTool] = useState("none");
  const [drawings, setDrawings] = useState([]);
  /** Drawing picked for editing (drag, reshape, recolour, delete). */
  const [selectedDrawingId, setSelectedDrawingId] = useState(null);
  /** Phones: the info status message fades after a few seconds so it does not sit on the chart. */
  const [statusVisible, setStatusVisible] = useState(true);
  const selectedDrawingIdRef = useRef(null);
  selectedDrawingIdRef.current = selectedDrawingId;
  /** TradingView-style drawing options (magnet, stay in drawing mode, hide all), kept in this browser. */
  const [drawPrefs, setDrawPrefs] = useState(() => {
    const fallback = { magnet: "off", stay: false, hidden: false };
    try {
      return { ...fallback, ...JSON.parse(window.localStorage.getItem("lwt.drawPrefs") || "{}") };
    } catch (e) {
      return fallback;
    }
  });
  const drawPrefsRef = useRef(drawPrefs);
  drawPrefsRef.current = drawPrefs;
  /** Undo / redo of drawing changes (one step per finished drag). */
  const drawHistoryRef = useRef({ past: [], future: [], last: undefined, applying: false, suspended: false, resetting: false });
  const [, setDrawHistoryTick] = useState(0);
  /** Converts bar positions <-> time for the bars on the chart (drawings are pinned to time). */
  const drawingTimeAxisRef = useRef(makeTimeAxis([]));
  const drawingTimeAxisKeyRef = useRef("");
  const latestDrawingsRef = useRef([]);
  /** Set every render: closes open menus/side panels; returns true when something was closed. */
  const closeFloatingUiRef = useRef(() => false);
  const [drawStartPoint, setDrawStartPoint] = useState(null);
  const [drawRrStopPoint, setDrawRrStopPoint] = useState(null);
  const [drawHoverPoint, setDrawHoverPoint] = useState(null);
  const [isReplaySelecting, setIsReplaySelecting] = useState(false);
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [isReplayPlaying, setIsReplayPlaying] = useState(false);
  const [replayIndex, setReplayIndex] = useState(null);
  const [replayStartDate, setReplayStartDate] = useState("");
  const [replayPreviewIndex, setReplayPreviewIndex] = useState(null);
  const [measurePoints, setMeasurePoints] = useState([]);
  const [hoverMeasurePoint, setHoverMeasurePoint] = useState(null);
  const [hoveredCandle, setHoveredCandle] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({
    tone: "info",
    message: "Showing sample candles until you load an .xlsx or .csv file.",
  });
  const [isBackendFeedActive, setIsBackendFeedActive] = useState(
    () => initialMarketChartState?.isBackendFeedActive ?? true
  );
  const marketDataKeyRef = useRef(initialMarketChartState?.datasetKey || null);
  /** True while /candles is being fetched for the current symbol + timeframe. */
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [marketSymbol, setMarketSymbol] = useState(() => {
    try {
      const requested = window.localStorage.getItem("lwt.openChartSymbol");
      if (requested) window.localStorage.removeItem("lwt.openChartSymbol");
      return requested
        ? requested.toUpperCase()
        : initialMarketChartState?.marketSymbol || BACKEND_DEFAULT_SYMBOL;
    } catch {
      return initialMarketChartState?.marketSymbol || BACKEND_DEFAULT_SYMBOL;
    }
  });
  const [marketSymbols, setMarketSymbols] = useState(BACKEND_SYMBOL_FALLBACK);
  /** A small, user-curated subset of marketSymbols to show live quotes for in the
   * Watchlist panel. Kept separate from the full search universe (which can be ~1000
   * real instruments from a live broker feed) so quote polling stays bounded — polling
   * hundreds of symbols every few seconds would hammer the backend and the live
   * cTrader connection's per-symbol spot subscriptions. */
  const [watchlistSymbols, setWatchlistSymbols] = useState(() => {
    try {
      const raw = window.localStorage.getItem("lwt.watchlistSymbols");
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) && parsed.length ? parsed : BACKEND_SYMBOL_FALLBACK;
    } catch (e) {
      return BACKEND_SYMBOL_FALLBACK;
    }
  });
  const [watchlistAddText, setWatchlistAddText] = useState("");
  const [watchlistQuotes, setWatchlistQuotes] = useState({});
  const [symbolSearchText, setSymbolSearchText] = useState(BACKEND_DEFAULT_SYMBOL);
  const [symbolSearchOpen, setSymbolSearchOpen] = useState(false);
  /** Live ratio picked via the SYMB search (e.g. typing "EURUSD/GBPUSD") — both legs are
   * fetched + streamed from the backend and divided the same way as the R1/R2 file-upload
   * ratio (see ratioData below), just sourced live instead of from uploaded files. */
  const [liveRatioSymbols, setLiveRatioSymbols] = useState(null);
  const symbolSearchWrapRef = useRef(null);
  const liveRatioStreamRef = useRef(null);
  /** Set true whenever a genuinely new instrument/dataset starts loading (symbol switch,
   * ratio switch, file upload) so the chart-data effect fits the view + resets price-axis
   * autoscale exactly once for it. Left false the rest of the time so ordinary live ticks
   * (which fire this same effect every ~1s while streaming) don't keep resetting the
   * user's zoom/pan out from under them. */
  const pendingScaleFitRef = useRef(!initialMarketChartState?.visibleTimeRange);
  /** Named layouts (instrument/ratio + timeframe + chart style + grid), saved to
   * localStorage so picking one is an instant local state switch — no save/load
   * round-trip to the backend. */
  const [savedLayouts, setSavedLayouts] = useState(() => {
    try {
      const raw = window.localStorage.getItem("lwt.savedLayouts");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });
  const [layoutsMenuOpen, setLayoutsMenuOpen] = useState(false);
  const [layoutNameDraft, setLayoutNameDraft] = useState("");
  const layoutsMenuWrapRef = useRef(null);
  const layoutRestoredRef = useRef(false);
  const [, setOverlayTick] = useState(0);
  /** Coalesce overlay redraw triggers (viewport scroll / resize fire many events per pan). */
  const overlayRefreshRafRef = useRef(null);
  /** Avoid React re-render every crosshair pixel when still on the same bar. */
  const lastHoveredBarTimeRef = useRef(null);
  /** Skip redundant replay preview index updates while scrubbing the same bar. */
  const lastReplayPreviewIndexRef = useRef(null);
  /** Batch draw-tool + measure hover overlay updates to once per animation frame. */
  const crosshairPointerRafRef = useRef(null);
  const pendingCrosshairDrawRef = useRef(null);
  const pendingCrosshairMeasureRef = useRef(null);
  const defaultBullishCandleBodyColor = isWhiteTheme ? "#111111" : isInstrumentTheme ? "#5fe0a8" : isTradingViewTheme ? "#26a69a" : isChartXTheme ? "#22C55E" : "#16a34a";
  const defaultBullishCandleBorderColor = defaultBullishCandleBodyColor;
  const defaultBullishCandleWickColor = defaultBullishCandleBodyColor;
  const defaultBearishCandleBodyColor = isWhiteTheme ? "#7a7a7a" : isInstrumentTheme ? "#ff7d8c" : isTradingViewTheme ? "#ef5350" : isChartXTheme ? "#EF4444" : "#86efac";
  const defaultBearishCandleBorderColor = defaultBearishCandleBodyColor;
  const defaultBearishCandleWickColor = defaultBearishCandleBodyColor;
  const defaultChartBackgroundColor = isWhiteTheme
    ? "#ffffff"
    : isBlackTheme
      ? "#000000"
      : isInstrumentTheme
        ? "#050608"
        : isTradingViewTheme
          ? "#000000"
          : isChartXTheme
            ? "#080B10"
            : "#07111f";
  const activeCandlePresetKey =
    CANDLE_COLOR_PRESETS.find(
      (preset) =>
        preset.bullishBody.toLowerCase() ===
        bullishCandleBodyColor.toLowerCase() &&
        preset.bullishBorder.toLowerCase() ===
        bullishCandleBorderColor.toLowerCase() &&
        preset.bullishWick.toLowerCase() === bullishCandleWickColor.toLowerCase() &&
        preset.bearishBody.toLowerCase() ===
        bearishCandleBodyColor.toLowerCase() &&
        preset.bearishBorder.toLowerCase() ===
        bearishCandleBorderColor.toLowerCase() &&
        preset.bearishWick.toLowerCase() === bearishCandleWickColor.toLowerCase()
    )?.key ?? null;

  const activeChartBackgroundPresetKey =
    CHART_BACKGROUND_PRESETS.find(
      (preset) =>
        preset.color.toLowerCase() === chartBackgroundColor.toLowerCase()
    )?.key ?? null;

  const applyCandlePreset = (preset) => {
    setBullishCandleBodyColor(preset.bullishBody);
    setBullishCandleBorderColor(preset.bullishBorder);
    setBullishCandleWickColor(preset.bullishWick);
    setBearishCandleBodyColor(preset.bearishBody);
    setBearishCandleBorderColor(preset.bearishBorder);
    setBearishCandleWickColor(preset.bearishWick);
  };

  const resetCandleColors = () => {
    setBullishCandleBodyColor(defaultBullishCandleBodyColor);
    setBullishCandleBorderColor(defaultBullishCandleBorderColor);
    setBullishCandleWickColor(defaultBullishCandleWickColor);
    setBearishCandleBodyColor(defaultBearishCandleBodyColor);
    setBearishCandleBorderColor(defaultBearishCandleBorderColor);
    setBearishCandleWickColor(defaultBearishCandleWickColor);
    setChartBackgroundColor(defaultChartBackgroundColor);
  };

  const palette = useMemo(
    () =>
      isWhiteTheme
        ? {
          shellText: "#111111",
          mutedText: "#2f2f2f",
          accentText: "#111111",
          chartBackground: "#ffffff",
          chartPanelBackground:
            "linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(245, 245, 245, 1))",
          overlayBackground:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(247, 247, 247, 0.98))",
          replayBackground:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(242, 242, 242, 0.98))",
          toolbarBackground: "rgba(255, 255, 255, 0.92)",
          chipBackground: "rgba(247, 247, 247, 0.96)",
          buttonBackground:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(240, 240, 240, 0.98))",
          buttonActiveBackground:
            "linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(228, 228, 228, 0.95))",
          inputBackground: "rgba(255, 255, 255, 0.96)",
          borderStrong: "rgba(180, 180, 180, 0.95)",
          borderSoft: "rgba(205, 205, 205, 0.72)",
          activeBorder: "rgba(80, 80, 80, 0.95)",
          dangerBorder: "rgba(120, 120, 120, 0.38)",
          dangerBackground:
            "linear-gradient(180deg, rgba(245, 245, 245, 0.98), rgba(255, 255, 255, 0.98))",
          dangerText: "#111111",
          statusInfoBorder: "rgba(180, 180, 180, 0.95)",
          statusInfoBackground: "rgba(247, 247, 247, 0.94)",
          statusInfoText: "#111111",
          statusSuccessBorder: "rgba(160, 160, 160, 0.8)",
          statusSuccessBackground: "rgba(245, 245, 245, 0.96)",
          statusSuccessText: "#111111",
          statusErrorBorder: "rgba(160, 160, 160, 0.82)",
          statusErrorBackground: "rgba(245, 245, 245, 0.96)",
          statusErrorText: "#111111",
          crosshairVert: "rgba(120, 120, 120, 0.55)",
          crosshairHorz: "rgba(140, 140, 140, 0.5)",
          gridLine: "rgba(230, 230, 230, 0.82)",
          scaleBorder: "rgba(190, 190, 190, 0.95)",
          candleUp: "#ffffff",
          candleDown: "#444444",
          candleBorderUp: "#bcbcbc",
          candleBorderDown: "#7a7a7a",
          wickUp: "#bcbcbc",
          wickDown: "#7a7a7a",
          measureLine: "#111111",
          drawLine: "rgba(17, 17, 17, 0.82)",
          drawLinePreview: "rgba(110, 110, 110, 0.6)",
          drawFill: "rgba(120, 120, 120, 0.12)",
          drawFillPreview: "rgba(120, 120, 120, 0.08)",
          markerGradient:
            "radial-gradient(circle at 30% 30%, #ffffff 0%, #d9d9d9 42%, #7a7a7a 100%)",
          measureLabelBackground:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(235, 235, 235, 0.96))",
          measureLabelText: "#111111",
          ma: "#111111",
          sma: "#333333",
          ema: "#555555",
          wma: "#777777",
          bbPrimary: "#444444",
          bbMiddle: "#888888",
          rsi: "#111111",
          rsiUpper: "rgba(90, 90, 90, 0.42)",
          rsiLower: "rgba(140, 140, 140, 0.42)",
          atr: "#555555",
          donchianPrimary: "#444444",
          donchianMiddle: "#888888",
          stochasticK: "#333333",
          stochasticD: "#666666",
          stochasticUpper: "rgba(90, 90, 90, 0.42)",
          stochasticLower: "rgba(140, 140, 140, 0.42)",
          macdLine: "#333333",
          macdSignal: "#777777",
          vwap: "#c2410c",
          hma: "#7c3aed",
          dema: "#0891b2",
          tema: "#db2777",
          ichiTenkan: "#2563eb",
          ichiKijun: "#9333ea",
          ichiSpanA: "rgba(37, 99, 235, 0.55)",
          ichiSpanB: "rgba(147, 51, 234, 0.45)",
          ichiChikou: "#64748b",
          sar: "#ca8a04",
          keltUpper: "#0d9488",
          cci: "#0369a1",
          williams: "#7c2d12",
          roc: "#3f6212",
          mom: "#57534e",
          stochRsiK: "#4338ca",
          stochRsiD: "#a21caf",
          plusDI: "#16a34a",
          minusDI: "#dc2626",
          adxLine: "#ea580c",
          aoUp: "#16a34a",
          aoDown: "#dc2626",
          tsi: "#0f766e",
          obv: "#4f46e5",
          cmf: "#be185d",
          mfi: "#c026d3",
          adLine: "#1d4ed8",
          chaikinVol: "#b45309",
          volOsc: "#65a30d",
          stdDev: "#78716c",
          kaufmanRange: "#a16207",
          stUp: "#16a34a",
          stDown: "#dc2626",
          userScript1: "#7c3aed",
          userScript2: "#0891b2",
          userScript3: "#db2777",
          paneSeparator: "rgba(160, 160, 160, 0.9)",
          paneSeparatorHover: "rgba(60, 60, 60, 0.35)",
        }
        : isBlackTheme
          ? {
            shellText: "#f5f5f5",
            mutedText: "#d4d4d8",
            accentText: "#f5f5f5",
            chartBackground: "#000000",
            chartPanelBackground:
              "linear-gradient(180deg, rgba(0, 0, 0, 1), rgba(6, 6, 6, 1))",
            overlayBackground:
              "linear-gradient(180deg, rgba(12, 12, 12, 0.98), rgba(4, 4, 4, 0.98))",
            replayBackground:
              "linear-gradient(180deg, rgba(16, 16, 16, 0.98), rgba(2, 2, 2, 0.98))",
            toolbarBackground: "rgba(2, 2, 2, 0.92)",
            chipBackground: "rgba(14, 14, 14, 0.94)",
            buttonBackground:
              "linear-gradient(180deg, rgba(18, 18, 18, 0.98), rgba(4, 4, 4, 0.98))",
            buttonActiveBackground:
              "linear-gradient(180deg, rgba(38, 38, 38, 0.98), rgba(10, 10, 10, 0.98))",
            inputBackground: "rgba(10, 10, 10, 0.96)",
            borderStrong: "rgba(255, 255, 255, 0.14)",
            borderSoft: "rgba(255, 255, 255, 0.1)",
            activeBorder: "rgba(255, 255, 255, 0.28)",
            dangerBorder: "rgba(255, 255, 255, 0.14)",
            dangerBackground:
              "linear-gradient(180deg, rgba(28, 28, 28, 0.98), rgba(8, 8, 8, 0.98))",
            dangerText: "#f5f5f5",
            statusInfoBorder: "rgba(255, 255, 255, 0.14)",
            statusInfoBackground: "rgba(255, 255, 255, 0.03)",
            statusInfoText: "#f5f5f5",
            statusSuccessBorder: "rgba(255, 255, 255, 0.14)",
            statusSuccessBackground: "rgba(255, 255, 255, 0.03)",
            statusSuccessText: "#f5f5f5",
            statusErrorBorder: "rgba(255, 255, 255, 0.14)",
            statusErrorBackground: "rgba(255, 255, 255, 0.03)",
            statusErrorText: "#f5f5f5",
            crosshairVert: "rgba(255, 255, 255, 0.22)",
            crosshairHorz: "rgba(212, 212, 212, 0.2)",
            gridLine: "rgba(255, 255, 255, 0.05)",
            scaleBorder: "rgba(255, 255, 255, 0.1)",
            candleUp: "#16a34a",
            candleDown: "#86efac",
            candleBorderUp: "#22c55e",
            candleBorderDown: "#bbf7d0",
            wickUp: "#22c55e",
            wickDown: "#bbf7d0",
            measureLine: "#f5f5f5",
            drawLine: "rgba(255, 255, 255, 0.88)",
            drawLinePreview: "rgba(212, 212, 212, 0.68)",
            drawFill: "rgba(255, 255, 255, 0.08)",
            drawFillPreview: "rgba(255, 255, 255, 0.05)",
            markerGradient:
              "radial-gradient(circle at 30% 30%, #ffffff 0%, #d4d4d8 42%, #52525b 100%)",
            measureLabelBackground:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(212, 212, 216, 0.92))",
            measureLabelText: "#000000",
            ma: "#f5f5f5",
            sma: "#d4d4d8",
            ema: "#a3a3a3",
            wma: "#737373",
            bbPrimary: "#e5e7eb",
            bbMiddle: "#a1a1aa",
            rsi: "#f5f5f5",
            rsiUpper: "rgba(255, 255, 255, 0.28)",
            rsiLower: "rgba(212, 212, 212, 0.24)",
            atr: "#a3a3a3",
            donchianPrimary: "#e5e7eb",
            donchianMiddle: "#a1a1aa",
            stochasticK: "#d4d4d8",
            stochasticD: "#a3a3a3",
            stochasticUpper: "rgba(255, 255, 255, 0.24)",
            stochasticLower: "rgba(212, 212, 212, 0.22)",
            macdLine: "#d4d4d8",
            macdSignal: "#a3a3a3",
            vwap: "#fb923c",
            hma: "#a78bfa",
            dema: "#22d3ee",
            tema: "#f472b6",
            ichiTenkan: "#60a5fa",
            ichiKijun: "#c084fc",
            ichiSpanA: "rgba(96, 165, 250, 0.45)",
            ichiSpanB: "rgba(192, 132, 252, 0.4)",
            ichiChikou: "#a1a1aa",
            sar: "#fbbf24",
            keltUpper: "#2dd4bf",
            cci: "#38bdf8",
            williams: "#fdba74",
            roc: "#86efac",
            mom: "#d4d4d8",
            stochRsiK: "#818cf8",
            stochRsiD: "#e879f9",
            plusDI: "#4ade80",
            minusDI: "#f87171",
            adxLine: "#fb923c",
            aoUp: "#4ade80",
            aoDown: "#f87171",
            tsi: "#5eead4",
            obv: "#a5b4fc",
            cmf: "#f9a8d4",
            mfi: "#e879f9",
            adLine: "#93c5fd",
            chaikinVol: "#fcd34d",
            volOsc: "#bef264",
            stdDev: "#d4d4d8",
            kaufmanRange: "#facc15",
            stUp: "#22c55e",
            stDown: "#f87171",
            userScript1: "#a78bfa",
            userScript2: "#22d3ee",
            userScript3: "#f472b6",
            paneSeparator: "rgba(255, 255, 255, 0.18)",
            paneSeparatorHover: "rgba(255, 255, 255, 0.1)",
          }
          : isInstrumentTheme
            ? {
              shellText: "#eceef1",
              mutedText: "#9097a3",
              accentText: "#6ea8ff",
              chartBackground: "#050608",
              chartPanelBackground: "transparent",
              overlayBackground:
                "rgba(10, 12, 16, 0.86)",
              replayBackground:
                "rgba(13, 16, 21, 0.97)",
              toolbarBackground: "rgba(10, 12, 16, 0.82)",
              chipBackground: "rgba(255, 255, 255, 0.04)",
              buttonBackground: "#0d1015",
              buttonActiveBackground: "rgba(110, 168, 255, 0.14)",
              inputBackground: "#0a0c10",
              borderStrong: "rgba(255, 255, 255, 0.14)",
              borderSoft: "rgba(255, 255, 255, 0.075)",
              activeBorder: "#6ea8ff",
              dangerBorder: "rgba(255, 125, 140, 0.4)",
              dangerBackground:
                "rgba(255, 125, 140, 0.12)",
              dangerText: "#eceef1",
              statusInfoBorder: "rgba(255, 255, 255, 0.075)",
              statusInfoBackground: "rgba(5, 6, 8, 0.9)",
              statusInfoText: "#eceef1",
              statusSuccessBorder: "rgba(95, 224, 168, 0.4)",
              statusSuccessBackground: "rgba(5, 6, 8, 0.9)",
              statusSuccessText: "#5fe0a8",
              statusErrorBorder: "rgba(255, 125, 140, 0.4)",
              statusErrorBackground: "rgba(5, 6, 8, 0.9)",
              statusErrorText: "#ff7d8c",
              crosshairVert: "rgba(242, 243, 245, 0.4)",
              crosshairHorz: "rgba(242, 243, 245, 0.32)",
              gridLine: "rgba(255, 255, 255, 0.045)",
              scaleBorder: "rgba(255, 255, 255, 0.075)",
              candleUp: "#5fe0a8",
              candleDown: "#ff7d8c",
              candleBorderUp: "#5fe0a8",
              candleBorderDown: "#ff7d8c",
              wickUp: "#5fe0a8",
              wickDown: "#ff7d8c",
              measureLine: "#eceef1",
              drawLine: "rgba(242, 243, 245, 0.85)",
              drawLinePreview: "rgba(144, 151, 163, 0.7)",
              drawFill: "rgba(110, 168, 255, 0.1)",
              drawFillPreview: "rgba(110, 168, 255, 0.06)",
              markerGradient: "#eceef1",
              measureLabelBackground: "#eceef1",
              measureLabelText: "#050608",
              ma: "#eceef1",
              sma: "#9097a3",
              ema: "#a6c8ff",
              wma: "#5a606b",
              bbPrimary: "#6ea8ff",
              bbMiddle: "#9097a3",
              rsi: "#eceef1",
              rsiUpper: "rgba(255, 125, 140, 0.42)",
              rsiLower: "rgba(95, 224, 168, 0.42)",
              atr: "#9097a3",
              donchianPrimary: "#6ea8ff",
              donchianMiddle: "#9097a3",
              stochasticK: "#6ea8ff",
              stochasticD: "#a6c8ff",
              stochasticUpper: "rgba(255, 125, 140, 0.42)",
              stochasticLower: "rgba(95, 224, 168, 0.42)",
              macdLine: "#6ea8ff",
              macdSignal: "#a6c8ff",
              vwap: "#a6c8ff",
              hma: "#a497e6",
              dema: "#6fbfb3",
              tema: "#c98aa6",
              ichiTenkan: "#6ea8ff",
              ichiKijun: "#a497e6",
              ichiSpanA: "rgba(110, 168, 255, 0.5)",
              ichiSpanB: "rgba(164, 151, 230, 0.42)",
              ichiChikou: "#9097a3",
              sar: "#a6c8ff",
              keltUpper: "#6fbfb3",
              cci: "#6ea8ff",
              williams: "#a6c8ff",
              roc: "#6ea8ff",
              mom: "#9097a3",
              stochRsiK: "#6ea8ff",
              stochRsiD: "#c98aa6",
              plusDI: "#5fe0a8",
              minusDI: "#ff7d8c",
              adxLine: "#a6c8ff",
              aoUp: "#5fe0a8",
              aoDown: "#ff7d8c",
              tsi: "#6fbfb3",
              obv: "#6ea8ff",
              cmf: "#c98aa6",
              mfi: "#a497e6",
              adLine: "#6ea8ff",
              chaikinVol: "#a6c8ff",
              volOsc: "#9cb874",
              stdDev: "#9097a3",
              kaufmanRange: "#a6c8ff",
              stUp: "#5fe0a8",
              stDown: "#ff7d8c",
              userScript1: "#a497e6",
              userScript2: "#6fbfb3",
              userScript3: "#c98aa6",
              paneSeparator: "rgba(255, 255, 255, 0.075)",
              paneSeparatorHover: "rgba(110, 168, 255, 0.3)",
            }
          : isTradingViewTheme
            ? {
              shellText: "#e6e6e6",
              mutedText: "#787b86",
              accentText: "#e6e6e6",
              chartBackground: "#000000",
              chartPanelBackground: "#000000",
              overlayBackground:
                "linear-gradient(180deg, rgba(22, 22, 22, 0.98), rgba(14, 14, 14, 0.98))",
              replayBackground:
                "linear-gradient(180deg, rgba(22, 22, 22, 0.98), rgba(8, 8, 8, 0.98))",
              toolbarBackground: "rgba(10, 10, 10, 0.94)",
              chipBackground: "rgba(26, 26, 26, 0.94)",
              buttonBackground:
                "linear-gradient(180deg, rgba(26, 26, 26, 0.98), rgba(16, 16, 16, 0.98))",
              buttonActiveBackground:
                "linear-gradient(180deg, rgba(41, 98, 255, 0.28), rgba(20, 20, 20, 0.98))",
              inputBackground: "rgba(26, 26, 26, 0.96)",
              borderStrong: "rgba(70, 70, 70, 0.9)",
              borderSoft: "rgba(60, 60, 60, 0.6)",
              activeBorder: "rgba(41, 98, 255, 0.85)",
              dangerBorder: "rgba(239, 83, 80, 0.4)",
              dangerBackground:
                "linear-gradient(180deg, rgba(38, 26, 26, 0.98), rgba(22, 26, 35, 0.98))",
              dangerText: "#d1d4dc",
              statusInfoBorder: "rgba(42, 46, 57, 0.95)",
              statusInfoBackground: "rgba(30, 34, 45, 0.7)",
              statusInfoText: "#d1d4dc",
              statusSuccessBorder: "rgba(38, 166, 154, 0.4)",
              statusSuccessBackground: "rgba(30, 34, 45, 0.7)",
              statusSuccessText: "#26a69a",
              statusErrorBorder: "rgba(239, 83, 80, 0.4)",
              statusErrorBackground: "rgba(30, 34, 45, 0.7)",
              statusErrorText: "#ef5350",
              crosshairVert: "rgba(150, 150, 150, 0.6)",
              crosshairHorz: "rgba(150, 150, 150, 0.5)",
              gridLine: "rgba(255, 255, 255, 0.06)",
              scaleBorder: "rgba(255, 255, 255, 0.12)",
              candleUp: "#26a69a",
              candleDown: "#ef5350",
              candleBorderUp: "#26a69a",
              candleBorderDown: "#ef5350",
              wickUp: "#26a69a",
              wickDown: "#ef5350",
              measureLine: "#d1d4dc",
              drawLine: "rgba(209, 212, 220, 0.85)",
              drawLinePreview: "rgba(120, 123, 134, 0.65)",
              drawFill: "rgba(41, 98, 255, 0.1)",
              drawFillPreview: "rgba(41, 98, 255, 0.06)",
              markerGradient:
                "radial-gradient(circle at 30% 30%, #ffffff 0%, #d1d4dc 42%, #2962ff 100%)",
              measureLabelBackground:
                "linear-gradient(135deg, rgba(41, 98, 255, 0.95), rgba(30, 34, 45, 0.95))",
              measureLabelText: "#ffffff",
              ma: "#d1d4dc",
              sma: "#787b86",
              ema: "#b2b5be",
              wma: "#5d606b",
              bbPrimary: "#2962ff",
              bbMiddle: "#787b86",
              rsi: "#d1d4dc",
              rsiUpper: "rgba(239, 83, 80, 0.42)",
              rsiLower: "rgba(38, 166, 154, 0.42)",
              atr: "#787b86",
              donchianPrimary: "#2962ff",
              donchianMiddle: "#787b86",
              stochasticK: "#2962ff",
              stochasticD: "#ff9800",
              stochasticUpper: "rgba(239, 83, 80, 0.42)",
              stochasticLower: "rgba(38, 166, 154, 0.42)",
              macdLine: "#2962ff",
              macdSignal: "#ff9800",
              vwap: "#ff9800",
              hma: "#9c27b0",
              dema: "#00bcd4",
              tema: "#e91e63",
              ichiTenkan: "#2962ff",
              ichiKijun: "#9c27b0",
              ichiSpanA: "rgba(41, 98, 255, 0.5)",
              ichiSpanB: "rgba(156, 39, 176, 0.42)",
              ichiChikou: "#787b86",
              sar: "#ff9800",
              keltUpper: "#00bcd4",
              cci: "#2962ff",
              williams: "#ff9800",
              roc: "#26a69a",
              mom: "#787b86",
              stochRsiK: "#2962ff",
              stochRsiD: "#e91e63",
              plusDI: "#26a69a",
              minusDI: "#ef5350",
              adxLine: "#ff9800",
              aoUp: "#26a69a",
              aoDown: "#ef5350",
              tsi: "#00897b",
              obv: "#2962ff",
              cmf: "#e91e63",
              mfi: "#9c27b0",
              adLine: "#2962ff",
              chaikinVol: "#ff9800",
              volOsc: "#8bc34a",
              stdDev: "#787b86",
              kaufmanRange: "#ff9800",
              stUp: "#26a69a",
              stDown: "#ef5350",
              userScript1: "#9c27b0",
              userScript2: "#00bcd4",
              userScript3: "#e91e63",
              paneSeparator: "rgba(42, 46, 57, 0.9)",
              paneSeparatorHover: "rgba(41, 98, 255, 0.3)",
            }
            : isChartXTheme
              ? {
                shellText: "#E5E7EB",
                mutedText: "#7C8796",
                accentText: "#E5E7EB",
                chartBackground: "#080B10",
                chartPanelBackground:
                  "linear-gradient(180deg, rgba(13, 18, 25, 1), rgba(8, 11, 16, 1))",
                overlayBackground:
                  "linear-gradient(180deg, rgba(17, 24, 32, 0.98), rgba(13, 18, 25, 0.98))",
                replayBackground:
                  "linear-gradient(180deg, rgba(17, 24, 32, 0.98), rgba(8, 11, 16, 0.98))",
                toolbarBackground: "rgba(13, 18, 25, 0.92)",
                chipBackground: "rgba(17, 24, 32, 0.94)",
                buttonBackground:
                  "linear-gradient(180deg, rgba(17, 24, 32, 0.98), rgba(13, 18, 25, 0.98))",
                buttonActiveBackground:
                  "linear-gradient(180deg, rgba(59, 130, 246, 0.25), rgba(17, 24, 32, 0.98))",
                inputBackground: "rgba(17, 24, 32, 0.96)",
                borderStrong: "rgba(29, 38, 48, 0.95)",
                borderSoft: "rgba(29, 38, 48, 0.6)",
                activeBorder: "rgba(59, 130, 246, 0.85)",
                dangerBorder: "rgba(239, 68, 68, 0.4)",
                dangerBackground:
                  "linear-gradient(180deg, rgba(36, 20, 20, 0.98), rgba(13, 18, 25, 0.98))",
                dangerText: "#E5E7EB",
                statusInfoBorder: "rgba(29, 38, 48, 0.95)",
                statusInfoBackground: "rgba(17, 24, 32, 0.7)",
                statusInfoText: "#E5E7EB",
                statusSuccessBorder: "rgba(34, 197, 94, 0.4)",
                statusSuccessBackground: "rgba(17, 24, 32, 0.7)",
                statusSuccessText: "#22C55E",
                statusErrorBorder: "rgba(239, 68, 68, 0.4)",
                statusErrorBackground: "rgba(17, 24, 32, 0.7)",
                statusErrorText: "#EF4444",
                crosshairVert: "rgba(124, 135, 150, 0.6)",
                crosshairHorz: "rgba(124, 135, 150, 0.5)",
                gridLine: "rgba(29, 38, 48, 0.55)",
                scaleBorder: "rgba(29, 38, 48, 0.95)",
                candleUp: "#22C55E",
                candleDown: "#EF4444",
                candleBorderUp: "#22C55E",
                candleBorderDown: "#EF4444",
                wickUp: "#22C55E",
                wickDown: "#EF4444",
                measureLine: "#E5E7EB",
                drawLine: "rgba(229, 231, 235, 0.85)",
                drawLinePreview: "rgba(124, 135, 150, 0.65)",
                drawFill: "rgba(59, 130, 246, 0.1)",
                drawFillPreview: "rgba(59, 130, 246, 0.06)",
                markerGradient:
                  "radial-gradient(circle at 30% 30%, #ffffff 0%, #E5E7EB 42%, #3B82F6 100%)",
                measureLabelBackground:
                  "linear-gradient(135deg, rgba(59, 130, 246, 0.95), rgba(17, 24, 32, 0.95))",
                measureLabelText: "#ffffff",
                ma: "#E5E7EB",
                sma: "#7C8796",
                ema: "#a7b0bd",
                wma: "#5b6572",
                bbPrimary: "#3B82F6",
                bbMiddle: "#7C8796",
                rsi: "#E5E7EB",
                rsiUpper: "rgba(239, 68, 68, 0.42)",
                rsiLower: "rgba(34, 197, 94, 0.42)",
                atr: "#7C8796",
                donchianPrimary: "#3B82F6",
                donchianMiddle: "#7C8796",
                stochasticK: "#3B82F6",
                stochasticD: "#f59e0b",
                stochasticUpper: "rgba(239, 68, 68, 0.42)",
                stochasticLower: "rgba(34, 197, 94, 0.42)",
                macdLine: "#3B82F6",
                macdSignal: "#f59e0b",
                vwap: "#f59e0b",
                hma: "#a855f7",
                dema: "#06b6d4",
                tema: "#ec4899",
                ichiTenkan: "#3B82F6",
                ichiKijun: "#a855f7",
                ichiSpanA: "rgba(59, 130, 246, 0.5)",
                ichiSpanB: "rgba(168, 85, 247, 0.42)",
                ichiChikou: "#7C8796",
                sar: "#f59e0b",
                keltUpper: "#06b6d4",
                cci: "#3B82F6",
                williams: "#f59e0b",
                roc: "#22C55E",
                mom: "#7C8796",
                stochRsiK: "#3B82F6",
                stochRsiD: "#ec4899",
                plusDI: "#22C55E",
                minusDI: "#EF4444",
                adxLine: "#f59e0b",
                aoUp: "#22C55E",
                aoDown: "#EF4444",
                tsi: "#0d9488",
                obv: "#3B82F6",
                cmf: "#ec4899",
                mfi: "#a855f7",
                adLine: "#3B82F6",
                chaikinVol: "#f59e0b",
                volOsc: "#84cc16",
                stdDev: "#7C8796",
                kaufmanRange: "#f59e0b",
                stUp: "#22C55E",
                stDown: "#EF4444",
                userScript1: "#a855f7",
                userScript2: "#06b6d4",
                userScript3: "#ec4899",
                paneSeparator: "rgba(29, 38, 48, 0.9)",
                paneSeparatorHover: "rgba(59, 130, 246, 0.3)",
              }
            : {
            shellText: "#f8fafc",
            mutedText: "#e2e8f0",
            accentText: "#f8fafc",
            chartBackground: "#07111f",
            chartPanelBackground:
              "radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 22%), radial-gradient(circle at bottom right, rgba(249, 115, 22, 0.06), transparent 28%), linear-gradient(180deg, rgba(6, 10, 17, 0.99), rgba(3, 6, 12, 1))",
            overlayBackground:
              "linear-gradient(180deg, rgba(12, 17, 26, 0.96), rgba(8, 11, 18, 0.95))",
            replayBackground:
              "linear-gradient(180deg, rgba(8, 47, 73, 0.95), rgba(8, 15, 28, 0.94))",
            toolbarBackground: "rgba(8, 12, 19, 0.86)",
            chipBackground: "rgba(15, 23, 42, 0.72)",
            buttonBackground:
              "linear-gradient(180deg, rgba(17, 24, 39, 0.96), rgba(7, 11, 19, 0.98))",
            buttonActiveBackground:
              "linear-gradient(180deg, rgba(8, 145, 178, 0.34), rgba(15, 23, 42, 0.92))",
            inputBackground: "rgba(12, 16, 24, 0.9)",
            borderStrong: "rgba(71, 85, 105, 0.85)",
            borderSoft: "rgba(71, 85, 105, 0.58)",
            activeBorder: "rgba(56, 189, 248, 0.58)",
            dangerBorder: "rgba(248, 113, 113, 0.55)",
            dangerBackground:
              "linear-gradient(180deg, rgba(127, 29, 29, 0.88), rgba(69, 10, 10, 0.92))",
            dangerText: "#fee2e2",
            statusInfoBorder: "rgba(125, 211, 252, 0.2)",
            statusInfoBackground: "rgba(8, 47, 73, 0.22)",
            statusInfoText: "#bae6fd",
            statusSuccessBorder: "rgba(74, 222, 128, 0.32)",
            statusSuccessBackground: "rgba(20, 83, 45, 0.28)",
            statusSuccessText: "#bbf7d0",
            statusErrorBorder: "rgba(251, 113, 133, 0.38)",
            statusErrorBackground: "rgba(127, 29, 29, 0.3)",
            statusErrorText: "#fecdd3",
            crosshairVert: "rgba(34, 211, 238, 0.45)",
            crosshairHorz: "rgba(248, 113, 113, 0.4)",
            gridLine: "rgba(148, 163, 184, 0.08)",
            scaleBorder: "rgba(148, 163, 184, 0.18)",
            candleUp: "#14b8a6",
            candleDown: "#f97316",
            candleBorderUp: "#22d3ee",
            candleBorderDown: "#fb7185",
            wickUp: "#67e8f9",
            wickDown: "#fdba74",
            measureLine: "#f8fafc",
            drawLine: "rgba(56, 189, 248, 0.9)",
            drawLinePreview: "rgba(56, 189, 248, 0.7)",
            drawFill: "rgba(56, 189, 248, 0.12)",
            drawFillPreview: "rgba(56, 189, 248, 0.08)",
            markerGradient:
              "radial-gradient(circle at 30% 30%, #67e8f9 0%, #ffffff 38%, #fb7185 100%)",
            measureLabelBackground:
              "linear-gradient(135deg, rgba(34, 211, 238, 0.92), rgba(249, 115, 22, 0.92))",
            measureLabelText: "#04111d",
            ma: "#38bdf8",
            sma: "#14b8a6",
            ema: "#f59e0b",
            wma: "#f87171",
            bbPrimary: "#a78bfa",
            bbMiddle: "#7c3aed",
            rsi: "#facc15",
            rsiUpper: "rgba(248, 113, 113, 0.55)",
            rsiLower: "rgba(16, 185, 129, 0.55)",
            atr: "#f97316",
            donchianPrimary: "#38bdf8",
            donchianMiddle: "#f8fafc",
            stochasticK: "#14b8a6",
            stochasticD: "#facc15",
            stochasticUpper: "rgba(248, 113, 113, 0.45)",
            stochasticLower: "rgba(16, 185, 129, 0.45)",
            macdLine: "#60a5fa",
            macdSignal: "#f97316",
            vwap: "#fb923c",
            hma: "#c084fc",
            dema: "#22d3ee",
            tema: "#fb7185",
            ichiTenkan: "#38bdf8",
            ichiKijun: "#a78bfa",
            ichiSpanA: "rgba(56, 189, 248, 0.4)",
            ichiSpanB: "rgba(167, 139, 250, 0.35)",
            ichiChikou: "#94a3b8",
            sar: "#fbbf24",
            keltUpper: "#2dd4bf",
            cci: "#0ea5e9",
            williams: "#fdba74",
            roc: "#86efac",
            mom: "#e2e8f0",
            stochRsiK: "#818cf8",
            stochRsiD: "#e879f9",
            plusDI: "#4ade80",
            minusDI: "#f87171",
            adxLine: "#fb923c",
            aoUp: "#4ade80",
            aoDown: "#f87171",
            tsi: "#5eead4",
            obv: "#a5b4fc",
            cmf: "#f9a8d4",
            mfi: "#e879f9",
            adLine: "#93c5fd",
            chaikinVol: "#fcd34d",
            volOsc: "#bef264",
            stdDev: "#cbd5e1",
            kaufmanRange: "#eab308",
            stUp: "#22c55e",
            stDown: "#f87171",
            userScript1: "#c084fc",
            userScript2: "#2dd4bf",
            userScript3: "#fb7185",
            paneSeparator: "rgba(148, 163, 184, 0.32)",
            paneSeparatorHover: "rgba(226, 232, 240, 0.18)",
          },
    [isBlackTheme, isWhiteTheme, isTradingViewTheme, isChartXTheme, isInstrumentTheme]
  );

  const indicatorColorFallbacks = useMemo(
    () => ({
      ma: palette.ma,
      sma: palette.sma,
      ema: palette.ema,
      wma: palette.wma,
      bb: palette.bbPrimary,
      rsi: palette.rsi,
      atr: palette.atr,
      donchian: palette.donchianPrimary,
      stochastic: palette.stochasticK,
      macd: palette.macdLine,
      vwap: palette.vwap,
      hma: palette.hma,
      dema: palette.dema,
      tema: palette.tema,
      ichimoku: palette.ichiTenkan,
      sar: palette.sar,
      keltner: palette.keltUpper,
      cci: palette.cci,
      williams: palette.williams,
      roc: palette.roc,
      mom: palette.mom,
      stochRsi: palette.stochRsiK,
      adx: palette.adxLine,
      ao: palette.aoUp,
      tsi: palette.tsi,
      obv: palette.obv,
      cmf: palette.cmf,
      mfi: palette.mfi,
      adLine: palette.adLine,
      chaikinVol: palette.chaikinVol,
      volOsc: palette.volOsc,
      stdDev: palette.stdDev,
      kaufmanRange: palette.kaufmanRange,
      bbUpper: palette.bbPrimary,
      bbMid: palette.bbMiddle,
      bbLower: palette.bbPrimary,
      donchianUpper: palette.donchianPrimary,
      donchianMid: palette.donchianMiddle,
      donchianLower: palette.donchianPrimary,
      stochasticK: palette.stochasticK,
      stochasticD: palette.stochasticD,
      macdLine: palette.macdLine,
      macdSignal: palette.macdSignal,
      ichiTenkan: palette.ichiTenkan,
      ichiKijun: palette.ichiKijun,
      ichiSpanA: palette.ichiSpanA,
      ichiSpanB: palette.ichiSpanB,
      ichiChikou: palette.ichiChikou,
      keltUpper: palette.keltUpper,
      keltMid: palette.keltUpper,
      keltLower: palette.keltUpper,
      stochRsiK: palette.stochRsiK,
      stochRsiD: palette.stochRsiD,
      adxLine: palette.adxLine,
      plusDI: palette.plusDI,
      minusDI: palette.minusDI,
    }),
    [palette]
  );

  const getIndicatorColor = useCallback(
    (key) =>
      normalizeHexColor(indicatorColors[key], indicatorColorFallbacks[key] ?? palette.shellText),
    [indicatorColorFallbacks, indicatorColors, palette.shellText]
  );

  const getIndicatorLineWidth = useCallback(
    (key) => normalizeIndicatorLineWidth(indicatorLineWidths[key], INDICATOR_LINE_WIDTH),
    [indicatorLineWidths]
  );

  const handleIndicatorColorChange = (key, nextColor) => {
    setIndicatorColors((current) => ({
      ...current,
      [key]: normalizeHexColor(
        nextColor,
        indicatorColorFallbacks[key] ?? "#888888"
      ),
    }));
  };

  const handleIndicatorLineWidthChange = (key, nextWidth) => {
    setIndicatorLineWidths((current) => ({
      ...current,
      [key]: normalizeIndicatorLineWidth(nextWidth, current[key]),
    }));
  };


  const requestOverlayRefresh = useCallback(() => {
    if (overlayRefreshRafRef.current !== null) {
      return;
    }
    overlayRefreshRafRef.current = window.requestAnimationFrame(() => {
      overlayRefreshRafRef.current = null;
      setOverlayTick((currentTick) => currentTick + 1);
    });
  }, []);

  const scheduleCrosshairOverlayPointers = useCallback((drawNext, measureNext) => {
    pendingCrosshairDrawRef.current = drawNext;
    pendingCrosshairMeasureRef.current = measureNext;
    if (crosshairPointerRafRef.current !== null) {
      return;
    }
    crosshairPointerRafRef.current = window.requestAnimationFrame(() => {
      crosshairPointerRafRef.current = null;
      setDrawHoverPoint(pendingCrosshairDrawRef.current);
      setHoverMeasurePoint(pendingCrosshairMeasureRef.current);
    });
  }, []);

  const findNearestDataIndex = (targetTime) => {
    if (!data.length || !Number.isFinite(targetTime)) {
      return 0;
    }

    return data.reduce((closestIndex, candle, index) => {
      const closestDistance = Math.abs(data[closestIndex].time - targetTime);
      const currentDistance = Math.abs(candle.time - targetTime);

      return currentDistance < closestDistance ? index : closestIndex;
    }, 0);
  };

  const zoomChart = (direction) => {
    const timeScale = chartRef.current?.timeScale();
    const logicalRange = timeScale?.getVisibleLogicalRange();

    if (!timeScale || !logicalRange) return;

    const currentWidth = logicalRange.to - logicalRange.from;
    const zoomAmount = Math.max(currentWidth * ZOOM_FACTOR, 1);
    const nextWidth =
      direction === "in"
        ? Math.max(currentWidth - zoomAmount, 5)
        : currentWidth + zoomAmount;
    const center = (logicalRange.from + logicalRange.to) / 2;

    timeScale.setVisibleLogicalRange({
      from: center - nextWidth / 2,
      to: center + nextWidth / 2,
    });
  };

  const panChart = (direction) => {
    const timeScale = chartRef.current?.timeScale();
    const logicalRange = timeScale?.getVisibleLogicalRange();

    if (!timeScale || !logicalRange) return;

    const currentWidth = logicalRange.to - logicalRange.from;
    const shift = Math.max(currentWidth * PAN_FACTOR, 1);
    const offset = direction === "left" ? -shift : shift;

    timeScale.setVisibleLogicalRange({
      from: logicalRange.from + offset,
      to: logicalRange.to + offset,
    });
  };

  /** TradingView-style bottom date-range buttons (1D/5D/.../All) — see DATE_RANGE_PRESETS.
   * "All" fits the whole loaded dataset; everything else sets a fixed trailing window
   * ending at the latest loaded bar. Panning past what's loaded still triggers the
   * existing backfill (see BACKFILL_LOGICAL_THRESHOLD) once the resulting visible range
   * lands near the start of the array. */
  const applyDateRangePreset = (preset) => {
    const timeScale = chartRef.current?.timeScale();
    const current = dataRef.current;
    if (!timeScale || !current.length) return;

    if (preset.key === "ALL") {
      timeScale.fitContent();
      return;
    }

    const to = current[current.length - 1].time;
    let seconds = preset.seconds;
    if (preset.key === "YTD") {
      const yearStartMs = Date.UTC(new Date(to * 1000).getUTCFullYear(), 0, 1);
      seconds = Math.max(to - Math.floor(yearStartMs / 1000), 86400);
    }
    const from = Math.max(current[0].time, to - seconds);
    timeScale.setVisibleRange({ from, to });
  };

  const takeChartSnapshot = () => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    try {
      const canvas = chart.takeScreenshot(true, false);
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      const tfLabel =
        chartTimeframeId === CUSTOM_TIMEFRAME_ID
          ? `custom-${customTimeframeInput}${customTimeframeUnit}`
          : chartTimeframeId;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      link.download = `chart-${marketSymbol}-${tfLabel}-${stamp}.png`;
      link.href = url;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setUploadStatus({
        tone: "error",
        message: "Could not capture chart snapshot. Try again after the chart finishes loading.",
      });
    }
  };

  const toggleChartFullscreen = () => {
    const el = chartShellRef.current;
    if (!el) {
      return;
    }
    const doc = document;
    const fullscreenEl =
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.msFullscreenElement;
    if (fullscreenEl) {
      const exit =
        doc.exitFullscreen ||
        doc.webkitExitFullscreen ||
        doc.msExitFullscreen;
      if (exit) {
        exit.call(doc);
      }
      return;
    }
    const request =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.msRequestFullscreen;
    if (request) {
      try {
        const result = request.call(el);
        if (result && typeof result.catch === "function") {
          result.catch(() => {});
        }
      } catch {
        // Ignore fullscreen errors (browser may reject without a user gesture).
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      setIsChartFullscreen(fullscreenEl === chartShellRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  const clearMeasurement = () => {
    setMeasurePoints([]);
    setHoverMeasurePoint(null);
  };

  useEffect(() => {
    setStatusVisible(true);
    if (!isMobile) return undefined;
    const timer = window.setTimeout(() => setStatusVisible(false), 6000);
    return () => window.clearTimeout(timer);
  }, [uploadStatus?.message, isMobile]);

  const updateDrawing = useCallback((next) => {
    const anchored = anchorDrawing(next, drawingTimeAxisRef.current);
    setDrawings((current) => current.map((d) => (d.id === anchored.id ? anchored : d)));
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem("lwt.drawPrefs", JSON.stringify(drawPrefs));
    } catch (e) {
      // storage blocked: options still work for this visit
    }
  }, [drawPrefs]);

  // history: every change to the drawings is one undo step, except while a drag is running
  useEffect(() => {
    const h = drawHistoryRef.current;
    latestDrawingsRef.current = drawings;
    if (h.last === undefined || h.resetting) {
      h.resetting = false;
      h.past = [];
      h.future = [];
      h.last = drawings;
      setDrawHistoryTick((t) => t + 1);
      return;
    }
    if (h.applying) {
      h.applying = false;
      h.last = drawings;
      return;
    }
    if (h.suspended || h.last === drawings) return;
    h.past = [...h.past.slice(-49), h.last];
    h.future = [];
    h.last = drawings;
    setDrawHistoryTick((t) => t + 1);
  }, [drawings]);

  // drawings are saved per symbol in this browser and come back when the symbol is opened again
  const drawingsSymbolRef = useRef(null);
  const drawingsLoadRef = useRef({ pending: false, list: null });
  useEffect(() => {
    const list = loadSavedDrawings(marketSymbol);
    drawingsSymbolRef.current = marketSymbol;
    drawingsLoadRef.current = { pending: true, list };
    drawHistoryRef.current.resetting = true;
    setSelectedDrawingId(null);
    setDrawings(list);
  }, [marketSymbol]);
  useEffect(() => {
    const load = drawingsLoadRef.current;
    if (load.pending) {
      if (drawings === load.list) load.pending = false;
      return;
    }
    saveDrawings(drawingsSymbolRef.current, drawings);
  }, [drawings]);

  const beginDrawingEdit = useCallback(() => {
    drawHistoryRef.current.suspended = true;
  }, []);
  const endDrawingEdit = useCallback(() => {
    // wait for the last drag update to render, then record the whole drag as one step
    window.setTimeout(() => {
      const h = drawHistoryRef.current;
      if (!h.suspended) return;
      h.suspended = false;
      const current = latestDrawingsRef.current;
      if (current !== h.last) {
        h.past = [...h.past.slice(-49), h.last];
        h.future = [];
        h.last = current;
        setDrawHistoryTick((t) => t + 1);
      }
    }, 40);
  }, []);
  const undoDrawings = useCallback(() => {
    const h = drawHistoryRef.current;
    if (!h.past.length) return;
    const prev = h.past[h.past.length - 1];
    h.past = h.past.slice(0, -1);
    h.future = [h.last, ...h.future];
    h.applying = true;
    h.last = prev;
    setSelectedDrawingId(null);
    setDrawings(prev);
    setDrawHistoryTick((t) => t + 1);
  }, []);
  const redoDrawings = useCallback(() => {
    const h = drawHistoryRef.current;
    if (!h.future.length) return;
    const next = h.future[0];
    h.future = h.future.slice(1);
    h.past = [...h.past, h.last];
    h.applying = true;
    h.last = next;
    setSelectedDrawingId(null);
    setDrawings(next);
    setDrawHistoryTick((t) => t + 1);
  }, []);
  const toggleDrawPref = useCallback((key) => {
    setDrawPrefs((p) => {
      if (key === "magnet") {
        const order = ["off", "weak", "strong"];
        return { ...p, magnet: order[(order.indexOf(p.magnet) + 1) % order.length] };
      }
      return { ...p, [key]: !p[key] };
    });
  }, []);

  /** Adds a finished drawing. Like TradingView, the tool then switches off and the new drawing
   * is selected for editing, unless "stay in drawing mode" is on. */
  const addDrawing = (drawing) => {
    const anchored = anchorDrawing(drawing, drawingTimeAxisRef.current);
    setDrawings((current) => [...current, anchored]);
    if (drawPrefsRef.current.hidden) setDrawPrefs((p) => ({ ...p, hidden: false }));
    if (!drawPrefsRef.current.stay) {
      setDrawTool("none");
      setDrawStartPoint(null);
      setDrawRrStopPoint(null);
      setDrawHoverPoint(null);
      setSelectedDrawingId(anchored.id);
    }
  };
  const addDrawingRef = useRef(addDrawing);
  addDrawingRef.current = addDrawing;

  /** Esc / right-click while drawing: drop the unfinished points, or switch the tool off. */
  const cancelDrawing = () => {
    if (drawStartPointRef.current || drawRrStopPointRef.current) {
      setDrawStartPoint(null);
      setDrawRrStopPoint(null);
      setDrawHoverPoint(null);
      return;
    }
    setDrawTool("none");
    setDrawHoverPoint(null);
  };
  const cancelDrawingRef = useRef(cancelDrawing);
  cancelDrawingRef.current = cancelDrawing;

  // TradingView shortcuts: Ctrl/Cmd+Z undo, Ctrl/Cmd+Shift+Z or Ctrl+Y redo, Esc cancels drawing
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === "z" && !e.shiftKey) {
        e.preventDefault();
        undoDrawings();
      } else if (mod && (k === "y" || (k === "z" && e.shiftKey))) {
        e.preventDefault();
        redoDrawings();
      } else if (e.key === "Escape" && drawToolRef.current !== "none") {
        cancelDrawingRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undoDrawings, redoDrawings]);
  const deleteDrawing = useCallback((id) => {
    setDrawings((current) => current.filter((d) => d.id !== id));
    setSelectedDrawingId(null);
  }, []);
  const duplicateDrawing = useCallback((id) => {
    const newId = `${Date.now()}-copy`;
    setDrawings((current) => {
      const original = current.find((d) => d.id === id);
      if (!original) return current;
      return [...current, anchorDrawing({ ...moveDrawing(original, 5, 0), id: newId, locked: false }, drawingTimeAxisRef.current)];
    });
    setSelectedDrawingId(newId);
  }, []);

  const clearDrawings = () => {
    setSelectedDrawingId(null);
    setDrawings([]);
    setDrawStartPoint(null);
    setDrawRrStopPoint(null);
    setDrawHoverPoint(null);
  };

  const toggleDrawTool = (tool) => {
    setSelectedDrawingId(null);
    setDrawTool((currentTool) => {
      const nextTool = currentTool === tool ? "none" : tool;

      if (nextTool !== "none") {
        setIsMeasureMode(false);
        setIsReplaySelecting(false);
        // picking a drawing tool closes Indicators / Settings / Script / AI
        setActivePanel("none");
        setDrawPrefs((p) => (p.hidden ? { ...p, hidden: false } : p));
        setActiveIndicatorKey(null);
      }
      setDrawStartPoint(null);
      setDrawRrStopPoint(null);
      setDrawHoverPoint(null);

      return nextTool;
    });
  };

  const togglePanel = (panel) => {
    setActivePanel((currentPanel) => {
      const nextPanel = currentPanel === panel ? "none" : panel;

      if (nextPanel !== "indicators") {
        setActiveIndicatorKey(null);
      }

      return nextPanel;
    });
  };

  const stopReplay = () => {
    if (replayTimerRef.current) {
      window.clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
    if (replayHoldTimeoutRef.current) {
      window.clearTimeout(replayHoldTimeoutRef.current);
      replayHoldTimeoutRef.current = null;
    }
    setIsReplayPlaying(false);
    setIsReplaySelecting(false);
    setIsReplayMode(false);
    setReplayIndex(null);
    setReplayPreviewIndex(null);
  };

  const getReplayStartIndex = (dateValue = replayStartDate) => {
    if (!data.length) {
      return 0;
    }

    if (dateValue) {
      const targetTime = unixSecondsFromUtcYmd(dateValue);

      if (!Number.isNaN(targetTime)) {
        return findNearestDataIndex(targetTime);
      }
    }

    if (Number.isInteger(replayAnchorIndexRef.current)) {
      return Math.max(
        0,
        Math.min(data.length - 1, replayAnchorIndexRef.current)
      );
    }

    if (hoveredCandle) {
      const hoveredIndex = data.findIndex((candle) => candle.time === hoveredCandle.time);

      if (hoveredIndex >= 0) {
        return hoveredIndex;
      }
    }

    const logicalRange = chartRef.current?.timeScale().getVisibleLogicalRange();

    if (logicalRange) {
      return Math.max(0, Math.min(data.length - 1, Math.round(logicalRange.to)));
    }

    return Math.max(0, data.length - Math.min(REPLAY_WINDOW_BARS, data.length));
  };

  const enterReplaySelection = () => {
    if (!data.length) return;

    if (isReplayMode || isReplaySelecting) {
      stopReplay();
      return;
    }

    replayAnchorIndexRef.current = Number.isInteger(replayAnchorIndexRef.current)
      ? replayAnchorIndexRef.current
      : null;
    setReplayStartDate(
      hoveredCandle?.time
        ? formatInputDate(hoveredCandle.time)
        : goToDate || ""
    );
    setReplayPreviewIndex(getReplayStartIndex());
    setDrawTool("none");
    setIsReplaySelecting(true);
    clearMeasurement();
  };

  const beginReplay = (startIndex = getReplayStartIndex()) => {
    if (!data.length) return;

    const clamped = Math.max(0, Math.min(data.length - 1, Math.floor(startIndex)));

    setIsReplaySelecting(false);
    setIsReplayMode(true);
    setIsReplayPlaying(false);
    setReplayIndex(clamped);
    setReplayPreviewIndex(clamped);
    setHoveredCandle(data[clamped] ?? null);
    clearMeasurement();
  };

  const stepReplayForward = () => {
    if (!data.length) return;

    setIsReplayMode(true);
    setReplayIndex((currentIndex) => {
      const nextIndex =
        currentIndex === null
          ? getReplayStartIndex()
          : Math.min(currentIndex + 1, data.length - 1);

      return nextIndex;
    });
  };

  const startReplayContinuous = () => {
    if (!data.length) return;

    setIsReplayMode(true);
    setIsReplayPlaying(true);
    setReplayIndex((currentIndex) => {
      if (currentIndex === null) {
        return getReplayStartIndex();
      }

      return currentIndex;
    });
  };

  const stopReplayContinuous = () => {
    if (replayHoldTimeoutRef.current) {
      window.clearTimeout(replayHoldTimeoutRef.current);
      replayHoldTimeoutRef.current = null;
    }

    if (replayTimerRef.current) {
      window.clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }

    setIsReplayPlaying(false);
  };

  const handleReplayAdvancePressStart = () => {
    if (!data.length || !isReplayMode) return;

    if (replayHoldTimeoutRef.current) {
      window.clearTimeout(replayHoldTimeoutRef.current);
    }

    replayHoldTimeoutRef.current = window.setTimeout(() => {
      replayHoldTimeoutRef.current = null;
      startReplayContinuous();
    }, REPLAY_HOLD_DELAY_MS);
  };

  const handleReplayAdvancePressEnd = () => {
    if (!data.length || !isReplayMode) return;

    const shouldStepOnce = Boolean(replayHoldTimeoutRef.current);

    if (replayHoldTimeoutRef.current) {
      window.clearTimeout(replayHoldTimeoutRef.current);
      replayHoldTimeoutRef.current = null;
    }

    if (replayTimerRef.current) {
      stopReplayContinuous();
      return;
    }

    if (shouldStepOnce) {
      stepReplayForward();
    }
  };

  const handleDrawingClick = (point) => {
    const currentTool = drawToolRef.current;

    if (!point || currentTool === "none") {
      return false;
    }

    if (currentTool === "hline") {
      addDrawing({
          id: `${Date.now()}-hline-${point.price}`,
          type: "hline",
          price: point.price,
        });
      return true;
    }

    if (currentTool === "vline") {
      addDrawing({
          id: `${Date.now()}-vline-${point.logical}`,
          type: "vline",
          logical: point.logical,
        });
      return true;
    }

    if (currentTool === "pitchfork") {
      if (!drawStartPointRef.current) {
        setDrawStartPoint(point);
        return true;
      }
      if (!drawRrStopPointRef.current) {
        setDrawRrStopPoint(point);
        return true;
      }
      addDrawing({
        id: `${Date.now()}-pitchfork-${point.logical}`,
        type: "pitchfork",
        start: drawStartPointRef.current,
        end: drawRrStopPointRef.current,
        fork: point,
      });
      setDrawStartPoint(null);
      setDrawRrStopPoint(null);
      setDrawHoverPoint(null);
      return true;
    }

    if (currentTool === "rr") {
      if (!drawStartPointRef.current) {
        setDrawStartPoint(point);
        return true;
      }

      if (!drawRrStopPointRef.current) {
        setDrawRrStopPoint(point);
        return true;
      }

      const entry = drawStartPointRef.current;
      const stop = drawRrStopPointRef.current;
      const target = point;
      const riskAbs = Math.abs(entry.price - stop.price);
      const rewardAbs = Math.abs(target.price - entry.price);
      const rrRatio = riskAbs > 1e-12 ? rewardAbs / riskAbs : null;

      addDrawing({
          id: `${Date.now()}-rr-${point.logical}`,
          type: "rr",
          entry,
          stop,
          target,
          rrRatio,
        });
      setDrawStartPoint(null);
      setDrawRrStopPoint(null);
      setDrawHoverPoint(null);
      return true;
    }

    if (
      currentTool === "trend" ||
      currentTool === "rect" ||
      currentTool === "ray" ||
      currentTool === "ellipse" ||
      currentTool === "arrow" ||
      currentTool === "fib" ||
      currentTool === "fibext" ||
      currentTool === "range"
    ) {
      if (!drawStartPointRef.current) {
        setDrawStartPoint(point);
        return true;
      }

      addDrawing({
          id: `${Date.now()}-${currentTool}-${point.logical}`,
          type: currentTool,
          start: drawStartPointRef.current,
          end: point,
        });
      setDrawStartPoint(null);
      setDrawHoverPoint(null);
      return true;
    }

    if (currentTool === "channel") {
      if (!drawStartPointRef.current) {
        setDrawStartPoint(point);
        return true;
      }

      if (!drawRrStopPointRef.current) {
        setDrawRrStopPoint(point);
        return true;
      }

      const start = drawStartPointRef.current;
      const end = drawRrStopPointRef.current;
      // 3rd click sets the channel width: the vertical (price) distance between it and
      // where the main trend line would be at that same logical position.
      const t =
        end.logical !== start.logical
          ? (point.logical - start.logical) / (end.logical - start.logical)
          : 0;
      const mainLinePriceAtPoint = start.price + (end.price - start.price) * t;
      const offset = point.price - mainLinePriceAtPoint;

      addDrawing({
          id: `${Date.now()}-channel-${point.logical}`,
          type: "channel",
          start,
          end,
          offset,
        });
      setDrawStartPoint(null);
      setDrawRrStopPoint(null);
      setDrawHoverPoint(null);
      return true;
    }

    if (currentTool === "text") {
      const text = window.prompt("Annotation text:", "Note");
      if (text && text.trim()) {
        addDrawing({
            id: `${Date.now()}-text-${point.logical}`,
            type: "text",
            at: point,
            text: text.trim(),
          });
      }
      return true;
    }

    return false;
  };

  const applyMovingAveragePeriod = () => {
    const nextPeriod = Number.parseInt(maPeriodInput, 10);

    if (Number.isNaN(nextPeriod)) {
      setMaPeriodInput(String(maPeriod));
      return;
    }

    const normalizedPeriod = Math.max(1, nextPeriod);
    setMaPeriod(normalizedPeriod);
    setMaPeriodInput(String(normalizedPeriod));
  };

  const applySmaPeriod = () => {
    const nextPeriod = Number.parseInt(smaPeriodInput, 10);

    if (Number.isNaN(nextPeriod)) {
      setSmaPeriodInput(String(smaPeriod));
      return;
    }

    const normalizedPeriod = Math.max(1, nextPeriod);
    setSmaPeriod(normalizedPeriod);
    setSmaPeriodInput(String(normalizedPeriod));
  };

  const applyEmaPeriod = () => {
    const nextPeriod = Number.parseInt(emaPeriodInput, 10);

    if (Number.isNaN(nextPeriod)) {
      setEmaPeriodInput(String(emaPeriod));
      return;
    }

    const normalizedPeriod = Math.max(1, nextPeriod);
    setEmaPeriod(normalizedPeriod);
    setEmaPeriodInput(String(normalizedPeriod));
  };

  const applyWmaPeriod = () => {
    const nextPeriod = Number.parseInt(wmaPeriodInput, 10);

    if (Number.isNaN(nextPeriod)) {
      setWmaPeriodInput(String(wmaPeriod));
      return;
    }

    const normalizedPeriod = Math.max(1, nextPeriod);
    setWmaPeriod(normalizedPeriod);
    setWmaPeriodInput(String(normalizedPeriod));
  };

  const applyBollingerSettings = () => {
    const nextPeriod = Number.parseInt(bbPeriodInput, 10);
    const nextDeviation = Number.parseFloat(bbDeviationInput);

    if (Number.isNaN(nextPeriod) || Number.isNaN(nextDeviation)) {
      setBbPeriodInput(String(bbPeriod));
      setBbDeviationInput(String(bbDeviation));
      return;
    }

    const normalizedPeriod = Math.max(2, nextPeriod);
    const normalizedDeviation = Math.max(0.1, Number(nextDeviation.toFixed(2)));

    setBbPeriod(normalizedPeriod);
    setBbDeviation(normalizedDeviation);
    setBbPeriodInput(String(normalizedPeriod));
    setBbDeviationInput(String(normalizedDeviation));
  };

  const applyRsiSettings = () => {
    const nextPeriod = Number.parseInt(rsiPeriodInput, 10);
    const nextUpper = Number.parseFloat(rsiUpperInput);
    const nextLower = Number.parseFloat(rsiLowerInput);

    if (
      Number.isNaN(nextPeriod) ||
      Number.isNaN(nextUpper) ||
      Number.isNaN(nextLower)
    ) {
      setRsiPeriodInput(String(rsiPeriod));
      setRsiUpperInput(String(rsiUpper));
      setRsiLowerInput(String(rsiLower));
      return;
    }

    const normalizedPeriod = Math.max(2, nextPeriod);
    const normalizedUpper = Math.min(100, Math.max(0, nextUpper));
    const normalizedLower = Math.max(0, Math.min(normalizedUpper, nextLower));

    setRsiPeriod(normalizedPeriod);
    setRsiUpper(normalizedUpper);
    setRsiLower(normalizedLower);
    setRsiPeriodInput(String(normalizedPeriod));
    setRsiUpperInput(String(normalizedUpper));
    setRsiLowerInput(String(normalizedLower));
  };

  const applyAtrPeriod = () => {
    const nextPeriod = Number.parseInt(atrPeriodInput, 10);

    if (Number.isNaN(nextPeriod)) {
      setAtrPeriodInput(String(atrPeriod));
      return;
    }

    const normalizedPeriod = Math.max(1, nextPeriod);
    setAtrPeriod(normalizedPeriod);
    setAtrPeriodInput(String(normalizedPeriod));
  };

  const applyDonchianPeriod = () => {
    const nextPeriod = Number.parseInt(donchianPeriodInput, 10);

    if (Number.isNaN(nextPeriod)) {
      setDonchianPeriodInput(String(donchianPeriod));
      return;
    }

    const normalizedPeriod = Math.max(1, nextPeriod);
    setDonchianPeriod(normalizedPeriod);
    setDonchianPeriodInput(String(normalizedPeriod));
  };

  const applyStochasticPeriods = () => {
    const nextKPeriod = Number.parseInt(stochKPeriodInput, 10);
    const nextDPeriod = Number.parseInt(stochDPeriodInput, 10);
    const nextUpper = Number.parseFloat(stochUpperInput);
    const nextLower = Number.parseFloat(stochLowerInput);

    if (
      Number.isNaN(nextKPeriod) ||
      Number.isNaN(nextDPeriod) ||
      Number.isNaN(nextUpper) ||
      Number.isNaN(nextLower)
    ) {
      setStochKPeriodInput(String(stochKPeriod));
      setStochDPeriodInput(String(stochDPeriod));
      setStochUpperInput(String(stochUpper));
      setStochLowerInput(String(stochLower));
      return;
    }

    const normalizedK = Math.max(1, nextKPeriod);
    const normalizedD = Math.max(1, nextDPeriod);
    const normalizedUpper = Math.min(100, Math.max(0, nextUpper));
    const normalizedLower = Math.max(0, Math.min(normalizedUpper, nextLower));

    setStochKPeriod(normalizedK);
    setStochDPeriod(normalizedD);
    setStochUpper(normalizedUpper);
    setStochLower(normalizedLower);
    setStochKPeriodInput(String(normalizedK));
    setStochDPeriodInput(String(normalizedD));
    setStochUpperInput(String(normalizedUpper));
    setStochLowerInput(String(normalizedLower));
  };

  const applyMacdPeriods = () => {
    const nextFast = Number.parseInt(macdFastPeriodInput, 10);
    const nextSlow = Number.parseInt(macdSlowPeriodInput, 10);
    const nextSignal = Number.parseInt(macdSignalPeriodInput, 10);

    if (
      Number.isNaN(nextFast) ||
      Number.isNaN(nextSlow) ||
      Number.isNaN(nextSignal)
    ) {
      setMacdFastPeriodInput(String(macdFastPeriod));
      setMacdSlowPeriodInput(String(macdSlowPeriod));
      setMacdSignalPeriodInput(String(macdSignalPeriod));
      return;
    }

    const normalizedFast = Math.max(1, nextFast);
    const normalizedSlow = Math.max(normalizedFast + 1, nextSlow);
    const normalizedSignal = Math.max(1, nextSignal);

    setMacdFastPeriod(normalizedFast);
    setMacdSlowPeriod(normalizedSlow);
    setMacdSignalPeriod(normalizedSignal);
    setMacdFastPeriodInput(String(normalizedFast));
    setMacdSlowPeriodInput(String(normalizedSlow));
    setMacdSignalPeriodInput(String(normalizedSignal));
  };

  const applyKaufmanRangeSettings = () => {
    const nextPeriod = Number.parseInt(kaufmanRangePeriodInput, 10);
    const nextSmooth = Number.parseInt(kaufmanRangeSmoothInput, 10);

    if (Number.isNaN(nextPeriod) || Number.isNaN(nextSmooth)) {
      setKaufmanRangePeriodInput(String(kaufmanRangePeriod));
      setKaufmanRangeSmoothInput(String(kaufmanRangeSmooth));
      return;
    }

    const normalizedPeriod = Math.max(2, nextPeriod);
    const normalizedSmooth = Math.max(1, nextSmooth);

    setKaufmanRangePeriod(normalizedPeriod);
    setKaufmanRangeSmooth(normalizedSmooth);
    setKaufmanRangePeriodInput(String(normalizedPeriod));
    setKaufmanRangeSmoothInput(String(normalizedSmooth));
  };

  const applyKaufmanRangeThresholds = () => {
    const nextTrendThreshold = Number(kaufmanRangeTrendThresholdInput);
    const nextRangeThreshold = Number(kaufmanRangeRangeThresholdInput);
    if (!Number.isFinite(nextTrendThreshold) || !Number.isFinite(nextRangeThreshold)) {
      setKaufmanRangeTrendThresholdInput(String(kaufmanRangeTrendThreshold));
      setKaufmanRangeRangeThresholdInput(String(kaufmanRangeRangeThreshold));
      return;
    }
    const trendThreshold = Math.min(0.95, Math.max(0.05, nextTrendThreshold));
    let rangeThreshold = Math.min(0.95, Math.max(0, nextRangeThreshold));
    if (rangeThreshold >= trendThreshold) rangeThreshold = Math.max(0, trendThreshold - 0.05);
    setKaufmanRangeTrendThreshold(trendThreshold);
    setKaufmanRangeRangeThreshold(rangeThreshold);
    setKaufmanRangeTrendThresholdInput(trendThreshold.toFixed(2));
    setKaufmanRangeRangeThresholdInput(rangeThreshold.toFixed(2));
  };

  const applyDRegimeSettings = () => {
    const next = { ...dRegimeSettings };
    D_REGIME_SETTING_DEFS.forEach(({ key, min, max, step }) => {
      const parsed = Number(dRegimeInputs[key]);
      if (!Number.isFinite(parsed)) return;
      const bounded = Math.min(max, Math.max(min, parsed));
      const stepped = step === 1 ? Math.round(bounded) : Math.round(bounded / step) * step;
      next[key] = Number(Math.min(max, Math.max(min, stepped)).toFixed(3));
    });
    if (next.enterTrend <= next.exitTrend) {
      if (next.exitTrend <= -4) next.enterTrend = -3.75;
      else next.exitTrend = next.enterTrend - 0.25;
    }
    if (next.enterRange >= next.exitRange) {
      if (next.exitRange >= 4) next.enterRange = 3.75;
      else next.enterRange = next.exitRange - 0.25;
    }
    if (next.adxRange >= next.adxTrend) {
      if (next.adxRange >= 100) next.adxRange = 99.9;
      else next.adxTrend = next.adxRange + 0.1;
    }
    setDRegimeSettings(next);
    setDRegimeInputs(Object.fromEntries(
      D_REGIME_SETTING_DEFS.map(({ key }) => [key, String(next[key])])
    ));
  };





  const applyHmaPeriod = () => {
    const n = Number.parseInt(hmaPeriodInput, 10);
    if (Number.isNaN(n)) {
      setHmaPeriodInput(String(hmaPeriod));
      return;
    }
    const normalized = Math.max(2, n);
    setHmaPeriod(normalized);
    setHmaPeriodInput(String(normalized));
  };
  const applyDemaPeriod = () => {
    const n = Number.parseInt(demaPeriodInput, 10);
    if (Number.isNaN(n)) {
      setDemaPeriodInput(String(demaPeriod));
      return;
    }
    const normalized = Math.max(1, n);
    setDemaPeriod(normalized);
    setDemaPeriodInput(String(normalized));
  };
  const applyTemaPeriod = () => {
    const n = Number.parseInt(temaPeriodInput, 10);
    if (Number.isNaN(n)) {
      setTemaPeriodInput(String(temaPeriod));
      return;
    }
    const normalized = Math.max(1, n);
    setTemaPeriod(normalized);
    setTemaPeriodInput(String(normalized));
  };
  const applyKeltnerSettings = () => {
    const e = Number.parseInt(keltEmaInput, 10);
    const a = Number.parseInt(keltAtrInput, 10);
    const m = Number.parseFloat(keltMultInput, 10);
    if (Number.isNaN(e) || Number.isNaN(a) || Number.isNaN(m)) {
      setKeltEmaInput(String(keltEma));
      setKeltAtrInput(String(keltAtr));
      setKeltMultInput(String(keltMult));
      return;
    }
    setKeltEma(Math.max(1, e));
    setKeltAtr(Math.max(1, a));
    setKeltMult(Math.max(0.1, m));
    setKeltEmaInput(String(Math.max(1, e)));
    setKeltAtrInput(String(Math.max(1, a)));
    setKeltMultInput(String(Math.max(0.1, m)));
  };

  /** Adds another instance of `typeKey` (e.g. a 2nd/3rd/... EMA). Returns false without
   * adding once MAX_EXTRA_INSTANCES_PER_TYPE is reached for that type. */
  const addExtraIndicatorInstance = (typeKey) => {
    const def = DUPLICABLE_INDICATOR_TYPES[typeKey];
    if (!def) return false;
    const existing = extraIndicatorInstances.filter((inst) => inst.type === typeKey);
    if (existing.length >= MAX_EXTRA_INSTANCES_PER_TYPE) return false;
    extraInstanceIdRef.current += 1;
    const id = `dup${extraInstanceIdRef.current}`;
    const params = {};
    const paramInputs = {};
    def.paramDefs.forEach((p) => {
      params[p.key] = p.default;
      paramInputs[p.key] = String(p.default);
    });
    const colorKey = `dup:${id}`;
    const color = EXTRA_INSTANCE_DEFAULT_COLORS[existing.length % EXTRA_INSTANCE_DEFAULT_COLORS.length];
    setIndicatorColors((current) => ({ ...current, [colorKey]: color }));
    setExtraIndicatorInstances((current) => [...current, { id, type: typeKey, params, paramInputs }]);
    setActivePanel("indicators");
    setActiveIndicatorKey(colorKey);
    return true;
  };

  const removeExtraIndicatorInstance = (id) => {
    setExtraIndicatorInstances((current) => current.filter((inst) => inst.id !== id));
  };

  const updateExtraIndicatorInstanceParamInput = (id, paramKey, value) => {
    setExtraIndicatorInstances((current) =>
      current.map((inst) =>
        inst.id === id
          ? { ...inst, paramInputs: { ...inst.paramInputs, [paramKey]: value } }
          : inst
      )
    );
  };

  const applyExtraIndicatorInstanceParams = (id) => {
    setExtraIndicatorInstances((current) =>
      current.map((inst) => {
        if (inst.id !== id) return inst;
        const def = DUPLICABLE_INDICATOR_TYPES[inst.type];
        if (!def) return inst;
        const nextParams = { ...inst.params };
        const nextInputs = { ...inst.paramInputs };
        def.paramDefs.forEach((p) => {
          const parsed = Number.parseFloat(inst.paramInputs[p.key]);
          const normalized = Number.isFinite(parsed)
            ? Math.max(p.min ?? -Infinity, parsed)
            : inst.params[p.key];
          nextParams[p.key] = normalized;
          nextInputs[p.key] = String(normalized);
        });
        return { ...inst, params: nextParams, paramInputs: nextInputs };
      })
    );
  };

  const addGoIndicator = (type) => {
    const def = GO_INDICATORS[type];
    if (!def) return;
    setGoInstances((current) => {
      if (current.some((inst) => inst.type === type)) return current;
      const params = {};
      const paramInputs = {};
      def.paramDefs.forEach((p) => {
        params[p.key] = p.default;
        paramInputs[p.key] = String(p.default);
      });
      return [...current, { type, params, paramInputs }];
    });
    setIndicatorColors((current) =>
      current[`go:${type}`]
        ? current
        : {
            ...current,
            [`go:${type}`]:
              GO_DEFAULT_COLORS[GO_INDICATOR_KEYS.indexOf(type) % GO_DEFAULT_COLORS.length],
          }
    );
  };

  const removeGoIndicator = (type) => {
    setGoInstances((current) => current.filter((inst) => inst.type !== type));
  };

  const updateGoIndicatorParamInput = (type, paramKey, value) => {
    setGoInstances((current) =>
      current.map((inst) =>
        inst.type === type
          ? { ...inst, paramInputs: { ...inst.paramInputs, [paramKey]: value } }
          : inst
      )
    );
  };

  const applyGoIndicatorParams = (type) => {
    const def = GO_INDICATORS[type];
    if (!def) return;
    setGoInstances((current) =>
      current.map((inst) => {
        if (inst.type !== type) return inst;
        const nextParams = { ...inst.params };
        const nextInputs = { ...inst.paramInputs };
        def.paramDefs.forEach((p) => {
          const parsed = Number.parseFloat(inst.paramInputs[p.key]);
          const normalized = Number.isFinite(parsed)
            ? Math.min(p.max ?? Infinity, Math.max(p.min ?? -Infinity, parsed))
            : inst.params[p.key];
          nextParams[p.key] = normalized;
          nextInputs[p.key] = String(normalized);
        });
        return { ...inst, params: nextParams, paramInputs: nextInputs };
      })
    );
  };

  const applyCciPeriod = () => {
    const n = Number.parseInt(cciPeriodInput, 10);
    if (Number.isNaN(n)) {
      setCciPeriodInput(String(cciPeriod));
      return;
    }
    const normalized = Math.max(2, n);
    setCciPeriod(normalized);
    setCciPeriodInput(String(normalized));
  };
  const applyWilliamsPeriod = () => {
    const n = Number.parseInt(williamsPeriodInput, 10);
    if (Number.isNaN(n)) {
      setWilliamsPeriodInput(String(williamsPeriod));
      return;
    }
    const normalized = Math.max(2, n);
    setWilliamsPeriod(normalized);
    setWilliamsPeriodInput(String(normalized));
  };
  const applyRocPeriod = () => {
    const n = Number.parseInt(rocPeriodInput, 10);
    if (Number.isNaN(n)) {
      setRocPeriodInput(String(rocPeriod));
      return;
    }
    const normalized = Math.max(1, n);
    setRocPeriod(normalized);
    setRocPeriodInput(String(normalized));
  };
  const applyMomPeriod = () => {
    const n = Number.parseInt(momPeriodInput, 10);
    if (Number.isNaN(n)) {
      setMomPeriodInput(String(momPeriod));
      return;
    }
    const normalized = Math.max(1, n);
    setMomPeriod(normalized);
    setMomPeriodInput(String(normalized));
  };
  const applyStochRsiSettings = () => {
    const r = Number.parseInt(stochRsiRsiInput, 10);
    const st = Number.parseInt(stochRsiStochInput, 10);
    const k = Number.parseInt(stochRsiKInput, 10);
    const d = Number.parseInt(stochRsiDInput, 10);
    if (Number.isNaN(r) || Number.isNaN(st) || Number.isNaN(k) || Number.isNaN(d)) {
      setStochRsiRsiInput(String(stochRsiRsi));
      setStochRsiStochInput(String(stochRsiStoch));
      setStochRsiKInput(String(stochRsiK));
      setStochRsiDInput(String(stochRsiD));
      return;
    }
    setStochRsiRsi(Math.max(2, r));
    setStochRsiStoch(Math.max(2, st));
    setStochRsiK(Math.max(1, k));
    setStochRsiD(Math.max(1, d));
    setStochRsiRsiInput(String(Math.max(2, r)));
    setStochRsiStochInput(String(Math.max(2, st)));
    setStochRsiKInput(String(Math.max(1, k)));
    setStochRsiDInput(String(Math.max(1, d)));
  };
  const applyAdxPeriod = () => {
    const n = Number.parseInt(adxPeriodInput, 10);
    if (Number.isNaN(n)) {
      setAdxPeriodInput(String(adxPeriod));
      return;
    }
    const normalized = Math.max(2, n);
    setAdxPeriod(normalized);
    setAdxPeriodInput(String(normalized));
  };
  const applyCmfPeriod = () => {
    const n = Number.parseInt(cmfPeriodInput, 10);
    if (Number.isNaN(n)) {
      setCmfPeriodInput(String(cmfPeriod));
      return;
    }
    const normalized = Math.max(2, n);
    setCmfPeriod(normalized);
    setCmfPeriodInput(String(normalized));
  };
  const applyMfiPeriod = () => {
    const n = Number.parseInt(mfiPeriodInput, 10);
    if (Number.isNaN(n)) {
      setMfiPeriodInput(String(mfiPeriod));
      return;
    }
    const normalized = Math.max(2, n);
    setMfiPeriod(normalized);
    setMfiPeriodInput(String(normalized));
  };
  const applyStdDevPeriod = () => {
    const n = Number.parseInt(stdDevPeriodInput, 10);
    if (Number.isNaN(n)) {
      setStdDevPeriodInput(String(stdDevPeriod));
      return;
    }
    const normalized = Math.max(2, n);
    setStdDevPeriod(normalized);
    setStdDevPeriodInput(String(normalized));
  };

  useEffect(() => {
    setBoxData((prev) => {
      if (prev.length === chartBoxCount) {
        return prev;
      }
      if (prev.length > chartBoxCount) {
        return prev.slice(0, chartBoxCount);
      }
      const next = [...prev];
      while (next.length < chartBoxCount) {
        next.push([]);
      }
      return next;
    });
    setSelectedBoxIndex((i) => Math.min(i, Math.max(0, chartBoxCount - 1)));
    Object.keys(paneLoadedKeyRef.current).forEach((k) => {
      if (Number(k) >= chartBoxCount) delete paneLoadedKeyRef.current[k];
    });
    paneFileUploadRef.current.forEach((i) => {
      if (i >= chartBoxCount) paneFileUploadRef.current.delete(i);
    });
  }, [chartBoxCount]);

  const setBoxAtIndex = (index, candles) => {
    setBoxData((prev) => {
      const next = [...prev];
      while (next.length <= index) {
        next.push([]);
      }
      next[index] = candles;
      return next;
    });
  };

  const rawVisibleCandles = useMemo(() => {
    if (!isReplayMode || replayIndex === null) {
      return data;
    }
    return data.slice(0, replayIndex + 1);
  }, [data, isReplayMode, replayIndex]);

  const customBarSecondsResolved = useMemo(
    () => customTimeframeToSeconds(customTimeframeInput, customTimeframeUnit),
    [customTimeframeInput, customTimeframeUnit]
  );

  const chartPeriodSecondsResolved = useMemo(
    () => resolvedBarPeriodSeconds(data, chartTimeframeId, customBarSecondsResolved),
    [chartTimeframeId, customBarSecondsResolved, data]
  );

  /** Stable TF for backend only — must not depend on live candle data (avoids refetch/WS thrash). */
  const backendFeedTimeframe = useMemo(() => {
    if (chartTimeframeId === CUSTOM_TIMEFRAME_ID) {
      const s = customTimeframeToSeconds(customTimeframeInput, customTimeframeUnit);
      const sec = Math.max(1, Math.floor(Number.isFinite(s) && s > 0 ? s : 60));
      return `${sec}s`;
    }
    return typeof chartTimeframeId === "string" && chartTimeframeId.trim()
      ? chartTimeframeId.trim()
      : BACKEND_DEFAULT_TF;
  }, [chartTimeframeId, customTimeframeInput, customTimeframeUnit]);

  useEffect(() => {
    retainedMarketChartState = {
      marketSymbol,
      chartTimeframeId,
      customTimeframeInput,
      customTimeframeUnit,
      chartBoxCount,
      boxData,
      isBackendFeedActive,
      datasetKey: marketDataKeyRef.current,
      visibleTimeRange:
        retainedMarketChartState?.visibleTimeRange ?? initialMarketChartState?.visibleTimeRange ?? null,
    };
  }, [
    backendFeedTimeframe,
    boxData,
    chartBoxCount,
    chartTimeframeId,
    customTimeframeInput,
    customTimeframeUnit,
    isBackendFeedActive,
    marketSymbol,
  ]);

  useEffect(() => {
    let cancelled = false;
    marketFetch(`${BACKEND_API_BASE}/symbols`)
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled) return;
        const rows = Array.isArray(payload?.symbols)
          ? payload.symbols
              .map((s) => String(s || "").trim().toUpperCase())
              .filter(Boolean)
          : [];
        if (rows.length) {
          setMarketSymbols(rows);
          setMarketSymbol((prev) => (rows.includes(prev) ? prev : rows[0]));
        }
      })
      .catch(() => {
        // Keep fallback symbols when backend symbol endpoint is unavailable.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isBackendFeedActive) {
      setIsChartLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const url = `${BACKEND_API_BASE}/candles?symbol=${encodeURIComponent(
      marketSymbol
    )}&tf=${encodeURIComponent(backendFeedTimeframe)}&limit=1500`;
    const requestKey = `${marketSymbol}:${backendFeedTimeframe}`;
    const hasRetainedHistory =
      marketDataKeyRef.current === requestKey && mainCandlesRef.current.length > 0;

    setIsChartLoading(!hasRetainedHistory);
    fetchCandlesWithWakeRetry(url, controller.signal, (attempt) => {
      setUploadStatus({
        tone: "info",
        message: `Market service is reconnecting after idle time (${attempt}/5). Retrying automatically…`,
      });
    })
      .then((rows) => {
        setIsChartLoading(false);
        const normalized = normalizeBackendCandles(rows);
        if (!normalized.length) return;
        const preserveLoadedHistory = marketDataKeyRef.current === requestKey;
        if (!preserveLoadedHistory) pendingScaleFitRef.current = true;
        marketDataKeyRef.current = requestKey;
        setBoxData((prev) => {
          const next = [...prev];
          const current = Array.isArray(next[0]) ? next[0] : [];
          if (!preserveLoadedHistory || !current.length) {
            next[0] = normalized;
            return next;
          }
          const byTime = new Map(current.map((candle) => [candle.time, candle]));
          normalized.forEach((candle) => byTime.set(candle.time, candle));
          next[0] = [...byTime.values()].sort((a, b) => a.time - b.time);
          return next;
        });
        setUploadStatus({
          tone: "info",
          message: `${describeMarketCandle(normalized[normalized.length - 1])} (${marketSymbol} ${backendFeedTimeframe}).`,
        });
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setIsChartLoading(false);
        setUploadStatus({
          tone: "info",
          message: `Market data is still unavailable (${err?.message || "connection failed"}). Showing sample candles; press Resume live to retry.`,
        });
        setBoxData((prev) => {
          if (prev[0]?.length) return prev;
          const next = [...prev];
          next[0] = sampleData;
          return next;
        });
      });

    return () => controller.abort();
  }, [backendFeedTimeframe, isBackendFeedActive, marketSymbol]);

  useEffect(() => {
    if (!isBackendFeedActive || isReplayMode || isReplaySelecting) {
      if (marketStreamRef.current) {
        marketStreamRef.current.close();
        marketStreamRef.current = null;
      }
      return undefined;
    }

    const wsUrl = `${BACKEND_STREAM_BASE}/stream?symbols=${encodeURIComponent(
      marketSymbol
    )}&tf=${encodeURIComponent(backendFeedTimeframe)}`;
    let disposed = false;
    let reconnectTimer = null;
    let stableTimer = null;
    let retry = 0;
    let ws = null;
    let reportedFeed = false;

    const connect = () => {
      if (disposed) return;
      const socket = openMarketSocket(wsUrl);
      ws = socket;
      marketStreamRef.current = socket;

      socket.onopen = () => {
        if (stableTimer !== null) window.clearTimeout(stableTimer);
        stableTimer = window.setTimeout(() => { retry = 0; }, 30000);
      };
      socket.onmessage = (event) => {
        let parsed;
        try {
          parsed = JSON.parse(event.data);
        } catch (e) {
          return;
        }

        const candle = normalizeBackendCandles([parsed?.candle])[0];
        if (!candle) return;
        if (
          typeof parsed?.symbol === "string" &&
          parsed.symbol.toUpperCase() !== marketSymbol.toUpperCase()
        ) return;

        if (!reportedFeed) {
          reportedFeed = true;
          setUploadStatus({ tone: "success", message: describeMarketCandle(candle, { streaming: true }) });
        }

        setBoxData((prev) => {
          const next = [...prev];
          const current = Array.isArray(next[0]) ? next[0] : [];
          if (!current.length) {
            next[0] = [candle];
            return next;
          }
          const last = current[current.length - 1];
          if (last.time === candle.time) {
            next[0] = [...current.slice(0, -1), candle];
            return next;
          }
          if (last.time < candle.time) {
            next[0] = [...current, candle];
            return next;
          }
          return prev;
        });
      };

      socket.onclose = (event) => {
        if (stableTimer !== null) window.clearTimeout(stableTimer);
        stableTimer = null;
        if (marketStreamRef.current === socket) marketStreamRef.current = null;
        // A 4401 close means the login expired; marketFetch/openMarketSocket already
        // reports it so let AuthContext take the user back to sign in instead of looping.
        if (disposed || event.code === 4401) return;
        const delay = Math.min(1000 * (2 ** retry), 30000);
        retry += 1;
        setUploadStatus({
          tone: "error",
          message: `Live feed interrupted. Reconnecting automatically in ${Math.ceil(delay / 1000)} seconds…`,
        });
        reconnectTimer = window.setTimeout(connect, delay);
      };
      socket.onerror = () => socket.close();
    };

    connect();
    return () => {
      disposed = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (stableTimer !== null) window.clearTimeout(stableTimer);
      if (ws) ws.close();
      if (marketStreamRef.current === ws) marketStreamRef.current = null;
    };
  }, [
    backendFeedTimeframe,
    isBackendFeedActive,
    isReplayMode,
    isReplaySelecting,
    marketSymbol,
  ]);

  /** Symbol each extra pane shows (index 0 is the main chart). */
  const resolvedPaneSymbols = useMemo(() => {
    const out = [];
    for (let i = 1; i < chartBoxCount; i += 1) {
      out[i] = (paneSymbols[i] || marketSymbol || "").toUpperCase();
    }
    return out;
  }, [chartBoxCount, paneSymbols, marketSymbol]);
  const paneSymbolsKey = resolvedPaneSymbols.join("|");

  /** Multi-pane grid: load history for panes 2+ from the backend (each pane its own symbol). */
  useEffect(() => {
    if (chartBoxCount < 2) return;
    for (let i = 1; i < chartBoxCount; i += 1) {
      if (paneFileUploadRef.current.has(i)) continue;
      const symbol = resolvedPaneSymbols[i];
      if (!symbol) continue;
      const key = `${symbol}:${backendFeedTimeframe}`;
      if (paneLoadedKeyRef.current[i] === key) continue;
      paneLoadedKeyRef.current[i] = key;
      const index = i;
      setBoxAtIndex(index, []);
      marketFetch(
        `${BACKEND_API_BASE}/candles?symbol=${encodeURIComponent(symbol)}&tf=${encodeURIComponent(
          backendFeedTimeframe
        )}&limit=1500`
      )
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((rows) => {
          if (paneLoadedKeyRef.current[index] !== key) return;
          setBoxAtIndex(index, normalizeBackendCandles(rows));
        })
        .catch(() => {
          // Allow a retry next time this pane's symbol/timeframe effect runs.
          if (paneLoadedKeyRef.current[index] === key) delete paneLoadedKeyRef.current[index];
        });
    }
    // No abort on cleanup: a stale response is ignored by the key check above, and
    // aborting here would also cancel other panes' requests that this run skips.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by paneSymbolsKey
  }, [backendFeedTimeframe, chartBoxCount, paneSymbolsKey]);

  /** Multi-pane grid: one WebSocket streams live ticks for every extra pane's symbol. */
  useEffect(() => {
    if (chartBoxCount < 2) return undefined;
    const symbols = Array.from(new Set(resolvedPaneSymbols.filter(Boolean)));
    if (!symbols.length) return undefined;
    const wsUrl = `${BACKEND_STREAM_BASE}/stream?symbols=${symbols
      .map((s) => encodeURIComponent(s))
      .join(",")}&tf=${encodeURIComponent(backendFeedTimeframe)}`;
    const ws = openMarketSocket(wsUrl);
    ws.onmessage = (event) => {
      let parsed;
      try {
        parsed = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      const candle = normalizeBackendCandles([parsed?.candle])[0];
      const tickSymbol = typeof parsed?.symbol === "string" ? parsed.symbol.toUpperCase() : "";
      if (!candle || !tickSymbol) return;
      setBoxData((prev) => {
        let next = prev;
        for (let i = 1; i < prev.length; i += 1) {
          if (resolvedPaneSymbols[i] !== tickSymbol) continue;
          if (paneFileUploadRef.current.has(i)) continue;
          const current = Array.isArray(prev[i]) ? prev[i] : [];
          // Wait for the pane's history before adding live bars.
          if (!current.length) continue;
          const last = current[current.length - 1];
          let updated = null;
          if (last.time === candle.time) updated = [...current.slice(0, -1), candle];
          else if (last.time < candle.time) updated = [...current, candle];
          if (!updated) continue;
          if (next === prev) next = [...prev];
          next[i] = updated;
        }
        return next;
      });
    };
    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by paneSymbolsKey
  }, [backendFeedTimeframe, chartBoxCount, paneSymbolsKey]);

  const setPaneSymbol = (index, symbol) => {
    paneFileUploadRef.current.delete(index);
    delete paneLoadedKeyRef.current[index];
    setPaneSymbols((prev) => {
      const next = [...prev];
      next[index] = symbol || null;
      return next;
    });
  };

  /** Live ratio (SYMB search box, "SYM1/SYM2") — fetches both legs' history from the
   * backend then streams live ticks for both over one WS connection, routing each tick
   * to ratioCandles1/ratioCandles2 by symbol. Mirrors the single-symbol effect above,
   * but feeds the existing file-upload ratio state instead of boxData. */
  useEffect(() => {
    if (!liveRatioSymbols || !isBackendFeedActive || isReplayMode || isReplaySelecting) {
      if (liveRatioStreamRef.current) {
        liveRatioStreamRef.current.close();
        liveRatioStreamRef.current = null;
      }
      return undefined;
    }

    const { leg1, leg2 } = liveRatioSymbols;
    let cancelled = false;
    const controller = new AbortController();

    const fetchLeg = (symbol) =>
      marketFetch(
        `${BACKEND_API_BASE}/candles?symbol=${encodeURIComponent(
          symbol
        )}&tf=${encodeURIComponent(backendFeedTimeframe)}&limit=1500`,
        { signal: controller.signal }
      )
        .then((res) => (res.ok ? res.json() : []))
        .then((rows) => normalizeBackendCandles(rows))
        .catch(() => []);

    Promise.all([fetchLeg(leg1), fetchLeg(leg2)]).then(([c1, c2]) => {
      if (cancelled) return;
      pendingScaleFitRef.current = true;
      setRatioCandles1(c1);
      setRatioFileName1(leg1);
      setRatioCandles2(c2);
      setRatioFileName2(leg2);
      if (c1.length && c2.length) {
        setRatioModeEnabled(true);
      }
      setUploadStatus({
        tone: c1.length && c2.length ? "info" : "error",
        message:
          c1.length && c2.length
            ? `Live ratio: ${leg1} ÷ ${leg2}`
            : `Could not load live data for ${!c1.length ? leg1 : leg2}.`,
      });
    });

    const wsUrl = `${BACKEND_STREAM_BASE}/stream?symbols=${encodeURIComponent(
      leg1
    )},${encodeURIComponent(leg2)}&tf=${encodeURIComponent(backendFeedTimeframe)}`;
    const ws = openMarketSocket(wsUrl);
    liveRatioStreamRef.current = ws;

    ws.onmessage = (event) => {
      let parsed;
      try {
        parsed = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      const candle = normalizeBackendCandles([parsed?.candle])[0];
      if (!candle) return;
      const sym = String(parsed?.symbol || "").toUpperCase();
      const setTarget =
        sym === leg1 ? setRatioCandles1 : sym === leg2 ? setRatioCandles2 : null;
      if (!setTarget) return;
      setTarget((prev) => {
        if (!prev.length) return [candle];
        const last = prev[prev.length - 1];
        if (last.time === candle.time) return [...prev.slice(0, -1), candle];
        if (last.time < candle.time) return [...prev, candle];
        return prev;
      });
    };

    ws.onclose = () => {
      if (liveRatioStreamRef.current === ws) {
        liveRatioStreamRef.current = null;
      }
    };

    return () => {
      cancelled = true;
      controller.abort();
      ws.close();
      if (liveRatioStreamRef.current === ws) {
        liveRatioStreamRef.current = null;
      }
    };
  }, [
    backendFeedTimeframe,
    isBackendFeedActive,
    isReplayMode,
    isReplaySelecting,
    liveRatioSymbols,
  ]);

  /** Keeps the SYMB search box's displayed text in sync with the committed symbol/ratio
   * while the user isn't actively editing it (dropdown closed). */
  useEffect(() => {
    if (symbolSearchOpen) return;
    setSymbolSearchText(
      liveRatioSymbols ? `${liveRatioSymbols.leg1}/${liveRatioSymbols.leg2}` : marketSymbol
    );
  }, [marketSymbol, liveRatioSymbols, symbolSearchOpen]);

  useEffect(() => {
    if (!symbolSearchOpen) return undefined;
    const onPointerDown = (e) => {
      if (symbolSearchWrapRef.current?.contains(e.target)) return;
      setSymbolSearchOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [symbolSearchOpen]);

  useEffect(() => {
    if (!symbolSearchOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setSymbolSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [symbolSearchOpen]);

  const symbolSearchQuery = useMemo(() => {
    const raw = symbolSearchText.trim().toUpperCase();
    const slashIdx = raw.indexOf("/");
    if (slashIdx === -1) {
      return { mode: "single", query: raw };
    }
    return {
      mode: "ratio",
      leg1: raw.slice(0, slashIdx).trim(),
      leg2: raw.slice(slashIdx + 1).trim(),
    };
  }, [symbolSearchText]);

  const symbolSearchSuggestions = useMemo(() => {
    if (symbolSearchQuery.mode === "ratio") return [];
    const q = symbolSearchQuery.query;
    return q ? marketSymbols.filter((s) => s.includes(q)) : marketSymbols;
  }, [symbolSearchQuery, marketSymbols]);

  /** Groups the full broker-provided instrument list by tradeable asset class. */
  const symbolSearchGroups = useMemo(() => {
    const byCategory = new Map();
    for (const symbol of symbolSearchSuggestions) {
      const category = categorizeSymbol(symbol);
      if (!byCategory.has(category)) byCategory.set(category, []);
      byCategory.get(category).push(symbol);
    }
    const perCategoryCap = Infinity;
    return SYMBOL_CATEGORY_ORDER.map((category) => {
      const all = byCategory.get(category) || [];
      return {
        category,
        symbols: all.slice(0, perCategoryCap),
        moreCount: Math.max(0, all.length - perCategoryCap),
      };
    }).filter((group) => group.symbols.length > 0);
  }, [symbolSearchSuggestions]);

  /** Commits whatever's in the SYMB search box: a plain symbol switches the main pane
   * (and turns any active ratio off), "SYM1/SYM2" starts a live ratio. */
  const commitSymbolSelection = (rawValue) => {
    const raw = String(rawValue || "").trim().toUpperCase();
    if (!raw) {
      setSymbolSearchOpen(false);
      return;
    }
    const slashIdx = raw.indexOf("/");
    if (slashIdx > 0 && slashIdx < raw.length - 1) {
      const leg1 = raw.slice(0, slashIdx).trim();
      const leg2 = raw.slice(slashIdx + 1).trim();
      setLiveRatioSymbols({ leg1, leg2 });
      setSymbolSearchText(`${leg1}/${leg2}`);
    } else {
      setLiveRatioSymbols(null);
      setRatioModeEnabled(false);
      setMarketSymbol(raw);
      setSymbolSearchText(raw);
    }
    setSymbolSearchOpen(false);
  };

  const captureCurrentLayout = () => ({
    marketSymbol,
    liveRatioSymbols,
    chartTimeframeId,
    customTimeframeInput,
    customTimeframeUnit,
    priceSeriesStyle,
    chartBoxCount,
  });

  const applyLayout = (layout) => {
    if (!layout) return;
    if (layout.liveRatioSymbols) {
      setLiveRatioSymbols(layout.liveRatioSymbols);
      setSymbolSearchText(`${layout.liveRatioSymbols.leg1}/${layout.liveRatioSymbols.leg2}`);
    } else {
      setLiveRatioSymbols(null);
      setRatioModeEnabled(false);
      if (layout.marketSymbol) {
        setMarketSymbol(layout.marketSymbol);
        setSymbolSearchText(layout.marketSymbol);
      }
    }
    if (layout.chartTimeframeId) setChartTimeframeId(layout.chartTimeframeId);
    if (layout.customTimeframeInput != null) {
      setCustomTimeframeInput(layout.customTimeframeInput);
    }
    if (layout.customTimeframeUnit) setCustomTimeframeUnit(layout.customTimeframeUnit);
    if (layout.priceSeriesStyle) setPriceSeriesStyle(layout.priceSeriesStyle);
    if (layout.chartBoxCount) setChartBoxCount(layout.chartBoxCount);
  };

  /** Persists every save to localStorage so the list survives a reload. */
  useEffect(() => {
    try {
      window.localStorage.setItem("lwt.savedLayouts", JSON.stringify(savedLayouts));
    } catch (e) {
      // Storage full/unavailable — saved layouts just won't persist this session.
    }
  }, [savedLayouts]);

  /** Auto-saves the current instrument/timeframe/style as "last used" on every change,
   * and restores it once on mount — reopening the app lands back where you left off
   * instead of resetting to the EURUSD default. */
  useEffect(() => {
    if (!layoutRestoredRef.current) return;
    try {
      window.localStorage.setItem(
        "lwt.lastLayout",
        JSON.stringify({
          marketSymbol,
          liveRatioSymbols,
          chartTimeframeId,
          customTimeframeInput,
          customTimeframeUnit,
          priceSeriesStyle,
          chartBoxCount,
        })
      );
    } catch (e) {
      // ignore
    }
  }, [
    marketSymbol,
    liveRatioSymbols,
    chartTimeframeId,
    customTimeframeInput,
    customTimeframeUnit,
    priceSeriesStyle,
    chartBoxCount,
  ]);

  useEffect(() => {
    if (layoutRestoredRef.current) return;
    layoutRestoredRef.current = true;
    try {
      const raw = window.localStorage.getItem("lwt.lastLayout");
      const layout = raw ? JSON.parse(raw) : null;
      // Always open with a single chart; pick 2/4/8 panes from the grid menu when needed.
      if (layout) applyLayout({ ...layout, chartBoxCount: undefined });
    } catch (e) {
      // ignore — start from defaults
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore-once on mount
  }, []);

  const saveCurrentLayoutAs = (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const layout = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: trimmed, ...captureCurrentLayout() };
    setSavedLayouts((prev) => [...prev.filter((l) => l.name !== trimmed), layout]);
  };

  const deleteSavedLayout = (id) => {
    setSavedLayouts((prev) => prev.filter((l) => l.id !== id));
  };

  useEffect(() => {
    try {
      window.localStorage.setItem("lwt.watchlistSymbols", JSON.stringify(watchlistSymbols));
    } catch (e) {
      // ignore
    }
    window.dispatchEvent(new Event("lwt:watchlist-updated"));
  }, [watchlistSymbols]);

  const addToWatchlist = (rawSymbol) => {
    const sym = String(rawSymbol || "").trim().toUpperCase();
    if (!sym) return;
    setWatchlistSymbols((prev) => (prev.includes(sym) ? prev : [...prev, sym]));
  };

  const removeFromWatchlist = (sym) => {
    setWatchlistSymbols((prev) => prev.filter((s) => s !== sym));
  };

  /** Polls live quotes for the (small, bounded) watchlist only — not the full symbol
   * search universe, which can be hundreds of real instruments from a live broker feed
   * and would overwhelm the backend/broker connection if polled continuously. Change %
   * is relative to the first quote seen this session (no separate "prior close" endpoint
   * exists yet), so it resets whenever the page reloads. */
  useEffect(() => {
    if (!isBackendFeedActive || rightSideTab !== "watchlist" || !watchlistSymbols.length) {
      return undefined;
    }
    let cancelled = false;
    const baselineBySymbol = {};

    const pollOnce = () => {
      watchlistSymbols.forEach((sym) => {
        marketFetch(`${BACKEND_API_BASE}/quote?symbol=${encodeURIComponent(sym)}`)
          .then((res) => (res.ok ? res.json() : null))
          .then((q) => {
            if (cancelled || !q || !Number.isFinite(q.last)) return;
            if (!Number.isFinite(baselineBySymbol[sym])) {
              baselineBySymbol[sym] = q.last;
            }
            const baseline = baselineBySymbol[sym];
            const changeAbs = q.last - baseline;
            const changePct = baseline ? (changeAbs / baseline) * 100 : 0;
            setWatchlistQuotes((prev) => ({
              ...prev,
              [sym]: { last: q.last, changeAbs, changePct },
            }));
          })
          .catch(() => {});
      });
    };

    pollOnce();
    const id = window.setInterval(pollOnce, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [isBackendFeedActive, rightSideTab, watchlistSymbols]);

  useEffect(() => {
    if (!layoutsMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (layoutsMenuWrapRef.current?.contains(e.target)) return;
      setLayoutsMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [layoutsMenuOpen]);

  useEffect(() => {
    if (!layoutsMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLayoutsMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [layoutsMenuOpen]);

  useEffect(() => {
    isMeasureModeRef.current = isMeasureMode;
  }, [isMeasureMode]);

  useEffect(() => {
    isReplaySelectingRef.current = isReplaySelecting;
  }, [isReplaySelecting]);

  useEffect(() => {
    activePanelRef.current = activePanel;
    if (activePanel !== "none" && drawToolRef.current !== "none") {
      // opening Indicators / Settings / Script / AI puts the drawing tool away
      setDrawTool("none");
      setDrawStartPoint(null);
      setDrawRrStopPoint(null);
      setDrawHoverPoint(null);
    }
    if (activePanel !== "none") setIsMeasureMode(false);
  }, [activePanel]);

  useEffect(() => {
    isReplayModeRef.current = isReplayMode;
  }, [isReplayMode]);

  useEffect(() => {
    marketSymbolRef.current = marketSymbol;
  }, [marketSymbol]);

  useEffect(() => {
    backendFeedTimeframeRef.current = backendFeedTimeframe;
  }, [backendFeedTimeframe]);

  useEffect(() => {
    isBackendFeedActiveRef.current = isBackendFeedActive;
  }, [isBackendFeedActive]);

  useEffect(() => {
    ratioModeEnabledRef.current = ratioModeEnabled;
  }, [ratioModeEnabled]);

  useEffect(() => {
    liveRatioSymbolsRef.current = liveRatioSymbols;
  }, [liveRatioSymbols]);

  useEffect(() => {
    ratioCandles1Ref.current = ratioCandles1;
  }, [ratioCandles1]);

  useEffect(() => {
    ratioCandles2Ref.current = ratioCandles2;
  }, [ratioCandles2]);

  useEffect(() => {
    setChartGridMenuOpen(false);
    setSettingsChartGridMenuOpen(false);
  }, [activePanel]);

  useLayoutEffect(() => {
    if (!settingsChartGridMenuOpen) {
      setSettingsChartGridRect(null);
      return undefined;
    }
    const el = chartGridSettingsWrapRef.current;
    if (!el) {
      return undefined;
    }
    const update = () => {
      if (chartGridSettingsWrapRef.current) {
        setSettingsChartGridRect(chartGridSettingsWrapRef.current.getBoundingClientRect());
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [settingsChartGridMenuOpen]);

  useEffect(() => {
    if (!chartGridMenuOpen && !settingsChartGridMenuOpen) {
      return undefined;
    }
    const onPointerDown = (e) => {
      const el = e.target;
      if (
        chartGridToolbarWrapRef.current?.contains(el) ||
        chartGridSettingsWrapRef.current?.contains(el) ||
        chartGridSettingsDropdownRef.current?.contains(el)
      ) {
        return;
      }
      setChartGridMenuOpen(false);
      setSettingsChartGridMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [chartGridMenuOpen, settingsChartGridMenuOpen]);

  useEffect(() => {
    if (!chartGridMenuOpen && !settingsChartGridMenuOpen) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        setChartGridMenuOpen(false);
        setSettingsChartGridMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chartGridMenuOpen, settingsChartGridMenuOpen]);

  useEffect(() => {
    drawToolRef.current = drawTool;
  }, [drawTool]);

  useEffect(() => {
    measurePointsRef.current = measurePoints;
  }, [measurePoints]);

  useEffect(() => {
    drawStartPointRef.current = drawStartPoint;
  }, [drawStartPoint]);

  useEffect(() => {
    drawRrStopPointRef.current = drawRrStopPoint;
  }, [drawRrStopPoint]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    selectedBoxIndexRef.current = selectedBoxIndex;
  }, [selectedBoxIndex]);

  const visibleData = useMemo(
    () => aggregateOhlcToPeriod(rawVisibleCandles, chartPeriodSecondsResolved),
    [chartPeriodSecondsResolved, rawVisibleCandles]
  );

  const patternMapByTime = useMemo(
    () => (showPatterns ? scanAllPatterns(visibleData, patternToggles) : new Map()),
    [patternToggles, showPatterns, visibleData]
  );

  const patternMarkersComputed = useMemo(() => {
    if (!showPatterns || !patternMapByTime.size) return [];
    return buildPatternMarkers(patternMapByTime, visibleData, {
      bullishColor: isWhiteTheme ? "#16a34a" : "#4ade80",
      bearishColor: isWhiteTheme ? "#dc2626" : "#f87171",
      neutralColor: isWhiteTheme ? "#64748b" : "#94a3b8",
    });
  }, [isWhiteTheme, patternMapByTime, showPatterns, visibleData]);

  useEffect(() => {
    const refresh = () => {
      try { setBacktestTrades(JSON.parse(window.localStorage.getItem("lwt.backtestTrades") || "null")); } catch { setBacktestTrades(null); }
    };
    window.addEventListener("lwt:backtest-overlay-updated", refresh);
    return () => window.removeEventListener("lwt:backtest-overlay-updated", refresh);
  }, []);

  /** Trades handed over by the Backtest page (localStorage "lwt.backtestTrades"): arrow at the
   * entry, circle with the result at the exit. Shown only for the symbol that was backtested. */
  const backtestTradeMarkers = useMemo(() => {
    try {
      if (window.localStorage.getItem("lwt.backtestShowOnMain") === "0") return [];
      const saved = backtestTrades;
      if (!saved || !Array.isArray(saved.trades)) return [];
      if (String(saved.symbol || "").toUpperCase() !== String(marketSymbol || "").toUpperCase()) return [];
      const out = [];
      const shown = saved.trades.slice(-1500);
      const showText = shown.length <= 120; // too many labels make the chart unreadable
      shown.forEach((tr) => {
        if (!Number.isFinite(tr.t) || !Number.isFinite(tr.x)) return;
        const long = tr.s === 1;
        const win = tr.r > 0;
        const shortState = (kind) => kind === "range" ? "R" : kind === "trend" ? "T" : kind === "highVol" ? "HV" : "";
        const hmmEntryText = saved.strategyId === "hmm_bollinger_touch" && tr.g
          ? `${shortState(tr.g)}${tr.h && tr.h !== tr.g ? `→${shortState(tr.h)}` : ""}`
          : "";
        if (saved.strategyId === "hmm_bollinger_touch" && Number.isFinite(tr.q)) {
          out.push({
            time: tr.q,
            position: tr.u === "lowerBand" ? "belowBar" : "aboveBar",
            color: "#f6c453",
            shape: "square",
            text: showText ? "TOUCH" : "",
            size: 0.8,
          });
        }
        if (saved.strategyId === "hmm_bollinger_touch" && Number.isFinite(tr.z) && tr.v) {
          out.push({
            time: tr.z,
            position: long ? "aboveBar" : "belowBar",
            color: "#b59cff",
            shape: "square",
            text: showText ? (tr.v === "oppositeBand" ? "BAND EXIT SIGNAL" : tr.v === "regimeChange" ? "HMM EXIT SIGNAL" : "MODEL END") : "",
            size: 0.8,
          });
        }
        out.push(
          long
            ? { time: tr.t, position: "belowBar", color: "#26a69a", shape: "arrowUp", text: showText ? `ENTRY${hmmEntryText ? ` ${hmmEntryText}` : ""}` : "", size: 0.9 }
            : { time: tr.t, position: "aboveBar", color: "#ef5350", shape: "arrowDown", text: showText ? `ENTRY${hmmEntryText ? ` ${hmmEntryText}` : ""}` : "", size: 0.9 }
        );
        const exitCause = tr.v === "oppositeBand" ? "BAND" : tr.v === "regimeChange" ? "HMM" : "";
        out.push({
          time: tr.x,
          position: long ? "aboveBar" : "belowBar",
          color: win ? "#26a69a" : "#ef5350",
          shape: "circle",
          text: showText ? `EXIT${exitCause ? ` ${exitCause}` : ""} ${tr.r >= 0 ? "+" : ""}${(tr.r * 100).toFixed(1)}%` : "",
          size: 0.9,
        });
      });
      return out;
    } catch {
      return [];
    }
  }, [backtestTrades, marketSymbol]);

  useEffect(() => {
    if (!chartContainerEl || !backtestTradeMarkers.length) return undefined;
    const openMenu = (event) => {
      event.preventDefault();
      const menuWidth = 240;
      const menuHeight = 48;
      setBacktestContextMenu({
        x: Math.max(8, Math.min(event.clientX, window.innerWidth - menuWidth - 8)),
        y: Math.max(8, Math.min(event.clientY, window.innerHeight - menuHeight - 8)),
      });
    };
    chartContainerEl.addEventListener("contextmenu", openMenu);
    return () => chartContainerEl.removeEventListener("contextmenu", openMenu);
  }, [chartContainerEl, backtestTradeMarkers.length]);

  useEffect(() => {
    if (!backtestContextMenu) return undefined;
    const dismiss = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest("[data-backtest-context-menu]")) {
        setBacktestContextMenu(null);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setBacktestContextMenu(null);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [backtestContextMenu]);

  /** GoCharting indicators: computed once per data/settings change, keyed by type. */
  const goComputed = useMemo(() => {
    const map = new Map();
    goInstances.forEach((inst) => {
      const def = GO_INDICATORS[inst.type];
      if (!def) return;
      try {
        map.set(inst.type, def.compute(visibleData, inst.params));
      } catch (e) {
        map.set(inst.type, { lines: [] });
      }
    });
    return map;
  }, [goInstances, visibleData]);

  const goMarkers = useMemo(() => {
    const all = [];
    goComputed.forEach((res) => {
      if (res?.markers?.length) all.push(...res.markers);
    });
    return all.sort((a, b) => seriesPointTimeToUnix(a.time) - seriesPointTimeToUnix(b.time));
  }, [goComputed]);

  const ofAnalyzed = useMemo(
    () =>
      ofData?.bars?.length
        ? analyzeOrderflow(ofData.bars, {
            imbalancePct: ofSettings.imbalancePct,
            imbalanceMin: ofSettings.imbalanceMin,
            stackCount: ofSettings.stackCount,
          })
        : [],
    [ofData, ofSettings.imbalanceMin, ofSettings.imbalancePct, ofSettings.stackCount]
  );
  const ofMarkers = useMemo(
    () => (ofTools.trader && ofAnalyzed.length ? orderflowMarkers(ofAnalyzed, ofSignals) : []),
    [ofAnalyzed, ofSignals, ofTools.trader]
  );

  const visibleMarkers = useMemo(() => {
    let patterns = patternMarkersComputed;
    let signals = [...backtestTradeMarkers, ...goMarkers, ...ofMarkers];
    const cutoffTime = isReplayMode && replayIndex !== null ? data[replayIndex]?.time : null;
    const applyCutoff = Number.isFinite(cutoffTime);

    if (!showPatterns) {
      patterns = [];
    } else if (patterns.length && applyCutoff) {
      patterns = patterns.filter((marker) => marker.time <= cutoffTime);
    }

    if (signals.length && applyCutoff) {
      signals = signals.filter((marker) => marker.time <= cutoffTime);
    }

    const merged = [...patterns, ...signals];
    if (!merged.length) return merged;
    if (!backtestTradeMarkers.length && !goMarkers.length && !ofMarkers.length) {
      if (!patterns.length) return signals;
      if (!signals.length) return patterns;
    }
    return merged.sort((a, b) => seriesPointTimeToUnix(a.time) - seriesPointTimeToUnix(b.time));
  }, [
    backtestTradeMarkers,
    data,
    goMarkers,
    isReplayMode,
    ofMarkers,
    patternMarkersComputed,
    replayIndex,
    showPatterns,
  ]);

  const renkoBrickApplied = useMemo(() => {
    const n = parseFloat(String(renkoBrickInput).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [renkoBrickInput]);
  const rangeSizeApplied = useMemo(() => {
    const n = parseFloat(String(rangeSizeInput).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [rangeSizeInput]);
  const lineBreakLinesApplied = useMemo(() => {
    const n = parseInt(String(lineBreakLinesInput).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : 3;
  }, [lineBreakLinesInput]);
  const kagiReversalApplied = useMemo(() => {
    const n = parseFloat(String(kagiReversalInput).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [kagiReversalInput]);
  const renkoAtrPeriodApplied = useMemo(() => {
    const n = parseInt(String(renkoAtrPeriodInput).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : 14;
  }, [renkoAtrPeriodInput]);
  const pnfBoxApplied = useMemo(() => {
    const n = parseFloat(String(pnfBoxInput).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [pnfBoxInput]);
  const pnfReversalApplied = useMemo(() => {
    const n = parseFloat(String(pnfReversalInput).trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [pnfReversalInput]);

  const priceChartPayload = useMemo(
    () =>
      getPriceChartPayload(priceSeriesStyle, visibleData, {
        renkoBrickSize: renkoBrickApplied,
        renkoBrickMode,
        renkoAtrPeriod: renkoAtrPeriodApplied,
        renkoSource,
        rangeSize: rangeSizeApplied,
        rangeSource,
        lineBreakLines: lineBreakLinesApplied,
        kagiReversal: kagiReversalApplied,
        kagiMode,
        pnfBoxSize: pnfBoxApplied,
        pnfReversal: pnfReversalApplied,
        linePriceSource,
        lineChartLineType,
        baselinePriceInput,
      }),
    [
      baselinePriceInput,
      kagiMode,
      kagiReversalApplied,
      lineBreakLinesApplied,
      lineChartLineType,
      linePriceSource,
      pnfBoxApplied,
      pnfReversalApplied,
      priceSeriesStyle,
      rangeSizeApplied,
      rangeSource,
      renkoAtrPeriodApplied,
      renkoBrickApplied,
      renkoBrickMode,
      renkoSource,
      visibleData,
    ]
  );

  const goToSelectedDate = (dateValue) => {
    const seriesPoints = priceChartPayload?.data;
    const dateStr =
      typeof dateValue === "string" && dateValue.trim()
        ? dateValue.trim()
        : String(goToDate || "").trim();

    if (!seriesPoints?.length || !dateStr) {
      return;
    }

    let logicalIndex = findLogicalIndexForCalendarDate(seriesPoints, dateStr);
    if (logicalIndex === null) {
      const targetTime = unixSecondsFromUtcYmd(dateStr);
      if (Number.isNaN(targetTime)) {
        return;
      }
      logicalIndex = findNearestSeriesTimeIndex(seriesPoints, targetTime);
    }

    const barCount = seriesPoints.length;
    const safeIndex = Math.min(Math.max(logicalIndex, 0), barCount - 1);

    // Defer scrolling so it runs after React effects that call setData + fitContent()
    // for the same interaction (e.g. date input onChange + setGoToDate).
    window.setTimeout(() => {
      const timeScale = chartRef.current?.timeScale();
      if (!timeScale || !seriesPoints.length) {
        return;
      }

      const logicalRange = timeScale.getVisibleLogicalRange();
      const currentWidth = logicalRange
        ? logicalRange.to - logicalRange.from
        : Math.min(50, barCount);
      const halfWidth = Math.max(currentWidth / 2, 5);

      let from = safeIndex - halfWidth;
      let to = safeIndex + halfWidth;

      if (from < 0) {
        to = Math.min(barCount - 1, to - from);
        from = 0;
      }
      if (to > barCount - 1) {
        from = Math.max(0, from - (to - (barCount - 1)));
        to = barCount - 1;
      }

      timeScale.setVisibleLogicalRange({ from, to });
    }, 0);
  };

  const handleUserScriptRun = () => {
    if (!userScriptCode.trim()) {
      setUserScriptError("Script is empty.");
      return;
    }
    if (!visibleData.length) {
      setUserScriptError("No candle data.");
      return;
    }
    setUserScriptError(null);
    setUserScriptEnabled(true);
    setUserScriptRunRequest((current) => current + 1);
  };

  const buildAiChatContext = () => {
    const source =
      Array.isArray(visibleData) && visibleData.length ? visibleData : data;
    const slice = source.slice(-80);
    const tfLabel =
      chartTimeframeId === CUSTOM_TIMEFRAME_ID
        ? `custom-${customTimeframeInput}${customTimeframeUnit}`
        : chartTimeframeId;
    return {
      symbol: marketSymbol,
      timeframe: tfLabel,
      candles: slice.map((c) => ({
        time: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: typeof c.volume === "number" && Number.isFinite(c.volume) ? c.volume : 0,
      })),
    };
  };

  const sendAiChat = async () => {
    const text = aiChatInput.trim();
    if (!text || aiChatLoading) {
      return;
    }

    const messages = [...aiChatMessages, { role: "user", content: text }];
    setAiChatInput("");
    setAiChatLoading(true);
    setAiChatError(null);

    try {
      // /chat uses the server-configured Claude credential; no provider key is sent by this browser.
      const chatAuthToken = getAuthToken();
      const res = await marketFetch(`${BACKEND_API_BASE}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(chatAuthToken ? { Authorization: `Bearer ${chatAuthToken}` } : {}),
        },
        body: JSON.stringify({
          messages,
          provider: "anthropic",
          context: aiIncludeContext ? buildAiChatContext() : null,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = payload?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d?.msg || d).join("; ")
              : res.statusText;
        throw new Error(msg || "Chat request failed");
      }
      setAiChatMessages([
        ...messages,
        { role: "assistant", content: payload.message },
      ]);
    } catch (err) {
      setAiChatError(err?.message || "Request failed");
    } finally {
      setAiChatLoading(false);
    }
  };

  const oscillatorPaneIndices = useMemo(() => {
    const paneMap = {};
    let nextPaneIndex = 1;
    const flagByKey = {
      rsi: showRsi,
      cci: showCci,
      williams: showWilliams,
      roc: showRoc,
      momentum: showMom,
      stochastic: showStochastic,
      stochRsi: showStochRsi,
      macd: showMacd,
      atr: showAtr,
      adx: showAdx,
      ao: showAo,
      tsi: showTsi,
      obv: showObv,
      cmf: showCmf,
      mfi: showMfi,
      chaikinVol: showChaikinVol,
      volOsc: showVolOsc,
      adLine: showAdLine,
      stdDev: showStdDev,
      kaufmanRange: showKaufmanRange && kaufmanRangeDisplayMode === "efficiency",
      userScript:
        userScriptEnabled && userScriptOutput?.pane === "oscillator",
    };
    OSCILLATOR_ORDER_KEYS.forEach((key) => {
      if (flagByKey[key]) {
        paneMap[key] = nextPaneIndex;
        nextPaneIndex += 1;
      }
    });
    return paneMap;
  }, [
    showAdLine,
    showAdx,
    showAo,
    showAtr,
    showCci,
    showChaikinVol,
    showCmf,
    showKaufmanRange,
    kaufmanRangeDisplayMode,
    showMacd,
    showMfi,
    showMom,
    showObv,
    showRoc,
    showRsi,
    showStdDev,
    showStochRsi,
    showStochastic,
    showTsi,
    showVolOsc,
    showWilliams,
    userScriptEnabled,
    userScriptOutput,
  ]);

  const movingAverageData = useMemo(
    () => calculateSMA(visibleData, maPeriod),
    [maPeriod, visibleData]
  );
  const smaData = useMemo(
    () => calculateSMA(visibleData, smaPeriod),
    [smaPeriod, visibleData]
  );
  const emaData = useMemo(
    () => calculateEMA(visibleData, emaPeriod),
    [emaPeriod, visibleData]
  );
  const wmaData = useMemo(
    () => calculateWMA(visibleData, wmaPeriod),
    [visibleData, wmaPeriod]
  );
  const bollingerData = useMemo(
    () => calculateBollingerBands(visibleData, bbPeriod, bbDeviation),
    [bbDeviation, bbPeriod, visibleData]
  );
  const rsiData = useMemo(
    () => calculateRSI(visibleData, rsiPeriod),
    [rsiPeriod, visibleData]
  );
  const atrData = useMemo(
    () => calculateATR(visibleData, atrPeriod),
    [atrPeriod, visibleData]
  );
  const donchianData = useMemo(
    () => calculateDonchianChannels(visibleData, donchianPeriod),
    [donchianPeriod, visibleData]
  );
  const stochasticData = useMemo(
    () => calculateStochastic(visibleData, stochKPeriod, stochDPeriod),
    [stochDPeriod, stochKPeriod, visibleData]
  );
  const macdData = useMemo(
    () =>
      calculateMACD(
        visibleData,
        macdFastPeriod,
        macdSlowPeriod,
        macdSignalPeriod
      ),
    [macdFastPeriod, macdSignalPeriod, macdSlowPeriod, visibleData]
  );

  const hasRealVolume = useMemo(() => dataHasRealVolume(visibleData), [visibleData]);

  const vwapData = useMemo(() => calculateVWAP(visibleData), [visibleData]);
  const hmaData = useMemo(
    () => calculateHMA(visibleData, hmaPeriod),
    [hmaPeriod, visibleData]
  );
  const demaData = useMemo(
    () => calculateDEMA(visibleData, demaPeriod),
    [demaPeriod, visibleData]
  );
  const temaData = useMemo(
    () => calculateTEMA(visibleData, temaPeriod),
    [temaPeriod, visibleData]
  );
  const ichimokuData = useMemo(
    () =>
      calculateIchimoku(
        visibleData,
        DEFAULT_ICH_TENKAN,
        DEFAULT_ICH_KIJUN,
        DEFAULT_ICH_SENKOU_B,
        DEFAULT_ICH_DISP
      ),
    [visibleData]
  );
  const sarData = useMemo(() => calculateParabolicSAR(visibleData), [visibleData]);
  const keltnerData = useMemo(
    () => calculateKeltner(visibleData, keltEma, keltAtr, keltMult),
    [keltAtr, keltEma, keltMult, visibleData]
  );
  const cciData = useMemo(
    () => calculateCCI(visibleData, cciPeriod),
    [cciPeriod, visibleData]
  );
  const williamsData = useMemo(
    () => calculateWilliamsR(visibleData, williamsPeriod),
    [visibleData, williamsPeriod]
  );
  const rocData = useMemo(
    () => calculateROC(visibleData, rocPeriod),
    [rocPeriod, visibleData]
  );
  const momData = useMemo(
    () => calculateMomentum(visibleData, momPeriod),
    [momPeriod, visibleData]
  );
  const stochRsiData = useMemo(
    () =>
      calculateStochRSI(
        visibleData,
        stochRsiRsi,
        stochRsiStoch,
        stochRsiK,
        stochRsiD
      ),
    [stochRsiD, stochRsiK, stochRsiRsi, stochRsiStoch, visibleData]
  );
  const adxBundle = useMemo(
    () => calculateADX(visibleData, adxPeriod),
    [adxPeriod, visibleData]
  );
  const aoData = useMemo(
    () => calculateAwesomeOscillator(visibleData),
    [visibleData]
  );
  const tsiData = useMemo(() => calculateTSI(visibleData), [visibleData]);
  const obvData = useMemo(() => calculateOBV(visibleData), [visibleData]);
  const cmfData = useMemo(
    () => calculateCMF(visibleData, cmfPeriod),
    [cmfPeriod, visibleData]
  );
  const mfiData = useMemo(
    () => calculateMFI(visibleData, mfiPeriod),
    [mfiPeriod, visibleData]
  );
  const adLineData = useMemo(
    () => calculateAccumulationDistribution(visibleData),
    [visibleData]
  );
  const chaikinVolData = useMemo(
    () =>
      calculateChaikinVolatility(
        visibleData,
        DEFAULT_CHAIKIN_VOL_EMA,
        DEFAULT_CHAIKIN_VOL_ROC
      ),
    [visibleData]
  );
  const volOscData = useMemo(
    () =>
      calculateVolumeOscillator(
        visibleData,
        DEFAULT_VOLUME_OSC_FAST,
        DEFAULT_VOLUME_OSC_SLOW
      ),
    [visibleData]
  );
  const stdDevData = useMemo(
    () => calculateRollingStdDev(visibleData, stdDevPeriod),
    [stdDevPeriod, visibleData]
  );
  const kaufmanRangeData = useMemo(
    () => calculateKaufmanEfficiencyRatio(visibleData, kaufmanRangePeriod, kaufmanRangeSmooth),
    [kaufmanRangePeriod, kaufmanRangeSmooth, visibleData]
  );
  const kaufmanRangeRegimes = useMemo(() => {
    const efficiencyByTime = new Map(kaufmanRangeData.map((point) => [point.time, point.value]));
    return visibleData.flatMap((bar) => {
      const efficiency = efficiencyByTime.get(bar.time);
      return Number.isFinite(efficiency)
        ? [{
            time: bar.time,
            kind: efficiency >= kaufmanRangeTrendThreshold
              ? "trend"
              : efficiency <= kaufmanRangeRangeThreshold ? "range" : "transition",
          }]
        : [];
    });
  }, [kaufmanRangeData, kaufmanRangeRangeThreshold, kaufmanRangeTrendThreshold, visibleData]);
  const localHmmRegimeResult = useMemo(
    () => kaufmanRangeRegimeMethod === "hmm"
      ? detectHMMRegimes(visibleData, { lookback: kaufmanRangePeriod, trainingFraction: Number(hmmTrainingSplit) / 100 })
      : { status: "disabled", reason: "Select HMM to fit this candle history.", bars: [] },
    [hmmModelMode, hmmTrainingSplit, kaufmanRangePeriod, kaufmanRangeRegimeMethod, visibleData]
  );
  const savedHmmRegime = backtestTrades?.strategyId === "hmm_bollinger_touch"
    ? backtestTrades.hmmRegime
    : null;
  const savedHmmRegimeMatches = Boolean(
    kaufmanRangeRegimeMethod === "hmm"
    && savedHmmRegime?.status === "ready"
    && String(backtestTrades?.symbol || "").toUpperCase() === String(marketSymbol || "").toUpperCase()
    && String(backtestTrades?.tf || "").toLowerCase() === String(backendFeedTimeframe || "").toLowerCase()
    && Number(savedHmmRegime.lookback) === Number(kaufmanRangePeriod)
    && savedHmmRegime.modelMode === hmmModelMode
    && (hmmModelMode === "causal200" || Number(savedHmmRegime.trainingFraction) === Number(hmmTrainingSplit) / 100)
    && (hmmModelMode === "causal200" || [savedHmmRegime.trainingBars, savedHmmRegime.validationBars, savedHmmRegime.holdoutLogScore, savedHmmRegime.baselineLogScore, savedHmmRegime.scoreDelta]
      .every((value) => typeof value === "number" && Number.isFinite(value)))
  );
  const savedHmmRegimeBars = useMemo(
    () => savedHmmRegimeMatches ? decodeHMMRegimeBars(savedHmmRegime?.bars) : [],
    [savedHmmRegime?.bars, savedHmmRegimeMatches]
  );
  const localHmmDisplayBars = hmmModelMode === "causal200"
    ? localHmmRegimeResult.causalBars || []
    : localHmmRegimeResult.bars || [];
  const hmmRegimeResult = useMemo(() => {
    const localResult = {
      ...localHmmRegimeResult,
      status: hmmModelMode === "causal200"
        ? (localHmmRegimeResult.causalStatus || localHmmRegimeResult.status)
        : localHmmRegimeResult.status,
      bars: localHmmDisplayBars,
      reason: hmmModelMode === "causal200"
        ? localHmmRegimeResult.causalReason || localHmmRegimeResult.reason
        : localHmmRegimeResult.reason,
    };
    if (!savedHmmRegimeMatches || !savedHmmRegimeBars.length) return localResult;
    const savedByTime = new Map(savedHmmRegimeBars.map((bar) => [bar.time, bar]));
    const localByTime = new Map(localHmmDisplayBars.map((bar) => [Number(bar.time), bar]));
    const bars = visibleData.flatMap((candle) => {
      const state = savedByTime.get(Number(candle.time)) || localByTime.get(Number(candle.time));
      return state ? [state] : [];
    });
    return {
      ...localHmmRegimeResult,
      status: "ready",
      reason: "",
      bars,
      lookback: savedHmmRegime.lookback,
      trainingBars: savedHmmRegime.trainingBars,
      validationBars: savedHmmRegime.validationBars,
      trainingFraction: savedHmmRegime.trainingFraction,
      modelMode: savedHmmRegime.modelMode,
      causalWarmupBars: savedHmmRegime.causalWarmupBars,
      holdoutLogScore: savedHmmRegime.holdoutLogScore,
      baselineLogScore: savedHmmRegime.baselineLogScore,
      scoreDelta: savedHmmRegime.scoreDelta,
      usesSavedBacktestFit: true,
    };
  }, [hmmModelMode, localHmmDisplayBars, localHmmRegimeResult, savedHmmRegime, savedHmmRegimeBars, savedHmmRegimeMatches, visibleData]);
  const gmmRegimeResult = useMemo(
    () => kaufmanRangeRegimeMethod === "gmm"
      ? detectGMMRegimes(visibleData, { lookback: kaufmanRangePeriod })
      : { status: "disabled", reason: "Select GMM to fit this candle history.", bars: [] },
    [kaufmanRangePeriod, kaufmanRangeRegimeMethod, visibleData]
  );
  const bocpdRegimeResult = useMemo(
    () => kaufmanRangeRegimeMethod === "bocpd"
      ? detectBOCPDRegimes(visibleData, {
          lookback: kaufmanRangePeriod,
          expectedRunLength: bocpdExpectedRunLength,
          threshold: bocpdChangeThreshold,
        })
      : { status: "disabled", reason: "Select BOCPD to calculate change probabilities.", bars: [] },
    [bocpdChangeThreshold, bocpdExpectedRunLength, kaufmanRangePeriod, kaufmanRangeRegimeMethod, visibleData]
  );
  const dRegimeResult = useMemo(
    () => kaufmanRangeRegimeMethod === "composite"
      ? detectDRegimes(visibleData, dRegimeSettings)
      : { status: "disabled", reason: "Select the D composite filter to calculate this regime.", bars: [] },
    [dRegimeSettings, kaufmanRangeRegimeMethod, visibleData]
  );
  const selectedRegimeBars = kaufmanRangeRegimeMethod === "hmm"
    ? hmmRegimeResult.bars
    : kaufmanRangeRegimeMethod === "gmm"
      ? gmmRegimeResult.bars
      : kaufmanRangeRegimeMethod === "bocpd"
        ? bocpdRegimeResult.bars.filter((bar) => bar.kind === "change")
    : kaufmanRangeRegimeMethod === "composite"
      ? dRegimeResult.bars
      : kaufmanRangeRegimes;
  const selectedCurrentRegime = kaufmanRangeRegimeMethod === "bocpd"
    ? bocpdRegimeResult.bars.at(-1)?.kind
    : kaufmanRangeRegimeMethod === "composite" && dRegimeResult.status !== "ready"
      ? null
      : selectedRegimeBars.at(-1)?.kind;
  const selectedHmmProbabilities = hmmRegimeResult.bars.at(-1)?.probabilities;

  // Share the loaded candles and the selected admin regime with the quant strip.
  useEffect(() => {
    publishChartData({
      candles: visibleData,
      symbol: marketSymbol,
      timeframe: backendFeedTimeframe,
      barSeconds: chartPeriodSecondsResolved,
      regimeTrendThreshold: kaufmanRangeTrendThreshold,
      regimeRangeThreshold: kaufmanRangeRangeThreshold,
      regimePeriod: kaufmanRangePeriod,
      regimeSmooth: kaufmanRangeSmooth,
      regimeModel: kaufmanRangeRegimeMethod,
      regimeCurrent: kaufmanRangeRegimeMethod === "bocpd"
        ? selectedCurrentRegime === "change" ? "Change alarm" : selectedCurrentRegime === "quiet" ? "No change alarm" : "Warming up"
        : selectedCurrentRegime === "highVol" ? "High vol"
          : selectedCurrentRegime ? `${selectedCurrentRegime[0].toUpperCase()}${selectedCurrentRegime.slice(1)}`
            : "Warming up",
      regimeValidation: kaufmanRangeRegimeMethod === "hmm" && hmmModelMode === "holdout" && hmmRegimeResult.status === "ready" ? hmmRegimeResult.scoreDelta : null,
    });
  }, [visibleData, marketSymbol, backendFeedTimeframe, chartPeriodSecondsResolved, kaufmanRangePeriod, kaufmanRangeRangeThreshold, kaufmanRangeSmooth, kaufmanRangeTrendThreshold, kaufmanRangeRegimeMethod, hmmModelMode, hmmTrainingSplit, selectedCurrentRegime, hmmRegimeResult, gmmRegimeResult, bocpdRegimeResult, dRegimeSettings]);

  useEffect(() => {
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    const enabled = showKaufmanRange && kaufmanRangeDisplayMode === "regime";
    const current = efficiencyRegimePrimitiveRef.current;

    if (current && (current.chart !== chart || current.series !== series || !enabled)) {
      try {
        current.series.detachPrimitive(current.primitive);
      } catch { /* chart or series was already removed */ }
      efficiencyRegimePrimitiveRef.current = null;
    }
    if (!enabled || !chart || !series) return;

    if (!efficiencyRegimePrimitiveRef.current) {
      const primitive = new EfficiencyRegimePrimitive();
      series.attachPrimitive(primitive);
      efficiencyRegimePrimitiveRef.current = { primitive, series, chart };
    }
    efficiencyRegimePrimitiveRef.current.primitive.setState({ bars: selectedRegimeBars });
  }, [chartContainerEl, kaufmanRangeDisplayMode, selectedRegimeBars, showKaufmanRange]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const toggleRegimeZones = () => {
      if (showKaufmanRange && kaufmanRangeDisplayMode === "regime") {
        setShowKaufmanRange(false);
      } else {
        setShowKaufmanRange(true);
        setKaufmanRangeDisplayMode("regime");
      }
    };
    window.addEventListener("lwt:toggle-regime-zones", toggleRegimeZones);
    return () => window.removeEventListener("lwt:toggle-regime-zones", toggleRegimeZones);
  }, [isAdmin, kaufmanRangeDisplayMode, showKaufmanRange]);

  const movingAverageValuesByTime = useMemo(() => {
    return new Map(
      movingAverageData.map((entry) => [entry.time, entry.value])
    );
  }, [movingAverageData]);
  const smaValuesByTime = useMemo(
    () => new Map(smaData.map((entry) => [entry.time, entry.value])),
    [smaData]
  );
  const emaValuesByTime = useMemo(
    () => new Map(emaData.map((entry) => [entry.time, entry.value])),
    [emaData]
  );
  const wmaValuesByTime = useMemo(
    () => new Map(wmaData.map((entry) => [entry.time, entry.value])),
    [wmaData]
  );
  const bbUpperValuesByTime = useMemo(
    () => new Map(bollingerData.upper.map((entry) => [entry.time, entry.value])),
    [bollingerData.upper]
  );
  const bbLowerValuesByTime = useMemo(
    () => new Map(bollingerData.lower.map((entry) => [entry.time, entry.value])),
    [bollingerData.lower]
  );
  const rsiValuesByTime = useMemo(
    () => new Map(rsiData.map((entry) => [entry.time, entry.value])),
    [rsiData]
  );
  const atrValuesByTime = useMemo(
    () => new Map(atrData.map((entry) => [entry.time, entry.value])),
    [atrData]
  );
  const donchianUpperValuesByTime = useMemo(
    () => new Map(donchianData.upper.map((entry) => [entry.time, entry.value])),
    [donchianData.upper]
  );
  const donchianLowerValuesByTime = useMemo(
    () => new Map(donchianData.lower.map((entry) => [entry.time, entry.value])),
    [donchianData.lower]
  );
  const stochasticKValuesByTime = useMemo(
    () => new Map(stochasticData.k.map((entry) => [entry.time, entry.value])),
    [stochasticData.k]
  );
  const stochasticDValuesByTime = useMemo(
    () => new Map(stochasticData.d.map((entry) => [entry.time, entry.value])),
    [stochasticData.d]
  );
  const macdLineValuesByTime = useMemo(
    () => new Map(macdData.line.map((entry) => [entry.time, entry.value])),
    [macdData.line]
  );
  const macdSignalValuesByTime = useMemo(
    () => new Map(macdData.signal.map((entry) => [entry.time, entry.value])),
    [macdData.signal]
  );
  const macdHistogramValuesByTime = useMemo(
    () => new Map(macdData.histogram.map((entry) => [entry.time, entry.value])),
    [macdData.histogram]
  );

  const excelDateToJS = (excelDate) => {
    return new Date((excelDate - 25569) * 86400 * 1000);
  };

  const dateFromUtcParts = (
    year,
    month,
    day,
    hour = 0,
    minute = 0,
    second = 0,
    millisecond = 0
  ) =>
    new Date(
      Date.UTC(
        year,
        month - 1,
        day,
        hour,
        minute,
        second,
        millisecond
      )
    );

  const dateFromParsedWallClockAsUtc = (value) => {
    const raw = String(value ?? "").trim();
    // Matches ISO-style (-, /) and MetaTrader/MT4/MT5-style (.) date separators, e.g. "2020.01.02 00:00".
    const match = raw.match(
      /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[ T](\d{1,2})(?::(\d{1,2})(?::(\d{1,2})(?:\.(\d{1,3}))?)?)?)?$/
    );
    if (match) {
      const [, y, mo, d, h = "0", mi = "0", s = "0", ms = "0"] = match;
      return dateFromUtcParts(
        Number(y),
        Number(mo),
        Number(d),
        Number(h),
        Number(mi),
        Number(s),
        Number(ms.padEnd(3, "0"))
      );
    }

    const slashMatch = raw.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2})(?::(\d{1,2})(?::(\d{1,2})(?:\.(\d{1,3}))?)?)?)?$/
    );
    if (slashMatch) {
      const [, first, second, y, h = "0", mi = "0", s = "0", ms = "0"] =
        slashMatch;
      const a = Number(first);
      const b = Number(second);
      const month = a > 12 ? b : a;
      const day = a > 12 ? a : b;
      return dateFromUtcParts(
        Number(y),
        month,
        day,
        Number(h),
        Number(mi),
        Number(s),
        Number(ms.padEnd(3, "0"))
      );
    }

    return new Date(raw);
  };

  const dateFromExplicitOffsetString = (value) => {
    const raw = String(value ?? "").trim();
    const match = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:?\d{2})$/i
    );
    if (!match) {
      return null;
    }

    const [, y, mo, d, h, mi, s = "0", ms = "0", offset] = match;
    const baseMs = Date.UTC(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s),
      Number(ms.padEnd(3, "0"))
    );

    if (offset.toUpperCase() === "Z") {
      return new Date(baseMs);
    }

    const sign = offset.startsWith("-") ? -1 : 1;
    const cleanOffset = offset.slice(1).replace(":", "");
    const offsetHours = Number(cleanOffset.slice(0, 2));
    const offsetMinutes = Number(cleanOffset.slice(2, 4));
    const offsetMs = sign * (offsetHours * 60 + offsetMinutes) * 60 * 1000;
    return new Date(baseMs - offsetMs);
  };

  const parseSpreadsheetTimeValue = (timeValue) => {
    if (typeof timeValue === "number") {
      if (timeValue > 100000000000) {
        return new Date(normalizeEpochSeconds(timeValue) * 1000);
      }
      if (timeValue > 1000000000) {
        return new Date(normalizeEpochSeconds(timeValue) * 1000);
      }
      return excelDateToJS(timeValue);
    }

    if (typeof timeValue !== "string") {
      return new Date(timeValue);
    }

    const raw = timeValue.trim();
    if (/^-?\d+(?:\.\d+)?$/.test(raw)) {
      const n = Number(raw);
      if (Math.abs(n) > 1000000000) {
        return new Date(normalizeEpochSeconds(n) * 1000);
      }
      return excelDateToJS(n);
    }
    const hasTzSuffix = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
    if (hasTzSuffix) {
      return dateFromExplicitOffsetString(raw) ?? new Date(raw.replace(" ", "T"));
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return new Date(`${raw}T00:00:00Z`);
    }

    const isoish = raw.replace(" ", "T");
    if (/^\d{4}-\d{2}-\d{2}T/.test(isoish)) {
      return new Date(`${isoish}Z`);
    }

    return dateFromParsedWallClockAsUtc(raw);
  };

  const normalizeColumnName = (name) =>
    String(name ?? "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const findColumn = (row, candidates) => {
    const entries = Object.keys(row).map((key) => ({
      key,
      normalized: normalizeColumnName(key),
    }));

    for (const candidate of candidates) {
      const match = entries.find(({ normalized }) => normalized === candidate);
      if (match) return match.key;
    }

    for (const candidate of candidates) {
      const partialMatch = entries.find(
        ({ normalized }) =>
          normalized.includes(candidate) || candidate.includes(normalized)
      );
      if (partialMatch) return partialMatch.key;
    }

    return null;
  };

  /**
   * Parses a spreadsheet File into candles and hands the result to `onDone` as
   * `{ candles, error }` (candles is [] when error is set). Shared by the main box
   * importer and the two ratio-mode importers so the column-detection/parsing logic
   * (and its date-parsing footgun, see the raw:true note below) lives in one place.
   */
  const MAX_SPREADSHEET_FILE_BYTES = 25 * 1024 * 1024;
  const MAX_SPREADSHEET_ROWS = 250_000;
  const parseSpreadsheetFile = (file, onDone) => {
    const extension = String(file?.name || "").split(".").pop().toLowerCase();
    if (!file || !["xlsx", "csv"].includes(extension)) {
      onDone({ candles: [], error: "Choose a .xlsx or .csv data file." });
      return;
    }
    if (file.size > MAX_SPREADSHEET_FILE_BYTES) {
      onDone({ candles: [], error: "Files must be 25 MB or smaller." });
      return;
    }
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
      // `raw: true` here (distinct from sheet_to_json's own `raw` option below) stops
      // SheetJS's plaintext/CSV parser from auto-detecting date-looking strings and
      // silently re-encoding them as Excel date serials via a local-timezone-dependent
      // JS Date parse — which corrupted explicit-offset timestamps (e.g.
      // "2026-05-01 06:00:00+00:00") before our own timezone-safe parsing ever saw them.
      const workbook = XLSX.read(evt.target.result, { type: "binary", raw: true, sheetRows: MAX_SPREADSHEET_ROWS + 1 });

      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!sheet) {
        onDone({ candles: [], error: "The selected file has no readable worksheet." });
        return;
      }
      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        raw: true,
        defval: null,
      });

      if (jsonData.length > MAX_SPREADSHEET_ROWS) {
        onDone({ candles: [], error: `Files may contain at most ${MAX_SPREADSHEET_ROWS.toLocaleString()} rows.` });
        return;
      }

      if (!jsonData.length) {
        onDone({ candles: [], error: "The selected file has no rows to chart." });
        return;
      }

      const timeColumn = findColumn(jsonData[0], TIME_COLUMN_CANDIDATES);
      const openColumn = findColumn(jsonData[0], ["open"]);
      const highColumn = findColumn(jsonData[0], ["high"]);
      const lowColumn = findColumn(jsonData[0], ["low"]);
      const closeColumn = findColumn(jsonData[0], ["close"]);
      const volumeColumn = findColumn(jsonData[0], ["volume", "vol", "Volume", "VOL"]);
      // MetaTrader-style exports (common for forex OHLC) split date and time-of-day into
      // separate columns. TIME_COLUMN_CANDIDATES matches "date" before "time", so without this,
      // every intraday bar's time-of-day is silently dropped and all bars for a day collapse
      // onto midnight — decimating the file down to ~1 row/day after the de-dupe below.
      const timeIsDateOnlyColumn =
        !!timeColumn && normalizeColumnName(timeColumn) === "date";
      const timeOfDayColumn = timeIsDateOnlyColumn
        ? findColumn(jsonData[0], ["time"])
        : null;

      if (!timeColumn || !openColumn || !highColumn || !lowColumn || !closeColumn) {
        onDone({
          candles: [],
          error: "Missing one or more required columns: time/date, open, high, low, close.",
        });
        return;
      }

      const parsedRows = jsonData
        .map((row) => {
          const rowTimeColumn = timeColumn ?? findColumn(row, TIME_COLUMN_CANDIDATES);
          const rowTimeOfDayColumn =
            timeOfDayColumn ??
            (timeIsDateOnlyColumn ? findColumn(row, ["time"]) : null);
          const rowOpenColumn = openColumn ?? findColumn(row, ["open"]);
          const rowHighColumn = highColumn ?? findColumn(row, ["high"]);
          const rowLowColumn = lowColumn ?? findColumn(row, ["low"]);
          const rowCloseColumn = closeColumn ?? findColumn(row, ["close"]);
          const rowVolumeColumn = volumeColumn ?? findColumn(row, ["volume", "vol", "Volume", "VOL"]);

          const dateValue = rowTimeColumn ? row[rowTimeColumn] : undefined;
          const timeOfDayValue = rowTimeOfDayColumn ? row[rowTimeOfDayColumn] : undefined;
          const timeValue =
            typeof dateValue === "number" && typeof timeOfDayValue === "number"
              ? dateValue + timeOfDayValue
              : timeOfDayValue != null && timeOfDayValue !== ""
                ? `${dateValue} ${timeOfDayValue}`
                : dateValue;
          const open = rowOpenColumn ? row[rowOpenColumn] : undefined;
          const high = rowHighColumn ? row[rowHighColumn] : undefined;
          const low = rowLowColumn ? row[rowLowColumn] : undefined;
          const close = rowCloseColumn ? row[rowCloseColumn] : undefined;
          const volRaw = rowVolumeColumn ? row[rowVolumeColumn] : undefined;
          const date = parseSpreadsheetTimeValue(timeValue);

          const time = Math.floor(date.getTime() / 1000);

          const o = Number(open);
          const h = Number(high);
          const l = Number(low);
          const c = Number(close);
          const v = volRaw != null && volRaw !== "" ? Number(volRaw) : NaN;

          if (
            !time ||
            isNaN(o) ||
            isNaN(h) ||
            isNaN(l) ||
            isNaN(c)
          ) {
            return null;
          }

          const candle = { time, open: o, high: h, low: l, close: c };
          if (Number.isFinite(v) && v >= 0) {
            candle.volume = v;
          }

          return {
            candle,
            row,
          };
        })
        .filter(Boolean);

      const uniqueRows = parsedRows
        .sort((a, b) => a.candle.time - b.candle.time)
        .filter((item, i, arr) => {
          return i === 0 || item.candle.time !== arr[i - 1].candle.time;
        });

      onDone({ candles: uniqueRows.map(({ candle }) => candle) });
      } catch (_) {
        onDone({ candles: [], error: "Could not safely read this spreadsheet. Check that it is a valid .xlsx or .csv file." });
      }
    };

    reader.onerror = () => {
      onDone({ candles: [], error: `Could not read ${file.name}.` });
    };

    reader.readAsBinaryString(file);
  };

  // 📌 Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const target = selectedBoxIndexRef.current;
    if (target === 0) {
      setIsBackendFeedActive(false);
    } else {
      paneFileUploadRef.current.add(target);
      paneLoadedKeyRef.current[target] = "file";
    }

    setUploadStatus({
      tone: "info",
      message: `Reading ${file.name}...`,
    });

    parseSpreadsheetFile(file, ({ candles, error }) => {
      if (error) {
        setBoxAtIndex(target, []);
        setUploadStatus({ tone: "error", message: error });
        return;
      }

      pendingScaleFitRef.current = true;
      setBoxAtIndex(target, candles);
      stopReplay();
      clearMeasurement();
      clearDrawings();
      setUploadStatus({
        tone: candles.length ? "success" : "error",
        message: candles.length
          ? chartBoxCount > 1
            ? `Loaded ${candles.length} candles into pane ${target + 1} from ${file.name}.`
            : `Loaded ${candles.length} candles from ${file.name}.`
          : `No valid OHLC rows found in ${file.name}.`,
      });
    });
  };

  /** Handles the "Ratio 1" / "Ratio 2" file inputs — loads a dataset into the ratio slot
   * without touching the normal box data, and auto-enables ratio mode once both are set. */
  const handleRatioFileUpload = (slot) => (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadStatus({
      tone: "info",
      message: `Reading ${file.name} for Ratio ${slot}...`,
    });

    parseSpreadsheetFile(file, ({ candles, error }) => {
      if (error || !candles.length) {
        setUploadStatus({
          tone: "error",
          message: error || `No valid OHLC rows found in ${file.name}.`,
        });
        return;
      }

      pendingScaleFitRef.current = true;
      setLiveRatioSymbols(null);
      if (slot === 1) {
        setRatioCandles1(candles);
        setRatioFileName1(file.name);
        if (ratioCandles2.length) {
          setRatioModeEnabled(true);
        }
      } else {
        setRatioCandles2(candles);
        setRatioFileName2(file.name);
        if (ratioCandles1.length) {
          setRatioModeEnabled(true);
        }
      }
      stopReplay();
      clearMeasurement();
      clearDrawings();
      setUploadStatus({
        tone: "success",
        message: `Loaded ${candles.length} candles into Ratio ${slot} from ${file.name}.`,
      });
    });
  };

  const clearRatioMode = () => {
    setRatioModeEnabled(false);
    setRatioCandles1(EMPTY_CANDLES);
    setRatioCandles2(EMPTY_CANDLES);
    setRatioFileName1("");
    setRatioFileName2("");
    setLiveRatioSymbols(null);
  };

  /** Surfaces the otherwise-invisible failure mode where Ratio 1 and Ratio 2 share no
   * exact bar times (e.g. different providers/timezones/session hours), so the ratio
   * chart would silently render empty instead of the expected 1÷2 series. */
  useEffect(() => {
    if (!ratioModeEnabled || !ratioCandles1.length || !ratioCandles2.length) return;
    if (ratioData.length) return;
    setUploadStatus({
      tone: "error",
      message:
        "Ratio 1 and Ratio 2 have no bars with matching timestamps, so the 1÷2 chart is empty. Check that both files use the same date/time format and bar interval.",
    });
  }, [ratioModeEnabled, ratioCandles1, ratioCandles2, ratioData]);

  // 📊 Create chart when container mounts only. Theme/candle colors update via separate effects.
  /* eslint-disable react-hooks/exhaustive-deps -- chartContainerEl is the intentional trigger; avoid full chart teardown */
  useLayoutEffect(() => {
    if (!chartContainerEl) {
      return;
    }

    const getPointFromChartEvent = (param) => {
      if (!param.point || !candleSeriesRef.current || !chartRef.current) {
        return null;
      }

      const chart = chartRef.current;
      const timeScale = chart.timeScale();
      const series = candleSeriesRef.current;
      const seriesData = param.seriesData?.get(series);

      let logical =
        typeof param.logical === "number" && Number.isFinite(param.logical)
          ? param.logical
          : null;

      if (logical == null) {
        const fromX = timeScale.coordinateToLogical(param.point.x);
        if (fromX != null && Number.isFinite(fromX)) {
          logical = fromX;
        }
      }

      if (logical == null && seriesData?.time != null) {
        const idx = timeScale.timeToIndex(seriesData.time, true);
        if (idx != null && Number.isFinite(idx)) {
          logical = idx;
        }
      }

      if (logical == null) {
        const tAtX = timeScale.coordinateToTime(param.point.x);
        if (tAtX != null) {
          const idx = timeScale.timeToIndex(tAtX, true);
          if (idx != null && Number.isFinite(idx)) {
            logical = idx;
          }
        }
      }

      const rawTime = param.time ?? seriesData?.time ?? null;
      let normalizedTime = null;
      if (rawTime != null) {
        if (typeof rawTime === "number" && Number.isFinite(rawTime)) {
          normalizedTime = rawTime;
        } else {
          const u = seriesPointTimeToUnix(rawTime);
          normalizedTime = Number.isFinite(u) ? u : null;
        }
      }

      let price = series.coordinateToPrice(param.point.y);
      if (!Number.isFinite(price) && seriesData) {
        if (
          typeof seriesData.open === "number" &&
          typeof seriesData.close === "number"
        ) {
          price = seriesData.close;
        } else if (typeof seriesData.value === "number") {
          price = seriesData.value;
        }
      }

      return buildMeasurePoint(logical, normalizedTime, price);
    };

    const containerWidth = Math.max(1, chartContainerEl.clientWidth);
    const containerHeight = Math.max(1, chartContainerEl.clientHeight);

    // eslint-disable-next-line no-console
    console.log("[TZ-MARKER-v2] chart create, chartTimeZone =", chartTimeZone);

    chartRef.current = createChart(chartContainerEl, {
      width: containerWidth,
      height: containerHeight,
      ...CHART_INTERACTION_DEFAULTS,

      layout: {
        background: { color: chartBackgroundColor },
        textColor: palette.shellText,
        fontFamily: isInstrumentTheme
          ? '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace'
          : '"Archivo", "Avenir Next", system-ui, sans-serif',
        attributionLogo: true,
      },
      crosshair: {
        mode: crosshairModeFromValue(chartCrosshairMode),
        vertLine: {
          color: palette.crosshairVert,
          width: 1,
        },
        horzLine: {
          color: palette.crosshairHorz,
          width: 1,
        },
      },

      grid: {
        vertLines: { color: palette.gridLine },
        horzLines: { color: palette.gridLine },
      },
      rightPriceScale: {
        visible: yAxisSide === "right",
        borderColor: palette.scaleBorder,
        mode: priceScaleModeFromValue(mainPriceScaleMode),
      },
      leftPriceScale: {
        visible: yAxisSide === "left",
        borderColor: palette.scaleBorder,
        mode: priceScaleModeFromValue(mainPriceScaleMode),
      },
      localization: {
        timeFormatter: (businessDayOrTimestamp) =>
          formatChartTooltipTime(businessDayOrTimestamp, chartTimeZone),
        ...chartLocalizationForPriceScale(mainPriceScaleMode),
      },
      timeScale: {
        borderColor: palette.scaleBorder,
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 5,
        minBarSpacing: 0.65,
        tickMarkFormatter: (time, tickMarkType, locale) =>
          formatChartTickMark(time, tickMarkType, locale, chartTimeZone),
      },
      panes: {
        enableResize: true,
        separatorColor: palette.paneSeparator,
        separatorHoverColor: palette.paneSeparatorHover,
      },
    });

    const detachChartWheel = attachChartWheelPan(
      () => chartRef.current,
      chartContainerEl
    );

    const initialPayload = getPriceChartPayload(priceSeriesStyle, visibleData, {
      renkoBrickSize: renkoBrickApplied,
      renkoBrickMode,
      renkoAtrPeriod: renkoAtrPeriodApplied,
      renkoSource,
      rangeSize: rangeSizeApplied,
      rangeSource,
      lineBreakLines: lineBreakLinesApplied,
      kagiReversal: kagiReversalApplied,
      kagiMode,
      pnfBoxSize: pnfBoxApplied,
      pnfReversal: pnfReversalApplied,
      linePriceSource,
      lineChartLineType,
      baselinePriceInput,
    });
    candleSeriesRef.current = addMainPriceSeries(chartRef.current, priceSeriesStyle, initialPayload, {
      bullishCandleBodyColor,
      bearishCandleBodyColor,
      bullishCandleBorderColor,
      bearishCandleBorderColor,
      bullishCandleWickColor,
      bearishCandleWickColor,
      chartBackgroundColor,
    });
    candleSeriesRef.current.setData(initialPayload.data);
    if (initialMarketChartState?.visibleTimeRange) {
      try {
        chartRef.current.timeScale().setVisibleRange(initialMarketChartState.visibleTimeRange);
      } catch {
        // The saved view may not overlap the currently loaded candles.
      }
    }
    candleSeriesRef.current.setSeriesOrder(0);
    measureSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.measureLine,
      lineWidth: 3,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
    });
    paneLineFillsRef.current = {};
    movingAverageSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.ma,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: true,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: `MA ${DEFAULT_MA_PERIOD}`,
    });
    smaSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.sma,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: `SMA ${DEFAULT_SMA_PERIOD}`,
    });
    emaSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.ema,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: `EMA ${DEFAULT_EMA_PERIOD}`,
    });
    wmaSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.wma,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: `WMA ${DEFAULT_WMA_PERIOD}`,
    });
    Object.assign(paneLineFillsRef.current, {
      rsi: createIndicatorAreaSeries(chartRef.current, palette.rsi, "rsi-scale"),
      atr: createIndicatorAreaSeries(chartRef.current, palette.atr, "atr-scale"),
      stochasticK: createIndicatorAreaSeries(chartRef.current, palette.stochasticK, "stoch-scale"),
      stochasticD: createIndicatorAreaSeries(chartRef.current, palette.stochasticD, "stoch-scale"),
    });
    bbUpperSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.bbPrimary,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: "BB Upper",
    });
    bbMiddleSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.bbMiddle,
      lineWidth: 2,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: "BB Mid",
    });
    bbLowerSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.bbPrimary,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: "BB Lower",
    });
    rsiSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.rsi,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "rsi-scale",
      title: `RSI ${DEFAULT_RSI_PERIOD}`,
    });
    rsiUpperSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.rsiUpper,
      lineWidth: 1,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "rsi-scale",
      title: "RSI 70",
    });
    rsiLowerSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.rsiLower,
      lineWidth: 1,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "rsi-scale",
      title: "RSI 30",
    });
    atrSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.atr,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "atr-scale",
      title: `ATR ${DEFAULT_ATR_PERIOD}`,
    });
    donchianUpperSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.donchianPrimary,
      lineWidth: INDICATOR_LINE_WIDTH,
      lineStyle: 2,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: `DC ${DEFAULT_DONCHIAN_PERIOD} Upper`,
    });
    donchianMiddleSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.donchianMiddle,
      lineWidth: 2,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: `DC ${DEFAULT_DONCHIAN_PERIOD} Mid`,
    });
    donchianLowerSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.donchianPrimary,
      lineWidth: INDICATOR_LINE_WIDTH,
      lineStyle: 2,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      title: `DC ${DEFAULT_DONCHIAN_PERIOD} Lower`,
    });
    stochasticKSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.stochasticK,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "stoch-scale",
      title: "Stoch %K",
    });
    stochasticDSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.stochasticD,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "stoch-scale",
      title: "Stoch %D",
    });
    stochasticUpperSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.stochasticUpper,
      lineWidth: 1,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "stoch-scale",
      title: "Stoch 80",
    });
    stochasticLowerSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.stochasticLower,
      lineWidth: 1,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "stoch-scale",
      title: "Stoch 20",
    });
    macdHistogramSeriesRef.current = chartRef.current.addSeries(HistogramSeries, {
      lastValueVisible: false,
      priceLineVisible: false,
      priceScaleId: "macd-scale",
      title: "MACD Hist",
    });
    Object.assign(paneLineFillsRef.current, {
      macdLine: createIndicatorAreaSeries(chartRef.current, palette.macdLine, "macd-scale"),
      macdSignal: createIndicatorAreaSeries(chartRef.current, palette.macdSignal, "macd-scale"),
    });
    macdLineSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.macdLine,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "macd-scale",
      title: "MACD",
    });
    macdSignalSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: palette.macdSignal,
      lineWidth: INDICATOR_LINE_WIDTH,
      lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      priceScaleId: "macd-scale",
      title: "MACD Signal",
    });
    userScriptLine1Ref.current = chartRef.current.addSeries(LineSeries, {
      color: palette.userScript1,
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      visible: false,
      title: "Script 1",
    });
    userScriptLine2Ref.current = chartRef.current.addSeries(LineSeries, {
      color: palette.userScript2,
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      visible: false,
      title: "Script 2",
    });
    userScriptLine3Ref.current = chartRef.current.addSeries(LineSeries, {
      color: palette.userScript3,
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
      crosshairMarkerVisible: false,
      pointMarkersVisible: false,
      visible: false,
      title: "Script 3",
    });

    extSeriesRef.current = {};
    const addIndicatorLine = (key, opts, { withFill = true } = {}) => {
      const col = opts.color ?? "#888888";
      const ps = opts.priceScaleId ?? "";
      if (withFill) {
        extSeriesRef.current[`${key}Fill`] = createIndicatorAreaSeries(
          chartRef.current,
          col,
          ps
        );
      }
      extSeriesRef.current[key] = chartRef.current.addSeries(LineSeries, {
        lastValueVisible: false,
        priceLineVisible: false,
        crosshairMarkerVisible: false,
        pointMarkersVisible: false,
        visible: false,
        lineWidth: INDICATOR_LINE_WIDTH,
        lastPriceAnimation: LastPriceAnimationMode.OnDataUpdate,
        ...opts,
      });
    };
    addIndicatorLine("vwap", { color: palette.vwap, title: "VWAP" }, { withFill: false });
    addIndicatorLine("hma", { color: palette.hma, title: "HMA" }, { withFill: false });
    addIndicatorLine("dema", { color: palette.dema, title: "DEMA" }, { withFill: false });
    addIndicatorLine("tema", { color: palette.tema, title: "TEMA" }, { withFill: false });
    addIndicatorLine("ichiTenkan", { color: palette.ichiTenkan, lineWidth: 2, title: "Tenkan" }, { withFill: false });
    addIndicatorLine("ichiKijun", { color: palette.ichiKijun, lineWidth: 2, title: "Kijun" }, { withFill: false });
    addIndicatorLine("ichiSpanA", { color: palette.ichiSpanA, lineWidth: 1, title: "Senkou A" }, { withFill: false });
    addIndicatorLine("ichiSpanB", { color: palette.ichiSpanB, lineWidth: 1, title: "Senkou B" }, { withFill: false });
    addIndicatorLine(
      "ichiChikou",
      {
        color: palette.ichiChikou,
        lineWidth: 1,
        lineStyle: 2,
        title: "Chikou",
      },
      { withFill: false }
    );
    addIndicatorLine(
      "sar",
      {
        color: palette.sar,
        lineWidth: 1,
        pointMarkersVisible: true,
        title: "SAR",
      },
      { withFill: false }
    );
    addIndicatorLine("keltUpper", { color: palette.keltUpper, lineWidth: 1, title: "Keltner +" }, { withFill: false });
    addIndicatorLine("keltMid", { color: palette.bbMiddle, lineWidth: 1, title: "Keltner mid" }, { withFill: false });
    addIndicatorLine(
      "keltLower",
      {
        color: palette.keltUpper,
        lineWidth: 1,
        lineStyle: 2,
        title: "Keltner −",
      },
      { withFill: false }
    );
    addIndicatorLine("cci", {
      color: palette.cci,
      priceScaleId: "cci-scale",
      title: "CCI",
    });
    addIndicatorLine("williams", {
      color: palette.williams,
      priceScaleId: "williams-scale",
      title: "Williams %R",
    });
    addIndicatorLine("roc", { color: palette.roc, priceScaleId: "roc-scale", title: "ROC" });
    addIndicatorLine("mom", {
      color: palette.mom,
      priceScaleId: "mom-scale",
      title: "Momentum",
    });
    addIndicatorLine("stochRsiK", {
      color: palette.stochRsiK,
      priceScaleId: "stoch-rsi-scale",
      title: "Stoch RSI K",
    });
    addIndicatorLine("stochRsiD", {
      color: palette.stochRsiD,
      priceScaleId: "stoch-rsi-scale",
      title: "Stoch RSI D",
    });
    addIndicatorLine("adx", {
      color: palette.adxLine,
      priceScaleId: "adx-scale",
      title: "ADX",
    });
    addIndicatorLine("plusDI", {
      color: palette.plusDI,
      lineWidth: 2,
      priceScaleId: "adx-scale",
      title: "+DI",
    });
    addIndicatorLine("minusDI", {
      color: palette.minusDI,
      lineWidth: 2,
      priceScaleId: "adx-scale",
      title: "−DI",
    });
    extSeriesRef.current.ao = chartRef.current.addSeries(HistogramSeries, {
      priceScaleId: "ao-scale",
      lastValueVisible: false,
      priceLineVisible: false,
      visible: false,
      title: "AO",
    });
    addIndicatorLine("tsi", { color: palette.tsi, priceScaleId: "tsi-scale", title: "TSI" });
    addIndicatorLine("obv", { color: palette.obv, priceScaleId: "obv-scale", title: "OBV" });
    addIndicatorLine("cmf", { color: palette.cmf, priceScaleId: "cmf-scale", title: "CMF" });
    addIndicatorLine("mfi", { color: palette.mfi, priceScaleId: "mfi-scale", title: "MFI" });
    addIndicatorLine("adLine", { color: palette.adLine, priceScaleId: "ad-scale", title: "A/D" });
    addIndicatorLine("chaikinVol", {
      color: palette.chaikinVol,
      priceScaleId: "chv-scale",
      title: "Chaikin Vol",
    });
    addIndicatorLine("volOsc", {
      color: palette.volOsc,
      priceScaleId: "vol-osc-scale",
      title: "Vol Osc %",
    });
    addIndicatorLine("stdDev", {
      color: palette.stdDev,
      priceScaleId: "stddev-scale",
      title: "StdDev",
    });
    addIndicatorLine("kaufmanRange", {
      color: palette.kaufmanRange,
      priceScaleId: "kaufman-range-scale",
      title: "Regime Detection",
    });

    markersRef.current = createSeriesMarkers(candleSeriesRef.current, []);

    const handleChartClick = (param) => {
      if (drawToolRef.current === "none" && !selectedDrawingIdRef.current) {
        try {
          const series = candleSeriesRef.current;
          const point = series ? param.seriesData?.get(series) : null;
          const clickedTime = seriesPointTimeToUnix(param.time ?? point?.time ?? null);
          const saved = JSON.parse(window.localStorage.getItem("lwt.backtestTrades") || "null");
          const sameSymbol = String(saved?.symbol || "").toUpperCase() === String(marketSymbolRef.current || "").toUpperCase();
          const hitTradeDot = sameSymbol && Number.isFinite(clickedTime) && saved?.trades?.some((trade) => trade.t === clickedTime || trade.x === clickedTime);
          if (hitTradeDot) {
            clearBacktestOverlayFromMain();
            return;
          }
        } catch { /* storage may be unavailable */ }
      }
      if (closeFloatingUiRef.current()) {
        return;
      }
      if (selectedDrawingIdRef.current && drawToolRef.current === "none") {
        setSelectedDrawingId(null);
        return;
      }
      if (
        activePanelRef.current === "indicators" ||
        activePanelRef.current === "settings" ||
        activePanelRef.current === "script" ||
        activePanelRef.current === "ai"
      ) {
        setActivePanel("none");
        setActiveIndicatorKey(null);
        return;
      }

      const nextPoint = getPointFromChartEvent(param);

      if (isReplaySelectingRef.current) {
        const currentData = dataRef.current;
        const series = candleSeriesRef.current;
        const seriesData = series ? param.seriesData?.get(series) : null;
        const replayTime = seriesPointTimeToUnix(
          param.time ?? seriesData?.time ?? null
        );

        let startIndex = null;
        if (Number.isFinite(replayTime) && currentData.length) {
          startIndex = findNearestSeriesTimeIndex(currentData, replayTime);
        } else if (
          nextPoint &&
          Number.isFinite(nextPoint.logical) &&
          currentData.length
        ) {
          startIndex = Math.max(
            0,
            Math.min(currentData.length - 1, Math.round(nextPoint.logical))
          );
        }

        if (startIndex != null && currentData.length) {
          const t = seriesPointTimeToUnix(currentData[startIndex]?.time);
          replayAnchorIndexRef.current = startIndex;
          setReplayStartDate(Number.isFinite(t) ? formatInputDate(t) : "");
          setReplayPreviewIndex(startIndex);
          setIsReplaySelecting(false);
          setIsReplayMode(true);
          setIsReplayPlaying(false);
          setReplayIndex(startIndex);
          setHoveredCandle(currentData[startIndex] ?? null);
          clearMeasurement();
        }

        return;
      }

      if (handleDrawingClick(nextPoint)) {
        return;
      }

      if (!isMeasureModeRef.current) {
        if (measurePointsRef.current.length >= 2) {
          clearMeasurement();
        }
        return;
      }

      if (!nextPoint) return;

      setMeasurePoints((currentPoints) => {
        if (currentPoints.length >= 2) {
          return [nextPoint];
        }

        const nextPoints = [...currentPoints, nextPoint];
        if (nextPoints.length === 2) {
          setIsMeasureMode(false);
        }
        return nextPoints;
      });
      setHoverMeasurePoint(null);
    };

    const handleCrosshairMove = (param) => {
      const hoveredSeriesData = candleSeriesRef.current
        ? param.seriesData.get(candleSeriesRef.current)
        : null;

      if (
        hoveredSeriesData &&
        typeof hoveredSeriesData.value === "number" &&
        typeof hoveredSeriesData.open !== "number"
      ) {
        const hoveredTime = seriesPointTimeToUnix(hoveredSeriesData.time);
        const sourceCandle = findClosestCandleByTime(dataRef.current, hoveredTime);

        if (sourceCandle) {
          const barTime = sourceCandle.time;
          if (lastHoveredBarTimeRef.current !== barTime) {
            lastHoveredBarTimeRef.current = barTime;
            setHoveredCandle({
              time: barTime,
              open: sourceCandle.open,
              high: sourceCandle.high,
              low: sourceCandle.low,
              close: sourceCandle.close,
            });
          }
        } else if (lastHoveredBarTimeRef.current !== null) {
          lastHoveredBarTimeRef.current = null;
          setHoveredCandle(null);
        }
      } else if (
        hoveredSeriesData &&
        typeof hoveredSeriesData.open === "number" &&
        typeof hoveredSeriesData.high === "number" &&
        typeof hoveredSeriesData.low === "number" &&
        typeof hoveredSeriesData.close === "number"
      ) {
        const hoveredTime = seriesPointTimeToUnix(hoveredSeriesData.time);

        if (lastHoveredBarTimeRef.current !== hoveredTime) {
          lastHoveredBarTimeRef.current = hoveredTime;
          setHoveredCandle({
            time: hoveredTime,
            open: hoveredSeriesData.open,
            high: hoveredSeriesData.high,
            low: hoveredSeriesData.low,
            close: hoveredSeriesData.close,
          });
        }

        const currentData = dataRef.current;

        if (Number.isFinite(hoveredTime) && currentData.length) {
          const nextReplayAnchorIndex = findNearestSeriesTimeIndex(
            currentData,
            hoveredTime
          );
          replayAnchorIndexRef.current = nextReplayAnchorIndex;

          if (
            isReplaySelectingRef.current &&
            lastReplayPreviewIndexRef.current !== nextReplayAnchorIndex
          ) {
            lastReplayPreviewIndexRef.current = nextReplayAnchorIndex;
            setReplayPreviewIndex(nextReplayAnchorIndex);
          }
        }
      } else if (lastHoveredBarTimeRef.current !== null) {
        lastHoveredBarTimeRef.current = null;
        setHoveredCandle(null);
      }

      const drawNext =
        drawToolRef.current !== "none" ? getPointFromChartEvent(param) : null;
      const measureNext =
        isMeasureModeRef.current && measurePointsRef.current.length === 1
          ? getPointFromChartEvent(param)
          : null;

      if (isBrushDrawingRef.current && drawNext) {
        brushPointsBufferRef.current = [...brushPointsBufferRef.current, drawNext];
        setBrushPreviewPoints(brushPointsBufferRef.current);
      }

      scheduleCrosshairOverlayPointers(drawNext, measureNext);
    };

    const handleVisibleRangeChange = () => {
      if (replayProgrammaticScrollRef.current) {
        return;
      }
      requestOverlayRefresh();
    };

    /** Backfills older bars from the backend when the user pans/zooms near the left
     * edge of what's loaded — see BACKFILL_LOGICAL_THRESHOLD. Skips file uploads
     * (already the full dataset the user chose); covers both the single-symbol main
     * pane and a live ratio (fetches older history for both legs and lets the
     * existing computeRatioCandles recompute once both have more overlap). */
    let backfillLoading = false;
    let backfillExhaustedKey = null;

    const fetchOlderCandles = (symbol, tf, earliest) =>
      marketFetch(
        `${BACKEND_API_BASE}/candles?symbol=${encodeURIComponent(
          symbol
        )}&tf=${encodeURIComponent(tf)}&to=${earliest - 1}&limit=1500`
      )
        .then((res) => (res.ok ? res.json() : []))
        .then((rows) => normalizeBackendCandles(rows).filter((c) => c.time < earliest))
        .catch(() => []);

    const backfillSingleSymbol = () => {
      const symbol = marketSymbolRef.current;
      const tf = backendFeedTimeframeRef.current;
      const key = `single:${symbol}:${tf}`;
      if (backfillExhaustedKey === key) return;

      const current = dataRef.current;
      if (!current.length) return;
      const earliest = current[0]?.time;
      if (!Number.isFinite(earliest)) return;

      backfillLoading = true;
      fetchOlderCandles(symbol, tf, earliest)
        .then((older) => {
          if (!older.length) {
            backfillExhaustedKey = key;
            return;
          }
          if (marketSymbolRef.current !== symbol || backendFeedTimeframeRef.current !== tf) {
            return;
          }
          setBoxData((prev) => {
            const next = [...prev];
            const box = Array.isArray(next[0]) ? next[0] : [];
            if (!box.length || box[0]?.time !== earliest) {
              return prev;
            }
            next[0] = [...older, ...box];
            return next;
          });
        })
        .finally(() => {
          backfillLoading = false;
        });
    };

    const backfillLiveRatio = () => {
      const legs = liveRatioSymbolsRef.current;
      if (!legs) return;
      const { leg1, leg2 } = legs;
      const tf = backendFeedTimeframeRef.current;
      const key = `ratio:${leg1}/${leg2}:${tf}`;
      if (backfillExhaustedKey === key) return;

      const c1 = ratioCandles1Ref.current;
      const c2 = ratioCandles2Ref.current;
      if (!c1.length || !c2.length) return;
      const earliest1 = c1[0]?.time;
      const earliest2 = c2[0]?.time;
      if (!Number.isFinite(earliest1) || !Number.isFinite(earliest2)) return;

      backfillLoading = true;
      Promise.all([
        fetchOlderCandles(leg1, tf, earliest1),
        fetchOlderCandles(leg2, tf, earliest2),
      ])
        .then(([older1, older2]) => {
          if (!older1.length && !older2.length) {
            backfillExhaustedKey = key;
            return;
          }
          const stillCurrent =
            liveRatioSymbolsRef.current?.leg1 === leg1 &&
            liveRatioSymbolsRef.current?.leg2 === leg2 &&
            backendFeedTimeframeRef.current === tf;
          if (!stillCurrent) return;
          if (older1.length) {
            setRatioCandles1((prev) =>
              prev[0]?.time === earliest1 ? [...older1, ...prev] : prev
            );
          }
          if (older2.length) {
            setRatioCandles2((prev) =>
              prev[0]?.time === earliest2 ? [...older2, ...prev] : prev
            );
          }
        })
        .finally(() => {
          backfillLoading = false;
        });
    };

    const handleBackfillRangeChange = (range) => {
      if (!range || replayProgrammaticScrollRef.current) return;
      if (!isBackendFeedActiveRef.current || isReplayModeRef.current || isReplaySelectingRef.current) {
        return;
      }
      if (range.from > BACKFILL_LOGICAL_THRESHOLD) return;
      if (backfillLoading) return;

      if (ratioModeEnabledRef.current) {
        if (liveRatioSymbolsRef.current) backfillLiveRatio();
        return;
      }
      backfillSingleSymbol();
    };

    /** Brush is drag-based (mousedown -> accumulate points on move -> mouseup commits),
     * unlike every other tool which is click-to-place via handleChartClick. mouseup is on
     * window, not the container, so a stroke still commits correctly if the drag ends
     * outside the chart. */
    const handleBrushMouseDown = () => {
      if (drawToolRef.current !== "brush") return;
      isBrushDrawingRef.current = true;
      brushPointsBufferRef.current = [];
      setBrushPreviewPoints([]);
    };
    const handleBrushMouseUp = () => {
      if (!isBrushDrawingRef.current) return;
      isBrushDrawingRef.current = false;
      const points = brushPointsBufferRef.current;
      brushPointsBufferRef.current = [];
      setBrushPreviewPoints([]);
      if (points.length > 1) {
        addDrawingRef.current({ id: `${Date.now()}-brush`, type: "brush", points });
      }
    };

    chartRef.current.subscribeClick(handleChartClick);
    chartRef.current.subscribeCrosshairMove(handleCrosshairMove);
    chartContainerEl.addEventListener("mousedown", handleBrushMouseDown);
    window.addEventListener("mouseup", handleBrushMouseUp);
    chartRef.current
      .timeScale()
      .subscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
    chartRef.current
      .timeScale()
      .subscribeVisibleLogicalRangeChange(handleBackfillRangeChange);

    resizeObserverRef.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect?.width;
      const height = entry?.contentRect?.height;

      if (
        !chartRef.current ||
        !Number.isFinite(width) ||
        !Number.isFinite(height)
      ) {
        return;
      }

      chartRef.current.applyOptions({ width, height });
      requestOverlayRefresh();
    });

    resizeObserverRef.current.observe(chartContainerEl);

    return () => {
      if (retainedMarketChartState && chartRef.current) {
        try {
          const visibleTimeRange = chartRef.current.timeScale().getVisibleRange();
          if (visibleTimeRange) {
            retainedMarketChartState = { ...retainedMarketChartState, visibleTimeRange };
          }
        } catch {
          // The chart may be between initialization and its first visible range.
        }
      }
      if (overlayRefreshRafRef.current !== null) {
        window.cancelAnimationFrame(overlayRefreshRafRef.current);
        overlayRefreshRafRef.current = null;
      }
      if (crosshairPointerRafRef.current !== null) {
        window.cancelAnimationFrame(crosshairPointerRafRef.current);
        crosshairPointerRafRef.current = null;
      }
      detachChartWheel();
      chartRef.current?.unsubscribeClick(handleChartClick);
      chartRef.current?.unsubscribeCrosshairMove(handleCrosshairMove);
      chartContainerEl.removeEventListener("mousedown", handleBrushMouseDown);
      window.removeEventListener("mouseup", handleBrushMouseUp);
      chartRef.current
        ?.timeScale()
        .unsubscribeVisibleLogicalRangeChange(handleVisibleRangeChange);
      chartRef.current
        ?.timeScale()
        .unsubscribeVisibleLogicalRangeChange(handleBackfillRangeChange);
      resizeObserverRef.current?.disconnect();
      chartRef.current?.remove();
      chartRef.current = null;
      efficiencyRegimePrimitiveRef.current = null;
      // Series belonged to the removed chart; they are recreated for the next one.
      extraInstanceSeriesRef.current.clear();
      goSeriesRef.current.clear();
      ofSeriesRef.current.clear();
      ofPrimitiveRef.current = null;
      ofBarStatsRef.current = null;
      candleSeriesRef.current = null;
      markersRef.current = null;
      measureSeriesRef.current = null;
      movingAverageSeriesRef.current = null;
      smaSeriesRef.current = null;
      emaSeriesRef.current = null;
      wmaSeriesRef.current = null;
      bbUpperSeriesRef.current = null;
      bbMiddleSeriesRef.current = null;
      bbLowerSeriesRef.current = null;
      rsiSeriesRef.current = null;
      rsiUpperSeriesRef.current = null;
      rsiLowerSeriesRef.current = null;
      atrSeriesRef.current = null;
      donchianUpperSeriesRef.current = null;
      donchianMiddleSeriesRef.current = null;
      donchianLowerSeriesRef.current = null;
      stochasticKSeriesRef.current = null;
      stochasticDSeriesRef.current = null;
      stochasticUpperSeriesRef.current = null;
      stochasticLowerSeriesRef.current = null;
      macdLineSeriesRef.current = null;
      macdSignalSeriesRef.current = null;
      macdHistogramSeriesRef.current = null;
      userScriptLine1Ref.current = null;
      userScriptLine2Ref.current = null;
      userScriptLine3Ref.current = null;
      extSeriesRef.current = {};
      paneLineFillsRef.current = null;
    };
  }, [chartContainerEl, scheduleCrosshairOverlayPointers]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    chartRef.current.applyOptions({
      ...CHART_INTERACTION_DEFAULTS,
      layout: {
        textColor: palette.shellText,
      },
      crosshair: {
        mode: crosshairModeFromValue(chartCrosshairMode),
        vertLine: {
          color: palette.crosshairVert,
        },
        horzLine: {
          color: palette.crosshairHorz,
        },
      },
      grid: {
        vertLines: { color: palette.gridLine },
        horzLines: { color: palette.gridLine },
      },
      rightPriceScale: {
        visible: yAxisSide === "right",
        borderColor: palette.scaleBorder,
        mode: priceScaleModeFromValue(mainPriceScaleMode),
      },
      leftPriceScale: {
        visible: yAxisSide === "left",
        borderColor: palette.scaleBorder,
        mode: priceScaleModeFromValue(mainPriceScaleMode),
      },
      localization: {
        timeFormatter: (businessDayOrTimestamp) =>
          formatChartTooltipTime(businessDayOrTimestamp, chartTimeZone),
        ...chartLocalizationForPriceScale(mainPriceScaleMode),
      },
      timeScale: {
        borderColor: palette.scaleBorder,
        tickMarkFormatter: (time, tickMarkType, locale) =>
          formatChartTickMark(time, tickMarkType, locale, chartTimeZone),
      },
      panes: {
        separatorColor: palette.paneSeparator,
        separatorHoverColor: palette.paneSeparatorHover,
      },
    });

    const applyIndicatorLineStyle = (series, colorKey, widthKey = colorKey) => {
      series?.applyOptions({
        color: getIndicatorColor(colorKey),
        lineWidth: getIndicatorLineWidth(widthKey),
      });
    };

    measureSeriesRef.current?.applyOptions({ color: palette.measureLine });
    applyIndicatorLineStyle(movingAverageSeriesRef.current, "ma");
    applyIndicatorLineStyle(smaSeriesRef.current, "sma");
    applyIndicatorLineStyle(emaSeriesRef.current, "ema");
    applyIndicatorLineStyle(wmaSeriesRef.current, "wma");
    applyIndicatorLineStyle(bbUpperSeriesRef.current, "bbUpper", "bb");
    applyIndicatorLineStyle(bbMiddleSeriesRef.current, "bbMid", "bb");
    applyIndicatorLineStyle(bbLowerSeriesRef.current, "bbLower", "bb");
    applyIndicatorLineStyle(rsiSeriesRef.current, "rsi");
    applyIndicatorLineStyle(rsiUpperSeriesRef.current, "rsi");
    applyIndicatorLineStyle(rsiLowerSeriesRef.current, "rsi");
    applyIndicatorLineStyle(atrSeriesRef.current, "atr");
    applyIndicatorLineStyle(donchianUpperSeriesRef.current, "donchianUpper", "donchian");
    applyIndicatorLineStyle(donchianMiddleSeriesRef.current, "donchianMid", "donchian");
    applyIndicatorLineStyle(donchianLowerSeriesRef.current, "donchianLower", "donchian");
    applyIndicatorLineStyle(stochasticKSeriesRef.current, "stochasticK", "stochastic");
    applyIndicatorLineStyle(stochasticDSeriesRef.current, "stochasticD", "stochastic");
    applyIndicatorLineStyle(stochasticUpperSeriesRef.current, "stochasticK", "stochastic");
    applyIndicatorLineStyle(stochasticLowerSeriesRef.current, "stochasticK", "stochastic");
    applyIndicatorLineStyle(macdLineSeriesRef.current, "macdLine", "macd");
    applyIndicatorLineStyle(macdSignalSeriesRef.current, "macdSignal", "macd");
    userScriptLine1Ref.current?.applyOptions({ color: palette.userScript1 });
    userScriptLine2Ref.current?.applyOptions({ color: palette.userScript2 });
    userScriptLine3Ref.current?.applyOptions({ color: palette.userScript3 });
    const E = extSeriesRef.current;
    applyIndicatorLineStyle(E.vwap, "vwap");
    applyIndicatorLineStyle(E.hma, "hma");
    applyIndicatorLineStyle(E.dema, "dema");
    applyIndicatorLineStyle(E.tema, "tema");
    applyIndicatorLineStyle(E.ichiTenkan, "ichimoku");
    applyIndicatorLineStyle(E.ichiKijun, "ichimoku");
    applyIndicatorLineStyle(E.ichiSpanA, "ichimoku");
    applyIndicatorLineStyle(E.ichiSpanB, "ichimoku");
    applyIndicatorLineStyle(E.ichiChikou, "ichimoku");
    applyIndicatorLineStyle(E.sar, "sar");
    applyIndicatorLineStyle(E.keltUpper, "keltner");
    applyIndicatorLineStyle(E.keltMid, "keltner");
    applyIndicatorLineStyle(E.keltLower, "keltner");
    applyIndicatorLineStyle(E.cci, "cci");
    applyIndicatorLineStyle(E.williams, "williams");
    applyIndicatorLineStyle(E.roc, "roc");
    applyIndicatorLineStyle(E.mom, "mom");
    applyIndicatorLineStyle(E.stochRsiK, "stochRsi");
    applyIndicatorLineStyle(E.stochRsiD, "stochRsi");
    applyIndicatorLineStyle(E.adx, "adx");
    applyIndicatorLineStyle(E.plusDI, "adx");
    applyIndicatorLineStyle(E.minusDI, "adx");
    applyIndicatorLineStyle(E.tsi, "tsi");
    applyIndicatorLineStyle(E.obv, "obv");
    applyIndicatorLineStyle(E.cmf, "cmf");
    applyIndicatorLineStyle(E.mfi, "mfi");
    applyIndicatorLineStyle(E.adLine, "adLine");
    applyIndicatorLineStyle(E.chaikinVol, "chaikinVol");
    applyIndicatorLineStyle(E.volOsc, "volOsc");
    applyIndicatorLineStyle(E.stdDev, "stdDev");
    applyIndicatorLineStyle(E.kaufmanRange, "kaufmanRange");
  }, [
    chartCrosshairMode,
    getIndicatorColor,
    getIndicatorLineWidth,
    indicatorColorFallbacks,
    indicatorColors,
    indicatorLineWidths,
    mainPriceScaleMode,
    palette,
    yAxisSide,
    chartTimeZone,
  ]);

  useEffect(() => {
    if (!candleSeriesRef.current) {
      return;
    }

    applyMainPriceSeriesColors(
      candleSeriesRef.current,
      priceSeriesStyle,
      {
        bullishCandleBodyColor,
        bearishCandleBodyColor,
        bullishCandleBorderColor,
        bearishCandleBorderColor,
        bullishCandleWickColor,
        bearishCandleWickColor,
        chartBackgroundColor,
      },
      priceChartPayload
    );

    chartRef.current?.applyOptions({
      layout: {
        background: { color: chartBackgroundColor },
      },
    });
  }, [
    bearishCandleBodyColor,
    bearishCandleBorderColor,
    bearishCandleWickColor,
    bullishCandleBodyColor,
    bullishCandleBorderColor,
    bullishCandleWickColor,
    chartBackgroundColor,
    priceChartPayload,
    priceSeriesStyle,
    themeMode,
  ]);

  /* Swap main price series type (candles / line / Renko / etc.) without rebuilding the whole chart */
  /* eslint-disable react-hooks/exhaustive-deps -- only priceSeriesStyle should trigger swap; reads latest data/colors */
  useEffect(() => {
    if (!chartRef.current || !candleSeriesRef.current) {
      return;
    }

    if (prevPriceSeriesStyleRef.current === priceSeriesStyle) {
      return;
    }

    prevPriceSeriesStyleRef.current = priceSeriesStyle;

    chartRef.current.removeSeries(candleSeriesRef.current);

    const payload = getPriceChartPayload(priceSeriesStyle, visibleData, {
      renkoBrickSize: renkoBrickApplied,
      renkoBrickMode,
      renkoAtrPeriod: renkoAtrPeriodApplied,
      renkoSource,
      rangeSize: rangeSizeApplied,
      rangeSource,
      lineBreakLines: lineBreakLinesApplied,
      kagiReversal: kagiReversalApplied,
      kagiMode,
      pnfBoxSize: pnfBoxApplied,
      pnfReversal: pnfReversalApplied,
      linePriceSource,
      lineChartLineType,
      baselinePriceInput,
    });

    const next = addMainPriceSeries(chartRef.current, priceSeriesStyle, payload, {
      bullishCandleBodyColor,
      bearishCandleBodyColor,
      bullishCandleBorderColor,
      bearishCandleBorderColor,
      bullishCandleWickColor,
      bearishCandleWickColor,
      chartBackgroundColor,
    });

    candleSeriesRef.current = next;
    next.setData(payload.data);
    next.setSeriesOrder(0);
    markersRef.current = createSeriesMarkers(next, []);
    markersRef.current.setMarkers(visibleMarkers);
    next.priceScale().applyOptions({ autoScale: true });
    chartRef.current.timeScale().fitContent();
  }, [priceSeriesStyle]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // 🔁 Update chart when data changes (replay pan is separate to avoid time-scale + overlay feedback)
  useEffect(() => {
    if (visibleData.length && candleSeriesRef.current) {
      candleSeriesRef.current.setData(priceChartPayload.data);
      if (priceSeriesStyle === PRICE_SERIES_STYLES.BASELINE) {
        candleSeriesRef.current.applyOptions({
          baseValue: {
            type: "price",
            price: priceChartPayload.baselinePrice ?? 0,
          },
        });
      }
      markersRef.current?.setMarkers(visibleMarkers);

      if (!isReplayMode && pendingScaleFitRef.current) {
        candleSeriesRef.current.priceScale().applyOptions({ autoScale: true });
        chartRef.current.timeScale().fitContent();
        pendingScaleFitRef.current = false;
      }
    }
    if (!visibleData.length) {
      candleSeriesRef.current?.setData([]);
      markersRef.current?.setMarkers([]);
      setHoveredCandle(null);
    }
  }, [isReplayMode, priceChartPayload, priceSeriesStyle, visibleData, visibleMarkers]);

  useEffect(() => {
    if (!isReplayMode || !chartRef.current || !visibleData.length) {
      return undefined;
    }

    const lastIndex = visibleData.length - 1;
    const from = Math.max(0, lastIndex - REPLAY_WINDOW_BARS);
    const to = Math.max(from + 1, Math.max(REPLAY_WINDOW_BARS * 0.35, lastIndex + 5));

    const rafId = window.requestAnimationFrame(() => {
      const chart = chartRef.current;
      if (!chart) {
        return;
      }
      try {
        replayProgrammaticScrollRef.current = true;
        chart.timeScale().setVisibleLogicalRange({ from, to });
        // The visibleLogicalRangeChange subscription skips its own refresh for
        // programmatic replay scrolls (replayProgrammaticScrollRef guards against a
        // feedback loop there), so trigger it directly here instead — otherwise
        // drawings (trend lines, rectangles, ...) stay pinned to their old screen
        // position while replay auto-scrolls the candles out from under them.
        requestOverlayRefresh();
      } catch {
        // Invalid range during rapid data/resize — skip this frame
      }
      window.queueMicrotask(() => {
        replayProgrammaticScrollRef.current = false;
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [isReplayMode, replayIndex, visibleData.length, requestOverlayRefresh]);

  useEffect(() => {
    markersRef.current?.setMarkers(visibleMarkers);
  }, [visibleMarkers]);

  useEffect(() => {
    if (!movingAverageSeriesRef.current) return;

    movingAverageSeriesRef.current.applyOptions({
      title: `MA ${maPeriod}`,
      visible: showMovingAverage,
    });
    movingAverageSeriesRef.current.setData(
      showMovingAverage ? movingAverageData : []
    );
    syncPaneLineFill(
      paneLineFillsRef.current?.ma,
      showMovingAverage,
      movingAverageData,
      getIndicatorColor("ma")
    );
  }, [getIndicatorColor, maPeriod, movingAverageData, showMovingAverage]);

  useEffect(() => {
    if (!smaSeriesRef.current) return;

    smaSeriesRef.current.applyOptions({
      title: `SMA ${smaPeriod}`,
      visible: showSma,
    });
    smaSeriesRef.current.setData(showSma ? smaData : []);
    syncPaneLineFill(
      paneLineFillsRef.current?.sma,
      showSma,
      smaData,
      getIndicatorColor("sma")
    );
  }, [getIndicatorColor, showSma, smaData, smaPeriod]);

  useEffect(() => {
    if (!emaSeriesRef.current) return;

    emaSeriesRef.current.applyOptions({
      title: `EMA ${emaPeriod}`,
      visible: showEma,
    });
    emaSeriesRef.current.setData(showEma ? emaData : []);
    syncPaneLineFill(
      paneLineFillsRef.current?.ema,
      showEma,
      emaData,
      getIndicatorColor("ema")
    );
  }, [emaData, emaPeriod, getIndicatorColor, showEma]);

  useEffect(() => {
    if (!wmaSeriesRef.current) return;

    wmaSeriesRef.current.applyOptions({
      title: `WMA ${wmaPeriod}`,
      visible: showWma,
    });
    wmaSeriesRef.current.setData(showWma ? wmaData : []);
    syncPaneLineFill(
      paneLineFillsRef.current?.wma,
      showWma,
      wmaData,
      getIndicatorColor("wma")
    );
  }, [getIndicatorColor, showWma, wmaData, wmaPeriod]);

  useEffect(() => {
    if (!bbUpperSeriesRef.current || !bbMiddleSeriesRef.current || !bbLowerSeriesRef.current) {
      return;
    }

    bbUpperSeriesRef.current.applyOptions({
      visible: showBollinger,
      title: `BB ${bbPeriod}, ${bbDeviation} Upper`,
    });
    bbMiddleSeriesRef.current.applyOptions({
      visible: showBollinger,
      title: `BB ${bbPeriod}, ${bbDeviation} Mid`,
    });
    bbLowerSeriesRef.current.applyOptions({
      visible: showBollinger,
      title: `BB ${bbPeriod}, ${bbDeviation} Lower`,
    });

    bbUpperSeriesRef.current.setData(showBollinger ? bollingerData.upper : []);
    bbMiddleSeriesRef.current.setData(showBollinger ? bollingerData.middle : []);
    bbLowerSeriesRef.current.setData(showBollinger ? bollingerData.lower : []);
    const bbCol = getIndicatorColor("bb");
    syncPaneLineFill(
      paneLineFillsRef.current?.bbUpper,
      showBollinger,
      bollingerData.upper,
      bbCol
    );
    syncPaneLineFill(
      paneLineFillsRef.current?.bbMiddle,
      showBollinger,
      bollingerData.middle,
      bbCol
    );
    syncPaneLineFill(
      paneLineFillsRef.current?.bbLower,
      showBollinger,
      bollingerData.lower,
      bbCol
    );
  }, [
    bbDeviation,
    bbPeriod,
    bollingerData.lower,
    bollingerData.middle,
    bollingerData.upper,
    getIndicatorColor,
    showBollinger,
  ]);

  useEffect(() => {
    if (!rsiSeriesRef.current || !rsiUpperSeriesRef.current || !rsiLowerSeriesRef.current) {
      return;
    }

    rsiSeriesRef.current.applyOptions({
      visible: showRsi,
      title: `RSI ${rsiPeriod}`,
    });
    rsiUpperSeriesRef.current.applyOptions({
      visible: showRsi,
      title: `RSI ${rsiUpper}`,
    });
    rsiLowerSeriesRef.current.applyOptions({
      visible: showRsi,
      title: `RSI ${rsiLower}`,
    });

    if (!showRsi) {
      rsiSeriesRef.current.setData([]);
      rsiUpperSeriesRef.current.setData([]);
      rsiLowerSeriesRef.current.setData([]);
      syncPaneLineFill(
        paneLineFillsRef.current?.rsi,
        false,
        [],
        getIndicatorColor("rsi")
      );
      return;
    }

    rsiSeriesRef.current.setData(rsiData);
    const thresholdTimes = rsiData.map((entry) => entry.time);
    rsiUpperSeriesRef.current.setData(
      thresholdTimes.map((time) => ({ time, value: rsiUpper }))
    );
    rsiLowerSeriesRef.current.setData(
      thresholdTimes.map((time) => ({ time, value: rsiLower }))
    );
    syncPaneLineFill(
      paneLineFillsRef.current?.rsi,
      true,
      rsiData,
      getIndicatorColor("rsi")
    );
  }, [getIndicatorColor, rsiData, rsiLower, rsiPeriod, rsiUpper, showRsi]);

  useEffect(() => {
    const paneIndex = oscillatorPaneIndices.rsi ?? 0;

    [
      () => paneLineFillsRef.current?.rsi,
      rsiSeriesRef,
      rsiUpperSeriesRef,
      rsiLowerSeriesRef,
    ].forEach((ref) => {
      const s = typeof ref === "function" ? ref() : ref.current;
      s?.moveToPane(paneIndex);
    });
  }, [oscillatorPaneIndices.rsi]);

  useEffect(() => {
    if (!atrSeriesRef.current) {
      return;
    }

    atrSeriesRef.current.applyOptions({
      visible: showAtr,
      title: `ATR ${atrPeriod}`,
    });
    atrSeriesRef.current.setData(showAtr ? atrData : []);
    syncPaneLineFill(
      paneLineFillsRef.current?.atr,
      showAtr,
      atrData,
      getIndicatorColor("atr")
    );
  }, [atrData, atrPeriod, getIndicatorColor, showAtr]);

  useEffect(() => {
    const p = oscillatorPaneIndices.atr ?? 0;
    paneLineFillsRef.current?.atr?.moveToPane(p);
    atrSeriesRef.current?.moveToPane(p);
  }, [oscillatorPaneIndices.atr]);

  useEffect(() => {
    if (
      !donchianUpperSeriesRef.current ||
      !donchianMiddleSeriesRef.current ||
      !donchianLowerSeriesRef.current
    ) {
      return;
    }

    donchianUpperSeriesRef.current.applyOptions({
      visible: showDonchian,
      title: `DC ${donchianPeriod} Upper`,
    });
    donchianMiddleSeriesRef.current.applyOptions({
      visible: showDonchian,
      title: `DC ${donchianPeriod} Mid`,
    });
    donchianLowerSeriesRef.current.applyOptions({
      visible: showDonchian,
      title: `DC ${donchianPeriod} Lower`,
    });

    donchianUpperSeriesRef.current.setData(
      showDonchian ? donchianData.upper : []
    );
    donchianMiddleSeriesRef.current.setData(
      showDonchian ? donchianData.middle : []
    );
    donchianLowerSeriesRef.current.setData(
      showDonchian ? donchianData.lower : []
    );
    const dcCol = getIndicatorColor("donchian");
    syncPaneLineFill(
      paneLineFillsRef.current?.donchianUpper,
      showDonchian,
      donchianData.upper,
      dcCol
    );
    syncPaneLineFill(
      paneLineFillsRef.current?.donchianMiddle,
      showDonchian,
      donchianData.middle,
      dcCol
    );
    syncPaneLineFill(
      paneLineFillsRef.current?.donchianLower,
      showDonchian,
      donchianData.lower,
      dcCol
    );
  }, [
    donchianData.lower,
    donchianData.middle,
    donchianData.upper,
    donchianPeriod,
    getIndicatorColor,
    showDonchian,
  ]);

  useEffect(() => {
    if (
      !stochasticKSeriesRef.current ||
      !stochasticDSeriesRef.current ||
      !stochasticUpperSeriesRef.current ||
      !stochasticLowerSeriesRef.current
    ) {
      return;
    }

    stochasticKSeriesRef.current.applyOptions({
      visible: showStochastic,
      title: `Stoch %K ${stochKPeriod}`,
    });
    stochasticDSeriesRef.current.applyOptions({
      visible: showStochastic,
      title: `Stoch %D ${stochDPeriod}`,
    });
    stochasticUpperSeriesRef.current.applyOptions({
      visible: showStochastic,
    });
    stochasticLowerSeriesRef.current.applyOptions({
      visible: showStochastic,
    });

    if (!showStochastic) {
      stochasticKSeriesRef.current.setData([]);
      stochasticDSeriesRef.current.setData([]);
      stochasticUpperSeriesRef.current.setData([]);
      stochasticLowerSeriesRef.current.setData([]);
      const sc = getIndicatorColor("stochastic");
      syncPaneLineFill(paneLineFillsRef.current?.stochasticK, false, [], sc);
      syncPaneLineFill(paneLineFillsRef.current?.stochasticD, false, [], sc);
      return;
    }

    stochasticKSeriesRef.current.setData(stochasticData.k);
    stochasticDSeriesRef.current.setData(stochasticData.d);
    const thresholdTimes = stochasticData.k.map((entry) => entry.time);
    stochasticUpperSeriesRef.current.setData(
      thresholdTimes.map((time) => ({ time, value: stochUpper }))
    );
    stochasticLowerSeriesRef.current.setData(
      thresholdTimes.map((time) => ({ time, value: stochLower }))
    );
    const sc = getIndicatorColor("stochastic");
    syncPaneLineFill(
      paneLineFillsRef.current?.stochasticK,
      true,
      stochasticData.k,
      sc
    );
    syncPaneLineFill(
      paneLineFillsRef.current?.stochasticD,
      true,
      stochasticData.d,
      sc
    );
  }, [
    getIndicatorColor,
    showStochastic,
    stochDPeriod,
    stochKPeriod,
    stochLower,
    stochUpper,
    stochasticData.d,
    stochasticData.k,
  ]);

  useEffect(() => {
    const paneIndex = oscillatorPaneIndices.stochastic ?? 0;

    [
      () => paneLineFillsRef.current?.stochasticK,
      () => paneLineFillsRef.current?.stochasticD,
      stochasticKSeriesRef,
      stochasticDSeriesRef,
      stochasticUpperSeriesRef,
      stochasticLowerSeriesRef,
    ].forEach((ref) => {
      const s = typeof ref === "function" ? ref() : ref.current;
      s?.moveToPane(paneIndex);
    });
  }, [oscillatorPaneIndices.stochastic]);

  useEffect(() => {
    if (
      !macdLineSeriesRef.current ||
      !macdSignalSeriesRef.current ||
      !macdHistogramSeriesRef.current
    ) {
      return;
    }

    macdLineSeriesRef.current.applyOptions({
      visible: showMacd,
      title: `MACD ${macdFastPeriod}/${macdSlowPeriod}`,
    });
    macdSignalSeriesRef.current.applyOptions({
      visible: showMacd,
      title: `Signal ${macdSignalPeriod}`,
    });
    macdHistogramSeriesRef.current.applyOptions({
      visible: showMacd,
    });

    const macdColor = getIndicatorColor("macd");
    syncPaneLineFill(
      paneLineFillsRef.current?.macdLine,
      showMacd,
      macdData.line,
      macdColor
    );
    syncPaneLineFill(
      paneLineFillsRef.current?.macdSignal,
      showMacd,
      macdData.signal,
      macdColor
    );

    macdLineSeriesRef.current.setData(showMacd ? macdData.line : []);
    macdSignalSeriesRef.current.setData(showMacd ? macdData.signal : []);
    macdHistogramSeriesRef.current.setData(
      showMacd
        ? macdData.histogram.map((point) => ({
            ...point,
            color: getIndicatorColor("macd"),
          }))
        : []
    );
  }, [
    macdData.histogram,
    macdData.line,
    macdData.signal,
    macdFastPeriod,
    macdSignalPeriod,
    macdSlowPeriod,
    getIndicatorColor,
    indicatorColorFallbacks,
    indicatorColors,
    showMacd,
  ]);

  useEffect(() => {
    const paneIndex = oscillatorPaneIndices.macd ?? 0;

    [
      () => paneLineFillsRef.current?.macdLine,
      () => paneLineFillsRef.current?.macdSignal,
      macdHistogramSeriesRef,
      macdLineSeriesRef,
      macdSignalSeriesRef,
    ].forEach((ref) => {
      const s = typeof ref === "function" ? ref() : ref.current;
      s?.moveToPane(paneIndex);
    });
  }, [oscillatorPaneIndices.macd]);

  useEffect(() => {
    const refs = [userScriptLine1Ref, userScriptLine2Ref, userScriptLine3Ref];
    if (!userScriptEnabled || !userScriptOutput?.series?.length) {
      refs.forEach((r) => {
        r.current?.applyOptions({ visible: false });
        r.current?.setData([]);
      });
      return;
    }

    const paletteColors = [palette.userScript1, palette.userScript2, palette.userScript3];
    const series = userScriptOutput.series.slice(0, 3);
    const isOsc = userScriptOutput.pane === "oscillator";

    series.forEach((s, i) => {
      const ref = refs[i];
      if (!ref.current) return;
      ref.current.applyOptions({
        visible: true,
        title: s.name || `Script ${i + 1}`,
        color: s.color || paletteColors[i],
        priceScaleId: isOsc ? "user-script-scale" : "",
      });
      ref.current.setData(s.data || []);
    });
    for (let i = series.length; i < 3; i += 1) {
      refs[i].current?.applyOptions({ visible: false });
      refs[i].current?.setData([]);
    }
  }, [
    palette.userScript1,
    palette.userScript2,
    palette.userScript3,
    userScriptEnabled,
    userScriptOutput,
  ]);

  useEffect(() => {
    const paneIndex =
      userScriptEnabled && userScriptOutput?.pane === "oscillator"
        ? oscillatorPaneIndices.userScript ?? 0
        : 0;
    [userScriptLine1Ref, userScriptLine2Ref, userScriptLine3Ref].forEach((r) => {
      r.current?.moveToPane(paneIndex);
    });
  }, [
    oscillatorPaneIndices.userScript,
    userScriptEnabled,
    userScriptOutput?.pane,
  ]);

  useEffect(() => {
    const E = extSeriesRef.current;
    if (!E.vwap) return;

    const line = (key, show, data, extra = {}) => {
      const colorKey = extra.colorKey ?? extSeriesIndicatorColorKey(key);
      const { colorKey: _drop, ...lineExtra } = extra;
      const fill = E[`${key}Fill`];
      if (fill) {
        if (show && data?.length) {
          const col = getIndicatorColor(colorKey);
          fill.applyOptions({ visible: true });
          fill.setData(
            data
              .filter((p) => Number.isFinite(p.value))
              .map((p) => ({
                time: p.time,
                value: p.value,
                topColor: hexToRgba(col, INDICATOR_FILL_TOP_ALPHA),
                bottomColor: hexToRgba(col, 0),
              }))
          );
        } else {
          fill.applyOptions({ visible: false });
          fill.setData([]);
        }
      }
      const s = E[key];
      if (!s) return;
      s.applyOptions({ visible: show, ...lineExtra });
      s.setData(show && data?.length ? data : []);
    };

    line("vwap", showVwap, vwapData, {
      title: hasRealVolume ? "VWAP" : "VWAP (synthetic vol)",
    });
    line("hma", showHma, hmaData, { title: `HMA ${hmaPeriod}` });
    line("dema", showDema, demaData, { title: `DEMA ${demaPeriod}` });
    line("tema", showTema, temaData, { title: `TEMA ${temaPeriod}` });
    line("ichiTenkan", showIchimoku, ichimokuData.tenkan, { title: "Tenkan" });
    line("ichiKijun", showIchimoku, ichimokuData.kijun, { title: "Kijun" });
    line("ichiSpanA", showIchimoku, ichimokuData.spanA, { title: "Senkou A" });
    line("ichiSpanB", showIchimoku, ichimokuData.spanB, { title: "Senkou B" });
    line("ichiChikou", showIchimoku, ichimokuData.chikou, { title: "Chikou" });
    line("sar", showSar, sarData, { title: "SAR" });
    line("keltUpper", showKeltner, keltnerData.upper, { title: "Keltner +" });
    line("keltMid", showKeltner, keltnerData.middle, { title: "Keltner mid" });
    line("keltLower", showKeltner, keltnerData.lower, { title: "Keltner −" });
    line("cci", showCci, cciData, { title: `CCI ${cciPeriod}` });
    line("williams", showWilliams, williamsData, { title: `Williams %R ${williamsPeriod}` });
    line("roc", showRoc, rocData, { title: `ROC ${rocPeriod}` });
    line("mom", showMom, momData, { title: `Momentum ${momPeriod}` });
    line("stochRsiK", showStochRsi, stochRsiData.k, { title: "Stoch RSI K" });
    line("stochRsiD", showStochRsi, stochRsiData.d, { title: "Stoch RSI D" });
    line("adx", showAdx, adxBundle.adx, { title: `ADX ${adxPeriod}` });
    line("plusDI", showAdx, adxBundle.plusDI, { title: "+DI" });
    line("minusDI", showAdx, adxBundle.minusDI, { title: "−DI" });
    if (E.ao) {
      E.ao.applyOptions({ visible: showAo, title: "AO" });
      E.ao.setData(
        showAo && aoData.length
          ? aoData.map((d) => ({
              ...d,
              color: getIndicatorColor("ao"),
            }))
          : []
      );
    }
    line("tsi", showTsi, tsiData, { title: "TSI" });
    line("obv", showObv, obvData, {
      title: hasRealVolume ? "OBV" : "OBV (synthetic vol)",
    });
    line("cmf", showCmf, cmfData, {
      title: hasRealVolume ? `CMF ${cmfPeriod}` : `CMF ${cmfPeriod} (synth vol)`,
    });
    line("mfi", showMfi, mfiData, {
      title: hasRealVolume ? `MFI ${mfiPeriod}` : `MFI ${mfiPeriod} (synth vol)`,
    });
    line("adLine", showAdLine, adLineData, {
      title: hasRealVolume ? "A/D" : "A/D (synth vol)",
    });
    line("chaikinVol", showChaikinVol, chaikinVolData, { title: "Chaikin Vol" });
    line("volOsc", showVolOsc, volOscData, {
      title: hasRealVolume ? "Vol Osc %" : "Vol Osc % (synth vol)",
    });
    line("stdDev", showStdDev, stdDevData, { title: `StdDev ${stdDevPeriod}` });
    line("kaufmanRange", showKaufmanRange && kaufmanRangeDisplayMode === "efficiency", kaufmanRangeData, {
      title: `Kaufman Range ${kaufmanRangePeriod}/${kaufmanRangeSmooth}`,
    });
  }, [
    adxBundle,
    adLineData,
    aoData,
    adxPeriod,
    cciData,
    cciPeriod,
    chaikinVolData,
    cmfData,
    cmfPeriod,
    demaData,
    demaPeriod,
    hasRealVolume,
    hmaData,
    hmaPeriod,
    ichimokuData,
    getIndicatorColor,
    kaufmanRangeData,
    kaufmanRangeDisplayMode,
    kaufmanRangePeriod,
    kaufmanRangeSmooth,
    keltnerData,
    momData,
    momPeriod,
    mfiData,
    mfiPeriod,
    obvData,
    indicatorColorFallbacks,
    indicatorColors,
    rocData,
    rocPeriod,
    sarData,
    showAdLine,
    showAdx,
    showAo,
    showCci,
    showChaikinVol,
    showCmf,
    showDema,
    showHma,
    showIchimoku,
    showKaufmanRange,
    showKeltner,
    showMom,
    showMfi,
    showObv,
    showRoc,
    showSar,
    showStdDev,
    showStochRsi,
    showTema,
    showTsi,
    showVolOsc,
    showVwap,
    showWilliams,
    stdDevData,
    stdDevPeriod,
    stochRsiData,
    temaData,
    temaPeriod,
    tsiData,
    volOscData,
    williamsData,
    williamsPeriod,
    vwapData,
  ]);

  useEffect(() => {
    const E = extSeriesRef.current;
    if (!E.vwap) return;
    const p0 = 0;
    [
      "vwap",
      "vwapFill",
      "hma",
      "hmaFill",
      "dema",
      "demaFill",
      "tema",
      "temaFill",
      "ichiTenkan",
      "ichiTenkanFill",
      "ichiKijun",
      "ichiKijunFill",
      "ichiSpanA",
      "ichiSpanAFill",
      "ichiSpanB",
      "ichiSpanBFill",
      "ichiChikou",
      "sar",
      "keltUpper",
      "keltUpperFill",
      "keltMid",
      "keltMidFill",
      "keltLower",
    ].forEach((k) => {
      E[k]?.moveToPane(p0);
    });
    extraInstanceSeriesRef.current.forEach((bundle) => {
      Object.values(bundle).forEach((series) => series?.moveToPane(p0));
    });
    const oscPair = (key, idxKey) => {
      const p = oscillatorPaneIndices[idxKey] ?? 0;
      E[`${key}Fill`]?.moveToPane(p);
      E[key]?.moveToPane(p);
    };
    oscPair("cci", "cci");
    oscPair("williams", "williams");
    oscPair("roc", "roc");
    oscPair("mom", "momentum");
    oscPair("stochRsiK", "stochRsi");
    oscPair("stochRsiD", "stochRsi");
    if (E.ao) E.ao.moveToPane(oscillatorPaneIndices.ao ?? 0);
    oscPair("adx", "adx");
    oscPair("plusDI", "adx");
    oscPair("minusDI", "adx");
    oscPair("tsi", "tsi");
    oscPair("obv", "obv");
    oscPair("cmf", "cmf");
    oscPair("mfi", "mfi");
    oscPair("adLine", "adLine");
    oscPair("chaikinVol", "chaikinVol");
    oscPair("volOsc", "volOsc");
    oscPair("stdDev", "stdDev");
    oscPair("kaufmanRange", "kaufmanRange");
  }, [
    oscillatorPaneIndices,
    extraIndicatorInstances,
    showAdx,
    showAo,
    showCci,
    showChaikinVol,
    showCmf,
    showDema,
    showHma,
    showIchimoku,
    showKaufmanRange,
    showKeltner,
    showMom,
    showMfi,
    showObv,
    showRoc,
    showSar,
    showStdDev,
    showStochRsi,
    showTema,
    showTsi,
    showVolOsc,
    showVwap,
    showWilliams,
  ]);

  useEffect(() => {
    if (!userScriptEnabled || !userScriptCode.trim()) {
      setUserScriptRunning(false);
      return;
    }
    const controller = new AbortController();
    let active = true;
    setUserScriptRunning(true);
    const timer = window.setTimeout(() => {
      runUserScript(userScriptCode, visibleData, { signal: controller.signal }).then((out) => {
        if (!active) return;
        if (out.ok) {
          setUserScriptOutput(out.result);
          setUserScriptError(null);
        } else {
          setUserScriptError(out.error);
          setUserScriptOutput(null);
        }
      }).finally(() => {
        if (active) setUserScriptRunning(false);
      });
    }, 120);
    return () => {
      active = false;
      window.clearTimeout(timer);
      controller.abort();
      setUserScriptRunning(false);
    };
  }, [visibleData, userScriptEnabled, userScriptCode, userScriptRunRequest]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const panes = chartRef.current.panes();

    if (!panes.length) {
      return;
    }

    panes[0].setStretchFactor(
      Math.max(MAIN_PANE_STRETCH, Object.keys(oscillatorPaneIndices).length + 1)
    );

    panes.slice(1).forEach((pane) => {
      pane.setStretchFactor(OSCILLATOR_PANE_STRETCH);
    });
  }, [oscillatorPaneIndices]);

  useEffect(() => {
    if (!isMeasureMode) {
      setHoverMeasurePoint(null);
    }
  }, [isMeasureMode]);

  useEffect(() => {
    if (!isReplayMode || !isReplayPlaying) {
      if (replayTimerRef.current) {
        window.clearInterval(replayTimerRef.current);
        replayTimerRef.current = null;
      }
      return undefined;
    }

    replayTimerRef.current = window.setInterval(() => {
      setReplayIndex((currentIndex) => {
        if (currentIndex === null) {
          return 0;
        }

        if (currentIndex >= data.length - 1) {
          return currentIndex;
        }

        return currentIndex + 1;
      });
    }, REPLAY_INTERVAL_MS);

    return () => {
      if (replayTimerRef.current) {
        window.clearInterval(replayTimerRef.current);
        replayTimerRef.current = null;
      }
    };
  }, [data.length, isReplayMode, isReplayPlaying]);

  /** Stop continuous play at last bar; keep setState out of other updaters (avoids React errors / jank). */
  useEffect(() => {
    if (!isReplayMode || !isReplayPlaying || replayIndex === null || !data.length) {
      return;
    }
    if (replayIndex >= data.length - 1) {
      setIsReplayPlaying(false);
    }
  }, [data.length, isReplayMode, isReplayPlaying, replayIndex]);

  useEffect(() => {
    if (!isReplayMode || replayIndex === null || !data.length) {
      return;
    }
    const max = data.length - 1;
    if (replayIndex > max) {
      setReplayIndex(max);
    }
  }, [data.length, isReplayMode, replayIndex]);

  useEffect(() => {
    const handlePointerRelease = () => {
      handleReplayAdvancePressEnd();
    };

    window.addEventListener("mouseup", handlePointerRelease);
    window.addEventListener("touchend", handlePointerRelease);

    return () => {
      window.removeEventListener("mouseup", handlePointerRelease);
      window.removeEventListener("touchend", handlePointerRelease);
    };
  });

  useEffect(() => {
    if (!isReplayMode || replayIndex === null) {
      return;
    }

    setHoveredCandle(data[replayIndex] ?? null);
  }, [data, isReplayMode, replayIndex]);

  const replayLineOverlay = (() => {
    if (!chartRef.current || !candleSeriesRef.current || !data.length) {
      return null;
    }

    const targetIndex = isReplayMode
      ? replayIndex
      : isReplaySelecting
        ? replayPreviewIndex
        : null;

    if (!Number.isInteger(targetIndex)) {
      return null;
    }

    const clampedIndex = Math.max(0, Math.min(data.length - 1, targetIndex));
    const x = chartRef.current.timeScale().logicalToCoordinate(clampedIndex);

    if (x === null) {
      return null;
    }

    return {
      x,
      time: data[clampedIndex]?.time ?? null,
      isPreview: isReplaySelecting,
    };
  })();

  const activeMeasurePoints = useMemo(() => {
    if (measurePoints.length === 2) {
      return measurePoints;
    }

    if (measurePoints.length === 1 && hoverMeasurePoint) {
      return [measurePoints[0], hoverMeasurePoint];
    }

    return measurePoints;
  }, [hoverMeasurePoint, measurePoints]);

  useEffect(() => {
    if (!measureSeriesRef.current) return;

    if (activeMeasurePoints.length === 2) {
      const sortedPoints = [...activeMeasurePoints].sort(
        (firstPoint, secondPoint) => firstPoint.logical - secondPoint.logical
      );
      const measureLineData = sortedPoints.map((point) => ({
        time: point.time ?? Math.max(0, Math.round(point.logical)),
        value: point.price,
      }));

      const hasAscendingTimes =
        measureLineData.length === 2 &&
        measureLineData[0].time < measureLineData[1].time;

      if (!hasAscendingTimes) {
        measureSeriesRef.current.setData([]);
        return;
      }

      measureSeriesRef.current.setData(measureLineData);
      return;
    }

    measureSeriesRef.current.setData([]);
  }, [activeMeasurePoints]);

  const measurementOverlay = (() => {
    if (activeMeasurePoints.length < 1 || !chartRef.current || !candleSeriesRef.current) {
      return null;
    }

    const timeScale = chartRef.current.timeScale();
    const projectedPoints = activeMeasurePoints
      .map((point) => {
        const x = timeScale.logicalToCoordinate(point.logical);
        const y = candleSeriesRef.current.priceToCoordinate(point.price);

        if (x === null || y === null) {
          return null;
        }

        return {
          ...point,
          x,
          y,
        };
      })
      .filter(Boolean);

    if (!projectedPoints.length) {
      return null;
    }

    const [startPoint, endPoint] = projectedPoints;

    if (!endPoint) {
      return {
        points: projectedPoints,
        line: null,
        label: null,
      };
    }

    const priceDifference = endPoint.price - startPoint.price;
    const percentageDifference =
      startPoint.price !== 0 ? (priceDifference / startPoint.price) * 100 : 0;
    const barDifference = Math.round(endPoint.logical - startPoint.logical);
    const midpointX = (startPoint.x + endPoint.x) / 2;
    const midpointY = (startPoint.y + endPoint.y) / 2;

    return {
      points: projectedPoints,
      line: {
        left: Math.min(startPoint.x, endPoint.x),
        top: Math.min(startPoint.y, endPoint.y),
        width: Math.hypot(endPoint.x - startPoint.x, endPoint.y - startPoint.y),
        angle:
          (Math.atan2(endPoint.y - startPoint.y, endPoint.x - startPoint.x) *
            180) /
          Math.PI,
      },
      label: {
        left: midpointX,
        top: midpointY - 18,
        text: `${priceDifference >= 0 ? "+" : ""}${formatPrice(
          priceDifference
        )} (${formatPercentage(percentageDifference)}) | ${barDifference} bars`,
      },
      summary: {
        start: startPoint,
        end: endPoint,
      },
    };
  })();

  const hoveredMovingAverageValue =
    hoveredCandle && showMovingAverage
      ? movingAverageValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredSmaValue =
    hoveredCandle && showSma ? smaValuesByTime.get(hoveredCandle.time) : null;
  const hoveredEmaValue =
    hoveredCandle && showEma ? emaValuesByTime.get(hoveredCandle.time) : null;
  const hoveredWmaValue =
    hoveredCandle && showWma ? wmaValuesByTime.get(hoveredCandle.time) : null;
  const hoveredBbUpper =
    hoveredCandle && showBollinger
      ? bbUpperValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredBbLower =
    hoveredCandle && showBollinger
      ? bbLowerValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredRsi =
    hoveredCandle && showRsi ? rsiValuesByTime.get(hoveredCandle.time) : null;
  const hoveredAtr =
    hoveredCandle && showAtr ? atrValuesByTime.get(hoveredCandle.time) : null;
  const hoveredDonchianUpper =
    hoveredCandle && showDonchian
      ? donchianUpperValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredDonchianLower =
    hoveredCandle && showDonchian
      ? donchianLowerValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredStochasticK =
    hoveredCandle && showStochastic
      ? stochasticKValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredStochasticD =
    hoveredCandle && showStochastic
      ? stochasticDValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredMacdLine =
    hoveredCandle && showMacd
      ? macdLineValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredMacdSignal =
    hoveredCandle && showMacd
      ? macdSignalValuesByTime.get(hoveredCandle.time)
      : null;
  const hoveredMacdHistogram =
    hoveredCandle && showMacd
      ? macdHistogramValuesByTime.get(hoveredCandle.time)
      : null;

  /** Computed once per instance/data change; feeds both the chart series and the hover legend. */
  const extraInstanceComputedData = useMemo(() => {
    const map = new Map();
    extraIndicatorInstances.forEach((inst) => {
      const def = DUPLICABLE_INDICATOR_TYPES[inst.type];
      if (!def) return;
      map.set(inst.id, def.compute(visibleData, inst.params));
    });
    return map;
  }, [extraIndicatorInstances, visibleData]);

  /** Creates/destroys chart series as duplicated indicator instances are added/removed. */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const seriesMap = extraInstanceSeriesRef.current;
    const currentIds = new Set(extraIndicatorInstances.map((inst) => inst.id));

    seriesMap.forEach((bundle, id) => {
      if (currentIds.has(id)) return;
      Object.values(bundle).forEach((series) => {
        try {
          chart.removeSeries(series);
        } catch {
          // series already detached (e.g. chart torn down first)
        }
      });
      seriesMap.delete(id);
    });

    extraIndicatorInstances.forEach((inst) => {
      if (seriesMap.has(inst.id)) return;
      const def = DUPLICABLE_INDICATOR_TYPES[inst.type];
      if (!def) return;
      seriesMap.set(inst.id, createExtraInstanceSeriesBundle(chart, def));
    });
  }, [extraIndicatorInstances, chartContainerEl]);

  /** Recomputes + redraws every duplicated indicator instance's data and color. */
  useEffect(() => {
    const seriesMap = extraInstanceSeriesRef.current;
    extraIndicatorInstances.forEach((inst) => {
      const bundle = seriesMap.get(inst.id);
      if (!bundle) return;
      const def = DUPLICABLE_INDICATOR_TYPES[inst.type];
      if (!def) return;
      const data = extraInstanceComputedData.get(inst.id);
      applyExtraInstanceSeriesData(def, bundle, data, getIndicatorColor(`dup:${inst.id}`));
    });
  }, [extraIndicatorInstances, extraInstanceComputedData, getIndicatorColor, chartContainerEl]);

  /**
   * lightweight-charts 5.1 sometimes drops the price series' bars from the shared time axis
   * when a series is added to a new pane while candle data is being updated (the axis then
   * only knows the new pane's bars, and footprint / delta / indicator drawing stops). This
   * checks that the newest candle is still at the end of the axis and, if not, re-sends the
   * candle data (which rebuilds the axis) and puts the user's scroll/zoom position back.
   */
  const repairTimeScaleIfBroken = useCallback(() => {
    const chart = chartRef.current;
    const main = candleSeriesRef.current;
    if (!chart || !main) return false;
    const rows = main.data();
    if (!rows.length) return false;
    const ts = chart.timeScale();
    let broken = false;
    try {
      const idx = ts.timeToIndex(rows[rows.length - 1].time, false);
      broken = idx == null || idx < rows.length - 1;
      if (!broken) ts.getVisibleRange();
    } catch {
      broken = true;
    }
    if (!broken) return false;
    const view = ts.getVisibleLogicalRange();
    const before = rows.length;
    main.setData(rows);
    if (view) ts.setVisibleLogicalRange(view);
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`Chart time axis repaired (${before} bars)`);
    }
    return true;
  }, []);

  /** Runs the repair now, on the next frame and shortly after (the drop can happen a moment later). */
  const scheduleTimeScaleRepair = useCallback(() => {
    repairTimeScaleIfBroken();
    window.requestAnimationFrame(() => repairTimeScaleIfBroken());
    window.setTimeout(() => repairTimeScaleIfBroken(), 400);
    window.setTimeout(() => repairTimeScaleIfBroken(), 1500);
  }, [repairTimeScaleIfBroken]);

  /** GoCharting oscillators get their own panes, below the built-in oscillator panes. */
  const goPaneIndices = useMemo(() => {
    const used = Object.values(oscillatorPaneIndices).filter(Number.isFinite);
    let next = (used.length ? Math.max(...used) : 0) + 1;
    const map = {};
    goInstances.forEach((inst) => {
      const def = GO_INDICATORS[inst.type];
      if (!def) return;
      if (def.overlay) map[inst.type] = 0;
      else {
        map[inst.type] = next;
        next += 1;
      }
    });
    return map;
  }, [goInstances, oscillatorPaneIndices]);

  /** Creates, updates and removes the chart series for GoCharting indicators. */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const map = goSeriesRef.current;
    const activeTypes = new Set(goInstances.map((inst) => inst.type));
    const dropEntry = (type, entry) => {
      if (entry.chart === chart) {
        entry.series.forEach((series) => {
          try {
            chart.removeSeries(series);
          } catch {
            // already detached
          }
        });
      }
      map.delete(type);
    };
    map.forEach((entry, type) => {
      if (entry.chart !== chart || !activeTypes.has(type)) dropEntry(type, entry);
    });

    const upColor = "rgba(34, 197, 94, 0.75)";
    const downColor = "rgba(239, 68, 68, 0.75)";
    let goCreatedSeries = false;
    goInstances.forEach((inst) => {
      const def = GO_INDICATORS[inst.type];
      const res = goComputed.get(inst.type);
      if (!def || !res) return;
      const pane = goPaneIndices[inst.type] ?? 0;
      let entry = map.get(inst.type);
      if (entry && entry.series.length !== res.lines.length) {
        dropEntry(inst.type, entry);
        entry = null;
      }
      if (!entry) {
        entry = { chart, series: [], panes: [], levelsKey: "", levelLines: [] };
        res.lines.forEach((l) => {
          const isHist = l.kind === "histogram";
          const options = {
            lastValueVisible: !def.overlay,
            priceLineVisible: false,
            crosshairMarkerVisible: !isHist,
            ...(isHist
              ? {}
              : {
                  lineWidth: l.lineWidth ?? INDICATOR_LINE_WIDTH,
                  lineStyle: l.lineStyle ?? 0,
                  lineType: l.step ? 1 : 0,
                }),
            ...(l.priceScaleId ? { priceScaleId: l.priceScaleId, lastValueVisible: false } : {}),
          };
          const series = chart.addSeries(isHist ? HistogramSeries : LineSeries, options, pane);
          goCreatedSeries = true;
          if (l.scaleMargins) series.priceScale().applyOptions({ scaleMargins: l.scaleMargins });
          entry.series.push(series);
          entry.panes.push(pane);
        });
        map.set(inst.type, entry);
      }

      const baseColor = getIndicatorColor(`go:${inst.type}`);
      const multi = res.lines.length > 1;
      res.lines.forEach((l, k) => {
        const series = entry.series[k];
        if (entry.panes[k] !== pane) {
          series.moveToPane(pane);
          entry.panes[k] = pane;
        }
        const color = l.color ?? (k === 0 ? baseColor : GO_DEFAULT_COLORS[k % GO_DEFAULT_COLORS.length]);
        series.applyOptions({
          color: l.kind === "histogram" && !l.color ? hexToRgba(baseColor, 0.7) : color,
          title: multi ? (def.overlay ? `${def.shortLabel} ${l.name}` : l.name) : def.shortLabel,
        });
        // Volume under the price bars stays faint so candles remain readable.
        const faint = Boolean(l.priceScaleId);
        series.setData(
          toSeriesPoints(visibleData, l.values, {
            colorBy: l.colorBy,
            upColor: faint ? "rgba(34, 197, 94, 0.28)" : upColor,
            downColor: faint ? "rgba(239, 68, 68, 0.28)" : downColor,
          })
        );
      });

      const levels = res.levels ?? [];
      const levelsKey = levels.join(",");
      if (entry.series[0] && entry.levelsKey !== levelsKey) {
        entry.levelLines.forEach((pl) => entry.series[0].removePriceLine(pl));
        entry.levelLines = levels.map((price) =>
          entry.series[0].createPriceLine({
            price,
            color: palette.mutedText,
            lineWidth: 1,
            lineStyle: 2,
            axisLabelVisible: false,
          })
        );
        entry.levelsKey = levelsKey;
      }
    });
    if (goCreatedSeries) scheduleTimeScaleRepair();
  }, [
    chartContainerEl,
    getIndicatorColor,
    goComputed,
    goInstances,
    goPaneIndices,
    palette.mutedText,
    scheduleTimeScaleRepair,
    visibleData,
  ]);

  // ---------------------------------------------------------------- order flow
  // Market Profile is built from the candles; every other order-flow tool needs the tick data.
  const ofActive =
    (ofTools.trader || ofTools.barStats || ofTools.deltaBars || ofTools.volumeProfile || ofTools.buySellVwap) &&
    isBackendFeedActive &&
    !liveRatioSymbols &&
    !ratioModeEnabled;

  /**
   * Loads footprint bars from the server: a quick first batch, then the full window, then the
   * newest bars every 5 s. Older history is added by the scroll-back effect below.
   */
  useEffect(() => {
    if (!ofActive) {
      ofDataRef.current = null;
      setOfData(null);
      setOfError("");
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let current = null;
    let stage = 0; // 0 = quick first batch, 1 = full window, 2 = live refresh
    ofDataRef.current = null;
    setOfData(null);
    setUploadStatus({
      tone: "info",
      message: `Loading order flow for ${marketSymbol} ${backendFeedTimeframe} from provider quote updates…`,
    });
    const base = `${BACKEND_API_BASE}/orderflow?symbol=${encodeURIComponent(
      marketSymbol
    )}&tf=${encodeURIComponent(backendFeedTimeframe)}&sweep=${ofSettings.sweepRows}`;
    const rowParam = () =>
      current
        ? Math.max(1, Math.round(current.rowSize / 10 ** -current.digits))
        : ofSettings.rowPoints;
    const load = async () => {
      try {
        let url;
        if (stage === 0) url = `${base}&bars=${Math.min(OF_FIRST_BARS, ofSettings.bars)}&row=${ofSettings.rowPoints}`;
        else if (stage === 1) url = `${base}&bars=${ofSettings.bars}&row=${rowParam()}`;
        else {
          const last = current.bars[current.bars.length - 1];
          url = `${base}&bars=${ofSettings.bars}&row=${rowParam()}&since=${last.time - current.periodSeconds}`;
        }
        const res = await marketFetch(url);
        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          const err = new Error(
            res.status === 404 && !detail?.detail
              ? "Order flow needs the updated server — push the backend too."
              : detail?.detail || `Order flow request failed (${res.status}).`
          );
          // e.g. "works on timeframes up to 4h": asking again every 5 s will not change it
          err.final = /up to 4h/.test(String(detail?.detail || ""));
          throw err;
        }
        const body = await res.json();
        if (cancelled) return;
        // keep anything older that the scroll-back loader added meanwhile
        const prev = ofDataRef.current || current;
        if (!prev) current = body;
        else {
          const first = body.bars[0]?.time ?? Infinity;
          current = {
            ...prev,
            lastDataTime: body.lastDataTime,
            marketClosed: body.marketClosed,
            truncated: prev.truncated || body.truncated,
            bars: [...prev.bars.filter((b) => b.time < first), ...body.bars].slice(-OF_MAX_KEPT_BARS),
          };
        }
        ofDataRef.current = current;
        setOfData(current);
        setOfError(
          current.bars.length
            ? ""
            : `No order-flow ticks for ${marketSymbol} in the last ${ofSettings.bars} bars yet.`
        );
        if (stage < 2 && current.bars.length) {
          setUploadStatus({
            tone: "info",
            message:
              stage === 0 && ofSettings.bars > OF_FIRST_BARS
                ? `Order flow: showing the latest ${current.bars.length} bars, loading more…`
                : `Order flow loaded (${current.bars.length} bars). Scroll back to load older bars.`,
          });
        }
        if (stage === 0 && ofSettings.bars <= OF_FIRST_BARS) stage = 2;
        else if (stage < 2) stage += 1;
      } catch (err) {
        if (!cancelled) setOfError(String(err?.message || err));
        if (err?.final) return;
      }
      if (!cancelled) timer = window.setTimeout(load, stage === 1 ? 0 : 5000);
    };
    load();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [
    backendFeedTimeframe,
    marketSymbol,
    ofActive,
    ofSettings.bars,
    ofSettings.rowPoints,
    ofSettings.sweepRows,
  ]);

  /** Scroll back past the oldest order-flow bar and the previous chunk is fetched and added. */
  useEffect(() => {
    if (!ofActive) return undefined;
    const chart = chartRef.current;
    if (!chart) return undefined;
    const ts = chart.timeScale();
    let busy = false;
    let exhausted = false;
    let debounce = null;
    const check = async () => {
      const data = ofDataRef.current;
      const main = candleSeriesRef.current;
      if (busy || exhausted || !data?.bars?.length || !main) return;
      const range = ts.getVisibleLogicalRange();
      const rows = main.data();
      if (!range || !rows.length) return;
      const leftIdx = Math.max(0, Math.floor(range.from));
      const leftTime = rows[Math.min(leftIdx, rows.length - 1)]?.time;
      const oldest = data.bars[0].time;
      if (!Number.isFinite(leftTime) || leftTime >= oldest) return;
      if (data.bars.length >= OF_MAX_KEPT_BARS) return;
      busy = true;
      setOfLoadingOlder(true);
      try {
        const rowPts = Math.max(1, Math.round(data.rowSize / 10 ** -data.digits));
        const url = `${BACKEND_API_BASE}/orderflow?symbol=${encodeURIComponent(
          marketSymbol
        )}&tf=${encodeURIComponent(backendFeedTimeframe)}&sweep=${ofSettings.sweepRows}&bars=${
          ofSettings.bars
        }&row=${rowPts}&until=${oldest}`;
        const res = await marketFetch(url);
        if (!res.ok) throw new Error(`Older order flow request failed (${res.status}).`);
        const body = await res.json();
        const latest = ofDataRef.current;
        if (!latest || latest.symbol !== body.symbol || latest.periodSeconds !== body.periodSeconds) return;
        const older = body.bars.filter((b) => b.time < latest.bars[0].time);
        if (!older.length) {
          exhausted = true; // nothing earlier on the server (market closed / no ticks)
          return;
        }
        const next = { ...latest, bars: [...older, ...latest.bars].slice(-OF_MAX_KEPT_BARS) };
        ofDataRef.current = next;
        setOfData(next);
      } catch (err) {
        setOfError(String(err?.message || err));
      } finally {
        busy = false;
        setOfLoadingOlder(false);
      }
      // the view may still reach further back: check again
      debounce = window.setTimeout(check, 300);
    };
    const onRange = () => {
      if (debounce) window.clearTimeout(debounce);
      debounce = window.setTimeout(check, 250);
    };
    ts.subscribeVisibleLogicalRangeChange(onRange);
    return () => {
      ts.unsubscribeVisibleLogicalRangeChange(onRange);
      if (debounce) window.clearTimeout(debounce);
    };
  }, [backendFeedTimeframe, chartContainerEl, marketSymbol, ofActive, ofSettings.bars, ofSettings.sweepRows]);

  useEffect(() => {
    if (ofError) setUploadStatus({ tone: "error", message: ofError });
  }, [ofError]);

  const ofClosedNoticeRef = useRef("");
  useEffect(() => {
    if (!ofData?.marketClosed || !ofData.lastDataTime) return;
    const key = `${ofData.symbol}:${ofData.lastDataTime}`;
    if (ofClosedNoticeRef.current === key) return;
    ofClosedNoticeRef.current = key;
    setUploadStatus({
      tone: "info",
      message: `${ofData.symbol} market is closed — order flow shows the last session (to ${new Date(
        ofData.lastDataTime * 1000
      ).toUTCString()}).`,
    });
  }, [ofData]);

  const ofProfile = useMemo(
    () => (ofTools.volumeProfile && ofData?.bars?.length ? volumeProfile(ofData.bars) : null),
    [ofData, ofTools.volumeProfile]
  );
  const ofTpo = useMemo(
    () =>
      ofTools.marketProfile && visibleData.length
        ? marketProfileFromCandles(visibleData, {
            periodMinutes: ofSettings.tpoMinutes,
            sessionStartHour: ofSettings.sessionHour,
          })
        : null,
    [ofSettings.sessionHour, ofSettings.tpoMinutes, ofTools.marketProfile, visibleData]
  );

  /** Footprint, profiles and Orderflows Trader paint, drawn on the price series. */
  useEffect(() => {
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    const need = ofTools.trader || ofTools.volumeProfile || ofTools.marketProfile;
    const cur = ofPrimitiveRef.current;
    if (cur && (cur.series !== series || cur.chart !== chart || !need)) {
      try {
        cur.series.detachPrimitive(cur.primitive);
      } catch {
        // series already gone with its chart
      }
      ofPrimitiveRef.current = null;
    }
    if (!need || !chart || !series) return;
    if (!ofPrimitiveRef.current) {
      const primitive = new OrderflowPrimitive();
      series.attachPrimitive(primitive);
      ofPrimitiveRef.current = { primitive, series, chart };
    }
    ofPrimitiveRef.current.primitive.setState({
      bars: ofAnalyzed,
      rowSize: ofData?.rowSize || 0,
      profile: ofProfile,
      tpo: ofTpo,
      opts: {
        footprint: ofTools.trader,
        signalsPaint: ofTools.trader,
        volumeProfile: ofTools.volumeProfile,
        marketProfile: ofTools.marketProfile,
        valueArea: ofTools.trader && ofSignals.has("valueArea"),
        signals: ofTools.trader ? ofSignals : new Set(),
      },
    });
  }, [
    chartContainerEl,
    ofAnalyzed,
    ofData,
    ofProfile,
    ofSignals,
    ofTools.marketProfile,
    ofTools.trader,
    ofTools.volumeProfile,
    ofTpo,
    priceSeriesStyle,
  ]);

  /** Footprint numbers need room: zoom in once when it is switched on. */
  useEffect(() => {
    if (!ofTools.trader) return;
    const ts = chartRef.current?.timeScale();
    if (!ts) return;
    ts.applyOptions({ barSpacing: Math.max(ts.options().barSpacing, 64) });
    ts.scrollToRealTime();
  }, [ofTools.trader, chartContainerEl]);

  /** Delta & CVD bars, Buy/Sell VWAP and the Bar Statistics pane. */
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const map = ofSeriesRef.current;
    const used = [
      ...Object.values(oscillatorPaneIndices),
      ...Object.values(goPaneIndices),
    ].filter(Number.isFinite);
    let nextPane = (used.length ? Math.max(...used) : 0) + 1;
    const deltaPane = ofTools.deltaBars ? nextPane++ : null;
    const statsPane = ofTools.barStats ? nextPane++ : null;
    let ofCreatedSeries = false;
    const ensure = (key, type, options, pane) => {
      let e = map.get(key);
      if (e && e.chart !== chart) {
        map.delete(key);
        e = null;
      }
      if (!e) {
        e = { chart, series: chart.addSeries(type, options, pane), pane };
        map.set(key, e);
        ofCreatedSeries = true;
      } else if (e.pane !== pane) {
        e.series.moveToPane(pane);
        e.pane = pane;
      }
      return e.series;
    };
    const drop = (key) => {
      const e = map.get(key);
      if (!e) return;
      if (e.chart === chart) {
        if (key === "stats" && ofBarStatsRef.current) {
          try {
            e.series.detachPrimitive(ofBarStatsRef.current);
          } catch {
            // detached already
          }
          ofBarStatsRef.current = null;
        }
        try {
          chart.removeSeries(e.series);
        } catch {
          // removed with the chart
        }
      }
      map.delete(key);
    };
    const bars = ofAnalyzed;
    const base = { priceLineVisible: false, crosshairMarkerVisible: false };

    if (ofTools.deltaBars && bars.length) {
      const hist = ensure("delta", HistogramSeries, { ...base, lastValueVisible: true, title: "Delta" }, deltaPane);
      hist.setData(
        bars.map((b) => ({
          time: b.time,
          value: b.stats.delta,
          color: b.stats.delta >= 0 ? "rgba(34,197,94,0.75)" : "rgba(239,68,68,0.75)",
        }))
      );
      const cvd = ensure(
        "cvd",
        LineSeries,
        { ...base, lastValueVisible: true, title: "CVD", color: "#eab308", lineWidth: 2, priceScaleId: "of-cvd" },
        deltaPane
      );
      cvd.priceScale().applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } });
      cvd.setData(bars.map((b) => ({ time: b.time, value: b.stats.cumDelta })));
    } else {
      drop("delta");
      drop("cvd");
    }

    if (ofTools.buySellVwap && bars.length) {
      const v = buySellVwap(bars);
      ensure("buyVwap", LineSeries, { ...base, lastValueVisible: false, title: "Buy VWAP", color: "#3b82f6", lineWidth: 2 }, 0).setData(v.buy);
      ensure("sellVwap", LineSeries, { ...base, lastValueVisible: false, title: "Sell VWAP", color: "#f97316", lineWidth: 2 }, 0).setData(v.sell);
    } else {
      drop("buyVwap");
      drop("sellVwap");
    }

    if (ofTools.barStats && bars.length) {
      const holder = ensure(
        "stats",
        LineSeries,
        { ...base, lastValueVisible: false, lineVisible: false, color: "rgba(0,0,0,0)" },
        statsPane
      );
      holder.setData(bars.map((b) => ({ time: b.time, value: 0 })));
      if (!ofBarStatsRef.current) {
        ofBarStatsRef.current = new BarStatsPrimitive();
        holder.attachPrimitive(ofBarStatsRef.current);
      }
      ofBarStatsRef.current.setState({ bars, signals: ofSignals });
      try {
        chart.panes()[statsPane]?.setHeight(112);
      } catch {
        // pane not laid out yet
      }
    } else {
      drop("stats");
    }
    // new panes can knock the candles off the time axis (see repairTimeScaleIfBroken); the
    // 5-second order-flow refresh also lands here, so a later drop is caught too
    if (ofCreatedSeries) scheduleTimeScaleRepair();
    else repairTimeScaleIfBroken();
  }, [
    chartContainerEl,
    goPaneIndices,
    ofAnalyzed,
    ofSignals,
    ofTools.barStats,
    ofTools.buySellVwap,
    ofTools.deltaBars,
    oscillatorPaneIndices,
    repairTimeScaleIfBroken,
    scheduleTimeScaleRepair,
  ]);

  const extraInstanceHoverEntries = hoveredCandle
    ? extraIndicatorInstances
        .map((inst) => {
          const def = DUPLICABLE_INDICATOR_TYPES[inst.type];
          if (!def) return null;
          const data = extraInstanceComputedData.get(inst.id);
          const valueAt = (points) => {
            if (!Array.isArray(points)) return null;
            for (let i = points.length - 1; i >= 0; i -= 1) {
              if (points[i].time === hoveredCandle.time) return points[i].value;
            }
            return null;
          };
          let text;
          if (def.shape === "band") {
            text = `${formatPrice(valueAt(data?.upper))} / ${formatPrice(
              valueAt(data?.middle)
            )} / ${formatPrice(valueAt(data?.lower))}`;
          } else if (def.shape === "multi") {
            text = `${formatPrice(valueAt(data?.tenkan))} / ${formatPrice(valueAt(data?.kijun))}`;
          } else {
            text = formatPrice(valueAt(data));
          }
          const summary = def.paramDefs.length
            ? def.paramDefs.map((p) => `${inst.params[p.key]}`).join("/")
            : "—";
          return { id: inst.id, label: def.shortLabel, summary, text };
        })
        .filter(Boolean)
    : [];

  // the price pane the drawings live on (clip + pixel <-> chart conversion)
  const drawingPlot = (() => {
    const chart = chartRef.current;
    if (!chart) return { width: 0, height: 0, offsetX: 0 };
    try {
      const width = chart.timeScale().width();
      const pane = typeof chart.panes === "function" ? chart.panes()[0] : null;
      const height = pane && typeof pane.getHeight === "function"
        ? pane.getHeight()
        : Math.max(0, (chartContainerEl?.clientHeight || 0) - 28);
      const offsetX = yAxisSide === "left" ? chart.priceScale("left").width() : 0;
      return { width, height, offsetX };
    } catch (e) {
      return { width: 0, height: 0, offsetX: 0 };
    }
  })();
  // bar position <-> time for the bars now on the chart; rebuilt only when the bars change
  const drawingSeriesRows = (() => {
    try {
      return candleSeriesRef.current ? candleSeriesRef.current.data() : [];
    } catch (e) {
      return [];
    }
  })();
  const drawingRowsCount = drawingSeriesRows.length;
  const drawingAxisKey = drawingRowsCount
    ? `${drawingRowsCount}:${seriesPointTimeToUnix(drawingSeriesRows[0].time)}:${seriesPointTimeToUnix(drawingSeriesRows[drawingRowsCount - 1].time)}`
    : "";
  if (drawingAxisKey !== drawingTimeAxisKeyRef.current) {
    drawingTimeAxisKeyRef.current = drawingAxisKey;
    drawingTimeAxisRef.current = makeTimeAxis(drawingSeriesRows.map((r) => seriesPointTimeToUnix(r.time)));
  }
  const drawingTimeAxis = drawingTimeAxisRef.current;
  /** Drawings with their bar positions worked out from their times (what is shown and edited). */
  const liveDrawings = drawings.map((d) => resyncDrawing(d, drawingTimeAxis));

  /** Screen point in the price pane -> chart point, with the magnet applied (TradingView style). */
  const drawingToChartPoint = (x, y) => {
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    if (!chart || !series) return null;
    let logical = chart.timeScale().coordinateToLogical(x);
    let price = series.coordinateToPrice(y);
    if (!Number.isFinite(logical) || !Number.isFinite(price)) return null;
    const mode = drawPrefs.magnet;
    if (mode && mode !== "off") {
      const i = Math.round(logical);
      const bar = drawingSeriesRows[i];
      if (bar) {
        const m = magnetPrice(bar, price, y, (v) => series.priceToCoordinate(v), mode);
        if (m.snapped) {
          price = m.price;
          logical = i;
        }
      }
    }
    return { logical, price, time: drawingTimeAxis.logicalToTime(logical) };
  };

  /** While drawing: moves the chart crosshair (axis price/time labels) and the preview point. */
  const syncDrawingCrosshair = (point) => {
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    if (!chart || !series) return;
    try {
      if (!point || !drawingSeriesRows.length) {
        chart.clearCrosshairPosition();
      } else {
        const i = Math.max(0, Math.min(drawingSeriesRows.length - 1, Math.round(point.logical)));
        chart.setCrosshairPosition(point.price, drawingSeriesRows[i].time, series);
      }
    } catch (e) {
      // older chart versions: no crosshair sync
    }
    scheduleCrosshairOverlayPointers(point, null);
  };

  // closes whatever floating menu / side panel is open (used by a click on the chart)
  closeFloatingUiRef.current = () => {
    let closed = false;
    if (rightSideTab) { setRightSideTab(null); closed = true; }
    if (layoutsMenuOpen) { setLayoutsMenuOpen(false); closed = true; }
    if (chartGridMenuOpen) { setChartGridMenuOpen(false); closed = true; }
    if (symbolSearchOpen) { setSymbolSearchOpen(false); closed = true; }
    return closed;
  };

  const projectedDrawings = (() => {
    if (!chartRef.current || !candleSeriesRef.current) {
      return [];
    }

    const timeScale = chartRef.current.timeScale();
    const toCoordinate = (point) => {
      const x = timeScale.logicalToCoordinate(point.logical);
      const y = candleSeriesRef.current.priceToCoordinate(point.price);

      if (x === null || y === null) {
        return null;
      }

      return { x, y };
    };

    const resolvedDrawings = (drawPrefs.hidden ? [] : liveDrawings)
      .map((drawing) => {
        if (drawing.type === "hline") {
          const y = candleSeriesRef.current.priceToCoordinate(drawing.price);

          if (y === null) {
            return null;
          }

          return {
            ...drawing,
            y,
          };
        }

        if (drawing.type === "vline") {
          const x = timeScale.logicalToCoordinate(drawing.logical);

          if (x === null) {
            return null;
          }

          return {
            ...drawing,
            x,
          };
        }

        if (drawing.type === "rr") {
          const entry = toCoordinate(drawing.entry);
          const stop = toCoordinate(drawing.stop);
          const target = toCoordinate(drawing.target);

          if (!entry || !stop || !target) {
            return null;
          }

          const riskAbs = Math.abs(drawing.entry.price - drawing.stop.price);
          const rewardAbs = Math.abs(drawing.target.price - drawing.entry.price);
          const rrRatio = riskAbs > 1e-12 ? rewardAbs / riskAbs : null;

          return {
            ...drawing,
            entry,
            stop,
            target,
            rrRatio,
            entryPrice: drawing.entry.price,
            stopPrice: drawing.stop.price,
            targetPrice: drawing.target.price,
          };
        }

        if (drawing.type === "channel") {
          const start = toCoordinate(drawing.start);
          const end = toCoordinate(drawing.end);
          const start2 = toCoordinate({
            logical: drawing.start.logical,
            price: drawing.start.price + drawing.offset,
          });
          const end2 = toCoordinate({
            logical: drawing.end.logical,
            price: drawing.end.price + drawing.offset,
          });

          if (!start || !end || !start2 || !end2) {
            return null;
          }

          return {
            ...drawing,
            start,
            end,
            start2,
            end2,
          };
        }

        if (drawing.type === "pitchfork") {
          const start = toCoordinate(drawing.start);
          const end = toCoordinate(drawing.end);
          const fork = toCoordinate(drawing.fork);
          if (!start || !end || !fork) return null;
          return { ...drawing, start, end, fork };
        }

        if (drawing.type === "text") {
          const at = toCoordinate(drawing.at);

          if (!at) {
            return null;
          }

          return {
            ...drawing,
            at,
          };
        }

        if (drawing.type === "brush") {
          const points = drawing.points.map(toCoordinate).filter(Boolean);
          if (points.length < 2) {
            return null;
          }
          return {
            ...drawing,
            points,
          };
        }

        if (drawing.type === "fib" || drawing.type === "fibext" || drawing.type === "range") {
          const start = toCoordinate(drawing.start);
          const end = toCoordinate(drawing.end);

          if (!start || !end) {
            return null;
          }

          return {
            ...drawing,
            start,
            end,
            startPrice: drawing.start.price,
            endPrice: drawing.end.price,
          };
        }

        const start = toCoordinate(drawing.start);
        const end = toCoordinate(drawing.end);

        if (!start || !end) {
          return null;
        }

        return {
          ...drawing,
          start,
          end,
        };
      })
      .filter(Boolean);

    const activeTool = drawToolRef.current;

    if (activeTool === "rr" && drawStartPoint && drawHoverPoint) {
      const entry = toCoordinate(drawStartPoint);
      const hover = toCoordinate(drawHoverPoint);

      if (entry && hover) {
        if (!drawRrStopPoint) {
          return [
            ...resolvedDrawings,
            {
              id: "rr-preview-risk",
              type: "rr-line",
              isPreview: true,
              leg: "risk",
              start: entry,
              end: hover,
            },
          ];
        }

        const stop = toCoordinate(drawRrStopPoint);

        if (stop && hover) {
          // full profit / loss boxes while choosing the target (TradingView style)
          const risk = Math.abs(drawStartPoint.price - drawRrStopPoint.price);
          return [
            ...resolvedDrawings,
            {
              id: "rr-preview",
              type: "rr",
              isPreview: true,
              entry,
              stop,
              target: hover,
              rrRatio: risk > 1e-12 ? Math.abs(drawHoverPoint.price - drawStartPoint.price) / risk : null,
              entryPrice: drawStartPoint.price,
              stopPrice: drawRrStopPoint.price,
              targetPrice: drawHoverPoint.price,
            },
          ];
        }
      }
    }

    if (activeTool === "channel" && drawStartPoint && drawHoverPoint) {
      const a = toCoordinate(drawStartPoint);
      if (!drawRrStopPoint) {
        const b = toCoordinate(drawHoverPoint);
        if (a && b) {
          return [...resolvedDrawings, { id: "channel-preview-line", type: "trend", isPreview: true, start: a, end: b }];
        }
      } else {
        const sp = drawStartPoint;
        const ep = drawRrStopPoint;
        const t = ep.logical !== sp.logical ? (drawHoverPoint.logical - sp.logical) / (ep.logical - sp.logical) : 0;
        const offset = drawHoverPoint.price - (sp.price + (ep.price - sp.price) * t);
        const b = toCoordinate(ep);
        const a2 = toCoordinate({ logical: sp.logical, price: sp.price + offset });
        const b2 = toCoordinate({ logical: ep.logical, price: ep.price + offset });
        if (a && b && a2 && b2) {
          return [
            ...resolvedDrawings,
            { id: "channel-preview", type: "channel", isPreview: true, start: a, end: b, start2: a2, end2: b2 },
          ];
        }
      }
    }

    if (activeTool === "pitchfork" && drawStartPoint && drawHoverPoint) {
      const start = toCoordinate(drawStartPoint);
      if (!drawRrStopPoint) {
        const end = toCoordinate(drawHoverPoint);
        if (start && end) {
          return [...resolvedDrawings, { id: "pitchfork-preview-line", type: "trend", isPreview: true, start, end }];
        }
      } else {
        const end = toCoordinate(drawRrStopPoint);
        const fork = toCoordinate(drawHoverPoint);
        if (start && end && fork) {
          return [...resolvedDrawings, { id: "pitchfork-preview", type: "pitchfork", isPreview: true, start, end, fork }];
        }
      }
    }

    if (activeTool === "brush" && brushPreviewPoints.length >= 2) {
      const points = brushPreviewPoints.map(toCoordinate).filter(Boolean);
      if (points.length >= 2) {
        return [
          ...resolvedDrawings,
          { id: "brush-preview", type: "brush", isPreview: true, points },
        ];
      }
    }

    const previewableTools = [
      "trend",
      "rect",
      "ray",
      "ellipse",
      "arrow",
      "fib",
      "fibext",
      "range",
    ];
    const shouldPreviewPair =
      previewableTools.includes(activeTool) && drawStartPoint && drawHoverPoint;

    if (!shouldPreviewPair) {
      return resolvedDrawings;
    }

    const start = toCoordinate(drawStartPoint);
    const end = toCoordinate(drawHoverPoint);

    if (!start || !end) {
      return resolvedDrawings;
    }

    const needsRawPrice =
      activeTool === "fib" || activeTool === "fibext" || activeTool === "range";

    return [
      ...resolvedDrawings,
      {
        id: "drawing-preview",
        type: activeTool,
        isPreview: true,
        start,
        end,
        ...(needsRawPrice
          ? { startPrice: drawStartPoint.price, endPrice: drawHoverPoint.price }
          : {}),
      },
    ];
  })();

  // Space theme on desktop: flat, square-ish chrome that sits flush at the chart's edges
  // (top bar, left tool rail, right tab strip), matching the "Space quant" mockup.
  const isSpaceChrome = isInstrumentTheme && !isMobile;
  const SPACE_RAIL_W = 50;
  // Space look (near-black, hairline borders, pill buttons, Geist type)
  const spaceFont = '"Geist", "Archivo", system-ui, sans-serif';

  const actionButtonStyle = {
    appearance: "none",
    border: `1px solid ${palette.borderStrong}`,
    background: palette.buttonBackground,
    color: palette.shellText,
    borderRadius: "6px",
    padding: "5px 8px",
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: isSpaceChrome ? "none" : "uppercase",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
    cursor: "pointer",
    minHeight: "26px",
    ...(isSpaceChrome
      ? {
        border: "1px solid transparent",
        background: "transparent",
        borderRadius: "999px",
        padding: "5px 12px",
        fontFamily: spaceFont,
        fontSize: "0.78rem",
        fontWeight: 500,
        letterSpacing: "0.02em",
        boxShadow: "none",
        minHeight: "34px",
      }
      : null),
  };

  const mobilePanelSheet = isMobile
    ? {
      position: "absolute",
      top: "auto",
      left: 0,
      right: 0,
      bottom: 0,
      width: "auto",
      maxWidth: "none",
      maxHeight: "72vh",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      zIndex: 8,
      padding: "14px 14px calc(14px + env(safe-area-inset-bottom))",
      borderRadius: "12px 12px 0 0",
      boxShadow: "0 -12px 32px rgba(0, 0, 0, 0.4)",
    }
    : null;

  const overlayPanelStyle = {
    position: "absolute",
    left: "16px",
    top: "68px",
    zIndex: 4,
    width: "fit-content",
    maxWidth: "min(360px, calc(100% - 32px))",
    padding: "8px",
    borderRadius: "8px",
    border: `1px solid ${palette.borderSoft}`,
    background: palette.overlayBackground,
    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.36)",
    backdropFilter: "blur(18px)",
    ...(isSpaceChrome ? { borderRadius: "6px", boxShadow: "none", top: "64px", left: `${SPACE_RAIL_W + 12}px` } : null),
    ...mobilePanelSheet,
  };

  const chartGridMenuPopoverStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 4,
    zIndex: 85,
    minWidth: 180,
    maxWidth: "min(260px, calc(100vw - 32px))",
    listStyle: "none",
    margin: 0,
    padding: "4px 0",
    borderRadius: "6px",
    border: `1px solid ${palette.borderStrong}`,
    background: palette.overlayBackground,
    boxShadow: isSpaceChrome ? "none" : "0 10px 26px rgba(0, 0, 0, 0.34)",
    backdropFilter: "blur(14px)",
    boxSizing: "border-box",
  };

  const chartGridMenuRowButtonStyle = (isActive) => ({
    width: "100%",
    textAlign: "left",
    border: "none",
    background: isActive ? palette.buttonActiveBackground : "transparent",
    color: palette.shellText,
    padding: "6px 10px",
    fontSize: "0.7rem",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  });

  const inputStyle = {
    appearance: "none",
    border: `1px solid ${palette.borderStrong}`,
    background: palette.inputBackground,
    color: palette.shellText,
    borderRadius: "6px",
    padding: "5px 8px",
    fontSize: "0.7rem",
    fontWeight: 600,
    minHeight: "26px",
    ...(isSpaceChrome ? { borderRadius: "999px", minHeight: "34px", fontSize: "0.78rem", fontWeight: 500, borderColor: palette.borderSoft } : null),
  };

  const goToDateBoxStyle = {
    display: "inline-flex",
    alignItems: "stretch",
    minHeight: "36px",
    borderRadius: "12px",
    overflow: "hidden",
    border: `1px solid ${palette.borderStrong}`,
    background: palette.inputBackground,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
    ...(isSpaceChrome ? { borderRadius: "999px", minHeight: "34px", boxShadow: "none", borderColor: palette.borderSoft } : null),
  };

  const goToDateDisplayStyle = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: "190px",
    padding: "0 12px",
    color: palette.shellText,
    cursor: "pointer",
  };

  const goToDateDisplayValue = formatDateLabel(goToDate);
  const replayDateLabel =
    isReplayMode && replayIndex !== null ? formatTimestamp(data[replayIndex]?.time, chartTimeZone) : null;
  const chartCursor = drawTool !== "none"
    ? "crosshair"
    : isMeasureMode
      ? "crosshair"
      : isReplaySelecting
        ? "cell"
        : isReplayMode
          ? isReplayPlaying
            ? "progress"
            : "alias"
          : "grab";

  const toolbarStyle = {
    position: "absolute",
    zIndex: 4,
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexWrap: "wrap",
    padding: "4px",
    borderRadius: "8px",
    border: `1px solid ${palette.borderSoft}`,
    background: palette.toolbarBackground,
    backdropFilter: "blur(18px)",
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.28)",
    ...(isSpaceChrome ? { borderRadius: 0, boxShadow: "none", border: "none" } : null),
  };

  const iconButtonStyle = (isActive = false, accent = "cyan") => {
    const activePalette =
      accent === "red"
        ? {
          border: `1px solid ${palette.dangerBorder}`,
          background: palette.dangerBackground,
          color: palette.dangerText,
        }
        : {
          border: `1px solid ${palette.activeBorder}`,
          background: palette.buttonActiveBackground,
          color: palette.shellText,
        };

    return {
      ...actionButtonStyle,
      minWidth: isSpaceChrome ? "36px" : "26px",
      height: isSpaceChrome ? "36px" : "26px",
      padding: isSpaceChrome ? "0 8px" : "0 7px",
      borderRadius: isSpaceChrome ? "999px" : "6px",
      fontSize: isSpaceChrome ? "0.78rem" : "0.7rem",
      letterSpacing: "0.02em",
      color: isActive ? activePalette.color : isSpaceChrome ? palette.shellText : palette.mutedText,
      border: isActive ? activePalette.border : actionButtonStyle.border,
      background: isActive ? activePalette.background : actionButtonStyle.background,
    };
  };

  const compactLabelStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "0 7px",
    minHeight: "26px",
    borderRadius: "6px",
    border: `1px solid ${palette.borderStrong}`,
    background: palette.chipBackground,
    color: palette.mutedText,
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.03em",
    textTransform: isSpaceChrome ? "none" : "uppercase",
    whiteSpace: "nowrap",
    ...(isSpaceChrome
      ? {
        borderRadius: "999px",
        minHeight: "34px",
        padding: "0 12px",
        borderColor: palette.borderSoft,
        background: palette.inputBackground,
        color: palette.shellText,
        fontFamily: spaceFont,
        fontSize: "0.78rem",
        fontWeight: 500,
      }
      : null),
  };

  const activeIndicatorChipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minHeight: "28px",
    padding: "0 9px",
    borderRadius: "999px",
    border: `1px solid ${palette.borderSoft}`,
    background: palette.chipBackground,
    color: palette.mutedText,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: isSpaceChrome ? "none" : "uppercase",
    backdropFilter: "blur(14px)",
    opacity: 0.92,
    ...(isSpaceChrome ? { borderRadius: "999px", fontFamily: spaceFont } : null),
  };

  const drawHistory = drawHistoryRef.current;
  /** Magnet, stay-in-drawing-mode, hide-all, undo, redo (desktop rail and phone drawing bar). */
  const drawingOptionButtons = [
    {
      key: "magnet",
      Icon: MagnetIcon,
      label: `Magnet: ${drawPrefs.magnet}`,
      title:
        drawPrefs.magnet === "off"
          ? "Magnet off: click for weak magnet (snaps to open/high/low/close when close)"
          : drawPrefs.magnet === "weak"
            ? "Weak magnet: click for strong magnet (always snaps to open/high/low/close)"
            : "Strong magnet: click to switch the magnet off",
      pressed: drawPrefs.magnet !== "off",
      badge: drawPrefs.magnet === "weak" ? "W" : drawPrefs.magnet === "strong" ? "S" : "",
      onClick: () => toggleDrawPref("magnet"),
    },
    {
      key: "stay",
      Icon: StayDrawIcon,
      label: "Stay in drawing mode",
      title: drawPrefs.stay
        ? "Stay in drawing mode is on: the tool stays selected after each drawing"
        : "Stay in drawing mode is off: the tool switches off after each drawing",
      pressed: drawPrefs.stay,
      onClick: () => toggleDrawPref("stay"),
    },
    {
      key: "hide",
      Icon: EyeIcon,
      off: drawPrefs.hidden,
      label: drawPrefs.hidden ? "Show drawings" : "Hide drawings",
      title: drawPrefs.hidden ? "Show all drawings" : "Hide all drawings",
      pressed: drawPrefs.hidden,
      onClick: () => {
        setSelectedDrawingId(null);
        toggleDrawPref("hidden");
      },
    },
    {
      key: "undo",
      Icon: UndoIcon,
      label: "Undo",
      title: "Undo (Ctrl+Z)",
      disabled: !drawHistory.past.length,
      onClick: undoDrawings,
    },
    {
      key: "redo",
      Icon: RedoIcon,
      label: "Redo",
      title: "Redo (Ctrl+Y)",
      disabled: !drawHistory.future.length,
      onClick: redoDrawings,
    },
  ];

  const toolDefinitions = [
    { key: "trend", label: "Trend line", symbol: "/", Icon: DrawTrendIcon },
    { key: "ray", label: "Ray", symbol: "/.", Icon: DrawRayIcon },
    { key: "hline", label: "Horizontal line", symbol: "-", Icon: DrawHLineIcon },
    { key: "vline", label: "Vertical line", symbol: "|", Icon: DrawVLineIcon },
    { key: "channel", label: "Parallel channel", symbol: "//", Icon: DrawChannelIcon },
    { key: "pitchfork", label: "Andrews Pitchfork", symbol: "Y", Icon: DrawPitchforkIcon },
    { key: "fib", label: "Fibonacci retracement", symbol: "fib", Icon: DrawFibIcon },
    { key: "fibext", label: "Fibonacci extension", symbol: "fib+", Icon: DrawFibExtIcon },
    { key: "rect", label: "Rectangle", symbol: "[]", Icon: DrawRectIcon },
    { key: "ellipse", label: "Circle", symbol: "()", Icon: DrawEllipseIcon },
    { key: "arrow", label: "Arrow", symbol: "->", Icon: DrawArrowIcon },
    { key: "text", label: "Text", symbol: "T", Icon: DrawTextIcon },
    { key: "brush", label: "Brush", symbol: "~", Icon: DrawBrushIcon },
    { key: "range", label: "Price range", symbol: "%", Icon: DrawPriceRangeIcon },
    { key: "rr", label: "Risk / reward", symbol: "1:2", Icon: DrawRrIcon },
  ];

  const indicatorDefinitions = [
    {
      key: "ma",
      group: "trend",
      label: "MA",
      fullLabel: "Moving Average",
      summary: `Close, ${maPeriod}`,
      checked: showMovingAverage,
      onToggle: setShowMovingAverage,
      settings: [
        {
          key: "period",
          label: "Period",
          value: maPeriodInput,
          min: 1,
          onChange: setMaPeriodInput,
          onApply: applyMovingAveragePeriod,
        },
      ],
    },
    {
      key: "sma",
      group: "trend",
      label: "SMA",
      fullLabel: "Simple MA",
      summary: `Close, ${smaPeriod}`,
      checked: showSma,
      onToggle: setShowSma,
      settings: [
        {
          key: "period",
          label: "Period",
          value: smaPeriodInput,
          min: 1,
          onChange: setSmaPeriodInput,
          onApply: applySmaPeriod,
        },
      ],
    },
    {
      key: "ema",
      group: "trend",
      label: "EMA",
      fullLabel: "Exponential MA",
      summary: `Close, ${emaPeriod}`,
      checked: showEma,
      onToggle: setShowEma,
      settings: [
        {
          key: "period",
          label: "Period",
          value: emaPeriodInput,
          min: 1,
          onChange: setEmaPeriodInput,
          onApply: applyEmaPeriod,
        },
      ],
    },
    {
      key: "wma",
      group: "trend",
      label: "WMA",
      fullLabel: "Weighted MA",
      summary: `Close, ${wmaPeriod}`,
      checked: showWma,
      onToggle: setShowWma,
      settings: [
        {
          key: "period",
          label: "Period",
          value: wmaPeriodInput,
          min: 1,
          onChange: setWmaPeriodInput,
          onApply: applyWmaPeriod,
        },
      ],
    },
    {
      key: "bb",
      group: "volatility",
      label: "BB",
      fullLabel: "Bollinger Bands",
      summary: `${bbPeriod}, ${bbDeviation}`,
      checked: showBollinger,
      onToggle: setShowBollinger,
      settings: [
        {
          key: "period",
          label: "Period",
          value: bbPeriodInput,
          min: 2,
          onChange: setBbPeriodInput,
          onApply: applyBollingerSettings,
        },
        {
          key: "deviation",
          label: "Deviation",
          value: bbDeviationInput,
          min: 0.1,
          step: 0.1,
          onChange: setBbDeviationInput,
          onApply: applyBollingerSettings,
        },
      ],
    },
    {
      key: "rsi",
      group: "momentum",
      label: "RSI",
      fullLabel: "RSI",
      summary: `${rsiPeriod}, ${rsiUpper}/${rsiLower}`,
      checked: showRsi,
      onToggle: setShowRsi,
      settings: [
        {
          key: "period",
          label: "Period",
          value: rsiPeriodInput,
          min: 2,
          onChange: setRsiPeriodInput,
          onApply: applyRsiSettings,
        },
        {
          key: "upper",
          label: "Upper",
          value: rsiUpperInput,
          min: 0,
          max: 100,
          step: 1,
          onChange: setRsiUpperInput,
          onApply: applyRsiSettings,
        },
        {
          key: "lower",
          label: "Lower",
          value: rsiLowerInput,
          min: 0,
          max: 100,
          step: 1,
          onChange: setRsiLowerInput,
          onApply: applyRsiSettings,
        },
      ],
    },
    {
      key: "atr",
      group: "volatility",
      label: "ATR",
      fullLabel: "Average True Range",
      summary: `${atrPeriod}`,
      checked: showAtr,
      onToggle: setShowAtr,
      settings: [
        {
          key: "period",
          label: "Period",
          value: atrPeriodInput,
          min: 1,
          onChange: setAtrPeriodInput,
          onApply: applyAtrPeriod,
        },
      ],
    },
    {
      key: "donchian",
      group: "trend",
      label: "DC",
      fullLabel: "Donchian Channel",
      summary: `${donchianPeriod}`,
      checked: showDonchian,
      onToggle: setShowDonchian,
      settings: [
        {
          key: "period",
          label: "Period",
          value: donchianPeriodInput,
          min: 1,
          onChange: setDonchianPeriodInput,
          onApply: applyDonchianPeriod,
        },
      ],
    },
    {
      key: "stochastic",
      group: "momentum",
      label: "STOCH",
      fullLabel: "Stochastic Oscillator",
      summary: `${stochKPeriod}/${stochDPeriod}, ${stochUpper}/${stochLower}`,
      checked: showStochastic,
      onToggle: setShowStochastic,
      settings: [
        {
          key: "kPeriod",
          label: "%K",
          value: stochKPeriodInput,
          min: 1,
          onChange: setStochKPeriodInput,
          onApply: applyStochasticPeriods,
        },
        {
          key: "dPeriod",
          label: "%D",
          value: stochDPeriodInput,
          min: 1,
          onChange: setStochDPeriodInput,
          onApply: applyStochasticPeriods,
        },
        {
          key: "upper",
          label: "Upper",
          value: stochUpperInput,
          min: 0,
          max: 100,
          step: 1,
          onChange: setStochUpperInput,
          onApply: applyStochasticPeriods,
        },
        {
          key: "lower",
          label: "Lower",
          value: stochLowerInput,
          min: 0,
          max: 100,
          step: 1,
          onChange: setStochLowerInput,
          onApply: applyStochasticPeriods,
        },
      ],
    },
    {
      key: "macd",
      group: "momentum",
      label: "MACD",
      fullLabel: "MACD",
      summary: `${macdFastPeriod}/${macdSlowPeriod}/${macdSignalPeriod}`,
      checked: showMacd,
      onToggle: setShowMacd,
      settings: [
        {
          key: "fast",
          label: "Fast",
          value: macdFastPeriodInput,
          min: 1,
          step: 1,
          onChange: setMacdFastPeriodInput,
          onApply: applyMacdPeriods,
        },
        {
          key: "slow",
          label: "Slow",
          value: macdSlowPeriodInput,
          min: 2,
          step: 1,
          onChange: setMacdSlowPeriodInput,
          onApply: applyMacdPeriods,
        },
        {
          key: "signal",
          label: "Signal",
          value: macdSignalPeriodInput,
          min: 1,
          step: 1,
          onChange: setMacdSignalPeriodInput,
          onApply: applyMacdPeriods,
        },
      ],
    },
    {
      key: "vwap",
      group: "volume",
      label: "VWAP",
      fullLabel: "VWAP",
      summary: hasRealVolume ? "Session cum." : "Synthetic vol",
      checked: showVwap,
      onToggle: setShowVwap,
      settings: [],
    },
    {
      key: "hma",
      group: "trend",
      label: "HMA",
      fullLabel: "Hull MA",
      summary: `${hmaPeriod}`,
      checked: showHma,
      onToggle: setShowHma,
      settings: [
        {
          key: "period",
          label: "Period",
          value: hmaPeriodInput,
          min: 2,
          onChange: setHmaPeriodInput,
          onApply: applyHmaPeriod,
        },
      ],
    },
    {
      key: "dema",
      group: "trend",
      label: "DEMA",
      fullLabel: "Double EMA",
      summary: `${demaPeriod}`,
      checked: showDema,
      onToggle: setShowDema,
      settings: [
        {
          key: "period",
          label: "Period",
          value: demaPeriodInput,
          min: 1,
          onChange: setDemaPeriodInput,
          onApply: applyDemaPeriod,
        },
      ],
    },
    {
      key: "tema",
      group: "trend",
      label: "TEMA",
      fullLabel: "Triple EMA",
      summary: `${temaPeriod}`,
      checked: showTema,
      onToggle: setShowTema,
      settings: [
        {
          key: "period",
          label: "Period",
          value: temaPeriodInput,
          min: 1,
          onChange: setTemaPeriodInput,
          onApply: applyTemaPeriod,
        },
      ],
    },
    {
      key: "ichimoku",
      group: "trend",
      label: "ICH",
      fullLabel: "Ichimoku (9/26/52)",
      summary: "Lines + cloud",
      checked: showIchimoku,
      onToggle: setShowIchimoku,
      settings: [],
    },
    {
      key: "sar",
      group: "trend",
      label: "SAR",
      fullLabel: "Parabolic SAR",
      summary: "PSAR",
      checked: showSar,
      onToggle: setShowSar,
      settings: [],
    },
    {
      key: "keltner",
      group: "volatility",
      label: "KELT",
      fullLabel: "Keltner Channels",
      summary: `${keltEma}/${keltAtr}×${keltMult}`,
      checked: showKeltner,
      onToggle: setShowKeltner,
      settings: [
        {
          key: "ema",
          label: "EMA",
          value: keltEmaInput,
          min: 1,
          onChange: setKeltEmaInput,
          onApply: applyKeltnerSettings,
        },
        {
          key: "atr",
          label: "ATR",
          value: keltAtrInput,
          min: 1,
          onChange: setKeltAtrInput,
          onApply: applyKeltnerSettings,
        },
        {
          key: "mult",
          label: "Mult",
          value: keltMultInput,
          min: 0.1,
          step: 0.1,
          onChange: setKeltMultInput,
          onApply: applyKeltnerSettings,
        },
      ],
    },
    {
      key: "cci",
      group: "momentum",
      label: "CCI",
      fullLabel: "Commodity Channel Index",
      summary: `${cciPeriod}`,
      checked: showCci,
      onToggle: setShowCci,
      settings: [
        {
          key: "period",
          label: "Period",
          value: cciPeriodInput,
          min: 2,
          onChange: setCciPeriodInput,
          onApply: applyCciPeriod,
        },
      ],
    },
    {
      key: "williams",
      group: "momentum",
      label: "W%R",
      fullLabel: "Williams %R",
      summary: `${williamsPeriod}`,
      checked: showWilliams,
      onToggle: setShowWilliams,
      settings: [
        {
          key: "period",
          label: "Period",
          value: williamsPeriodInput,
          min: 2,
          onChange: setWilliamsPeriodInput,
          onApply: applyWilliamsPeriod,
        },
      ],
    },
    {
      key: "roc",
      group: "momentum",
      label: "ROC",
      fullLabel: "Rate of Change",
      summary: `${rocPeriod}`,
      checked: showRoc,
      onToggle: setShowRoc,
      settings: [
        {
          key: "period",
          label: "Period",
          value: rocPeriodInput,
          min: 1,
          onChange: setRocPeriodInput,
          onApply: applyRocPeriod,
        },
      ],
    },
    {
      key: "mom",
      group: "momentum",
      label: "MOM",
      fullLabel: "Momentum",
      summary: `${momPeriod}`,
      checked: showMom,
      onToggle: setShowMom,
      settings: [
        {
          key: "period",
          label: "Period",
          value: momPeriodInput,
          min: 1,
          onChange: setMomPeriodInput,
          onApply: applyMomPeriod,
        },
      ],
    },
    {
      key: "stochRsi",
      group: "momentum",
      label: "SRSI",
      fullLabel: "Stochastic RSI",
      summary: `${stochRsiRsi}/${stochRsiStoch}`,
      checked: showStochRsi,
      onToggle: setShowStochRsi,
      settings: [
        {
          key: "rsi",
          label: "RSI len",
          value: stochRsiRsiInput,
          min: 2,
          onChange: setStochRsiRsiInput,
          onApply: applyStochRsiSettings,
        },
        {
          key: "st",
          label: "Stoch len",
          value: stochRsiStochInput,
          min: 2,
          onChange: setStochRsiStochInput,
          onApply: applyStochRsiSettings,
        },
        {
          key: "k",
          label: "K smooth",
          value: stochRsiKInput,
          min: 1,
          onChange: setStochRsiKInput,
          onApply: applyStochRsiSettings,
        },
        {
          key: "d",
          label: "D smooth",
          value: stochRsiDInput,
          min: 1,
          onChange: setStochRsiDInput,
          onApply: applyStochRsiSettings,
        },
      ],
    },
    {
      key: "adx",
      group: "volatility",
      label: "ADX",
      fullLabel: "Average Directional Index",
      summary: `${adxPeriod}`,
      checked: showAdx,
      onToggle: setShowAdx,
      settings: [
        {
          key: "period",
          label: "Period",
          value: adxPeriodInput,
          min: 2,
          onChange: setAdxPeriodInput,
          onApply: applyAdxPeriod,
        },
      ],
    },
    {
      key: "ao",
      group: "momentum",
      label: "AO",
      fullLabel: "Awesome Oscillator",
      summary: "5/34 median",
      checked: showAo,
      onToggle: setShowAo,
      settings: [],
    },
    {
      key: "tsi",
      group: "momentum",
      label: "TSI",
      fullLabel: "True Strength Index",
      summary: "25/13",
      checked: showTsi,
      onToggle: setShowTsi,
      settings: [],
    },
    {
      key: "obv",
      group: "volume",
      label: "OBV",
      fullLabel: "On-Balance Volume",
      summary: hasRealVolume ? "Cumulative" : "Synth vol",
      checked: showObv,
      onToggle: setShowObv,
      settings: [],
    },
    {
      key: "cmf",
      group: "volume",
      label: "CMF",
      fullLabel: "Chaikin Money Flow",
      summary: `${cmfPeriod}`,
      checked: showCmf,
      onToggle: setShowCmf,
      settings: [
        {
          key: "period",
          label: "Period",
          value: cmfPeriodInput,
          min: 2,
          onChange: setCmfPeriodInput,
          onApply: applyCmfPeriod,
        },
      ],
    },
    {
      key: "mfi",
      group: "volume",
      label: "MFI",
      fullLabel: "Money Flow Index",
      summary: `${mfiPeriod}`,
      checked: showMfi,
      onToggle: setShowMfi,
      settings: [
        {
          key: "period",
          label: "Period",
          value: mfiPeriodInput,
          min: 2,
          onChange: setMfiPeriodInput,
          onApply: applyMfiPeriod,
        },
      ],
    },
    {
      key: "adLine",
      group: "volume",
      label: "A/D",
      fullLabel: "Accumulation/Distribution",
      summary: hasRealVolume ? "Flow" : "Synth vol",
      checked: showAdLine,
      onToggle: setShowAdLine,
      settings: [],
    },
    {
      key: "chaikinVol",
      group: "volatility",
      label: "CHV",
      fullLabel: "Chaikin Volatility",
      summary: "10/10 EMA(H-L)",
      checked: showChaikinVol,
      onToggle: setShowChaikinVol,
      settings: [],
    },
    {
      key: "volOsc",
      group: "volume",
      label: "VOSC",
      fullLabel: "Volume Oscillator",
      summary: "5/10 EMA(vol)",
      checked: showVolOsc,
      onToggle: setShowVolOsc,
      settings: [],
    },
    {
      key: "stdDev",
      group: "volatility",
      label: "SD",
      fullLabel: "Standard Deviation",
      summary: `${stdDevPeriod} closes`,
      checked: showStdDev,
      onToggle: setShowStdDev,
      settings: [
        {
          key: "period",
          label: "Period",
          value: stdDevPeriodInput,
          min: 2,
          onChange: setStdDevPeriodInput,
          onApply: applyStdDevPeriod,
        },
      ],
    },
    {
      key: "kaufmanRange",
      group: "momentum",
      label: "REG",
      fullLabel: "Regime Detection",
      summary: kaufmanRangeRegimeMethod === "hmm" ? hmmModelMode === "causal200" ? "HMM · causal 200" : `HMM · ${hmmTrainingSplit}/${100 - Number(hmmTrainingSplit)}`
        : kaufmanRangeRegimeMethod === "gmm" ? "GMM · causal 200"
          : kaufmanRangeRegimeMethod === "bocpd" ? `BOCPD · ${bocpdExpectedRunLength}-bar hazard`
            : kaufmanRangeRegimeMethod === "composite" ? "D · Hurst / VR / ER / ADX"
              : `${kaufmanRangePeriod}/${kaufmanRangeSmooth}`,
      checked: showKaufmanRange,
      onToggle: setShowKaufmanRange,
      settings: [
        {
          key: "regimeMethod",
          label: "Regime model",
          type: "select",
          value: kaufmanRangeRegimeMethod,
          options: [
            { value: "kaufman", label: "Kaufman efficiency thresholds" },
            { value: "hmm", label: "HMM · configurable models" },
            { value: "gmm", label: "GMM · causal static clusters" },
            { value: "bocpd", label: "BOCPD · causal change alarm" },
            { value: "composite", label: "D composite · Hurst / VR / ER / ADX" },
          ],
          onChange: (value) => {
            setKaufmanRangeRegimeMethod(value);
            if (["hmm", "gmm", "bocpd", "composite"].includes(value)) {
              setKaufmanRangeDisplayMode("regime");
              setShowKaufmanRange(true);
            }
          },
        },
        {
          key: "displayMode",
          label: "Display",
          type: "select",
          value: kaufmanRangeDisplayMode,
          options: [
            { value: "efficiency", label: "Indicator line" },
            { value: "regime", label: "Trend / range areas" },
          ],
          onChange: setKaufmanRangeDisplayMode,
        },
        {
          key: "hmmLookbackPreset",
          label: "Common HMM lookback",
          type: "select",
          value: HMM_LOOKBACK_OPTIONS.some((option) => Number(option.value) === kaufmanRangePeriod)
            ? String(kaufmanRangePeriod)
            : "custom",
          options: [
            { value: "custom", label: `Custom (${kaufmanRangePeriod} bars)` },
            ...HMM_LOOKBACK_OPTIONS,
          ],
          onChange: (value) => {
            if (value === "custom") return;
            const next = Number(value);
            setKaufmanRangePeriod(next);
            setKaufmanRangePeriodInput(String(next));
          },
        },
        {
          key: "hmmTrainingSplit",
          label: "HMM fit / holdout split",
          type: "select",
          value: hmmTrainingSplit,
          options: HMM_TRAINING_SPLITS,
          onChange: setHmmTrainingSplit,
        },
        {
          key: "hmmModelMode",
          label: "HMM model mode",
          type: "select",
          value: hmmModelMode,
          options: HMM_MODEL_OPTIONS,
          onChange: setHmmModelMode,
        },
        {
          key: "bocpdExpectedRunLength",
          label: "BOCPD expected run length",
          type: "select",
          value: String(bocpdExpectedRunLength),
          options: [50, 100, 200, 300].map((value) => ({ value: String(value), label: `${value} feature bars` })),
          onChange: (value) => setBocpdExpectedRunLength(Number(value)),
        },
        {
          key: "bocpdChangeThreshold",
          label: "BOCPD change alarm threshold",
          type: "select",
          value: String(bocpdChangeThreshold),
          options: [0.15, 0.25, 0.35, 0.5].map((value) => ({ value: String(value), label: `${Math.round(value * 100)}%` })),
          onChange: (value) => setBocpdChangeThreshold(Number(value)),
        },
        {
          key: "trendThreshold",
          label: "Trend starts at (0–1)",
          value: kaufmanRangeTrendThresholdInput,
          min: 0.05,
          max: 0.95,
          step: 0.05,
          onChange: setKaufmanRangeTrendThresholdInput,
          onApply: applyKaufmanRangeThresholds,
        },
        {
          key: "rangeThreshold",
          label: "Range starts at (0–1)",
          value: kaufmanRangeRangeThresholdInput,
          min: 0,
          max: 0.95,
          step: 0.05,
          onChange: setKaufmanRangeRangeThresholdInput,
          onApply: applyKaufmanRangeThresholds,
        },
        {
          key: "period",
          label: ["hmm", "gmm", "bocpd"].includes(kaufmanRangeRegimeMethod) ? "Feature lookback" : "Period",
          value: kaufmanRangePeriodInput,
          min: 2,
          step: 1,
          onChange: setKaufmanRangePeriodInput,
          onApply: applyKaufmanRangeSettings,
        },
        {
          key: "smooth",
          label: "Smooth",
          value: kaufmanRangeSmoothInput,
          min: 1,
          step: 1,
          onChange: setKaufmanRangeSmoothInput,
          onApply: applyKaufmanRangeSettings,
        },
        ...D_REGIME_SETTING_DEFS.map((definition) => ({
          key: `d_${definition.key}`,
          label: definition.label,
          value: dRegimeInputs[definition.key],
          min: definition.min,
          max: definition.max,
          step: definition.step,
          onChange: (value) => setDRegimeInputs((previous) => ({ ...previous, [definition.key]: value })),
          onApply: applyDRegimeSettings,
        })),
      ],
    },
    {
      key: "patterns",
      group: "patterns",
      label: "PAT",
      fullLabel: "Candlestick patterns",
      summary: showPatterns ? "Markers on" : "Off",
      checked: showPatterns,
      onToggle: setShowPatterns,
      settings: [],
    },
  ];

  extraIndicatorInstances.forEach((inst, index) => {
    const def = DUPLICABLE_INDICATOR_TYPES[inst.type];
    if (!def) return;
    const baseEntry = indicatorDefinitions.find((d) => d.key === inst.type);
    const priorSameType = extraIndicatorInstances
      .slice(0, index)
      .filter((other) => other.type === inst.type).length;
    const instanceNumber = priorSameType + 2; // the base slot is always "1"
    const colorKey = `dup:${inst.id}`;
    const summary = def.paramDefs.length
      ? def.paramDefs.map((p) => `${inst.params[p.key]}`).join("/")
      : "—";
    indicatorDefinitions.push({
      key: colorKey,
      group: baseEntry?.group ?? "trend",
      label: `${def.shortLabel} ${instanceNumber}`,
      fullLabel: `${def.fullLabel} #${instanceNumber}`,
      summary,
      checked: true,
      onToggle: (next) => {
        if (!next) removeExtraIndicatorInstance(inst.id);
      },
      settings: def.paramDefs.map((p) => ({
        key: p.key,
        label: p.label,
        value: inst.paramInputs[p.key],
        min: p.min,
        max: p.max,
        step: p.step ?? 1,
        onChange: (value) => updateExtraIndicatorInstanceParamInput(inst.id, p.key, value),
        onApply: () => applyExtraIndicatorInstanceParams(inst.id),
      })),
    });
  });

  GO_INDICATOR_KEYS.forEach((type) => {
    const def = GO_INDICATORS[type];
    const inst = goInstances.find((g) => g.type === type);
    const values = def.paramDefs.map((p) => (inst ? inst.params[p.key] : p.default));
    const where = def.group === "patterns" || def.overlay ? "on chart" : "own pane";
    indicatorDefinitions.push({
      key: `go:${type}`,
      group: def.group,
      label: def.shortLabel,
      fullLabel: def.fullLabel,
      summary: values.length ? `${values.join("/")} · ${where}` : where,
      checked: Boolean(inst),
      onToggle: (next) => (next ? addGoIndicator(type) : removeGoIndicator(type)),
      settings: inst
        ? def.paramDefs.map((p) => ({
            key: p.key,
            label: p.label,
            value: inst.paramInputs[p.key],
            min: p.min,
            max: p.max,
            step: p.step ?? 1,
            onChange: (value) => updateGoIndicatorParamInput(type, p.key, value),
            onApply: () => applyGoIndicatorParams(type),
          }))
        : [],
    });
  });

  const applyOfSettings = () => {
    setOfSettings((prev) => {
      const next = { ...prev };
      OF_SETTING_DEFS.forEach((d) => {
        const n = Number.parseInt(ofSettingInputs[d.key], 10);
        if (Number.isFinite(n)) next[d.key] = Math.min(d.max, Math.max(d.min, n));
      });
      setOfSettingInputs(Object.fromEntries(Object.entries(next).map(([k, v]) => [k, String(v)])));
      return next;
    });
  };
  const ofSettingsFor = (keys) =>
    OF_SETTING_DEFS.filter((d) => keys.includes(d.key)).map((d) => ({
      key: d.key,
      label: d.label,
      value: ofSettingInputs[d.key],
      min: d.min,
      max: d.max,
      step: 1,
      onChange: (value) => setOfSettingInputs((prev) => ({ ...prev, [d.key]: value })),
      onApply: applyOfSettings,
    }));
  const ofClosedAt = ofData?.marketClosed && ofData?.lastDataTime
    ? new Date(ofData.lastDataTime * 1000).toUTCString().replace(/:\d\d GMT$/, " UTC")
    : null;
  const ofProvenance = ofData?.isSimulated
    ? "simulated quote updates"
    : ofData
      ? `${ofData.dataSource || "provider"}${ofData.dataMode ? ` ${ofData.dataMode}` : ""} quote updates · tick-rule estimate`
      : "provider quote updates";
  const ofStatus = ofError
    ? `⚠ ${ofError}`
    : !ofData
      ? "loading quote updates…"
      : ofLoadingOlder
        ? `${ofData.bars.length} bars · loading older…`
      : ofClosedAt
        ? `market closed · last session to ${ofClosedAt}`
        : ofData.truncated
          ? `last 24 h · ${ofProvenance}`
          : ofData.dataStatus === "cached"
            ? `archived · ${ofProvenance}`
            : ofProvenance;
  [
    {
      tool: "trader",
      label: "OFT",
      fullLabel: "Orderflows Trader (footprint)",
      summary: `${ofSignals.size}/21 signals · ${ofData?.isSimulated ? "simulated quotes" : "estimated quote flow"}`,
      settings: ["bars", "rowPoints", "imbalancePct", "imbalanceMin", "stackCount", "sweepRows"],
    },
    { tool: "barStats", label: "STATS", fullLabel: "Bar Statistics", summary: "volume, delta, max/min, CVD", settings: ["bars"] },
    { tool: "deltaBars", label: "Δ", fullLabel: "Delta and Cumulative Delta Bars", summary: "own pane", settings: ["bars"] },
    { tool: "volumeProfile", label: "VP", fullLabel: "Volume Profile", summary: "loaded bars, POC + value area", settings: ["bars", "rowPoints"] },
    { tool: "marketProfile", label: "TPO", fullLabel: "Market Profile (TPO)", summary: `${ofSettings.tpoMinutes}-min letters per session from ${String(ofSettings.sessionHour).padStart(2, "0")}:00 UTC`, settings: ["tpoMinutes", "sessionHour"], fromCandles: true },
    { tool: "buySellVwap", label: "B/S VWAP", fullLabel: "Buy VWAP and Sell VWAP", summary: "session (UTC day)", settings: ["bars"] },
  ].forEach((t) => {
    indicatorDefinitions.push({
      key: `of:${t.tool}`,
      group: "orderflow",
      label: t.label,
      fullLabel: t.fullLabel,
      summary: t.fromCandles ? `${t.summary} · from candles` : `${t.summary} · ${ofStatus}`,
      checked: Boolean(ofTools[t.tool]),
      onToggle: (next) => setOfTools((prev) => ({ ...prev, [t.tool]: Boolean(next) })),
      settings: ofTools[t.tool] ? ofSettingsFor(t.settings) : [],
    });
  });

  const indicatorSearchNormalized = indicatorSearchTerm.trim().toLowerCase();
  const groupedIndicators = groupIndicatorDefinitions(indicatorDefinitions, {
    searchTerm: indicatorSearchTerm,
    hiddenKeys: isAdmin ? [] : ["kaufmanRange"],
  });
  const tradingViewIndicatorCatalog = [
    {
      category: "Trend",
      items: [
        "VWMA",
        "HMA",
        "DEMA",
        "TEMA",
        "Ichimoku Cloud",
        "Parabolic SAR",
        "Linear Regression",
      ],
    },
    {
      category: "Momentum",
      items: ["Stoch RSI", "CCI", "ROC", "Williams %R", "Awesome Oscillator", "TRIX", "PPO"],
    },
    {
      category: "Volume",
      items: ["VWAP", "OBV", "MFI", "CMF", "A/D", "Ease of Movement"],
    },
  ];

  const activeIndicators = indicatorDefinitions.filter(
    (indicator) => indicator.checked && (isAdmin || indicator.key !== "kaufmanRange")
  );
  const activeIndicator = indicatorDefinitions.find(
    (indicator) => indicator.key === activeIndicatorKey && (isAdmin || indicator.key !== "kaufmanRange")
  );
  const activeIndicatorSupportsLineSize = activeIndicator
    ? INDICATOR_KEYS_WITH_LINE_SIZE.has(activeIndicator.key)
    : false;
  const handleIndicatorSelect = (indicator) => {
    if (!indicator.checked) {
      indicator.onToggle(true);
      setActivePanel("indicators");
      setActiveIndicatorKey(indicator.key);
      return;
    }

    // Clicking an already-active duplicable indicator adds another instance (2nd, 3rd, ...)
    // instead of just re-selecting it — addExtraIndicatorInstance itself focuses the new one.
    if (DUPLICABLE_INDICATOR_TYPES[indicator.key] && addExtraIndicatorInstance(indicator.key)) {
      return;
    }

    setActivePanel("indicators");
    setActiveIndicatorKey(indicator.key);
  };

  return (
    <div
      style={{
        display: "grid",
        gap: "12px",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div
        ref={chartShellRef}
        style={{
          width: "100%",
          boxSizing: "border-box",
          height: isMobile
            ? "100%"
            : isChartFullscreen
              ? "100vh"
              : `calc(100dvh - ${48 + shellTopOffset}px)`,
          minHeight: 0,
          paddingTop: isMobile ? `${MOBILE_STAGE_TOP}px` : 0,
          paddingBottom: isMobile ? `${MOBILE_STAGE_BOTTOM}px` : 0,
          position: "relative",
          cursor: chartCursor,
          border: isMobile ? "none" : `1px solid ${isSpaceChrome ? palette.borderSoft : palette.borderStrong}`,
          borderRadius: isMobile || isChartFullscreen ? 0 : isSpaceChrome ? "6px" : "22px",
          overflow: "hidden",
          background: palette.chartPanelBackground,
          boxShadow: isMobile || isSpaceChrome
            ? "none"
            : "0 28px 90px rgba(2, 6, 23, 0.58), inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
      >
        {uploadStatus?.message && (statusVisible || !isMobile || uploadStatus.tone === "error") ? (
          <div
            style={{
              position: "absolute",
              left: isSpaceChrome ? `${SPACE_RAIL_W + 12}px` : "14px",
              // phones: just under the header card, so it never covers the time axis or bottom bar
              top: isMobile ? `${MOBILE_STAGE_TOP + 6}px` : "auto",
              right: isMobile ? "14px" : "auto",
              bottom: isMobile ? "auto" : isSpaceChrome ? "84px" : "14px",
              maxWidth: "min(420px, calc(100% - 28px))",
              padding: "8px 12px",
              borderRadius: "10px",
              fontSize: "12px",
              lineHeight: 1.4,
              color: palette.shellText,
              background: palette.chartPanelBackground,
              borderLeft: `4px solid ${
                uploadStatus.tone === "error"
                  ? "#dc2626"
                  : uploadStatus.tone === "success"
                    ? "#16a34a"
                    : "#2563eb"
              }`,
              boxShadow: "0 10px 30px rgba(2, 6, 23, 0.35)",
              pointerEvents: "none",
              zIndex: 5,
            }}
          >
            {uploadStatus.message}
          </div>
        ) : null}
        {!isMobile && (
        <>
        <div
          ref={setTopBarEl}
          style={{
            ...toolbarStyle,
            top: "14px",
            left: "14px",
            right: "14px",
            minHeight: "54px",
            ...(isSpaceChrome
              ? { top: 0, left: 0, right: 0, minHeight: "52px", padding: "8px 12px", gap: "6px", borderBottom: `1px solid ${palette.borderSoft}` }
              : null),
          }}
        >
          <label
            style={{
              ...compactLabelStyle,
              position: "relative",
              cursor: "pointer",
              minWidth: "104px",
              justifyContent: "center",
              flexDirection: "column",
              gap: "2px",
              border: `1px solid ${palette.activeBorder}`,
              background: palette.buttonActiveBackground,
              color: palette.shellText,
              order: 20,
            }}
            title={
              chartBoxCount > 1
                ? `Upload spreadsheet into pane ${selectedBoxIndex + 1} (click a pane or its chart to change)`
                : "Upload spreadsheet (Excel or CSV)"
            }
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <UploadIcon />
              Import
            </span>
            {chartBoxCount > 1 ? (
              <span
                style={{
                  fontSize: "9px",
                  fontWeight: 600,
                  opacity: 0.85,
                  lineHeight: 1,
                }}
              >
                → pane {selectedBoxIndex + 1}
              </span>
            ) : null}
            <input
              type="file"
              accept=".xlsx, .csv"
              onChange={handleFileUpload}
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                cursor: "pointer",
              }}
            />
          </label>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", order: 20 }}>
            <label
              style={{
                ...compactLabelStyle,
                position: "relative",
                cursor: "pointer",
                minWidth: "34px",
                justifyContent: "center",
                padding: "0 6px",
                border: ratioCandles1.length
                  ? `1px solid ${palette.activeBorder}`
                  : compactLabelStyle.border,
                background: ratioCandles1.length
                  ? palette.buttonActiveBackground
                  : compactLabelStyle.background,
                color: ratioCandles1.length ? palette.shellText : compactLabelStyle.color,
              }}
              title={
                ratioFileName1
                  ? `Ratio dataset 1: ${ratioFileName1} (click to replace)`
                  : "Import dataset 1 for ratio (1 ÷ 2)"
              }
            >
              R1
              <input
                type="file"
                accept=".xlsx, .csv"
                onChange={handleRatioFileUpload(1)}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  cursor: "pointer",
                }}
              />
            </label>
            <label
              style={{
                ...compactLabelStyle,
                position: "relative",
                cursor: "pointer",
                minWidth: "34px",
                justifyContent: "center",
                padding: "0 6px",
                border: ratioCandles2.length
                  ? `1px solid ${palette.activeBorder}`
                  : compactLabelStyle.border,
                background: ratioCandles2.length
                  ? palette.buttonActiveBackground
                  : compactLabelStyle.background,
                color: ratioCandles2.length ? palette.shellText : compactLabelStyle.color,
              }}
              title={
                ratioFileName2
                  ? `Ratio dataset 2: ${ratioFileName2} (click to replace)`
                  : "Import dataset 2 for ratio (1 ÷ 2)"
              }
            >
              R2
              <input
                type="file"
                accept=".xlsx, .csv"
                onChange={handleRatioFileUpload(2)}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  cursor: "pointer",
                }}
              />
            </label>
            {ratioCandles1.length && ratioCandles2.length ? (
              <button
                type="button"
                onClick={() => setRatioModeEnabled((v) => !v)}
                title={
                  ratioModeEnabled
                    ? "Showing ratio 1÷2 — click to show the normal chart"
                    : "Click to show the 1÷2 ratio chart"
                }
                style={iconButtonStyle(ratioModeEnabled)}
              >
                1÷2
              </button>
            ) : null}
            {ratioCandles1.length || ratioCandles2.length ? (
              <button
                type="button"
                onClick={clearRatioMode}
                title="Clear ratio datasets"
                style={iconButtonStyle(false)}
              >
                ✕
              </button>
            ) : null}
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", order: 20 }}>
          <button
            type="button"
            onClick={takeChartSnapshot}
            style={{
              ...iconButtonStyle(false),
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            title="Download PNG snapshot of the main chart (pane 1). Drawing overlays are not included."
            aria-label="Download chart snapshot"
          >
            <CameraIcon />
            <span style={{ fontSize: "0.7rem" }}>Snap</span>
          </button>
          <button
            type="button"
            onClick={toggleChartFullscreen}
            style={{
              ...iconButtonStyle(isChartFullscreen),
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            title={
              isChartFullscreen
                ? "Exit full screen (Esc)"
                : "Enter full screen — chart takes over the entire display"
            }
            aria-label={isChartFullscreen ? "Exit full screen" : "Enter full screen"}
            aria-pressed={isChartFullscreen}
          >
            {isChartFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            <span style={{ fontSize: "0.7rem" }}>
              {isChartFullscreen ? "Exit FS" : "Fullscreen"}
            </span>
          </button>
          </div>
          <span
            aria-hidden
            style={{
              width: 1,
              alignSelf: "stretch",
              background: palette.borderSoft,
              opacity: 0.7,
              margin: "0 2px",
              order: 19,
            }}
          />
          <div
            ref={symbolSearchWrapRef}
            style={{ position: "relative", display: "inline-flex", alignItems: "stretch" }}
          >
            <label
              style={{
                ...compactLabelStyle,
                gap: "8px",
                border: liveRatioSymbols
                  ? `1px solid ${palette.activeBorder}`
                  : compactLabelStyle.border,
                background: liveRatioSymbols
                  ? palette.buttonActiveBackground
                  : compactLabelStyle.background,
              }}
              title="Search a symbol, or type SYM1/SYM2 for a live ratio (e.g. EURUSD/GBPUSD)"
            >
              <span>{isSpaceChrome ? "Symbol" : "SYMB"}</span>
              <input
                type="text"
                value={symbolSearchText}
                onFocus={(e) => {
                  setSymbolSearchOpen(true);
                  e.target.select();
                }}
                onChange={(e) => {
                  setSymbolSearchText(e.target.value);
                  setSymbolSearchOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    commitSymbolSelection(symbolSearchText);
                    e.currentTarget.blur();
                  } else if (e.key === "Escape") {
                    setSymbolSearchOpen(false);
                    e.currentTarget.blur();
                  }
                }}
                placeholder="Symbol or SYM1/SYM2"
                style={{
                  background: "transparent",
                  border: "none",
                  color: palette.shellText,
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: "text",
                  outline: "none",
                  minWidth: "108px",
                  maxWidth: "150px",
                }}
                aria-label="Market symbol search — type SYM1/SYM2 for a live ratio, e.g. EURUSD/GBPUSD"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={symbolSearchOpen}
                aria-controls="symbol-search-listbox"
              />
              {liveRatioSymbols ? (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    clearRatioMode();
                    setSymbolSearchOpen(false);
                  }}
                  title="Exit live ratio, back to a single symbol"
                  aria-label="Clear live ratio"
                  style={{
                    border: "none",
                    background: "transparent",
                    color: palette.shellText,
                    opacity: 0.75,
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              ) : null}
            </label>
            {symbolSearchOpen ? (
              symbolSearchQuery.mode === "ratio" ? (
                <div
                  style={{
                    ...chartGridMenuPopoverStyle,
                    padding: "8px",
                    fontSize: "0.8rem",
                  }}
                >
                  {symbolSearchQuery.leg1 && symbolSearchQuery.leg2 ? (
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => commitSymbolSelection(symbolSearchText)}
                      style={chartGridMenuRowButtonStyle(false)}
                    >
                      Live ratio: {symbolSearchQuery.leg1} ÷ {symbolSearchQuery.leg2}
                    </button>
                  ) : (
                    <div style={{ color: palette.shellText, opacity: 0.7, padding: "8px 10px" }}>
                      Type SYM1/SYM2, e.g. EURUSD/GBPUSD, then press Enter
                    </div>
                  )}
                </div>
              ) : symbolSearchSuggestions.length ? (
                <ul
                  id="symbol-search-listbox"
                  role="listbox"
                  aria-label="Symbol search results by category"
                  style={{
                    ...chartGridMenuPopoverStyle,
                    minWidth: 260,
                    maxHeight: 300,
                    overflowY: "auto",
                    padding: "4px 0",
                  }}
                >
                  {symbolSearchGroups.map((group) => (
                    <li key={`symgroup-${group.category}`} style={{ margin: 0, padding: 0 }}>
                      <div
                        role="presentation"
                        style={{
                          padding: "6px 12px 4px",
                          fontSize: "0.66rem",
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                          textTransform: isSpaceChrome ? "none" : "uppercase",
                          color: palette.mutedText,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: "8px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span>{group.category}</span>
                        {group.moreCount > 0 ? (
                          <span style={{ opacity: 0.7, textTransform: "none", letterSpacing: 0 }}>
                            +{group.moreCount} more
                          </span>
                        ) : null}
                      </div>
                      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                        {group.symbols.map((symbol) => (
                          <li key={`symsearch-${symbol}`} style={{ margin: 0, padding: 0 }}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={symbol === marketSymbol && !liveRatioSymbols}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => commitSymbolSelection(symbol)}
                              style={chartGridMenuRowButtonStyle(
                                symbol === marketSymbol && !liveRatioSymbols
                              )}
                            >
                              {symbol}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              ) : (
                <div
                  style={{
                    ...chartGridMenuPopoverStyle,
                    padding: "10px 14px",
                    fontSize: "0.8rem",
                    color: palette.shellText,
                    opacity: 0.7,
                  }}
                >
                  No matching symbols. Try SYM1/SYM2 for a live ratio.
                </div>
              )
            ) : null}
          </div>
          <div
            ref={layoutsMenuWrapRef}
            style={{ position: "relative", display: "inline-flex", alignItems: "stretch" }}
          >
            <button
              type="button"
              onClick={() => setLayoutsMenuOpen((open) => !open)}
              style={{
                ...iconButtonStyle(layoutsMenuOpen),
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
              title="Save or load a layout (instrument, timeframe, chart style) — instant, no reload"
              aria-haspopup="true"
              aria-expanded={layoutsMenuOpen}
            >
              <span style={{ fontSize: "0.7rem" }}>Layouts</span>
              <span style={{ opacity: 0.72, fontSize: "0.65rem" }}>▾</span>
            </button>
            {layoutsMenuOpen ? (
              <div style={{ ...chartGridMenuPopoverStyle, minWidth: 240, padding: "10px" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <input
                    type="text"
                    value={layoutNameDraft}
                    onChange={(e) => setLayoutNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const label =
                          layoutNameDraft.trim() ||
                          (liveRatioSymbols
                            ? `${liveRatioSymbols.leg1}/${liveRatioSymbols.leg2}`
                            : marketSymbol) + ` ${chartTimeframeId}`;
                        saveCurrentLayoutAs(label);
                        setLayoutNameDraft("");
                      }
                    }}
                    placeholder="Layout name"
                    style={{
                      ...inputStyle,
                      flex: 1,
                      padding: "6px 8px",
                      fontSize: "0.78rem",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const label =
                        layoutNameDraft.trim() ||
                        (liveRatioSymbols
                          ? `${liveRatioSymbols.leg1}/${liveRatioSymbols.leg2}`
                          : marketSymbol) + ` ${chartTimeframeId}`;
                      saveCurrentLayoutAs(label);
                      setLayoutNameDraft("");
                    }}
                    style={{
                      ...chartGridMenuRowButtonStyle(false),
                      width: "auto",
                      padding: "6px 12px",
                      borderRadius: "8px",
                      border: `1px solid ${palette.borderStrong}`,
                    }}
                  >
                    Save
                  </button>
                </div>
                {savedLayouts.length ? (
                  <ul
                    role="listbox"
                    style={{
                      listStyle: "none",
                      margin: "10px 0 0",
                      padding: 0,
                      maxHeight: 220,
                      overflowY: "auto",
                    }}
                  >
                    {savedLayouts.map((layout) => {
                      const isCurrentLayout = layout.liveRatioSymbols
                        ? liveRatioSymbols?.leg1 === layout.liveRatioSymbols.leg1 &&
                          liveRatioSymbols?.leg2 === layout.liveRatioSymbols.leg2
                        : !liveRatioSymbols && layout.marketSymbol === marketSymbol;
                      return (
                        <li
                          key={layout.id}
                          style={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <button
                            type="button"
                            role="option"
                            aria-selected={isCurrentLayout}
                            onClick={() => {
                              applyLayout(layout);
                              setLayoutsMenuOpen(false);
                            }}
                            title={`${
                              layout.liveRatioSymbols
                                ? `${layout.liveRatioSymbols.leg1}/${layout.liveRatioSymbols.leg2}`
                                : layout.marketSymbol
                            } · ${layout.chartTimeframeId}`}
                            style={{
                              ...chartGridMenuRowButtonStyle(isCurrentLayout),
                              flex: 1,
                              textAlign: "left",
                              padding: "8px 10px",
                            }}
                          >
                            {layout.name}
                          </button>
                        <button
                          type="button"
                          onClick={() => deleteSavedLayout(layout.id)}
                          title={`Delete layout "${layout.name}"`}
                          aria-label={`Delete layout ${layout.name}`}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: palette.shellText,
                            opacity: 0.6,
                            cursor: "pointer",
                            padding: "0 10px",
                            fontSize: "0.75rem",
                          }}
                        >
                          ✕
                        </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div
                    style={{
                      color: palette.shellText,
                      opacity: 0.65,
                      fontSize: "0.76rem",
                      padding: "10px 2px 2px",
                    }}
                  >
                    No saved layouts yet — name this one and hit Save.
                  </div>
                )}
              </div>
            ) : null}
          </div>
          {!isBackendFeedActive ? (
            <button
              type="button"
              onClick={() => setIsBackendFeedActive(true)}
              style={{
                ...compactLabelStyle,
                cursor: "pointer",
                border: `1px solid ${palette.activeBorder}`,
                background: palette.buttonActiveBackground,
                color: palette.shellText,
              }}
              title="Resume backend live feed for the main pane (replaces current candles with live data)"
              aria-label="Resume live feed"
            >
              Resume live
            </button>
          ) : null}
          <div
            ref={chartGridToolbarWrapRef}
            style={{ position: "relative", display: "inline-flex", alignItems: "stretch" }}
          >
            <button
              type="button"
              onClick={() => {
                setChartGridMenuOpen((open) => !open);
                setSettingsChartGridMenuOpen(false);
              }}
              style={{
                ...iconButtonStyle(chartGridMenuOpen),
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                minWidth: "52px",
              }}
              title="Chart grid: number of panes"
              aria-haspopup="listbox"
              aria-expanded={chartGridMenuOpen}
            >
              <span style={{ letterSpacing: "0.04em" }}>
                {chartBoxCount === 1 ? "1×" : `${chartBoxCount}×`}
              </span>
              <span style={{ opacity: 0.72, marginLeft: 5, fontSize: "0.65rem" }}>▾</span>
            </button>
            {chartGridMenuOpen ? (
              <ul role="listbox" style={chartGridMenuPopoverStyle}>
                {CHART_GRID_SIZE_OPTIONS.map((n) => (
                  <li key={`toolbar-grid-${n}`} style={{ margin: 0, padding: 0 }}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={chartBoxCount === n}
                      onClick={() => {
                        setChartBoxCount(n);
                        setChartGridMenuOpen(false);
                      }}
                      style={chartGridMenuRowButtonStyle(chartBoxCount === n)}
                    >
                      {getChartGridOptionLabel(n)}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <div
            style={{
              ...compactLabelStyle,
              padding: "0 10px 0 12px",
              gap: "6px",
              cursor: "default",
              flexWrap: "wrap",
            }}
          >
            <span style={{ flexShrink: 0 }}>TF</span>
            <select
              value={chartTimeframeId}
              onChange={(e) => setChartTimeframeId(e.target.value)}
              style={{
                background: "transparent",
                border: "none",
                color: palette.shellText,
                fontSize: "0.78rem",
                fontWeight: 600,
                cursor: "pointer",
                outline: "none",
                minWidth: "52px",
                maxWidth: "88px",
              }}
              title="Bar timeframe (aggregates OHLC from loaded data; cannot go finer than source bars)"
            >
              {TIMEFRAME_OPTIONS.map((tf) => (
                <option key={tf.id} value={tf.id}>
                  {tf.label}
                </option>
              ))}
            </select>
            {chartTimeframeId === CUSTOM_TIMEFRAME_ID ? (
              <>
                <input
                  type="text"
                  inputMode="decimal"
                  value={customTimeframeInput}
                  onChange={(e) => setCustomTimeframeInput(e.target.value)}
                  placeholder="15"
                  style={{
                    width: 44,
                    padding: "4px 6px",
                    borderRadius: 8,
                    border: `1px solid ${palette.borderStrong}`,
                    background: palette.chipBackground,
                    color: palette.shellText,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                  }}
                  title="Custom bar length (number)"
                  aria-label="Custom timeframe value"
                />
                <select
                  value={customTimeframeUnit}
                  onChange={(e) => setCustomTimeframeUnit(e.target.value)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: palette.shellText,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    maxWidth: "80px",
                  }}
                  title="Unit for custom timeframe"
                  aria-label="Custom timeframe unit"
                >
                  {CUSTOM_TIMEFRAME_UNITS.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              padding: 3,
              borderRadius: 11,
              border: `1px solid ${palette.borderStrong}`,
              background: palette.chipBackground,
            }}
            aria-label="Quick timeframes"
          >
            {QUICK_INTERVAL_IDS.map((id) => {
              const tf = TIMEFRAME_OPTIONS.find((o) => o.id === id);
              if (!tf) return null;
              const isActive = chartTimeframeId === id;
              return (
                <button
                  key={`quicktf-${id}`}
                  type="button"
                  onClick={() => setChartTimeframeId(id)}
                  title={`Timeframe: ${tf.label}`}
                  style={{
                    appearance: "none",
                    border: "none",
                    background: isActive ? palette.buttonActiveBackground : "transparent",
                    color: isActive ? palette.shellText : palette.mutedText,
                    borderRadius: 8,
                    minWidth: 30,
                    height: 28,
                    padding: "0 7px",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    cursor: "pointer",
                  }}
                >
                  {tf.label}
                </button>
              );
            })}
          </div>
          <div style={goToDateBoxStyle}>
            <label
              onClick={() => {
                if (typeof goToDateInputRef.current?.showPicker === "function") {
                  goToDateInputRef.current.showPicker();
                  return;
                }

                goToDateInputRef.current?.focus();
              }}
              style={goToDateDisplayStyle}
            >
              <span
                style={{
                  fontSize: "0.68rem",
                  letterSpacing: "0.12em",
                  textTransform: isSpaceChrome ? "none" : "uppercase",
                  color: palette.accentText,
                  whiteSpace: "nowrap",
                }}
              >
                @
              </span>
              <span
                style={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {goToDateDisplayValue}
              </span>
              <input
                ref={goToDateInputRef}
                type="date"
                value={goToDate}
                onChange={(e) => {
                  const nextDate = e.target.value;
                  setGoToDate(nextDate);
                  goToSelectedDate(nextDate);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    goToSelectedDate();
                  }
                }}
                aria-label="Go to date"
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  cursor: "pointer",
                  colorScheme: isWhiteTheme ? "light" : "dark",
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => goToSelectedDate()}
              style={{
                ...iconButtonStyle(),
                borderRadius: 0,
                minWidth: "40px",
                borderTop: "none",
                borderRight: "none",
                borderBottom: "none",
                borderLeft: `1px solid ${palette.borderStrong}`,
              }}
              aria-label="Jump to selected date"
              title="Go to selected date"
            >
              GO
            </button>
          </div>
          <button
            type="button"
            onClick={() => togglePanel("indicators")}
            style={{
              ...iconButtonStyle(activePanel === "indicators"),
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            title="Indicators (fx)"
            aria-label="Indicators"
          >
            <FxIcon />
          </button>
          <button
            type="button"
            onClick={() => togglePanel("settings")}
            style={{
              ...iconButtonStyle(activePanel === "settings"),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Chart settings"
            aria-label="Settings"
          >
            <SettingsGearIcon />
          </button>
          <button
            type="button"
            onClick={() => togglePanel("script")}
            style={{
              ...iconButtonStyle(activePanel === "script"),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Custom script (Pine-style)"
            aria-label="Custom script"
          >
            <ScriptIcon />
          </button>
          <button
            type="button"
            onClick={() => togglePanel("ai")}
            style={{
              ...iconButtonStyle(activePanel === "ai"),
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            title="Chart assistant (Claude)"
            aria-label="AI assistant"
          >
            <AiSparkIcon />
          </button>
          <button
            type="button"
            onClick={enterReplaySelection}
            style={{
              ...iconButtonStyle(activePanel === "replay" || isReplayMode || isReplaySelecting),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Replay"
            aria-label="Replay"
          >
            <ReplayIcon />
          </button>
          <button
            type="button"
            onClick={() => {
              setIsMeasureMode((currentValue) => !currentValue);
            }}
            style={{
              ...iconButtonStyle(isMeasureMode),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Measure"
            aria-label="Measure"
          >
            <RulerIcon />
          </button>
          <button
            type="button"
            onClick={clearMeasurement}
            style={{
              ...iconButtonStyle(false, "red"),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Clear measurement"
            aria-label="Clear measurement"
          >
            <TrashIcon />
          </button>
        </div>

        <div
          style={{
            ...toolbarStyle,
            top: "140px",
            left: "14px",
            display: "grid",
            gap: 6,
            width: "52px",
            padding: "8px 6px",
            ...(isSpaceChrome
              ? {
                top: topBarHeight,
                left: 0,
                bottom: 0,
                width: SPACE_RAIL_W,
                display: "flex",
                flexDirection: "column",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 2,
                padding: "6px 4px",
                overflowY: "auto",
                borderRight: `1px solid ${palette.borderSoft}`,
              }
              : null),
          }}
          aria-label="Drawing tools"
        >
          {toolDefinitions.map((tool) => {
            const Icon = tool.Icon;
            const isActive = drawTool === tool.key;
            return (
              <button
                key={tool.key}
                type="button"
                onClick={() => toggleDrawTool(tool.key)}
                title={`${tool.label}`}
                aria-label={tool.label}
                style={{
                  ...iconButtonStyle(isActive),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  flexShrink: 0,
                ...(isSpaceChrome ? { minWidth: 34, width: 34, height: 32 } : null),
                }}
              >
                {Icon ? <Icon size={18} /> : tool.symbol}
              </button>
            );
          })}
          <div
            aria-hidden
            style={{
              height: 1,
              margin: "2px 4px",
              background: palette.borderSoft,
              opacity: 0.7,
            }}
          />
          <button
            type="button"
            onClick={clearDrawings}
            title="Clear drawings"
            aria-label="Clear drawings"
            style={{
              ...iconButtonStyle(false, "red"),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              flexShrink: 0,
                ...(isSpaceChrome ? { minWidth: 34, width: 34, height: 32 } : null),
            }}
          >
            <TrashIcon size={18} />
          </button>
          {drawingOptionButtons.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={b.onClick}
              disabled={b.disabled}
              title={b.title}
              aria-label={b.label}
              aria-pressed={b.pressed}
              style={{
                ...iconButtonStyle(Boolean(b.pressed)),
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                flexShrink: 0,
                ...(isSpaceChrome ? { minWidth: 34, width: 34, height: 32 } : null),
                position: "relative",
                opacity: b.disabled ? 0.4 : 1,
              }}
            >
              <b.Icon size={18} off={b.off} />
              {b.badge ? (
                <span style={{ position: "absolute", right: 2, bottom: 1, fontSize: 8, fontWeight: 700, lineHeight: 1 }}>{b.badge}</span>
              ) : null}
            </button>
          ))}
        </div>

        <div
          style={{
            ...toolbarStyle,
            top: "auto",
            bottom: "44px",
            left: "14px",
            right: "auto",
            ...(isSpaceChrome
              ? { left: SPACE_RAIL_W + 12, bottom: 40, padding: 3, gap: 2, border: `1px solid ${palette.borderSoft}`, borderRadius: 999 }
              : null),
          }}
          aria-label="Zoom and pan"
        >
          {DATE_RANGE_PRESETS.map((preset) => (
            <button
              key={`date-range-${preset.key}`}
              type="button"
              onClick={() => applyDateRangePreset(preset)}
              style={{
                ...iconButtonStyle(false),
                minWidth: "auto",
                padding: "0 8px",
                fontSize: "0.7rem",
                flexShrink: 0,
              }}
              title={`Show the ${preset.label === "All" ? "entire loaded history" : `last ${preset.label}`}`}
            >
              {preset.label}
            </button>
          ))}
          <span
            aria-hidden
            style={{
              width: 1,
              alignSelf: "stretch",
              background: palette.borderSoft,
              opacity: 0.7,
              margin: "0 2px",
              flexShrink: 0,
            }}
          />
          <button type="button" onClick={() => zoomChart("in")} style={{ ...iconButtonStyle(), flexShrink: 0 }} title="Zoom in">
            +
          </button>
          <button type="button" onClick={() => zoomChart("out")} style={{ ...iconButtonStyle(), flexShrink: 0 }} title="Zoom out">
            -
          </button>
          <button type="button" onClick={() => panChart("left")} style={{ ...iconButtonStyle(), flexShrink: 0 }} title="Pan left">
            {"<"}
          </button>
          <button type="button" onClick={() => panChart("right")} style={{ ...iconButtonStyle(), flexShrink: 0 }} title="Pan right">
            {">"}
          </button>
        </div>

        {(() => {
          const RIGHT_TABS = [
            { key: "watchlist", label: "Watchlist", Icon: ListIcon },
            { key: "details", label: "Details", Icon: InfoIcon },
            { key: "info", label: "Help", Icon: NewsIcon },
          ];
          const lastCandle = visibleData && visibleData.length ? visibleData[visibleData.length - 1] : null;
          const currentTfLabel =
            TIMEFRAME_OPTIONS.find((o) => o.id === chartTimeframeId)?.label || chartTimeframeId;
          const detailRow = (k, v) => (
            <div
              key={k}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 10,
                fontSize: "0.76rem",
              }}
            >
              <span style={{ color: palette.mutedText, fontWeight: 600, letterSpacing: "0.04em" }}>{k}</span>
              <span style={{ color: palette.shellText, fontWeight: 700 }}>{v}</span>
            </div>
          );
          return (
            <>
              <div
                style={{
                  ...toolbarStyle,
                  top: "74px",
                  right: "14px",
                  flexDirection: "column",
                  padding: "8px 6px",
                  gap: 6,
                  width: "52px",
                  ...(isSpaceChrome
                    ? {
                      top: topBarHeight,
                      right: 0,
                      bottom: 0,
                      width: SPACE_RAIL_W,
                      flexWrap: "nowrap",
                      alignItems: "center",
                      gap: 2,
                      padding: "6px 4px",
                      borderLeft: `1px solid ${palette.borderSoft}`,
                    }
                    : null),
                }}
                aria-label="Right-side panels"
              >
                {RIGHT_TABS.map((tab) => {
                  const Icon = tab.Icon;
                  const isActive = rightSideTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() =>
                        setRightSideTab((current) => (current === tab.key ? null : tab.key))
                      }
                      title={tab.label}
                      aria-label={tab.label}
                      style={{
                        ...iconButtonStyle(isActive),
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                    >
                      <Icon size={18} />
                    </button>
                  );
                })}
              </div>
              {rightSideTab ? (
                <div
                  style={{
                    position: "absolute",
                    top: isSpaceChrome ? topBarHeight : "74px",
                    right: isSpaceChrome ? SPACE_RAIL_W : "78px",
                    width: "min(280px, 36vw)",
                    maxHeight: isSpaceChrome ? `calc(100% - ${topBarHeight}px)` : "min(60vh, 520px)",
                    zIndex: 4,
                    padding: "12px 14px",
                    borderRadius: isSpaceChrome ? 0 : 14,
                    border: `1px solid ${palette.borderSoft}`,
                    background: palette.overlayBackground,
                    backdropFilter: "blur(18px)",
                    boxShadow: isSpaceChrome ? "none" : "0 22px 46px rgba(0,0,0,0.38)",
                    color: palette.shellText,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    overflow: "hidden",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ fontSize: "0.84rem", fontWeight: 800, letterSpacing: "0.04em" }}>
                      {RIGHT_TABS.find((t) => t.key === rightSideTab)?.label}
                    </div>
                    <button
                      type="button"
                      onClick={() => setRightSideTab(null)}
                      aria-label="Close panel"
                      style={{
                        appearance: "none",
                        border: "none",
                        background: "transparent",
                        color: palette.mutedText,
                        cursor: "pointer",
                        fontSize: "1rem",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {rightSideTab === "watchlist" ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        addToWatchlist(watchlistAddText);
                        setWatchlistAddText("");
                      }}
                      style={{ display: "flex", gap: 6 }}
                    >
                      <input
                        type="text"
                        value={watchlistAddText}
                        onChange={(e) => setWatchlistAddText(e.target.value)}
                        placeholder="Add symbol (e.g. USDJPY)"
                        style={{
                          ...inputStyle,
                          flex: 1,
                          padding: "6px 8px",
                          fontSize: "0.76rem",
                          borderRadius: 8,
                        }}
                      />
                      <button
                        type="submit"
                        style={{
                          ...chartGridMenuRowButtonStyle(false),
                          width: "auto",
                          padding: "6px 10px",
                          borderRadius: 8,
                        }}
                      >
                        Add
                      </button>
                    </form>
                  ) : null}
                  <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                    {rightSideTab === "watchlist" ? (
                      watchlistSymbols && watchlistSymbols.length ? (
                        watchlistSymbols.map((sym) => {
                          const isCurrent = sym === marketSymbol;
                          const quote = watchlistQuotes[sym];
                          const isUp = quote && quote.changeAbs >= 0;
                          return (
                            <div
                              key={`wl-${sym}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => commitSymbolSelection(sym)}
                                style={{
                                  appearance: "none",
                                  textAlign: "left",
                                  cursor: "pointer",
                                  flex: 1,
                                  padding: "8px 10px",
                                  borderRadius: 10,
                                  border: `1px solid ${isCurrent ? palette.activeBorder : palette.borderSoft}`,
                                  background: isCurrent ? palette.buttonActiveBackground : "transparent",
                                  color: palette.shellText,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 2,
                                }}
                                title={`Switch to ${sym}`}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "0.82rem",
                                      fontWeight: 700,
                                      letterSpacing: "0.04em",
                                    }}
                                  >
                                    {sym}
                                  </span>
                                  <span style={{ fontSize: "0.8rem", fontWeight: 700 }}>
                                    {quote ? formatPrice(quote.last) : "—"}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span style={{ fontSize: "0.64rem", color: palette.accentText }}>
                                    {isCurrent ? "current" : ""}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "0.72rem",
                                      fontWeight: 700,
                                      color: !quote
                                        ? palette.mutedText
                                        : isUp
                                          ? palette.plusDI
                                          : palette.minusDI,
                                    }}
                                  >
                                    {quote
                                      ? `${isUp ? "+" : ""}${formatPrice(quote.changeAbs)} (${isUp ? "+" : ""}${quote.changePct.toFixed(2)}%)`
                                      : "—"}
                                  </span>
                                </div>
                              </button>
                              <button
                                type="button"
                                onClick={() => removeFromWatchlist(sym)}
                                title={`Remove ${sym} from watchlist`}
                                aria-label={`Remove ${sym} from watchlist`}
                                style={{
                                  appearance: "none",
                                  border: "none",
                                  background: "transparent",
                                  color: palette.mutedText,
                                  cursor: "pointer",
                                  fontSize: "0.85rem",
                                  padding: "0 6px",
                                }}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ color: palette.mutedText, fontSize: "0.8rem" }}>
                          No symbols in watchlist yet — add one above.
                        </div>
                      )
                    ) : null}
                    {rightSideTab === "details" ? (
                      <>
                        {detailRow("Symbol", marketSymbol || "—")}
                        {detailRow("Timeframe", currentTfLabel)}
                        {detailRow("Bars", visibleData ? visibleData.length : 0)}
                        {lastCandle ? (
                          <>
                            {detailRow("Last", formatPrice(lastCandle.close))}
                            {detailRow("Open", formatPrice(lastCandle.open))}
                            {detailRow("High", formatPrice(lastCandle.high))}
                            {detailRow("Low", formatPrice(lastCandle.low))}
                          </>
                        ) : null}
                        {detailRow("Active indicators", activeIndicators.length)}
                        {detailRow("Live feed", isBackendFeedActive ? "on" : "off")}
                        {detailRow("Replay", isReplayMode ? "on" : "off")}
                      </>
                    ) : null}
                    {rightSideTab === "info" ? (
                      <div style={{ fontSize: "0.8rem", lineHeight: 1.55, color: palette.shellText }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>Quick tips</div>
                        <ul style={{ paddingLeft: 16, margin: 0, display: "grid", gap: 4, color: palette.mutedText }}>
                          <li>Use the top-right <strong>Import</strong> button to load Excel/CSV.</li>
                          <li>Click <strong>fx</strong> to browse indicators (trend, momentum, volume, volatility).</li>
                          <li>The left rail has drawing tools; click an icon then click on the chart.</li>
                          <li>Quick-interval chips switch timeframes fast; use <strong>TF</strong> dropdown for custom.</li>
                          <li>The legend (bottom-left of chart) shows OHLC and active indicators with toggle / settings / remove.</li>
                          <li>Open <strong>Replay</strong> to step through history bar-by-bar.</li>
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </>
          );
        })()}

        {(() => {
          const liveCandle =
            visibleData && visibleData.length ? visibleData[visibleData.length - 1] : null;
          const legendCandle = hoveredCandle || liveCandle;
          const prevCandle = legendCandle && visibleData
            ? visibleData[visibleData.findIndex((c) => c.time === legendCandle.time) - 1]
            : null;
          const change = legendCandle && prevCandle ? legendCandle.close - prevCandle.close : 0;
          const changePct = prevCandle && prevCandle.close ? (change / prevCandle.close) * 100 : 0;
          const up = change >= 0;
          const upColor = palette.stUp || "#22c55e";
          const downColor = palette.stDown || "#ef4444";
          const changeColor = up ? upColor : downColor;
          const currentTfLabel =
            TIMEFRAME_OPTIONS.find((o) => o.id === chartTimeframeId)?.label || chartTimeframeId;
          const labelStyle = {
            fontSize: "0.62rem",
            fontWeight: 800,
            letterSpacing: "0.08em",
            color: palette.mutedText,
            textTransform: isSpaceChrome ? "none" : "uppercase",
          };
          const valueStyle = { fontSize: "0.78rem", fontWeight: 700, color: palette.shellText };
          const iconBtn = {
            appearance: "none",
            border: "none",
            background: "transparent",
            color: palette.mutedText,
            cursor: "pointer",
            padding: 2,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 4,
          };
          return (
            <div
              style={{
                position: "absolute",
                left: isSpaceChrome ? `${SPACE_RAIL_W + 16}px` : "76px",
                right: "auto",
                top: isSpaceChrome ? `${topBarHeight + 14}px` : "116px",
                bottom: "auto",
                // Below the toolbar's zIndex (4) on purpose: the toolbar is its own
                // stacking context (position:absolute + z-index), so any dropdown opened
                // from within it (symbol search, Layouts, ...) needs this panel to stay
                // beneath it rather than tying and winning on DOM order.
                zIndex: 3,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: isSpaceChrome ? "12px 16px" : "10px 12px",
                borderRadius: isSpaceChrome ? 16 : 12,
                border: `1px solid ${palette.borderSoft}`,
                background: palette.overlayBackground,
                backdropFilter: "blur(14px)",
                boxShadow: isSpaceChrome ? "none" : "0 14px 32px rgba(0,0,0,0.3)",
                minWidth: 240,
                maxWidth: "min(400px, calc(100vw - 120px))",
                maxHeight: "min(38vh, 320px)",
                overflowY: "auto",
                color: palette.shellText,
              }}
              aria-label="Chart legend"
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.9rem", fontWeight: 800, letterSpacing: "0.04em" }}>
                  {ratioModeEnabled && ratioData.length
                    ? `${stripFileExtension(ratioFileName1) || "1"} / ${
                        stripFileExtension(ratioFileName2) || "2"
                      }`
                    : marketSymbol || "Symbol"}
                </span>
                <span
                  style={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    padding: "2px 6px",
                    borderRadius: 6,
                    background: palette.chipBackground,
                    color: palette.accentText,
                  }}
                >
                  {currentTfLabel}
                </span>
                {legendCandle && prevCandle ? (
                  <span style={{ color: changeColor, fontSize: "0.72rem", fontWeight: 700 }}>
                    {up ? "▲" : "▼"} {formatPrice(Math.abs(change))} ({changePct.toFixed(2)}%)
                  </span>
                ) : null}
              </div>
              {liveCandle && !(ratioModeEnabled && ratioData.length) ? (
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <LivePriceTicker
                    value={liveCandle.close}
                    format={formatPrice}
                    color={palette.shellText}
                    upColor={upColor}
                    downColor={downColor}
                  />
                  {(() => {
                    // where the last price sits between the low and high of the last 200 bars
                    const recent = visibleData.slice(-200);
                    let hi = -Infinity;
                    let lo = Infinity;
                    recent.forEach((c) => {
                      if (c.high > hi) hi = c.high;
                      if (c.low < lo) lo = c.low;
                    });
                    const position = hi > lo ? (liveCandle.close - lo) / (hi - lo) : NaN;
                    return (
                      <RangeGauge
                        position={Math.max(0, Math.min(1, position))}
                        color={up ? upColor : downColor}
                        track={palette.borderSoft}
                        text={palette.shellText}
                        label={`range of the last ${recent.length} bars`}
                      />
                    );
                  })()}
                </div>
              ) : null}
              {legendCandle ? (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, auto)",
                    columnGap: 12,
                    rowGap: 2,
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  }}
                >
                  <span style={labelStyle}>O</span>
                  <span style={labelStyle}>H</span>
                  <span style={labelStyle}>L</span>
                  <span style={labelStyle}>C</span>
                  <span style={valueStyle}>{formatPrice(legendCandle.open)}</span>
                  <span style={{ ...valueStyle, color: upColor }}>{formatPrice(legendCandle.high)}</span>
                  <span style={{ ...valueStyle, color: downColor }}>{formatPrice(legendCandle.low)}</span>
                  <span style={{ ...valueStyle, color: changeColor }}>{formatPrice(legendCandle.close)}</span>
                </div>
              ) : null}
              {hoveredCandle ? (
                <div
                  style={{
                    borderTop: `1px solid ${palette.borderSoft}`,
                    paddingTop: 8,
                    marginTop: 2,
                    fontSize: "0.74rem",
                    lineHeight: 1.5,
                    color: palette.shellText,
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {formatTimestamp(hoveredCandle.time, chartTimeZone)}
                  </div>
                  {showMovingAverage ? (
                    <div>
                      MA {maPeriod}: {formatPrice(hoveredMovingAverageValue)}
                    </div>
                  ) : null}
                  {showSma ? (
                    <div>
                      SMA {smaPeriod}: {formatPrice(hoveredSmaValue)}
                    </div>
                  ) : null}
                  {showEma ? (
                    <div>
                      EMA {emaPeriod}: {formatPrice(hoveredEmaValue)}
                    </div>
                  ) : null}
                  {showWma ? (
                    <div>
                      WMA {wmaPeriod}: {formatPrice(hoveredWmaValue)}
                    </div>
                  ) : null}
                  {showBollinger ? (
                    <div>
                      BB ({bbPeriod}): {formatPrice(hoveredBbLower)} / {formatPrice(hoveredBbUpper)}
                    </div>
                  ) : null}
                  {showRsi ? (
                    <div>
                      RSI {rsiPeriod}: {formatPrice(hoveredRsi)}
                    </div>
                  ) : null}
                  {showAtr ? (
                    <div>
                      ATR {atrPeriod}: {formatPrice(hoveredAtr)}
                    </div>
                  ) : null}
                  {showDonchian ? (
                    <div>
                      DC ({donchianPeriod}): {formatPrice(hoveredDonchianLower)} /{" "}
                      {formatPrice(hoveredDonchianUpper)}
                    </div>
                  ) : null}
                  {showStochastic ? (
                    <div>
                      STOCH {stochKPeriod}/{stochDPeriod}: {formatPrice(hoveredStochasticK)} /{" "}
                      {formatPrice(hoveredStochasticD)}
                    </div>
                  ) : null}
                  {showMacd ? (
                    <div>
                      MACD {macdFastPeriod}/{macdSlowPeriod}/{macdSignalPeriod}:{" "}
                      {formatPrice(hoveredMacdLine)} / {formatPrice(hoveredMacdSignal)} /{" "}
                      {formatPrice(hoveredMacdHistogram)}
                    </div>
                  ) : null}
                  {showPatterns && patternMapByTime.get(hoveredCandle.time)?.length ? (
                    <div>
                      Patterns:{" "}
                      {patternMapByTime
                        .get(hoveredCandle.time)
                        .map((p) => p.label)
                        .join(", ")}
                    </div>
                  ) : null}
                  {extraInstanceHoverEntries.map((entry) => (
                    <div key={entry.id}>
                      {entry.label} {entry.summary}: {entry.text}
                    </div>
                  ))}
                </div>
              ) : null}
              {activeIndicators.length || backtestTradeMarkers.length ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    borderTop: `1px solid ${palette.borderSoft}`,
                    paddingTop: 6,
                    marginTop: 2,
                  }}
                >
                  {backtestTradeMarkers.length > 0 ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", fontSize: "0.72rem" }}>
                      <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#26a69a", display: "inline-block" }} />
                      <span style={{ color: palette.shellText, fontWeight: 700 }}>Backtest trades</span>
                      <span style={{ color: palette.mutedText }}>{backtestTrades?.trades?.length || 0} round trips</span>
                      <span style={{ color: "#26a69a", fontWeight: 700 }}>↑ Entry fill</span>
                      {backtestTrades?.strategyId === "hmm_bollinger_touch" ? (
                        <>
                          <span style={{ color: "#f6c453", fontWeight: 700 }}>■ Entry touch</span>
                          <span style={{ color: "#b59cff", fontWeight: 700 }}>■ Exit signal</span>
                        </>
                      ) : null}
                      <span style={{ color: "#b59cff", fontWeight: 700 }}>● Exit fill</span>
                      <span style={{ flex: 1 }} />
                      <span style={{ color: palette.mutedText }}>Entry/exit fills use next bar open · right-click to hide</span>
                    </div>
                  ) : null}
                  {activeIndicators.map((indicator) => (
                    <div
                      key={`legend-${indicator.key}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: "0.72rem",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          indicator.onToggle(false);
                        }}
                        title={`Hide ${indicator.fullLabel}`}
                        aria-label={`Hide ${indicator.fullLabel}`}
                        style={iconBtn}
                      >
                        <EyeIcon />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleIndicatorSelect(indicator)}
                        title={`${indicator.fullLabel} settings`}
                        style={{
                          appearance: "none",
                          border: "none",
                          background: "transparent",
                          color: palette.shellText,
                          padding: 0,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          cursor: "pointer",
                          fontSize: "0.74rem",
                        }}
                      >
                        {indicator.label}
                        {indicator.summary ? (
                          <span style={{ color: palette.mutedText, fontWeight: 500, marginLeft: 4 }}>
                            {indicator.summary}
                          </span>
                        ) : null}
                      </button>
                      <div style={{ flex: 1 }} />
                      <button
                        type="button"
                        onClick={() => handleIndicatorSelect(indicator)}
                        title={`${indicator.fullLabel} settings`}
                        aria-label={`${indicator.fullLabel} settings`}
                        style={iconBtn}
                      >
                        <SettingsGearIcon size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          indicator.onToggle(false);
                          if (activeIndicatorKey === indicator.key) {
                            setActiveIndicatorKey(null);
                          }
                        }}
                        title={`Remove ${indicator.fullLabel}`}
                        aria-label={`Remove ${indicator.fullLabel}`}
                        style={iconBtn}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              {(isReplayMode || activePanel === "ai" || drawTool !== "none" || isMeasureMode) ? (
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                    borderTop: `1px solid ${palette.borderSoft}`,
                    paddingTop: 6,
                  }}
                >
                  {isReplayMode ? (
                    <span style={activeIndicatorChipStyle}>R {replayDateLabel || "ACTIVE"}</span>
                  ) : null}
                  {activePanel === "ai" ? <span style={activeIndicatorChipStyle}>AI</span> : null}
                  {drawTool !== "none" ? (
                    <span style={activeIndicatorChipStyle}>{drawTool.toUpperCase()}</span>
                  ) : null}
                  {isMeasureMode ? <span style={activeIndicatorChipStyle}>MEASURE</span> : null}
                </div>
              ) : null}
            </div>
          );
        })()}
        </>
        )}

        {isChartLoading && isBackendFeedActive ? (
          <ChartLoadingOverlay
            symbol={marketSymbol}
            timeframeLabel={backendFeedTimeframe}
            background={palette.chartBackground}
            gridColor={palette.gridLine}
            textColor={palette.shellText}
            mutedColor={palette.mutedText}
          />
        ) : null}

        {isMobile && (
          <MobileChartChrome
            space={isInstrumentTheme}
            palette={palette}
            sheet={mobileSheet}
            onOpenSheet={(s) => setMobileSheet((cur) => (cur === s ? null : s))}
            onCloseSheet={() => setMobileSheet(null)}
            header={{ hoveredCandle, visibleData }}
            symbol={{
              marketSymbol,
              marketSymbols,
              commitSymbolSelection,
              symbolSearchText,
              setSymbolSearchText,
              symbolSearchQuery,
              liveRatioSymbols,
              clearRatioMode,
              ratioCandles1,
              ratioCandles2,
              ratioModeEnabled,
              setRatioModeEnabled,
              handleFileUpload,
              handleRatioFileUpload,
              chartBoxCount,
              setChartBoxCount,
              isBackendFeedActive,
              setIsBackendFeedActive,
            }}
            timeframe={{
              chartTimeframeId,
              setChartTimeframeId,
              customTimeframeInput,
              setCustomTimeframeInput,
              customTimeframeUnit,
              setCustomTimeframeUnit,
              goToDate,
              setGoToDate,
              goToSelectedDate,
              goToDateInputRef,
              chartTimeZone,
              setChartTimeZone,
              chartTimeZoneOptions,
              applyDateRangePreset,
              isWhiteTheme,
            }}
            drawing={{
              toolDefinitions,
              drawTool,
              toggleDrawTool,
              clearDrawings,
              isMeasureMode,
              setIsMeasureMode,
              options: drawingOptionButtons,
            }}
            more={{
              togglePanel,
              activePanel,
              onOpenIndicators: () => togglePanel("indicators"),
              enterReplaySelection,
              isReplayMode,
              isReplaySelecting,
              zoomChart,
              panChart,
              toggleChartFullscreen,
              isChartFullscreen,
              takeChartSnapshot,
              rightSideTab,
              setRightSideTab,
              watchlistSymbols,
              watchlistQuotes,
              watchlistAddText,
              setWatchlistAddText,
              addToWatchlist,
              removeFromWatchlist,
              commitSymbolSelection,
              marketSymbol,
              chartTimeframeId,
              visibleData,
              activeIndicators,
              isBackendFeedActive,
            }}
          />
        )}

        {isReplaySelecting ? (
          <div
            style={{
              position: "absolute",
              right: "14px",
              top: "120px",
              zIndex: 2,
              background: palette.replayBackground,
              border: `1px solid ${palette.borderSoft}`,
              borderRadius: "14px",
              padding: "12px 14px",
              textAlign: "left",
              fontSize: "12px",
              lineHeight: 1.5,
              pointerEvents: "none",
              color: palette.shellText,
              minWidth: "260px",
              boxShadow: "0 18px 40px rgba(2, 6, 23, 0.36)",
            }}
          >
            <div>
              <strong>Replay Selection Mode</strong>
            </div>
            <div>Choose a date or click a candle to place the replay cursor.</div>
            <div>Cursor mode: cell</div>
          </div>
        ) : null}

        {activePanel === "indicators" ? (
          <div style={overlayPanelStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              <div style={{ color: palette.shellText, fontSize: "0.95rem", fontWeight: 800 }}>
                Indicators
              </div>
              <button
                type="button"
                onClick={() => {
                  setActivePanel("none");
                  setActiveIndicatorKey(null);
                }}
                style={{
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  color: palette.mutedText,
                  fontSize: "1rem",
                  cursor: "pointer",
                }}
              >
                x
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gap: "8px",
                maxHeight: "min(60vh, 520px)",
                overflowY: "auto",
                paddingRight: "4px",
                justifyItems: "start",
              }}
            >
              <input
                type="search"
                value={indicatorSearchTerm}
                onChange={(e) => setIndicatorSearchTerm(e.target.value)}
                placeholder="Search indicators..."
                style={{
                  ...inputStyle,
                  width: "100%",
                  minHeight: "34px",
                  padding: "7px 10px",
                }}
              />
              {groupedIndicators.map((section, sectionIndex) => {
                const categoryOpen = Boolean(indicatorSearchNormalized) || (openIndicatorCategories[section.key] ?? sectionIndex === 0);
                return (
                <div
                  key={section.key}
                  style={{
                    display: "grid",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  <button
                    type="button"
                    aria-expanded={categoryOpen}
                    onClick={() => setOpenIndicatorCategories((previous) => ({ ...previous, [section.key]: !categoryOpen }))}
                    style={{
                      color: palette.accentText,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: isSpaceChrome ? "none" : "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      width: "100%",
                      padding: 0,
                      border: 0,
                      background: "transparent",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <span aria-hidden="true" style={{ width: 12, color: palette.mutedText }}>{categoryOpen ? "▾" : "▸"}</span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "18px",
                        padding: "1px 5px",
                        borderRadius: "999px",
                        background: palette.chipBackground,
                        color: palette.shellText,
                        fontSize: "0.64rem",
                      }}
                    >
                      {section.items.length}
                    </span>
                    {section.label}
                  </button>
                  {categoryOpen && section.subgroups.map((subgroup, subgroupIndex) => {
                    const subgroupKey = `${section.key}:${subgroup.label}`;
                    const isOpen = Boolean(indicatorSearchNormalized) || (openIndicatorSubcategories[subgroupKey] ?? subgroupIndex === 0);
                    return (
                      <details key={subgroupKey} open={isOpen} onToggle={(event) => {
                        const nextOpen = event.currentTarget.open;
                        setOpenIndicatorSubcategories((previous) => previous[subgroupKey] === nextOpen ? previous : { ...previous, [subgroupKey]: nextOpen });
                      }} style={{ width: "100%", border: `1px solid ${palette.borderSoft}`, borderRadius: 9, padding: "6px 8px" }}>
                        <summary style={{ cursor: "pointer", color: palette.shellText, fontSize: "0.72rem", fontWeight: 650, display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <span>{subgroup.label}</span><span style={{ color: palette.mutedText, fontWeight: 450 }}>{subgroup.items.length}</span>
                        </summary>
                        <div style={{ display: "grid", gap: 8, width: "100%", paddingTop: 7 }}>
                  {subgroup.items.map((indicator) => (
                <div
                  key={indicator.key}
                  onClick={() => handleIndicatorSelect(indicator)}
                  style={{
                    display: "inline-grid",
                    gridTemplateColumns: "auto minmax(0, 1fr) auto",
                    gap: "8px",
                    alignItems: "center",
                    padding: "10px",
                    borderRadius: "12px",
                    border: `1px solid ${activeIndicatorKey === indicator.key
                        ? palette.activeBorder
                        : palette.borderSoft
                      }`,
                    background:
                      activeIndicatorKey === indicator.key
                        ? palette.buttonActiveBackground
                        : palette.chipBackground,
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      ...iconButtonStyle(activeIndicatorKey === indicator.key),
                      minWidth: "48px",
                      fontSize: "0.74rem",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {indicator.label}
                  </span>
                  <div
                    style={{
                      textAlign: "left",
                      display: "grid",
                      gap: "4px",
                      minWidth: "0",
                    }}
                  >
                    <div
                      style={{
                        color: palette.shellText,
                        fontSize: "0.82rem",
                        fontWeight: 700,
                      }}
                    >
                      {indicator.fullLabel}
                    </div>
                    <span
                      style={{
                        color: indicator.checked ? palette.shellText : palette.mutedText,
                        fontSize: "0.72rem",
                      }}
                    >
                      {indicator.checked
                        ? DUPLICABLE_INDICATOR_TYPES[indicator.key]
                          ? `Active • ${indicator.summary} • click to add another`
                          : `Active • ${indicator.summary}`
                        : `Click to add • ${indicator.summary}`}
                    </span>
                  </div>
                  <span
                    style={{
                      ...compactLabelStyle,
                      minHeight: "30px",
                      fontSize: "0.68rem",
                      padding: "0 8px",
                      justifyContent: "center",
                      color: indicator.checked ? palette.shellText : palette.mutedText,
                    }}
                  >
                    {indicator.checked ? "ACTIVE" : "ADD"}
                  </span>
                </div>
              ))}
                      </div>
                      </details>
                    );
                  })}
                </div>
                );
              })}
              {groupedIndicators.length === 0 ? (
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.74rem",
                    width: "100%",
                  }}
                >
                  No indicators match "{indicatorSearchTerm.trim()}".
                </div>
              ) : null}
              <div
                style={{
                  width: "100%",
                  borderTop: `1px dashed ${palette.borderSoft}`,
                  paddingTop: "8px",
                  display: "grid",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    color: palette.accentText,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  TradingView common built-ins
                </div>
                {tradingViewIndicatorCatalog.map((group) => (
                  <div
                    key={group.category}
                    style={{
                      display: "grid",
                      gap: "4px",
                    }}
                  >
                    <div
                      style={{
                        color: palette.shellText,
                        fontSize: "0.74rem",
                        fontWeight: 700,
                      }}
                    >
                      {group.category}
                    </div>
                    <div style={{ color: palette.mutedText, fontSize: "0.7rem", lineHeight: 1.45 }}>
                      {group.items.join(" • ")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {activePanel === "indicators" && activeIndicator ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: "min(520px, calc(100% - 48px))",
                padding: "12px",
                borderRadius: "16px",
                border: `1px solid ${palette.activeBorder}`,
                background: palette.inputBackground,
                boxShadow: "0 18px 40px rgba(2, 6, 23, 0.32)",
                minHeight: "220px",
                pointerEvents: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  marginBottom: "10px",
                }}
              >
                <div>
                  <div
                    style={{
                      color: palette.shellText,
                      fontSize: "0.92rem",
                      fontWeight: 800,
                    }}
                  >
                    {activeIndicator.fullLabel}
                  </div>
                  <div
                    style={{
                      color: palette.mutedText,
                      fontSize: "0.72rem",
                      letterSpacing: "0.06em",
                      textTransform: isSpaceChrome ? "none" : "uppercase",
                    }}
                  >
                    Indicator settings
                  </div>
                  <div
                    style={{
                      color: palette.mutedText,
                      fontSize: "0.74rem",
                      marginTop: "6px",
                    }}
                  >
                    {activeIndicator.key === "kaufmanRange"
                      ? kaufmanRangeRegimeMethod === "hmm"
                        ? hmmModelMode === "causal200"
                          ? "Causal 200 mode calibrates on the first 200 feature bars, then forward-filters every later state. Gold squares mark outer-band entry touches; purple squares mark exit signals. Arrows and circles mark next-bar-open fills. An entry fill can land near the middle band even though its signal candle touched an outer band."
                          : `HMM ${hmmTrainingSplit}/${100 - Number(hmmTrainingSplit)} fit/holdout. Fit-period labels are retrospective; only forward-filtered holdout states trigger backtest signals. A matching HMM + Bollinger overlay reuses its saved states. Gold squares mark outer-band entry touches; purple squares mark exit signals. Arrows and circles mark next-bar-open fills. An entry fill can land near the middle band even though its signal candle touched an outer band.`
                        : kaufmanRangeRegimeMethod === "gmm"
                          ? "Causal GMM fits the first 200 feature bars, then assigns probability-weighted static clusters to later bars. It has no transition model; compare its state changes and probabilities with HMM."
                        : kaufmanRangeRegimeMethod === "bocpd"
                          ? "BOCPD estimates the chance that rolling log volatility changed, using only observations up to each candle. Red bands mark alarms; this is a change detector, not a Range/Trend classifier."
                        : kaufmanRangeRegimeMethod === "composite"
                          ? "D composite combines Hurst, variance ratio, efficiency ratio, and ADX votes with a hysteresis state machine. Backtests use the prior candle’s regime."
                          : `Trend at or above ${kaufmanRangeTrendThreshold.toFixed(2)} · Range at or below ${kaufmanRangeRangeThreshold.toFixed(2)} · between them is mixed.`
                      : "Default values follow common trading presets."}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setActiveIndicatorKey(null)}
                    style={{
                      ...iconButtonStyle(false),
                      minHeight: "34px",
                      padding: "6px 12px",
                    }}
                    title="Close settings (indicator stays on)"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      activeIndicator.onToggle(false);
                      setActiveIndicatorKey(null);
                    }}
                    style={iconButtonStyle(false, "red")}
                    title={`Turn off ${activeIndicator.fullLabel}`}
                  >
                    Turn off
                  </button>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                  gap: "8px",
                }}
              >
                {activeIndicator.key === "patterns" ? (
                  <div
                    style={{
                      display: "grid",
                      gap: "8px",
                      gridColumn: "1 / -1",
                    }}
                  >
                    {Object.entries(patternToggles).map(([pk, val]) => (
                      <label
                        key={pk}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          color: palette.mutedText,
                          fontSize: "0.74rem",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={val}
                          onChange={(e) =>
                            setPatternToggles((prev) => ({
                              ...prev,
                              [pk]: e.target.checked,
                            }))
                          }
                        />
                        {pk}
                      </label>
                    ))}
                  </div>
                ) : (
                  <>
                  {activeIndicator.key === "of:trader" ? (
                    <div style={{ display: "grid", gap: "6px", gridColumn: "1 / -1" }}>
                      <div style={{ color: palette.mutedText, fontSize: "0.7rem" }}>
                        {ofData?.dataNote || "Bid/ask quote updates only; buy/sell direction is estimated by tick rule, not verified trade aggressor volume."}
                      </div>
                      {ORDERFLOW_SIGNALS.map((sig) => (
                        <label
                          key={sig.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: palette.mutedText,
                            fontSize: "0.74rem",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={ofSignals.has(sig.key)}
                            onChange={(e) =>
                              setOfSignals((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(sig.key);
                                else next.delete(sig.key);
                                return next;
                              })
                            }
                          />
                          {sig.label}
                        </label>
                      ))}
                    </div>
                  ) : null}
                  {activeIndicator.key === "kaufmanRange" && kaufmanRangeRegimeMethod === "hmm" ? (
                    <div style={{ gridColumn: "1 / -1", padding: "9px 10px", border: `1px solid ${palette.borderSoft}`, borderRadius: "9px", color: palette.mutedText, fontSize: "0.72rem", lineHeight: 1.5 }} role="status">
                      <strong style={{ color: palette.shellText }}>{hmmModelMode === "causal200" ? "Causal HMM · 200-feature calibration" : "HMM fit + forward-filtered holdout"}</strong>
                      {hmmRegimeResult.status === "ready" ? (
                        hmmModelMode === "causal200" ? (
                          <div>The first {hmmRegimeResult.causalWarmupBars.toLocaleString()} feature bars calibrate the model. States after calibration are forward-filtered and can be used by the Causal 200 backtest without future candles. {hmmRegimeResult.usesSavedBacktestFit ? "Overlapping candles reuse the exact saved HMM + Bollinger backtest states." : "Chart and Backtest states match when symbol, timeframe, lookback, and model mode match."} The 70/30 training split does not affect this mode.</div>
                        ) : (
                          <div>Fit / holdout split {Math.round(hmmRegimeResult.trainingFraction * 100)}/{100 - Math.round(hmmRegimeResult.trainingFraction * 100)}. The first {hmmRegimeResult.trainingBars.toLocaleString()} feature bars fit the model; their state labels are retrospective and for context only. The final {hmmRegimeResult.validationBars.toLocaleString()} bars are forward-filtered and are the only HMM states used by holdout backtests. {hmmRegimeResult.usesSavedBacktestFit ? "Overlapping candles reuse the exact saved HMM + Bollinger backtest fit." : "A chart and backtest fit match when their symbol, timeframe, lookback, split, and loaded candle history match."} Holdout average log score {hmmRegimeResult.holdoutLogScore.toFixed(3)} vs one-state baseline {hmmRegimeResult.baselineLogScore.toFixed(3)} ({hmmRegimeResult.scoreDelta >= 0 ? "+" : ""}{hmmRegimeResult.scoreDelta.toFixed(3)} per bar). Assumes 3 states (Range, Trend, High volatility) from rolling log volatility and directional efficiency; this score does not establish trading performance.</div>
                        )
                      ) : <div>{hmmRegimeResult.reason}</div>}
                      {hmmRegimeResult.status === "ready" && selectedHmmProbabilities ? (
                        <div style={{ marginTop: 5 }}>
                          Current forward-filtered probabilities: Range {(selectedHmmProbabilities.range * 100).toFixed(1)}% · Trend {(selectedHmmProbabilities.trend * 100).toFixed(1)}% · High volatility {(selectedHmmProbabilities.highVol * 100).toFixed(1)}%.
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {activeIndicator.key === "kaufmanRange" && kaufmanRangeRegimeMethod === "gmm" ? (
                    <div style={{ gridColumn: "1 / -1", padding: "9px 10px", border: `1px solid ${palette.borderSoft}`, borderRadius: "9px", color: palette.mutedText, fontSize: "0.72rem", lineHeight: 1.5 }} role="status">
                      <strong style={{ color: palette.shellText }}>Causal GMM · static clusters</strong>
                      {gmmRegimeResult.status === "ready" ? (
                        <div>The first {gmmRegimeResult.calibrationFeatures} feature bars fit three Gaussian clusters; later candles are classified with cluster probabilities. GMM has no transition model, so state labels may switch more often than HMM. Current cluster probabilities: Range {(gmmRegimeResult.bars.at(-1)?.probabilities?.range * 100 || 0).toFixed(1)}% · Trend {(gmmRegimeResult.bars.at(-1)?.probabilities?.trend * 100 || 0).toFixed(1)}% · High volatility {(gmmRegimeResult.bars.at(-1)?.probabilities?.highVol * 100 || 0).toFixed(1)}%.</div>
                      ) : <div>{gmmRegimeResult.reason}</div>}
                    </div>
                  ) : null}
                  {activeIndicator.key === "kaufmanRange" && kaufmanRangeRegimeMethod === "bocpd" ? (
                    <div style={{ gridColumn: "1 / -1", padding: "9px 10px", border: `1px solid ${palette.borderSoft}`, borderRadius: "9px", color: palette.mutedText, fontSize: "0.72rem", lineHeight: 1.5 }} role="status">
                      <strong style={{ color: palette.shellText }}>Causal BOCPD · volatility change alarm</strong>
                      {bocpdRegimeResult.status === "ready" ? (
                        <div>Uses a {bocpdRegimeResult.expectedRunLength}-feature expected run length and alarms at {(bocpdRegimeResult.threshold * 100).toFixed(0)}% change probability. It marks possible breaks; it does not label Range, Trend, or High volatility. Current change probability: {((bocpdRegimeResult.bars.at(-1)?.changeProbability || 0) * 100).toFixed(1)}%.</div>
                      ) : <div>{bocpdRegimeResult.reason}</div>}
                    </div>
                  ) : null}
                  {activeIndicator.key === "kaufmanRange" && kaufmanRangeRegimeMethod === "composite" ? (
                    <div style={{ gridColumn: "1 / -1", padding: "9px 10px", border: `1px solid ${palette.borderSoft}`, borderRadius: "9px", color: palette.mutedText, fontSize: "0.72rem", lineHeight: 1.5 }} role="status">
                      <strong style={{ color: palette.shellText }}>D composite · Hurst, variance ratio, efficiency ratio, and ADX</strong>
                      {dRegimeResult.status === "ready" ? (
                        <div>
                          {dRegimeResult.readyBars.toLocaleString()} of {visibleData.length.toLocaleString()} candles scored; {dRegimeResult.warmupBars.toLocaleString()} warm-up candles. Current chart shading follows the composite trend / range state. Hurst, VR, and efficiency cutoffs are recalibrated with a deterministic 50,000-bar random walk when their settings change.
                        </div>
                      ) : <div>{dRegimeResult.reason}</div>}
                      <div style={{ marginTop: "4px" }}>
                        The supplied script’s defaults were tuned on a different configuration. Validate them on the selected symbol and timeframe before using the regime as a trading filter.
                      </div>
                    </div>
                  ) : null}
                  {activeIndicator.settings
                    .filter((setting) => {
                      if (activeIndicator.key !== "kaufmanRange") return true;
                      if (kaufmanRangeRegimeMethod === "hmm") {
                        return !["displayMode", "trendThreshold", "rangeThreshold", "smooth"].includes(setting.key)
          && !setting.key.startsWith("d_")
                          && !setting.key.startsWith("gmm")
                          && !setting.key.startsWith("bocpd")
          && (setting.key !== "hmmTrainingSplit" || hmmModelMode === "holdout");
                      }
                      if (kaufmanRangeRegimeMethod === "gmm") {
                        return !["displayMode", "trendThreshold", "rangeThreshold", "smooth"].includes(setting.key)
                          && !setting.key.startsWith("hmm") && !setting.key.startsWith("d_") && !setting.key.startsWith("bocpd");
                      }
                      if (kaufmanRangeRegimeMethod === "bocpd") {
                        return !["displayMode", "trendThreshold", "rangeThreshold", "smooth"].includes(setting.key)
                          && !setting.key.startsWith("hmm") && !setting.key.startsWith("d_") && !setting.key.startsWith("gmm");
                      }
                      if (kaufmanRangeRegimeMethod === "composite") {
                        return !["displayMode", "trendThreshold", "rangeThreshold", "period", "smooth"].includes(setting.key)
                          && !setting.key.startsWith("hmm") && !setting.key.startsWith("gmm") && !setting.key.startsWith("bocpd");
                      }
                      return !setting.key.startsWith("d_") && !setting.key.startsWith("hmm") && !setting.key.startsWith("gmm") && !setting.key.startsWith("bocpd");
                    })
                    .map((setting) => (
                  <label
                    key={`${activeIndicator.key}-${setting.key}`}
                    style={{
                      display: "grid",
                      gap: "6px",
                      color: palette.mutedText,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: isSpaceChrome ? "none" : "uppercase",
                    }}
                  >
                    {setting.label}
                    {setting.type === "select" ? (
                      <select
                        value={setting.value}
                        onChange={(e) => setting.onChange(e.target.value)}
                        style={{ ...inputStyle, width: "100%", minHeight: "34px", padding: "7px 10px" }}
                      >
                        {setting.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    ) : (
                      <input
                        type="number"
                        min={setting.min}
                        max={setting.max}
                        step={setting.step ?? 1}
                        value={setting.value}
                        onChange={(e) => setting.onChange(e.target.value)}
                        onBlur={setting.onApply}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setting.onApply();
                        }}
                        style={{ ...inputStyle, width: "100%", minHeight: "34px", padding: "7px 10px" }}
                      />
                    )}
                  </label>
                  ))}
                  </>
                )}
                {activeIndicatorSupportsLineSize ? (
                  <label
                    key={`${activeIndicator.key}-line-width`}
                    style={{
                      display: "grid",
                      gap: "6px",
                      color: palette.mutedText,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: isSpaceChrome ? "none" : "uppercase",
                    }}
                  >
                    Line size
                    <select
                      value={getIndicatorLineWidth(activeIndicator.key)}
                      onChange={(e) =>
                        handleIndicatorLineWidthChange(activeIndicator.key, e.target.value)
                      }
                      style={{
                        ...inputStyle,
                        width: "100%",
                        minHeight: "34px",
                        padding: "7px 10px",
                        cursor: "pointer",
                      }}
                    >
                      {INDICATOR_LINE_WIDTH_OPTIONS.map((width) => (
                        <option key={`${activeIndicator.key}-line-width-${width}`} value={width}>
                          {width}px
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {activeIndicator.colorKeyUp && activeIndicator.colorKeyDown ? (
                  <>
                    {activeIndicator.colorKeyNeutral ? (
                      <label
                        key="dual-color-neutral"
                        style={{
                          display: "grid",
                          gap: "6px",
                          color: palette.mutedText,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: isSpaceChrome ? "none" : "uppercase",
                        }}
                      >
                        {activeIndicator.colorLabelNeutral ?? "Neutral line"}
                        <input
                          type="color"
                          value={getIndicatorColor(activeIndicator.colorKeyNeutral)}
                          onChange={(e) =>
                            handleIndicatorColorChange(
                              activeIndicator.colorKeyNeutral,
                              e.target.value
                            )
                          }
                          style={{
                            width: "100%",
                            minHeight: "34px",
                            padding: "2px",
                            borderRadius: "8px",
                            border: `1px solid ${palette.borderStrong}`,
                            background: palette.inputBackground,
                            cursor: "pointer",
                          }}
                        />
                      </label>
                    ) : null}
                    <label
                      key="dual-color-up"
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      {activeIndicator.colorLabelUp ?? "Uptrend line"}
                      <input
                        type="color"
                        value={getIndicatorColor(activeIndicator.colorKeyUp)}
                        onChange={(e) =>
                          handleIndicatorColorChange(activeIndicator.colorKeyUp, e.target.value)
                        }
                        style={{
                          width: "100%",
                          minHeight: "34px",
                          padding: "2px",
                          borderRadius: "8px",
                          border: `1px solid ${palette.borderStrong}`,
                          background: palette.inputBackground,
                          cursor: "pointer",
                        }}
                      />
                    </label>
                    <label
                      key="dual-color-down"
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      {activeIndicator.colorLabelDown ?? "Downtrend line"}
                      <input
                        type="color"
                        value={getIndicatorColor(activeIndicator.colorKeyDown)}
                        onChange={(e) =>
                          handleIndicatorColorChange(activeIndicator.colorKeyDown, e.target.value)
                        }
                        style={{
                          width: "100%",
                          minHeight: "34px",
                          padding: "2px",
                          borderRadius: "8px",
                          border: `1px solid ${palette.borderStrong}`,
                          background: palette.inputBackground,
                          cursor: "pointer",
                        }}
                      />
                    </label>
                  </>
                ) : activeIndicator.key !== "patterns" ? (
                  <label
                    key={`${activeIndicator.key}-color`}
                    style={{
                      display: "grid",
                      gap: "6px",
                      color: palette.mutedText,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: isSpaceChrome ? "none" : "uppercase",
                    }}
                  >
                    Color
                    <input
                      type="color"
                      value={getIndicatorColor(activeIndicator.key)}
                      onChange={(e) =>
                        handleIndicatorColorChange(activeIndicator.key, e.target.value)
                      }
                      style={{
                        width: "100%",
                        minHeight: "34px",
                        padding: "2px",
                        borderRadius: "8px",
                        border: `1px solid ${palette.borderStrong}`,
                        background: palette.inputBackground,
                        cursor: "pointer",
                      }}
                    />
                  </label>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {activePanel === "settings" ? (
          <div
            style={{
              ...overlayPanelStyle,
              width: "min(360px, calc(100% - 32px))",
              display: "flex",
              flexDirection: "column",
              maxHeight: "min(72vh, calc(100vh - 96px))",
              ...mobilePanelSheet,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexShrink: 0,
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  color: palette.shellText,
                  fontSize: "0.95rem",
                  fontWeight: 800,
                }}
              >
                <span style={{ display: "flex", color: palette.accentText }}>
                  <SettingsGearIcon size={22} />
                </span>
                Settings
              </div>
              <button
                type="button"
                onClick={() => setActivePanel("none")}
                style={{
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  color: palette.mutedText,
                  fontSize: "1rem",
                  cursor: "pointer",
                }}
              >
                x
              </button>
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: "6px",
                WebkitOverflowScrolling: "touch",
              }}
            >
            <div
              style={{
                display: "grid",
                gap: "12px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Theme
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onThemeChange?.("white")}
                    style={{
                      ...actionButtonStyle,
                      minHeight: "32px",
                      padding: "6px 10px",
                      border: isWhiteTheme
                        ? `1px solid ${palette.activeBorder}`
                        : actionButtonStyle.border,
                      background: isWhiteTheme
                        ? palette.buttonActiveBackground
                        : actionButtonStyle.background,
                    }}
                  >
                    White
                  </button>
                  <button
                    type="button"
                    onClick={() => onThemeChange?.("black")}
                    style={{
                      ...actionButtonStyle,
                      minHeight: "32px",
                      padding: "6px 10px",
                      border: isBlackTheme
                        ? `1px solid ${palette.activeBorder}`
                        : actionButtonStyle.border,
                      background: isBlackTheme
                        ? palette.buttonActiveBackground
                        : actionButtonStyle.background,
                    }}
                  >
                    Black
                  </button>
                  <button
                    type="button"
                    onClick={() => onThemeChange?.("navy")}
                    style={{
                      ...actionButtonStyle,
                      minHeight: "32px",
                      padding: "6px 10px",
                      border: isNavyTheme
                        ? `1px solid ${palette.activeBorder}`
                        : actionButtonStyle.border,
                      background: isNavyTheme
                        ? palette.buttonActiveBackground
                        : actionButtonStyle.background,
                    }}
                  >
                    Navy
                  </button>
                  <button
                    type="button"
                    onClick={() => onThemeChange?.("tradingview")}
                    title="TradingView-style dark theme (teal/red candles, blue accents)"
                    style={{
                      ...actionButtonStyle,
                      minHeight: "32px",
                      padding: "6px 10px",
                      border: isTradingViewTheme
                        ? `1px solid ${palette.activeBorder}`
                        : actionButtonStyle.border,
                      background: isTradingViewTheme
                        ? palette.buttonActiveBackground
                        : actionButtonStyle.background,
                    }}
                  >
                    TradingView
                  </button>
                  <button
                    type="button"
                    onClick={() => onThemeChange?.("chartx")}
                    title="ChartX — original dark theme (green/red candles, blue accent)"
                    style={{
                      ...actionButtonStyle,
                      minHeight: "32px",
                      padding: "6px 10px",
                      border: isChartXTheme
                        ? `1px solid ${palette.activeBorder}`
                        : actionButtonStyle.border,
                      background: isChartXTheme
                        ? palette.buttonActiveBackground
                        : actionButtonStyle.background,
                    }}
                  >
                    ChartX
                  </button>
                  <button
                    type="button"
                    onClick={() => onThemeChange?.("instrument")}
                    title="Space: deep-space background, soft blue/orange candles"
                    style={{
                      ...actionButtonStyle,
                      minHeight: "32px",
                      padding: "6px 10px",
                      border: isInstrumentTheme
                        ? `1px solid ${palette.activeBorder}`
                        : actionButtonStyle.border,
                      background: isInstrumentTheme
                        ? palette.buttonActiveBackground
                        : actionButtonStyle.background,
                    }}
                  >
                    Space
                  </button>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Chart display
                </div>
                <label
                  style={{
                    display: "grid",
                    gap: "6px",
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Timeframe
                  <select
                    value={chartTimeframeId}
                    onChange={(e) => setChartTimeframeId(e.target.value)}
                    style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                  >
                    {TIMEFRAME_OPTIONS.map((tf) => (
                      <option key={tf.id} value={tf.id}>
                        {tf.label}
                      </option>
                    ))}
                  </select>
                </label>
                {chartTimeframeId === CUSTOM_TIMEFRAME_ID ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px",
                    }}
                  >
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Custom length
                      <input
                        type="text"
                        inputMode="decimal"
                        value={customTimeframeInput}
                        onChange={(e) => setCustomTimeframeInput(e.target.value)}
                        placeholder="e.g. 7"
                        style={inputStyle}
                      />
                    </label>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Unit
                      <select
                        value={customTimeframeUnit}
                        onChange={(e) => setCustomTimeframeUnit(e.target.value)}
                        style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                      >
                        {CUSTOM_TIMEFRAME_UNITS.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ) : null}
                <label
                  style={{
                    display: "grid",
                    gap: "6px",
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Crosshair mode
                  <select
                    value={chartCrosshairMode}
                    onChange={(e) => setChartCrosshairMode(e.target.value)}
                    style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                  >
                    {CHART_CROSSHAIR_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label
                  style={{
                    display: "grid",
                    gap: "6px",
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Main price scale
                  <select
                    value={mainPriceScaleMode}
                    onChange={(e) => setMainPriceScaleMode(e.target.value)}
                    style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                  >
                    {CHART_PRICE_SCALE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                  marginTop: "8px",
                }}
              >
                <label
                  style={{
                    display: "grid",
                    gap: "6px",
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Y-Axis Position
                  <select
                    value={yAxisSide}
                    onChange={(e) => setYAxisSide(e.target.value)}
                    style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                  >
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </label>
                <label
                  style={{
                    display: "grid",
                    gap: "6px",
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Timezone
                  <select
                    value={chartTimeZone}
                    onChange={(e) => setChartTimeZone(e.target.value)}
                    style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                  >
                    {chartTimeZoneOptions.map((timeZone) => (
                      <option key={timeZone.value} value={timeZone.value}>
                        {timeZone.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div
                style={{
                  display: "grid",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Chart grid
                </div>
                <div ref={chartGridSettingsWrapRef} style={{ position: "relative", width: "100%" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSettingsChartGridMenuOpen((open) => !open);
                      setChartGridMenuOpen(false);
                    }}
                    style={{
                      ...actionButtonStyle,
                      width: "100%",
                      minHeight: "38px",
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      border: settingsChartGridMenuOpen
                        ? `1px solid ${palette.activeBorder}`
                        : actionButtonStyle.border,
                      background: settingsChartGridMenuOpen
                        ? palette.buttonActiveBackground
                        : actionButtonStyle.background,
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={settingsChartGridMenuOpen}
                  >
                    <span
                      style={{
                        color: palette.shellText,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        textAlign: "left",
                        lineHeight: 1.25,
                      }}
                    >
                      {getChartGridOptionLabel(chartBoxCount)}
                    </span>
                    <span style={{ opacity: 0.65, fontSize: "0.7rem", flexShrink: 0 }}>▾</span>
                  </button>
                  {settingsChartGridMenuOpen && settingsChartGridRect
                    ? createPortal(
                        <ul
                          ref={chartGridSettingsDropdownRef}
                          role="listbox"
                          style={{
                            position: "fixed",
                            top: settingsChartGridRect.bottom + 6,
                            left: settingsChartGridRect.left,
                            width: settingsChartGridRect.width,
                            zIndex: 10000,
                            listStyle: "none",
                            margin: 0,
                            padding: "6px 0",
                            borderRadius: "12px",
                            border: `1px solid ${palette.borderStrong}`,
                            background: palette.overlayBackground,
                            boxShadow: "0 18px 42px rgba(0, 0, 0, 0.38)",
                            backdropFilter: "blur(14px)",
                            boxSizing: "border-box",
                          }}
                        >
                          {CHART_GRID_SIZE_OPTIONS.map((n) => (
                            <li key={`settings-grid-${n}`} style={{ margin: 0, padding: 0 }}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={chartBoxCount === n}
                                onClick={() => {
                                  setChartBoxCount(n);
                                  setSettingsChartGridMenuOpen(false);
                                }}
                                style={chartGridMenuRowButtonStyle(chartBoxCount === n)}
                              >
                                {getChartGridOptionLabel(n)}
                              </button>
                            </li>
                          ))}
                        </ul>,
                        document.body
                      )
                    : null}
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Candle symbol chart type
                </div>
                <select
                  value={priceSeriesStyle}
                  onChange={(e) => setPriceSeriesStyle(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: "100%",
                    cursor: "pointer",
                  }}
                >
                  {PRICE_SERIES_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {priceSeriesStyle === PRICE_SERIES_STYLES.LINE ||
                priceSeriesStyle === PRICE_SERIES_STYLES.AREA ||
                priceSeriesStyle === PRICE_SERIES_STYLES.BASELINE ? (
                  <>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Line price source
                      <select
                        value={linePriceSource}
                        onChange={(e) => setLinePriceSource(e.target.value)}
                        style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                      >
                        {LINE_PRICE_SOURCE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {priceSeriesStyle === PRICE_SERIES_STYLES.BASELINE ? (
                      <label
                        style={{
                          display: "grid",
                          gap: "6px",
                          color: palette.mutedText,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: isSpaceChrome ? "none" : "uppercase",
                        }}
                      >
                        Baseline price (optional)
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="Defaults to first bar value"
                          value={baselinePriceInput}
                          onChange={(e) => setBaselinePriceInput(e.target.value)}
                          style={inputStyle}
                        />
                      </label>
                    ) : null}
                    {priceSeriesStyle === PRICE_SERIES_STYLES.LINE ||
                    priceSeriesStyle === PRICE_SERIES_STYLES.AREA ? (
                      <label
                        style={{
                          display: "grid",
                          gap: "6px",
                          color: palette.mutedText,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: isSpaceChrome ? "none" : "uppercase",
                        }}
                      >
                        Line style
                        <select
                          value={lineChartLineType}
                          onChange={(e) => setLineChartLineType(e.target.value)}
                          style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                        >
                          {LINE_CHART_LINE_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                  </>
                ) : null}
                {priceSeriesStyle === PRICE_SERIES_STYLES.RENKO ? (
                  <>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Brick size mode
                      <select
                        value={renkoBrickMode}
                        onChange={(e) => setRenkoBrickMode(e.target.value)}
                        style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                      >
                        <option value={RENKO_BRICK_MODE.FIXED}>Fixed (or auto median)</option>
                        <option value={RENKO_BRICK_MODE.ATR}>ATR-based</option>
                      </select>
                    </label>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Renko source
                      <select
                        value={renkoSource}
                        onChange={(e) => setRenkoSource(e.target.value)}
                        style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                      >
                        {LINE_PRICE_SOURCE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {renkoBrickMode === RENKO_BRICK_MODE.FIXED ? (
                      <label
                        style={{
                          display: "grid",
                          gap: "6px",
                          color: palette.mutedText,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: isSpaceChrome ? "none" : "uppercase",
                        }}
                      >
                        Renko brick (price, optional)
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder={`Auto ≈ ${defaultRenkoBrickSize(visibleData).toPrecision(4)}`}
                          value={renkoBrickInput}
                          onChange={(e) => setRenkoBrickInput(e.target.value)}
                          style={inputStyle}
                        />
                      </label>
                    ) : (
                      <label
                        style={{
                          display: "grid",
                          gap: "6px",
                          color: palette.mutedText,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          letterSpacing: "0.05em",
                          textTransform: isSpaceChrome ? "none" : "uppercase",
                        }}
                      >
                        ATR period
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={renkoAtrPeriodInput}
                          onChange={(e) => setRenkoAtrPeriodInput(e.target.value)}
                          style={inputStyle}
                        />
                      </label>
                    )}
                  </>
                ) : null}
                {priceSeriesStyle === PRICE_SERIES_STYLES.RANGE ? (
                  <>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Range size (price, optional)
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder={`Auto ≈ ${defaultRangeSize(visibleData).toPrecision(4)}`}
                        value={rangeSizeInput}
                        onChange={(e) => setRangeSizeInput(e.target.value)}
                        style={inputStyle}
                      />
                    </label>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Range direction source
                      <select
                        value={rangeSource}
                        onChange={(e) => setRangeSource(e.target.value)}
                        style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                      >
                        {LINE_PRICE_SOURCE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                ) : null}
                {priceSeriesStyle === PRICE_SERIES_STYLES.POINT_FIGURE ? (
                  <>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      P&amp;F box size (price, optional)
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder={`Auto ≈ ${defaultRangeSize(visibleData).toPrecision(4)}`}
                        value={pnfBoxInput}
                        onChange={(e) => setPnfBoxInput(e.target.value)}
                        style={inputStyle}
                      />
                    </label>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Reversal (boxes, optional)
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Default 3 (reserved for classic P&amp;F)"
                        value={pnfReversalInput}
                        onChange={(e) => setPnfReversalInput(e.target.value)}
                        style={inputStyle}
                      />
                    </label>
                  </>
                ) : null}
                {priceSeriesStyle === PRICE_SERIES_STYLES.LINE_BREAK ? (
                  <label
                    style={{
                      display: "grid",
                      gap: "6px",
                      color: palette.mutedText,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: isSpaceChrome ? "none" : "uppercase",
                    }}
                  >
                    Line break count
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={lineBreakLinesInput}
                      onChange={(e) => setLineBreakLinesInput(e.target.value)}
                      style={inputStyle}
                    />
                  </label>
                ) : null}
                {priceSeriesStyle === PRICE_SERIES_STYLES.KAGI ? (
                  <>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      Reversal mode
                      <select
                        value={kagiMode}
                        onChange={(e) => setKagiMode(e.target.value)}
                        style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
                      >
                        <option value={KAGI_MODE.ABSOLUTE}>Absolute (price)</option>
                        <option value={KAGI_MODE.PERCENT}>Percent of pivot</option>
                      </select>
                    </label>
                    <label
                      style={{
                        display: "grid",
                        gap: "6px",
                        color: palette.mutedText,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                      }}
                    >
                      {kagiMode === KAGI_MODE.PERCENT
                        ? "Kagi reversal (% , optional)"
                        : "Kagi reversal (price, optional)"}
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder={
                          kagiMode === KAGI_MODE.PERCENT
                            ? "e.g. 2.5 for 2.5%"
                            : `Auto ≈ ${defaultRangeSize(visibleData).toPrecision(4)}`
                        }
                        value={kagiReversalInput}
                        onChange={(e) => setKagiReversalInput(e.target.value)}
                        style={inputStyle}
                      />
                    </label>
                  </>
                ) : null}
              </div>
              <div
                style={{
                  display: "grid",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Candle Presets
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {CANDLE_COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => applyCandlePreset(preset)}
                      style={{
                        ...actionButtonStyle,
                        minHeight: "32px",
                        padding: "6px 10px",
                        border:
                          activeCandlePresetKey === preset.key
                            ? `1px solid ${palette.activeBorder}`
                            : actionButtonStyle.border,
                        background:
                          activeCandlePresetKey === preset.key
                            ? palette.buttonActiveBackground
                            : actionButtonStyle.background,
                        color:
                          activeCandlePresetKey === preset.key
                            ? palette.shellText
                            : palette.mutedText,
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              <label
                style={{
                  display: "grid",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: isSpaceChrome ? "none" : "uppercase",
                }}
              >
                Bullish Body
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "48px minmax(0, 1fr)",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="color"
                    value={bullishCandleBodyColor}
                    onChange={(e) => setBullishCandleBodyColor(e.target.value)}
                    style={{
                      width: "48px",
                      height: "36px",
                      padding: 0,
                      border: `1px solid ${palette.borderStrong}`,
                      borderRadius: "10px",
                      background: palette.inputBackground,
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    value={bullishCandleBodyColor}
                    onChange={(e) => setBullishCandleBodyColor(e.target.value)}
                    onBlur={() =>
                      setBullishCandleBodyColor((currentColor) =>
                        normalizeHexColor(currentColor, defaultBullishCandleBodyColor)
                      )
                    }
                    style={inputStyle}
                  />
                </div>
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: isSpaceChrome ? "none" : "uppercase",
                }}
              >
                Bullish Border
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "48px minmax(0, 1fr)",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="color"
                    value={bullishCandleBorderColor}
                    onChange={(e) => setBullishCandleBorderColor(e.target.value)}
                    style={{
                      width: "48px",
                      height: "36px",
                      padding: 0,
                      border: `1px solid ${palette.borderStrong}`,
                      borderRadius: "10px",
                      background: palette.inputBackground,
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    value={bullishCandleBorderColor}
                    onChange={(e) => setBullishCandleBorderColor(e.target.value)}
                    onBlur={() =>
                      setBullishCandleBorderColor((currentColor) =>
                        normalizeHexColor(currentColor, defaultBullishCandleBorderColor)
                      )
                    }
                    style={inputStyle}
                  />
                </div>
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: isSpaceChrome ? "none" : "uppercase",
                }}
              >
                Bullish Wick
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "48px minmax(0, 1fr)",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="color"
                    value={bullishCandleWickColor}
                    onChange={(e) => setBullishCandleWickColor(e.target.value)}
                    style={{
                      width: "48px",
                      height: "36px",
                      padding: 0,
                      border: `1px solid ${palette.borderStrong}`,
                      borderRadius: "10px",
                      background: palette.inputBackground,
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    value={bullishCandleWickColor}
                    onChange={(e) => setBullishCandleWickColor(e.target.value)}
                    onBlur={() =>
                      setBullishCandleWickColor((currentColor) =>
                        normalizeHexColor(currentColor, defaultBullishCandleWickColor)
                      )
                    }
                    style={inputStyle}
                  />
                </div>
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: isSpaceChrome ? "none" : "uppercase",
                }}
              >
                Bearish Body
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "48px minmax(0, 1fr)",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="color"
                    value={bearishCandleBodyColor}
                    onChange={(e) => setBearishCandleBodyColor(e.target.value)}
                    style={{
                      width: "48px",
                      height: "36px",
                      padding: 0,
                      border: `1px solid ${palette.borderStrong}`,
                      borderRadius: "10px",
                      background: palette.inputBackground,
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    value={bearishCandleBodyColor}
                    onChange={(e) => setBearishCandleBodyColor(e.target.value)}
                    onBlur={() =>
                      setBearishCandleBodyColor((currentColor) =>
                        normalizeHexColor(currentColor, defaultBearishCandleBodyColor)
                      )
                    }
                    style={inputStyle}
                  />
                </div>
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: isSpaceChrome ? "none" : "uppercase",
                }}
              >
                Bearish Border
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "48px minmax(0, 1fr)",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="color"
                    value={bearishCandleBorderColor}
                    onChange={(e) => setBearishCandleBorderColor(e.target.value)}
                    style={{
                      width: "48px",
                      height: "36px",
                      padding: 0,
                      border: `1px solid ${palette.borderStrong}`,
                      borderRadius: "10px",
                      background: palette.inputBackground,
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    value={bearishCandleBorderColor}
                    onChange={(e) => setBearishCandleBorderColor(e.target.value)}
                    onBlur={() =>
                      setBearishCandleBorderColor((currentColor) =>
                        normalizeHexColor(currentColor, defaultBearishCandleBorderColor)
                      )
                    }
                    style={inputStyle}
                  />
                </div>
              </label>
              <label
                style={{
                  display: "grid",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: isSpaceChrome ? "none" : "uppercase",
                }}
              >
                Bearish Wick
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "48px minmax(0, 1fr)",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="color"
                    value={bearishCandleWickColor}
                    onChange={(e) => setBearishCandleWickColor(e.target.value)}
                    style={{
                      width: "48px",
                      height: "36px",
                      padding: 0,
                      border: `1px solid ${palette.borderStrong}`,
                      borderRadius: "10px",
                      background: palette.inputBackground,
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    value={bearishCandleWickColor}
                    onChange={(e) => setBearishCandleWickColor(e.target.value)}
                    onBlur={() =>
                      setBearishCandleWickColor((currentColor) =>
                        normalizeHexColor(currentColor, defaultBearishCandleWickColor)
                      )
                    }
                    style={inputStyle}
                  />
                </div>
              </label>
              <div
                style={{
                  display: "grid",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: isSpaceChrome ? "none" : "uppercase",
                  }}
                >
                  Chart background
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {CHART_BACKGROUND_PRESETS.map((bgPreset) => (
                    <button
                      key={bgPreset.key}
                      type="button"
                      title={bgPreset.label}
                      aria-label={`Chart background ${bgPreset.label}`}
                      onClick={() => setChartBackgroundColor(bgPreset.color)}
                      style={{
                        width: "36px",
                        height: "36px",
                        padding: 0,
                        borderRadius: "10px",
                        border:
                          activeChartBackgroundPresetKey === bgPreset.key
                            ? `2px solid ${palette.activeBorder}`
                            : `1px solid ${palette.borderStrong}`,
                        background: bgPreset.color,
                        cursor: "pointer",
                        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
                      }}
                    />
                  ))}
                </div>
              </div>
              <label
                style={{
                  display: "grid",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: isSpaceChrome ? "none" : "uppercase",
                }}
              >
                Chart background (custom)
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "48px minmax(0, 1fr)",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="color"
                    value={chartBackgroundColor}
                    onChange={(e) => setChartBackgroundColor(e.target.value)}
                    style={{
                      width: "48px",
                      height: "36px",
                      padding: 0,
                      border: `1px solid ${palette.borderStrong}`,
                      borderRadius: "10px",
                      background: palette.inputBackground,
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    value={chartBackgroundColor}
                    onChange={(e) => setChartBackgroundColor(e.target.value)}
                    onBlur={() =>
                      setChartBackgroundColor((currentColor) =>
                        normalizeHexColor(currentColor, defaultChartBackgroundColor)
                      )
                    }
                    style={inputStyle}
                  />
                </div>
              </label>
              <button
                type="button"
                onClick={resetCandleColors}
                style={actionButtonStyle}
              >
                Reset Candle Colors
              </button>
            </div>
            </div>
          </div>
        ) : null}

        {activePanel === "script" ? (
          <div
            style={{
              ...overlayPanelStyle,
              width: "min(560px, calc(100% - 32px))",
              maxWidth: "min(560px, calc(100% - 32px))",
              display: "flex",
              flexDirection: "column",
              maxHeight: "min(78vh, calc(100vh - 96px))",
              gap: "10px",
              ...mobilePanelSheet,
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexShrink: 0,
              }}
            >
              <div>
                <div style={{ color: palette.shellText, fontSize: "0.95rem", fontWeight: 800 }}>
                  Script
                </div>
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.68rem",
                    marginTop: "4px",
                    lineHeight: 1.45,
                  }}
                >
                  Pine-style body: use <code style={{ color: palette.shellText }}>candles</code> and{" "}
                  <code style={{ color: palette.shellText }}>helpers</code> (sma, ema, wma, rsi, closes…).
                  Return{" "}
                  <code style={{ color: palette.shellText }}>{`{ pane, series }`}</code> or an array of{" "}
                  <code style={{ color: palette.shellText }}>{`{ time, value }`}</code>. Runs in this tab — do
                  not paste untrusted code; execution happens in a network-disabled isolated worker.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActivePanel("none")}
                style={{
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  color: palette.mutedText,
                  fontSize: "1rem",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                x
              </button>
            </div>
            <textarea
              value={userScriptCode}
              onChange={(e) => setUserScriptCode(e.target.value)}
              spellCheck={false}
              aria-label="Custom indicator script"
              style={{
                ...inputStyle,
                width: "100%",
                minHeight: "200px",
                maxHeight: "42vh",
                resize: "vertical",
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                fontSize: "0.78rem",
                lineHeight: 1.45,
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <button type="button" onClick={handleUserScriptRun} disabled={userScriptRunning} style={actionButtonStyle}>
                {userScriptRunning ? "Running…" : "Run"}
              </button>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={userScriptEnabled}
                  onChange={(e) => setUserScriptEnabled(e.target.checked)}
                />
                Show on chart
              </label>
              <button
                type="button"
                onClick={() => {
                  setUserScriptEnabled(false);
                  setUserScriptOutput(null);
                  setUserScriptError(null);
                }}
                style={{
                  ...actionButtonStyle,
                  background: palette.chipBackground,
                  color: palette.shellText,
                }}
              >
                Clear
              </button>
            </div>
            {userScriptError ? (
              <div
                style={{
                  color: "#f87171",
                  fontSize: "0.76rem",
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                }}
              >
                {userScriptError}
              </div>
            ) : null}
          </div>
        ) : null}

        {activePanel === "ai" ? (
          <div
            style={{
              ...overlayPanelStyle,
              width: "min(520px, calc(100% - 32px))",
              maxWidth: "min(520px, calc(100% - 32px))",
              display: "flex",
              flexDirection: "column",
              maxHeight: "min(78vh, calc(100vh - 96px))",
              gap: "10px",
              ...mobilePanelSheet,
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexShrink: 0,
              }}
            >
              <div>
                <div style={{ color: palette.shellText, fontSize: "0.95rem", fontWeight: 800 }}>
                  Chart assistant
                </div>
                <div
                  style={{
                    color: palette.mutedText,
                    fontSize: "0.68rem",
                    marginTop: "4px",
                    lineHeight: 1.45,
                  }}
                >
                  Uses your backend <code style={{ color: palette.shellText }}>POST /api/v1/chat</code>{" "}
                  (Anthropic). Set <code style={{ color: palette.shellText }}>ANTHROPIC_API_KEY</code> in{" "}
                  <code style={{ color: palette.shellText }}>backend/.env</code>. Not financial advice.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActivePanel("none")}
                style={{
                  appearance: "none",
                  border: "none",
                  background: "transparent",
                  color: palette.mutedText,
                  fontSize: "1rem",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                x
              </button>
            </div>
            <div
              style={{
                flex: 1,
                minHeight: "120px",
                maxHeight: "38vh",
                overflowY: "auto",
                padding: "10px 12px",
                borderRadius: "12px",
                border: `1px solid ${palette.borderSoft}`,
                background: palette.chipBackground,
                fontSize: "0.78rem",
                lineHeight: 1.5,
                color: palette.shellText,
              }}
            >
              {aiChatMessages.length === 0 ? (
                <span style={{ color: palette.mutedText }}>
                  Ask about the chart, structure, or indicators. Enable context to send the last ~80
                  visible bars (OHLC).
                </span>
              ) : (
                aiChatMessages.map((m, i) => (
                  <div
                    key={`ai-msg-${i}`}
                    style={{
                      marginBottom: "10px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 800,
                        color: m.role === "user" ? palette.accentText : palette.mutedText,
                      }}
                    >
                      {m.role === "user" ? "You" : "Assistant"}
                    </span>
                    {": "}
                    {m.content}
                  </div>
                ))
              )}
            </div>
            {aiChatError ? (
              <div
                style={{
                  color: "#f87171",
                  fontSize: "0.76rem",
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                }}
              >
                {aiChatError}
              </div>
            ) : null}
            <textarea
              value={aiChatInput}
              onChange={(e) => setAiChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendAiChat();
                }
              }}
              placeholder="Message… (Enter to send, Shift+Enter for newline)"
              spellCheck
              aria-label="AI chat message"
              style={{
                ...inputStyle,
                width: "100%",
                minHeight: "72px",
                maxHeight: "24vh",
                resize: "vertical",
                fontSize: "0.82rem",
                lineHeight: 1.45,
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <button
                type="button"
                onClick={sendAiChat}
                disabled={aiChatLoading}
                style={{
                  ...actionButtonStyle,
                  opacity: aiChatLoading ? 0.65 : 1,
                  pointerEvents: aiChatLoading ? "none" : "auto",
                }}
              >
                {aiChatLoading ? "Sending…" : "Send"}
              </button>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  color: palette.mutedText,
                  fontSize: "0.72rem",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={aiIncludeContext}
                  onChange={(e) => setAiIncludeContext(e.target.checked)}
                />
                Include last ~80 bars (OHLC)
              </label>
              <button
                type="button"
                onClick={() => {
                  setAiChatMessages([]);
                  setAiChatError(null);
                }}
                style={{
                  ...actionButtonStyle,
                  background: palette.chipBackground,
                  color: palette.shellText,
                }}
              >
                Clear chat
              </button>
            </div>
          </div>
        ) : null}

        {drawTool !== "none" && !isMobile ? (
          <div
            style={{
              position: "absolute",
              right: isSpaceChrome ? SPACE_RAIL_W + 14 : "14px",
              bottom: "56px",
              zIndex: 2,
              background: palette.replayBackground,
              border: `1px solid ${palette.borderSoft}`,
              borderRadius: isSpaceChrome ? 4 : "14px",
              padding: "8px 12px",
              textAlign: "left",
              fontSize: "12px",
              lineHeight: 1.45,
              pointerEvents: "none",
              color: palette.shellText,
              minWidth: "210px",
              boxShadow: isSpaceChrome ? "none" : "0 12px 30px rgba(2, 6, 23, 0.36)",
            }}
          >
            <div>
              <strong>{toolDefinitions.find((t) => t.key === drawTool)?.label || drawTool}</strong>
              {drawPrefs.magnet !== "off" ? <span style={{ color: palette.mutedText }}> · magnet {drawPrefs.magnet}</span> : null}
            </div>
            <div style={{ color: palette.mutedText }}>
              {drawTool === "rr"
                ? "Click entry, then stop loss, then take profit (or drag from entry to stop)."
                : drawTool === "channel"
                  ? "Click the start and end of the main line (or drag it), then click to set the width."
                  : drawTool === "text"
                    ? "Click where the label goes."
                    : drawTool === "brush"
                      ? "Press and drag to paint."
                      : drawTool === "hline" || drawTool === "vline"
                        ? "Click to place the line."
                        : "Click the start and end points, or press and drag."}
            </div>
            <div style={{ color: palette.mutedText, fontSize: 11 }}>Esc or right-click cancels.</div>
          </div>
        ) : null}

        {/* Phone drawing bar (TradingView style): tool, hint, magnet, stay, undo, done. */}
        {isMobile && drawTool !== "none" ? (
          <div
            role="toolbar"
            aria-label="Drawing options"
            style={{
              position: "absolute",
              left: 8,
              right: 8,
              bottom: MOBILE_STAGE_BOTTOM + 8,
              zIndex: 7,
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: 6,
              borderRadius: 8,
              border: `1px solid ${palette.borderSoft}`,
              background: palette.chartBackground || palette.replayBackground,
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
              userSelect: "none",
              WebkitUserSelect: "none",
            }}
          >
            <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: palette.shellText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {toolDefinitions.find((t) => t.key === drawTool)?.label || drawTool}
              </div>
              <div style={{ fontSize: 11, color: palette.mutedText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {drawTool === "rr"
                    ? (drawRrStopPoint ? "Tap the take profit" : drawStartPoint ? "Tap the stop loss" : "Tap the entry, or drag entry to stop")
                    : drawTool === "channel"
                      ? (drawRrStopPoint ? "Tap to set the width" : drawStartPoint ? "Tap the end of the line" : "Tap the start, or drag the line")
                      : drawTool === "text"
                        ? "Tap where the label goes"
                        : drawTool === "brush"
                          ? "Drag to paint"
                          : drawTool === "hline" || drawTool === "vline"
                            ? "Tap to place the line"
                            : drawStartPoint
                              ? "Tap the end point"
                              : "Tap two points, or drag"}
              </div>
            </div>
            {drawingOptionButtons
              .filter((b) => b.key === "magnet" || b.key === "stay" || b.key === "undo")
              .map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={b.onClick}
                  disabled={b.disabled}
                  title={b.title}
                  aria-label={b.label}
                  aria-pressed={b.pressed}
                  style={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    borderRadius: 6,
                    border: `1px solid ${b.pressed ? palette.activeBorder : palette.borderSoft}`,
                    background: b.pressed ? palette.buttonActiveBackground : "transparent",
                    color: palette.shellText,
                    opacity: b.disabled ? 0.4 : 1,
                  }}
                >
                  <b.Icon size={18} off={b.off} />
                  {b.badge ? (
                    <span style={{ position: "absolute", right: 3, bottom: 2, fontSize: 9, fontWeight: 700, lineHeight: 1 }}>{b.badge}</span>
                  ) : null}
                </button>
              ))}
            <button
              type="button"
              onClick={() => toggleDrawTool(drawTool)}
              aria-label="Done drawing"
              style={{
                height: 40,
                padding: "0 12px",
                flexShrink: 0,
                borderRadius: 6,
                border: `1px solid ${palette.activeBorder}`,
                background: palette.buttonActiveBackground,
                color: palette.shellText,
                fontWeight: 600,
              }}
            >
              Done
            </button>
          </div>
        ) : null}

        {/* Chart stage: the canvas plus everything drawn in chart coordinates (drawings, measure,
            replay line, time-zone chip, replay bar). In the Space layout it sits between the flush
            top bar and side rails; otherwise it fills the shell exactly as before. */}
        <div
          style={
            isSpaceChrome
              ? { position: "absolute", top: topBarHeight, left: SPACE_RAIL_W, right: SPACE_RAIL_W, bottom: 0 }
              : isMobile
                ? { position: "absolute", top: MOBILE_STAGE_TOP, left: 0, right: 0, bottom: MOBILE_STAGE_BOTTOM }
                : { display: "contents" } // no box: children lay out against the shell, exactly as before
          }
        >
        {chartBoxCount === 1 ? (
          <>
            <div
              ref={setChartContainerEl}
              style={{
                width: "100%",
                height: "100%",
                position: "relative",
                zIndex: 1,
              }}
            />
          </>
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "grid",
              boxSizing: "border-box",
              gap: "6px",
              padding:
                chartBoxCount > 1 && !isSpaceChrome
                  ? `${MULTI_GRID_PADDING_TOP}px ${MULTI_GRID_PADDING_RIGHT}px 6px 6px`
                  : "6px",
              gridTemplateColumns: `repeat(${CHART_BOX_LAYOUT[chartBoxCount].cols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${CHART_BOX_LAYOUT[chartBoxCount].rows}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: chartBoxCount }).map((_, index) => {
              const paneRaw = boxData[index] ?? [];
              const panePeriod = resolvedBarPeriodSeconds(
                paneRaw,
                chartTimeframeId,
                customBarSecondsResolved
              );
              const paneCandles = aggregateOhlcToPeriod(paneRaw, panePeriod);
              const mirrorPayload = getPriceChartPayload(priceSeriesStyle, paneCandles, {
                renkoBrickSize: renkoBrickApplied,
                renkoBrickMode,
                renkoAtrPeriod: renkoAtrPeriodApplied,
                renkoSource,
                rangeSize: rangeSizeApplied,
                rangeSource,
                lineBreakLines: lineBreakLinesApplied,
                kagiReversal: kagiReversalApplied,
                kagiMode,
                pnfBoxSize: pnfBoxApplied,
                pnfReversal: pnfReversalApplied,
                linePriceSource,
                lineChartLineType,
                baselinePriceInput,
              });
              const isSelectedPane = selectedBoxIndex === index;
              return (
                <div
                  key={`chart-box-${index}`}
                  role="presentation"
                  onPointerDown={() => setSelectedBoxIndex(index)}
                  style={{
                    border: isSelectedPane
                      ? `2px solid ${palette.activeBorder}`
                      : `1px solid ${palette.borderStrong}`,
                    borderRadius: "12px",
                    overflow: "hidden",
                    minHeight: 0,
                    minWidth: 0,
                    position: "relative",
                    background: chartBackgroundColor,
                    display: "flex",
                    flexDirection: "column",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedBoxIndex(index)}
                    style={{
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "4px 8px",
                      margin: 0,
                      border: "none",
                      borderBottom: `1px solid ${palette.borderStrong}`,
                      background: isSelectedPane
                        ? palette.buttonActiveBackground
                        : palette.chipBackground,
                      color: palette.shellText,
                      fontSize: "11px",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    {index === 0 ? (
                      <span>
                        {liveRatioSymbols
                          ? `${liveRatioSymbols.leg1}/${liveRatioSymbols.leg2}`
                          : marketSymbol}
                      </span>
                    ) : (
                      <select
                        value={
                          paneFileUploadRef.current.has(index)
                            ? "__file__"
                            : resolvedPaneSymbols[index] || ""
                        }
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          setSelectedBoxIndex(index);
                        }}
                        onChange={(e) => {
                          if (e.target.value !== "__file__") setPaneSymbol(index, e.target.value);
                        }}
                        title={`Symbol for pane ${index + 1}`}
                        aria-label={`Symbol for pane ${index + 1}`}
                        style={{
                          // Right end of the header: the left edge can sit under the drawing toolbar.
                          order: 2,
                          maxWidth: "60%",
                          background: "transparent",
                          color: palette.shellText,
                          border: `1px solid ${palette.borderStrong}`,
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          fontFamily: "inherit",
                          padding: "1px 4px",
                          cursor: "pointer",
                        }}
                      >
                        {paneFileUploadRef.current.has(index) ? (
                          <option value="__file__">Uploaded file</option>
                        ) : null}
                        {(marketSymbols.includes(resolvedPaneSymbols[index])
                          ? marketSymbols
                          : [resolvedPaneSymbols[index], ...marketSymbols]
                        )
                          .filter(Boolean)
                          .map((sym) => (
                            <option key={sym} value={sym} style={{ color: "#111" }}>
                              {sym}
                            </option>
                          ))}
                      </select>
                    )}
                    {isSelectedPane ? (
                      <span style={{ fontSize: "10px", opacity: 0.9 }}>upload target</span>
                    ) : (
                      <span style={{ fontSize: "10px", opacity: 0.55 }}>select</span>
                    )}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      position: "relative",
                    }}
                  >
                    {index === 0 ? (
                      <div
                        ref={setChartContainerEl}
                        style={{
                          width: "100%",
                          height: "100%",
                          position: "relative",
                        }}
                      />
                    ) : (
                      <ChartMirrorPane
                        key={`mirror-${index}-${priceSeriesStyle}`}
                        fitKey={`${resolvedPaneSymbols[index] || ""}:${backendFeedTimeframe}:${paneFileUploadRef.current.has(index) ? "file" : "live"}`}
                        priceChartPayload={mirrorPayload}
                        priceSeriesStyle={priceSeriesStyle}
                        chartBackgroundColor={chartBackgroundColor}
                        bullishCandleBodyColor={bullishCandleBodyColor}
                        bearishCandleBodyColor={bearishCandleBodyColor}
                        bullishCandleBorderColor={bullishCandleBorderColor}
                        bearishCandleBorderColor={bearishCandleBorderColor}
                        bullishCandleWickColor={bullishCandleWickColor}
                        bearishCandleWickColor={bearishCandleWickColor}
                        shellText={palette.shellText}
                        gridLine={palette.gridLine}
                        scaleBorder={palette.scaleBorder}
                        crosshairVert={palette.crosshairVert}
                        crosshairHorz={palette.crosshairHorz}
                        chartCrosshairMode={chartCrosshairMode}
                        mainPriceScaleMode={mainPriceScaleMode}
                        chartTimeZone={chartTimeZone}
                        yAxisSide={yAxisSide}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isMobile && (
        <label
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            right: yAxisSide === "right" ? "68px" : "12px",
            bottom: "44px",
            zIndex: 4,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            maxWidth: "min(320px, calc(100% - 120px))",
            padding: "6px 8px 6px 10px",
            borderRadius: "12px",
            border: `1px solid ${palette.borderSoft}`,
            background: palette.overlayBackground,
            backdropFilter: "blur(14px)",
            boxShadow: "0 12px 28px rgba(2, 6, 23, 0.28)",
            color: palette.shellText,
          }}
          title="Chart timezone"
        >
          <span
            style={{
              fontSize: "0.62rem",
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: palette.mutedText,
              textTransform: isSpaceChrome ? "none" : "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            TZ
          </span>
          <select
            value={chartTimeZone}
            onChange={(e) => setChartTimeZone(e.target.value)}
            style={{
              appearance: "none",
              border: "none",
              outline: "none",
              background: "transparent",
              color: palette.shellText,
              fontSize: "0.74rem",
              fontWeight: 700,
              minWidth: "170px",
              maxWidth: "250px",
              cursor: "pointer",
            }}
            aria-label="Chart timezone"
          >
            {chartTimeZoneOptions.map((timeZone) => (
              <option key={`chart-tz-bottom-${timeZone.value}`} value={timeZone.value}>
                {timeZone.label}
              </option>
            ))}
          </select>
        </label>
        )}

        <DrawingLayer
          projected={projectedDrawings}
          drawings={liveDrawings}
          plot={drawingPlot}
          interactive={drawTool === "none" && !isMeasureMode && !isReplaySelecting}
          selectedId={selectedDrawingId}
          onSelect={setSelectedDrawingId}
          onChange={updateDrawing}
          onDelete={deleteDrawing}
          onDuplicate={duplicateDrawing}
          toChartPoint={drawingToChartPoint}
          palette={palette}
          formatPrice={formatPrice}
          fibLevels={FIB_RETRACEMENT_LEVELS}
          fibExtLevels={FIB_EXTENSION_LEVELS}
          isMobile={isMobile}
          tool={isMeasureMode || isReplaySelecting ? "none" : drawTool}
          hasPendingPoint={Boolean(drawStartPoint)}
          onPlace={handleDrawingClick}
          onHover={syncDrawingCrosshair}
          onCreate={addDrawing}
          onCancel={cancelDrawing}
          onEditStart={beginDrawingEdit}
          onEditEnd={endDrawingEdit}
        />

        {replayLineOverlay ? (
          <>
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: replayLineOverlay.x,
                borderLeft: replayLineOverlay.isPreview
                  ? `2px dashed ${palette.drawLinePreview}`
                  : `2px solid ${palette.measureLine}`,
                pointerEvents: "none",
                zIndex: 3,
                opacity: replayLineOverlay.isPreview ? 0.95 : 0.9,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: replayLineOverlay.x,
                top: "88px",
                transform: "translateX(-50%)",
                background: replayLineOverlay.isPreview
                  ? palette.replayBackground
                  : palette.overlayBackground,
                color: palette.shellText,
                border: `1px solid ${palette.borderStrong}`,
                borderRadius: "999px",
                padding: "6px 10px",
                fontSize: "11px",
                fontWeight: 800,
                lineHeight: 1.1,
                pointerEvents: "none",
                whiteSpace: "nowrap",
                zIndex: 3,
                boxShadow: "0 10px 24px rgba(2, 6, 23, 0.18)",
              }}
            >
              {replayLineOverlay.isPreview ? "Replay Start" : "Replay"}
            </div>
          </>
        ) : null}

        {measurementOverlay?.line ? (
          <div
            style={{
              position: "absolute",
              left: measurementOverlay.line.left,
              top: measurementOverlay.line.top,
              width: measurementOverlay.line.width,
              height: "2px",
              background: palette.measureLine,
              transform: `rotate(${measurementOverlay.line.angle}deg)`,
              transformOrigin: "0 0",
              pointerEvents: "none",
              opacity: 0.9,
              zIndex: 3,
            }}
          />
        ) : null}

        {measurementOverlay?.points?.map((point) => (
          <div
            key={point.id}
            style={{
              position: "absolute",
              left: point.x - 7,
              top: point.y - 7,
              width: "14px",
              height: "14px",
              borderRadius: "999px",
              background: palette.markerGradient,
              border: "2px solid rgba(255,255,255,0.9)",
              boxShadow: isWhiteTheme || isBlackTheme
                ? "0 0 18px rgba(255, 255, 255, 0.22)"
                : "0 0 18px rgba(103, 232, 249, 0.55)",
              pointerEvents: "none",
              boxSizing: "border-box",
              zIndex: 3,
            }}
          />
        ))}

        {measurementOverlay?.label ? (
          <div
            style={{
              position: "absolute",
              left: measurementOverlay.label.left,
              top: measurementOverlay.label.top,
              transform: "translate(-50%, -100%)",
              background: palette.measureLabelBackground,
              color: palette.measureLabelText,
              padding: "8px 12px",
              borderRadius: "999px",
              fontSize: "12px",
              fontWeight: 800,
              lineHeight: 1.2,
              pointerEvents: "none",
              whiteSpace: "nowrap",
              zIndex: 3,
            }}
          >
            {measurementOverlay.label.text}
          </div>
        ) : null}

        {measurePoints.length ? (
          <div
            style={{
              position: "absolute",
              left: "78px",
              top: "74px",
              zIndex: 2,
              background: palette.overlayBackground,
              border: `1px solid ${palette.borderStrong}`,
              borderRadius: "14px",
              padding: "11px 12px",
              textAlign: "left",
              fontSize: "12px",
              lineHeight: 1.5,
              pointerEvents: "none",
              color: palette.shellText,
              minWidth: "220px",
              boxShadow: "0 18px 40px rgba(2, 6, 23, 0.36)",
            }}
          >
            <div>
              <strong>Measure</strong>
            </div>
            <div>Start: {formatTimestamp(measurePoints[0]?.time, chartTimeZone)}</div>
            <div>Start Price: {formatPrice(measurePoints[0]?.price)}</div>
            {activeMeasurePoints[1] ? (
              <>
                <div>End: {formatTimestamp(activeMeasurePoints[1]?.time, chartTimeZone)}</div>
                <div>End Price: {formatPrice(activeMeasurePoints[1]?.price)}</div>
              </>
            ) : (
              <div>Pick the second point on the chart.</div>
            )}
          </div>
        ) : null}

        {(isReplayMode || isReplaySelecting) ? (
          <div
            style={{
              ...toolbarStyle,
              left: isMobile ? "14px" : "230px",
              right: "14px",
              bottom: "14px",
              minHeight: "52px",
              ...(isSpaceChrome
                ? { left: "50%", right: "auto", transform: "translateX(-50%)", maxWidth: "calc(100% - 24px)", border: "1px solid #e3c46a", borderRadius: 6, padding: "4px 6px" }
                : null),
              ...(isMobile
                ? { flexWrap: "nowrap", overflowX: "auto", WebkitOverflowScrolling: "touch" }
                : null),
            }}
          >
            <button
              type="button"
              onClick={enterReplaySelection}
              style={iconButtonStyle(isReplayMode || isReplaySelecting)}
              title="Replay mode"
            >
              R
            </button>
            {isReplaySelecting ? (
              <>
                <div style={goToDateBoxStyle}>
                  <label style={goToDateDisplayStyle}>
                    <span
                      style={{
                        fontSize: "0.68rem",
                        letterSpacing: "0.12em",
                        textTransform: isSpaceChrome ? "none" : "uppercase",
                        color: palette.accentText,
                        whiteSpace: "nowrap",
                      }}
                    >
                      ST
                    </span>
                    <input
                      type="date"
                      value={replayStartDate}
                      onChange={(e) => setReplayStartDate(e.target.value)}
                      style={{
                        ...inputStyle,
                        minHeight: "unset",
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        borderRadius: 0,
                        colorScheme: "dark",
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const startIndex = getReplayStartIndex(replayStartDate);
                      replayAnchorIndexRef.current = startIndex;
                      beginReplay(startIndex);
                    }}
                    style={{
                      ...iconButtonStyle(),
                      borderRadius: 0,
                      minWidth: "54px",
                      borderTop: "none",
                      borderRight: "none",
                      borderBottom: "none",
                      borderLeft: `1px solid ${palette.borderStrong}`,
                    }}
                  >
                    GO
                  </button>
                </div>
                <div style={compactLabelStyle}>Click candle to arm replay</div>
                {Number.isInteger(replayPreviewIndex) ? (
                  <div style={compactLabelStyle}>
                    {formatTimestamp(data[replayPreviewIndex]?.time, chartTimeZone)}
                  </div>
                ) : null}
              </>
            ) : null}
            {isReplayMode ? (
              <>
                <button
                  type="button"
                  onMouseDown={handleReplayAdvancePressStart}
                  onMouseUp={handleReplayAdvancePressEnd}
                  onMouseLeave={handleReplayAdvancePressEnd}
                  onTouchStart={handleReplayAdvancePressStart}
                  onTouchEnd={handleReplayAdvancePressEnd}
                  onTouchCancel={handleReplayAdvancePressEnd}
                  style={iconButtonStyle(isReplayPlaying)}
                  title="Play replay"
                >
                  {isReplayPlaying ? "||" : ">"}
                </button>
                <button
                  type="button"
                  onClick={stepReplayForward}
                  style={iconButtonStyle()}
                  title="Step replay"
                >
                  +1
                </button>
                <div style={compactLabelStyle}>{replayDateLabel}</div>
                <button
                  type="button"
                  onClick={stopReplay}
                  style={iconButtonStyle(false, "red")}
                  title="Stop replay"
                >
                  X
                </button>
              </>
            ) : null}
          </div>
        ) : null}
        </div>
        {backtestContextMenu ? createPortal(
          <div
            data-backtest-context-menu
            role="menu"
            aria-label="Backtest chart options"
            style={{
              position: "fixed",
              left: backtestContextMenu.x,
              top: backtestContextMenu.y,
              zIndex: 10000,
              minWidth: 224,
              padding: 4,
              border: `1px solid ${palette.borderStrong}`,
              borderRadius: 8,
              background: palette.chartPanelBackground,
              boxShadow: "0 12px 32px rgba(0,0,0,0.42)",
            }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                clearBacktestOverlayFromMain();
                setBacktestContextMenu(null);
              }}
              style={{
                width: "100%",
                padding: "9px 12px",
                border: 0,
                borderRadius: 5,
                background: "transparent",
                color: palette.shellText,
                textAlign: "left",
                font: "inherit",
                cursor: "pointer",
              }}
            >
              Don’t show on main chart
            </button>
          </div>,
          document.body
        ) : null}
      </div>
    </div>
  );
};

export default ExcelChart;
